import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

// Default translations
const defaultTranslations = {
  en: {
    // Login Page
    'login.title': 'Matchmaker Dashboard',
    'login.subtitle': 'Sign in to your account',
    'login.email': 'Email',
    'login.phoneOtp': 'Phone OTP',
    'login.emailAddress': 'Email Address',
    'login.enterEmail': 'Enter your email',
    'login.password': 'Password',
    'login.enterPassword': 'Enter your password',
    'login.signingIn': 'Signing in...',
    'login.signIn': 'Sign In',
    'login.phoneNumber': 'Phone Number',
    'login.enterPhoneNumber': 'Enter phone number',
    'login.phoneFormat': 'Enter your phone number without country code',
    'login.searchCountry': 'Search country...',
    'login.sendingOtp': 'Sending OTP...',
    'login.sendOtp': 'Send OTP',
    'login.otpSent': 'OTP sent to your phone number',
    'login.otpFailed': 'Failed to send OTP. Please try again.',
    'login.enterOtp': 'Enter OTP',
    'login.otpPlaceholder': 'Enter 6-digit OTP',
    'login.resendIn': 'Resend OTP in',
    'login.resendOtp': 'Resend OTP',
    'login.verifying': 'Verifying...',
    'login.verifySignIn': 'Verify & Sign In',
    'login.invalidOtp': 'Invalid OTP. Please try again.',
    'login.enterValidPhone': 'Please enter a valid phone number',
    'login.enterValidOtp': 'Please enter a valid OTP',
    'login.change': 'Change',
    'login.noAccount': "Don't have an account?",
    'login.createOne': 'Create one',

    // Common
    'common.search': 'Search',
    'common.save': 'Save',
    'common.saveChanges': 'Save Changes',
    'common.saving': 'Saving...',
    'common.selected': 'Selected',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.view': 'View',
    'common.create': 'Create',
    'common.add': 'Add',
    'common.close': 'Close',
    'common.confirm': 'Confirm',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.loading': 'Loading...',
    'common.noData': 'No data available',
    'common.actions': 'Actions',
    'common.status': 'Status',
    'common.date': 'Date',
    'common.name': 'Name',
    'common.email': 'Email',
    'common.phone': 'Phone',
    'common.location': 'Location',
    'common.total': 'Total',
    'common.all': 'All',
    'common.active': 'Active',
    'common.inactive': 'Inactive',
    'common.pending': 'Pending',
    'common.approved': 'Approved',
    'common.rejected': 'Rejected',
    'common.success': 'Success',
    'common.error': 'Error',
    'common.warning': 'Warning',
    'common.info': 'Info',
    'common.viewAll': 'View All',
    'common.type': 'Type',
    'common.created': 'Created',
    'common.updated': 'Updated',
    'common.details': 'Details',
    'common.submit': 'Submit',
    'common.reset': 'Reset',
    'common.filter': 'Filter',
    'common.filters': 'Filters',
    'common.clearFilters': 'Clear Filters',
    'common.applyFilters': 'Apply Filters',
    'common.sortBy': 'Sort by',
    'common.newestFirst': 'Newest First',
    'common.oldestFirst': 'Oldest First',
    'common.nameAZ': 'Name A-Z',
    'common.nameZA': 'Name Z-A',
    'common.recentlyUpdated': 'Recently Updated',
    'common.loadMore': 'Load More',
    'common.showingOf': 'Showing {showing} of {total}',
    'common.noResults': 'No results found',
    'common.noDetails': 'No details',
    'common.page': 'Page',
    'common.previous': 'Prev',
    'common.remove': 'Remove',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.or': 'or',
    'common.and': 'and',
    'common.title': 'Title',
    'common.description': 'Description',
    'common.endDate': 'End Date',
    'common.copy': 'Copy',
    'common.copied': 'Copied!',
    'common.saving': 'Saving...',
    'common.selected': 'selected',
    'common.rows': 'Rows',
    'common.typeToSearch': 'Type to search...',
    'common.creating': 'Creating...',
    'common.uploading': 'Uploading...',

    // Navigation / Sidebar
    'nav.dashboard': 'Dashboard',
    'nav.profiles': 'Profiles',
    'nav.assignmentRequests': 'Assignment Requests',
    'nav.directory': 'Directory',
    'nav.matches': 'Matches',
    'nav.datingSchedules': 'Dating Schedules',
    'nav.videoMeetings': 'Video Meetings',
    'nav.audioCalls': 'Audio Calls',
    'nav.connections': 'Connections',
    'nav.messages': 'Messages',
    'nav.notifications': 'Notifications',
    'nav.tasks': 'Tasks',
    'nav.calendar': 'Calendar',
    'nav.affiliate': 'Affiliate',
    'nav.training': 'Training',
    'nav.billing': 'Billing',
    'nav.settings': 'Settings',
    'nav.admin': 'Administration',
    'nav.logout': 'Logout',
    'nav.mainMenu': 'Main Menu',
    'roles.coach': 'Coach',
    'roles.leaderCoach': 'Leader Coach',
    'roles.admin': 'Admin',
    'roles.supervisor': 'Supervisor',
    'roles.matchmaker': 'Matchmaker',
    'nav.collapse': 'Collapse',
    'nav.expand': 'Expand',
    'nav.searchPages': 'Search pages...',
    
    // Admin Navigation
    'nav.adminDashboard': 'Admin Dashboard',
    'nav.users': 'Users',
    'nav.roles': 'Roles',
    'nav.allProfiles': 'All Profiles',
    'nav.profileEditReviews': 'Profile Edit Reviews',
    'nav.customFields': 'Custom Fields',
    'nav.coaches': 'Coaches',
    'nav.adminDatingSchedules': 'Dating Schedules',
    'nav.adminMatches': 'Matches',
    'nav.trends': 'Trends',
    'nav.adminBilling': 'Billing',
    'nav.finance': 'Finance',
    'nav.upgradeRequests': 'Upgrade Requests',
    'nav.affiliates': 'Affiliates',
    'nav.plans': 'Plans',
    'nav.autoResponseManager': 'Auto-Response Manager',
    'nav.automationSettings': 'Automation Settings',
    'nav.starsignSettings': 'Star Sign Settings',
    'nav.starsignReports': 'Star-sign Reports',
    'nav.platformSettings': 'Platform Settings',
    'nav.translations': 'Translations',
    'nav.databaseSync': 'Database Sync',
    'nav.apiTokens': 'API Tokens',
    'nav.kpiManagement': 'KPI Management',
    'nav.coachKpi': 'KPI Performance',
    'nav.leaderCoachKpiPerformance': 'KPI Performance',
    'nav.payroll': 'Payroll',
    'nav.payslip': 'Payslip',
    'nav.myPayroll': 'My Payroll',
    'nav.leaderCoachReview': 'KPI Review',
    'nav.leaderCoachKpi': 'KPI Review',
    'nav.supervisorKpi': 'KPI Review',
    'nav.activityLogs': 'Activity Logs',
    'nav.signedDocuments': 'Signed Documents',
    'nav.home': 'Home',
    'nav.profile': 'Profile',
    'nav.search': 'Search',
    'nav.searchPlaceholder': 'Search profiles, trends, messages...',
    'nav.searchProfiles': 'Profiles',
    'nav.searchTrends': 'Trends',
    'nav.searchMessages': 'Messages',
    'nav.searchNoResults': 'No results found',
    'nav.searchViewAll': 'View all results',
    'nav.myAccount': 'My Account',

    // Weather
    'weather.clear': 'Clear',
    'weather.cloudy': 'Cloudy',
    'weather.foggy': 'Foggy',
    'weather.drizzle': 'Drizzle',
    'weather.rain': 'Rain',
    'weather.snow': 'Snow',
    'weather.showers': 'Showers',
    'weather.snowfall': 'Snowfall',
    'weather.thunderstorm': 'Thunderstorm',
    'weather.feelsLike': 'Feels',

    // Dashboard
    'dashboard.title': 'Dashboard',
    'dashboard.welcome': 'Welcome back',
    'dashboard.subtitle': "Here's what's happening with your matchmaking business today.",
    'dashboard.totalProfiles': 'Total Profiles',
    'dashboard.activeMatches': 'Active Matches',
    'dashboard.successfulMatches': 'Successful Matches',
    'dashboard.pendingTasks': 'Pending Tasks',
    'dashboard.upcomingEvents': 'Upcoming Events',
    'dashboard.todayEvents': "Today's Events",
    'dashboard.todaysTasks': "Today's Tasks",
    'dashboard.recentActivity': 'Recent Activity',
    'dashboard.recentProfiles': 'Recent Profiles',
    'dashboard.recentMatches': 'Recent Matches',
    'dashboard.pendingKpiApprovals': 'KPI Pending Approvals',
    'dashboard.noApprovalsPending': 'No Pending Approvals',
    'dashboard.allApprovalsUpToDate': 'All stage completion requests have been reviewed.',
    'dashboard.review': 'Review',
    'dashboard.quickActions': 'Quick Actions',
    'dashboard.thisMonth': 'this month',
    'dashboard.upcomingReminder': 'Upcoming Schedule',
    'dashboard.itemsUpcoming': 'items coming up',
    'dashboard.viewKPI': 'View KPI',
    'dashboard.noUpcomingItems': 'No upcoming items',

    // Profiles
    'profiles.title': 'Profiles',
    'profiles.subtitle': 'Manage your client profiles',
    'profiles.addProfile': 'Add Profile',
    'profiles.createProfile': 'Create Profile',
    'profiles.editProfile': 'Edit Profile',
    'profiles.deleteProfile': 'Delete Profile',
    'profiles.viewProfile': 'View Profile',
    'profiles.searchPlaceholder': 'Search by name, email, ID, offline ID...',
    'profiles.noProfiles': 'No profiles found',
    'profiles.noProfilesDesc': 'There are no profiles to display yet.',
    'profiles.startByAdding': 'Start by adding your first profile.',
    'profiles.firstName': 'First Name',
    'profiles.lastName': 'Last Name',
    'profiles.gender': 'Gender',
    'profiles.dateOfBirth': 'Date of Birth',
    'profiles.age': 'Age',
    'profiles.bio': 'Bio',
    'profiles.occupation': 'Occupation',
    'profiles.education': 'Education',
    'profiles.male': 'Male',
    'profiles.female': 'Female',
    'profiles.other': 'Other',
    'profiles.assignCoach': 'Assign Coach',
    'profiles.layoutEditor': 'Layout Editor',
    'profiles.profileType': 'Profile Type',
    'profiles.chooseCoach': 'Choose a coach...',
    'profiles.allTypes': 'All Types',
    'profiles.lead': 'Lead',
    'profiles.member': 'Member',
    'profiles.allGenders': 'All Genders',
    'profiles.status': 'Status',
    'profiles.allStatuses': 'All Statuses',
    'profiles.active': 'Active',
    'profiles.inactive': 'Inactive',
    'profiles.archived': 'Archived',
    'profiles.statusActive': 'Active',
    'profiles.statusInactive': 'Inactive',
    'profileForm.profileStatus': 'Profile Status',
    'profiles.minAge': 'Min Age',
    'profiles.maxAge': 'Max Age',
    'profiles.membershipPlan': 'Membership Plan',
    'profiles.allPlans': 'All Plans',
    'profiles.approvalStatus': 'Approval Status',
    'profiles.allApprovalStatuses': 'All',
    'profiles.approvalPending': 'Pending',
    'profiles.approvalApproved': 'Approved',
    'profiles.approvalRejected': 'Rejected',
    'profiles.maritalStatus': 'Marital Status',
    'profiles.allMaritalStatuses': 'All',
    'profiles.neverMarried': 'Never Married',
    'profiles.divorced': 'Divorced',
    'profiles.divorcedPending': 'Divorced (Pending)',
    'profiles.widowed': 'Widowed',
    'profiles.separated': 'Separated',
    'profiles.minHeight': 'Min Height',
    'profiles.maxHeight': 'Max Height',
    'profiles.heightRange': 'Height Range (cm)',
    'profiles.ageRange': 'Age Range',
    'profiles.country': 'Country',
    'profiles.state': 'State',
    'profiles.city': 'City',
    'directory.occupation': 'Occupation',
    'directory.profileType': 'Profile Type',
    'directory.maritalStatus': 'Marital Status',
    'directory.country': 'Country',
    'directory.state': 'State',
    'directory.city': 'City',
    'directory.minHeight': 'Min Height',
    'directory.maxHeight': 'Max Height',
    'profiles.name': 'Name',
    'profiles.type': 'Type',
    'profiles.membership': 'Membership',
    'profiles.location': 'Location',
    'profiles.created': 'Created',
    'profiles.photos': 'Photos',
    'profiles.yearsOld': 'years old',
    'profiles.coach': 'Coach',
    'profiles.myClient': 'My Client',
    'profiles.myAssignments': 'My Assignments',
    'profiles.requestAssignment': 'Request Profile Assignment',
    'profiles.requestAssignmentDesc': 'Submit a request to be assigned this profile. An admin will review and approve or reject your request.',
    'profiles.submitRequest': 'Submit Request',
    'profiles.submitting': 'Submitting...',
    'profiles.pending': 'Pending',
    'profiles.request': 'Request',
    'profiles.ownClientDisabled': 'This is your own client',
    'profiles.notesOptional': 'Notes (optional)',
    'profiles.notesPlaceholder': 'Reason for requesting this profile...',
    'profiles.noEmail': 'No email',
    'profiles.backToProfiles': 'Back to Profiles',
    'profiles.myAssignmentRequests': 'My Assignment Requests',
    'profiles.trackRequests': 'Track your profile assignment requests and their status',
    'profiles.sentRequests': 'Sent Requests',
    'profiles.receivedRequests': 'Received Requests',
    'profiles.requestedBy': 'Requested By',
    'profiles.requestTo': 'Request To',
    'profiles.requestFrom': 'Request From',
    'profiles.total': 'Total',
    'profiles.accepted': 'Accepted',
    'profiles.rejected': 'Rejected',
    'profiles.searchByNameEmail': 'Search by profile name or email...',
    'profiles.requestCount': 'request(s)',
    'profiles.profile': 'Profile',
    'profiles.email': 'Email',
    'profiles.phone': 'Phone',
    'profiles.height': 'Height',
    'profiles.citizenship': 'Citizenship',
    'profiles.columns': 'Columns',
    'profiles.customizeColumns': 'Customize columns',
    'profiles.displayColumns': 'Display columns',
    'profiles.notes': 'Notes',
    'profiles.requested': 'Requested',
    'profiles.responded': 'Responded',
    'profiles.adminResponse': 'Admin Response',
    'profiles.noRequestsYet': 'No assignment requests yet. Go to Profiles to request assignments.',
    'profiles.noMatchFilter': 'No requests match your filter.',
    'profiles.unknown': 'Unknown',

    // Matches
    'matches.title': 'Matches',
    'matches.subtitle': 'Manage and track profile matches',
    'matches.viewOnly': 'View profile matches and their status',
    'matches.createMatch': 'Create Match',
    'matches.editMatch': 'Edit Match',
    'matches.deleteMatch': 'Delete Match',
    'matches.viewMatch': 'View Match',
    'matches.allMatches': 'All Matches',
    'matches.sentRequests': 'Sent Requests',
    'matches.receivedRequests': 'Received Requests',
    'matches.matchPercentage': 'Match Percentage',
    'matches.approve': 'Approve',
    'matches.reject': 'Reject',
    'matches.succeeded': 'Succeeded',
    'matches.failed': 'Failed',
    'matches.searchPlaceholder': 'Search by name...',
    'matches.allStatuses': 'All Statuses',
    'matches.noMatches': 'No matches found',

    // Directory
    'directory.title': 'Profile Directory',
    'directory.subtitle': 'Browse and discover profiles in our network',
    'directory.searchPlaceholder': 'Search by name, occupation...',
    'directory.noResults': 'No profiles found in directory',
    'directory.filters': 'Filters',
    'directory.clearFilters': 'Clear Filters',
    'directory.myFavorites': 'My Favorites',
    'directory.favorites': 'Favorites',
    'directory.favoriteProfiles': 'favorite profiles',

    // Dating Schedules
    'datingSchedules.title': 'Dating Schedules',
    'datingSchedules.subtitle': 'Manage dates for your clients',
    'datingSchedules.viewOnly': 'View scheduled dates for clients',
    'datingSchedules.scheduleNew': 'Schedule New Date',
    'datingSchedules.today': 'Today',
    'datingSchedules.thisWeek': 'This Week',
    'datingSchedules.completed': 'Completed',
    'datingSchedules.upcoming': 'Upcoming',
    // Dating Schedules Modal
    'datingSchedules.modalTitle': 'Schedule New Date',
    'datingSchedules.editModalTitle': 'Edit Schedule',
    'datingSchedules.yourClient': 'Your Client',
    'datingSchedules.selectYourClient': 'Select Your Client',
    'datingSchedules.selectYourClientPlaceholder': 'Select your client...',
    'datingSchedules.otherMatchmakerClient': "Other Matchmaker's Client",
    'datingSchedules.matchmaker': 'Matchmaker',
    'datingSchedules.client': 'Client',
    'datingSchedules.selectMatchmaker': 'Select matchmaker...',
    'datingSchedules.selectMatchmakerFirst': 'Select matchmaker first',
    'datingSchedules.dateTitle': 'Title',
    'datingSchedules.dateTitlePlaceholder': 'e.g., Coffee Date',
    'datingSchedules.time': 'Time',
    'datingSchedules.duration': 'Duration (min)',
    'datingSchedules.locationType': 'Location Type',
    'datingSchedules.locationName': 'Location Name',
    'datingSchedules.locationNamePlaceholder': 'e.g., Blue Bottle Coffee',
    'datingSchedules.address': 'Address',
    'datingSchedules.addressPlaceholder': 'Full address',
    'datingSchedules.notificationPreference': 'Notification Preference',
    'datingSchedules.notes': 'Notes',
    'datingSchedules.notesPlaceholder': 'Internal notes...',
    'datingSchedules.createSchedule': 'Create Schedule',
    'datingSchedules.updateSchedule': 'Update Schedule',
    'datingSchedules.restaurant': 'Restaurant',
    'datingSchedules.cafe': 'Cafe',
    'datingSchedules.bar': 'Bar',
    'datingSchedules.park': 'Park',
    'datingSchedules.other': 'Other',
    'datingSchedules.none': 'None',
    'datingSchedules.sms': 'SMS',

    // Connections
    'connections.title': 'Connections',
    'connections.subtitle': 'Find and connect with external matchmakers and companies',
    'connections.searchPlaceholder': 'Search connections...',
    'connections.addConnection': 'Add Connection',
    'connections.noConnections': 'No connections found',

    // Common additional
    'common.refresh': 'Refresh',
    'common.of': 'of',
    'common.update': 'Update',

    // Messages
    'messages.title': 'Messages',
    'messages.subtitle': 'Communicate with your clients and team',
    'messages.newMessage': 'New Message',
    'messages.noMessages': 'No messages yet',
    'messages.noMessagesYet': 'No messages yet',
    'messages.noConversations': 'No messages yet',
    'messages.newChat': 'New Chat',
    'messages.typeMessage': 'Type a message...',
    'messages.send': 'Send',
    'messages.conversations': 'Conversations',
    'messages.searchConversations': 'Search conversations...',

    // Tasks
    'tasks.title': 'Tasks',
    'tasks.subtitle': 'Manage your tasks and follow-ups',
    'tasks.createTask': 'Create Task',
    'tasks.newTask': 'New Task',
    'tasks.addTask': 'Add Task',
    'tasks.noTasks': 'No tasks found',
    'tasks.noTasksDesc': 'There are no tasks to display yet.',
    'tasks.dueDate': 'Due Date',
    'tasks.priority': 'Priority',
    'tasks.high': 'High',
    'tasks.medium': 'Medium',
    'tasks.low': 'Low',
    'tasks.completed': 'Completed',
    'tasks.inProgress': 'In Progress',
    'tasks.allCaughtUp': 'All caught up!',
    'tasks.noPendingToday': 'No pending tasks for today.',
    'tasks.noDueTime': 'No due time',
    'tasks.searchPlaceholder': 'Search tasks...',
    'tasks.allStatus': 'All Status',
    'tasks.pending': 'Pending',
    'tasks.allPriorities': 'All Priorities',
    'tasks.dueDateEarliest': 'Due Date (Earliest First)',
    'tasks.dueDateLatest': 'Due Date (Latest First)',
    'tasks.createdNewest': 'Created (Newest First)',
    'tasks.createdOldest': 'Created (Oldest First)',
    'tasks.priorityHighToLow': 'Priority (High to Low)',
    'tasks.priorityLowToHigh': 'Priority (Low to High)',
    'tasks.loadingTasks': 'Loading tasks...',
    'tasks.recurringTask': 'Recurring Task',
    'tasks.daily': 'Daily',
    'tasks.weekly': 'Weekly',
    'tasks.monthly': 'Monthly',
    'tasks.yearly': 'Yearly',
    'tasks.linkedProfile': 'Linked Profile',
    'tasks.searchProfile': 'Search profile...',
    'tasks.noProfile': 'No profile linked',
    'tasks.taskType': 'Type',
    'tasks.typeOneTime': 'One Time',
    'tasks.typePostMeeting': 'Post Meeting',
    'tasks.typeCheckIn': 'Check In',
    'tasks.typeProgressReview': 'Progress Review',
    'tasks.typeCustom': 'Custom',

    // Calendar
    'calendar.title': 'Calendar',
    'calendar.subtitle': 'Manage your appointments and events',
    'calendar.createEvent': 'Create Event',
    'calendar.newEvent': 'New Event',
    'calendar.noEvents': 'No events scheduled',
    'calendar.noUpcomingEvents': 'No upcoming events',
    'calendar.today': 'Today',
    'calendar.month': 'Month',
    'calendar.week': 'Week',
    'calendar.day': 'Day',
    'calendar.noReminder': 'No reminder',
    'calendar.addressOrLink': 'Address or meeting link',
    'calendar.upcomingEvents': 'Upcoming Events',
    'calendar.editEvent': 'Edit Event',
    'calendar.sun': 'SUN',
    'calendar.mon': 'MON',
    'calendar.tue': 'TUE',
    'calendar.wed': 'WED',
    'calendar.thu': 'THU',
    'calendar.fri': 'FRI',
    'calendar.sat': 'SAT',
    'calendar.eventTitle': 'Event Title',
    'calendar.eventType': 'Event Type',
    'calendar.date': 'Date',
    'calendar.startTime': 'Start Time',
    'calendar.endTime': 'End Time',
    'calendar.location': 'Location',
    'calendar.description': 'Description',
    'calendar.reminder': 'Reminder',
    'calendar.minutes': 'minutes before',
    'calendar.hour': 'hour before',
    'calendar.hours': 'hours before',
    'calendar.dayBefore': 'day before',
    'calendar.eventTypes.date': 'Date',
    'calendar.eventTypes.meeting': 'Meeting',
    'calendar.eventTypes.call': 'Call',
    'calendar.eventTypes.followup': 'Follow-up',
    'calendar.eventTypes.event': 'Event',
    'calendar.eventTypes.other': 'Other',
    'calendar.eventTypes.videoCall': 'Video Call',
    'calendar.agoraVideoMeeting': 'Agora Video Meeting',
    'calendar.generateLink': 'Generate Link',

    // Notifications
    'notifications.title': 'Notifications',
    'notifications.subtitle': 'Stay updated',
    'notifications.markAllRead': 'Mark All Read',
    'notifications.noNotifications': 'No notifications',
    'notifications.unread': 'unread notifications',
    'notifications.allCaughtUp': 'All caught up!',

    // Settings
    'settings.title': 'Settings',
    'settings.subtitle': 'Manage your account settings and preferences',
    'settings.profile': 'Profile Settings',
    'settings.account': 'Account Settings',
    'settings.notifications': 'Notification Settings',
    'settings.privacy': 'Privacy Settings',
    'settings.companySettings': 'Company Settings',
    'settings.profileInfo': 'Profile Information',
    'settings.securitySettings': 'Security Settings',
    'settings.changePassword': 'Change Password',
    'settings.twoFactorAuth': 'Two-Factor Authentication',
    'settings.authenticatorApp': 'Authenticator App',
    'settings.sessions': 'Sessions',
    'settings.currentSession': 'Current Session',
    'settings.notificationSettings': 'Notification Settings',
    'settings.newMatchAlerts': 'New Match Alerts',
    'settings.newMatchAlertsDesc': 'Get notified when new potential matches are found',
    'settings.messagesNotif': 'Messages',
    'settings.messagesNotifDesc': 'Get notified when you receive new messages',
    'settings.taskReminders': 'Task Reminders',
    'settings.taskRemindersDesc': 'Get reminders for upcoming tasks and deadlines',
    'settings.newsletter': 'Newsletter',
    'settings.newsletterDesc': 'Receive our monthly newsletter with tips and updates',
    'settings.instantMatchAlerts': 'Instant notifications for new matches',
    'settings.instantMessages': 'Instant notifications for new messages',
    'settings.pushTaskReminders': 'Get push notifications for task reminders',
    'settings.preferences': 'Preferences',
    'settings.theme': 'Theme',
    'settings.light': 'Light',
    'settings.dark': 'Dark',
    'settings.system': 'System',
    'settings.language': 'Language',
    'settings.timezone': 'Timezone',
    'settings.tabProfile': 'Profile',
    'settings.tabSecurity': 'Security',
    'settings.tabNotifications': 'Notifications',
    'settings.tabPreferences': 'Preferences',
    'settings.tabCompany': 'Company',
    'settings.companySettingsDesc': 'Configure your company information and branding',
    'settings.companyLogo': 'Company Logo',
    'settings.uploadLogo': 'Upload Logo',
    'settings.logoRecommended': 'Recommended: 200x200px, PNG or SVG',
    'settings.companyName': 'Company Name',
    'settings.enterCompanyName': 'Enter your company name',
    'settings.phoneNumber': 'Phone Number',
    'settings.emailAddress': 'Email Address',
    'settings.locationAddress': 'Location / Address',
    'settings.businessHours': 'Business Hours',
    'settings.to': 'to',
    'settings.closed': 'Closed',
    'settings.features': 'Features',
    'settings.meetingScheduler': 'Meeting Scheduler',
    'settings.meetingSchedulerDesc': 'Allow clients to book meetings with you',
    'settings.customBranding': 'Custom Branding',
    'settings.customBrandingDesc': 'Display your company logo and branding to clients',
    'settings.saving': 'Saving...',
    'settings.saveCompanySettings': 'Save Company Settings',
    'settings.profileInformation': 'Profile Information',
    'settings.profileInformationDesc': 'Update your personal information and public profile',
    'settings.changePhoto': 'Change Photo',
    'settings.uploadPhoto': 'Upload Photo',
    'settings.newPhotoSelected': 'New photo selected',
    'settings.photoRequirements': 'JPG, PNG or GIF. Max 2MB.',
    'settings.firstName': 'First Name',
    'settings.lastName': 'Last Name',
    'settings.email': 'Email',
    'settings.phone': 'Phone',
    'settings.bio': 'Bio',
    'settings.bioPlaceholder': 'Tell us about yourself...',
    'settings.saveChanges': 'Save Changes',
    'settings.securitySettingsDesc': 'Manage your password and security preferences',
    'settings.currentPassword': 'Current Password',
    'settings.newPassword': 'New Password',
    'settings.confirmNewPassword': 'Confirm New Password',
    'settings.updating': 'Updating...',
    'settings.updatePassword': 'Update Password',
    'settings.authenticatorAppDesc': 'Use an authenticator app for additional security',
    'settings.enable': 'Enable',
    'settings.active': 'Active',
    'settings.notificationSettingsDesc': 'Choose what notifications you receive',
    'settings.emailNotifications': 'Email Notifications',
    'settings.messages': 'Messages',
    'settings.messagesDesc': 'Get notified when you receive new messages',
    'settings.pushNotifications': 'Push Notifications',
    'settings.pushNewMatchDesc': 'Instant notifications for new matches',
    'settings.pushMessagesDesc': 'Instant notifications for new messages',
    'settings.pushTaskRemindersDesc': 'Get push notifications for task reminders',
    'settings.savePreferences': 'Save Preferences',
    'settings.preferencesDesc': 'Customize your experience',
    'settings.themeLight': 'Light',
    'settings.themeDark': 'Dark',
    'settings.themeSystem': 'System',
    'settings.dateFormat': 'Date Format',
    'settings.currency': 'Currency',

    // Billing
    'billing.title': 'Client Membership Billing',
    'billing.subtitle': 'Manage client memberships, invoices, and payments',
    'billing.invoices': 'Invoices',
    'billing.payments': 'Payments',
    'billing.balance': 'Balance',
    'billing.createInvoice': 'Create Invoice',
    'billing.loadingBilling': 'Loading billing information...',
    'billing.allStatus': 'All Status',
    'billing.pending': 'Pending',
    'billing.paid': 'Paid',
    'billing.overdue': 'Overdue',
    'billing.awaitingApproval': 'Awaiting Approval',
    'billing.cancelled': 'Cancelled',
    'billing.rejected': 'Rejected',
    'billing.membershipRejected': 'Membership Rejected',
    'billing.invoiceNumber': 'Invoice #',
    'billing.client': 'Client',
    'billing.plan': 'Plan',
    'billing.amount': 'Amount',
    'billing.dueDate': 'Due Date',
    'billing.status': 'Status',
    'billing.actions': 'Actions',
    'billing.deposit': 'Deposit',
    'billing.regular': 'Regular',
    'billing.frequency': 'Frequency',
    'billing.progress': 'Progress',
    'billing.nextBilling': 'Next Billing',
    'billing.paypalEmail': 'PayPal Email',
    'billing.businessName': 'Business Name',
    'billing.notConfigured': 'Not configured',
    'billing.zelleEmail': 'Zelle Email',
    'billing.memo': 'Memo',
    'billing.includeInvoiceNumber': 'Include your invoice number',
    'billing.bankName': 'Bank Name',
    'billing.accountName': 'Account Name',
    'billing.accountNumber': 'Account Number',
    'billing.routingNumber': 'Routing Number',
    'billing.makePayableTo': 'Make Payable To',
    'billing.instruction': 'Instruction',
    'billing.internalMatchmaker': 'Internal Matchmaker: Payment instructions are managed by Admin.',
    'billing.createClientInvoice': 'Create Client Invoice',
    'billing.chooseClient': 'Choose a client...',
    'billing.choosePlan': 'Choose a plan...',
    'billing.customSelectPlan': 'Custom / Select Plan',
    'billing.weekly': 'Weekly',
    'billing.biWeekly': 'Bi-Weekly',
    'billing.monthly': 'monthly',
    'billing.quarterly': 'Quarterly',
    'billing.yearly': 'yearly',
    'billing.unlimitedCycles': 'Unlimited cycles (until client unsubscribes)',
    'billing.includeServiceFee': 'Include service fee in payment calculations',
    'billing.regularPayment': 'Regular Payment',
    'billing.billingCycles': 'Billing Cycles',
    'billing.commissionPerPayment': 'Commission per Payment',
    'billing.pointsPerPayment': 'Points per Payment',
    'billing.editPaymentInstructions': 'Edit Payment Instructions',
    'billing.checking': 'Checking',
    'billing.savings': 'Savings',
    'billing.totalRevenue': 'Total Revenue',
    'billing.activeSubscriptions': 'Active Subscriptions',
    'billing.subscriptions': 'Subscriptions',
    'billing.paymentByStages': 'Payment by Stages',
    'billing.installmentProgress': 'Installment Progress',
    'billing.stagesProgress': 'Stages Progress',
    'billing.amountPaid': 'Amount Paid',
    'billing.dueBalance': 'Due Balance',
    'billing.submitPayment': 'Submit Payment',
    'billing.itemsPaid': 'items paid',
    'billing.stagesPaid': 'stages paid',
    'billing.noInstallmentInvoices': 'No installment invoices found',
    'billing.noStagesInvoices': 'No stage-based invoices found',
    'billing.manageStagePayments': 'Manage stage-based payment invoices',
    'billing.membershipPlans': 'Membership Plans',
    'billing.paymentInstructions': 'Payment Instructions',
    'billing.plansManagedByAdmin': 'Plans are managed by Admin for internal matchmakers',
    'billing.searchByClient': 'Search by client or invoice number...',
    'billing.whiteLabel': 'White Label',
    'billing.clientSubscriptions': 'Client Subscriptions',
    'billing.paySecurelyOnline': 'Pay securely online',
    'billing.instantBankTransfer': 'Instant bank transfer',
    'billing.directBankDeposit': 'Direct bank deposit',
    'billing.mailPayment': 'Mail payment',
    'billing.bankTransferWire': 'Bank Transfer / Wire',
    'billing.check': 'Check',
    'billing.paymentDetailsManagedByAdmin': 'Payment details are managed by Admin (Read-only)',
    'billing.configureOwnPayment': 'Configure your own payment details for client invoices',
    'billing.editPaymentInfo': 'Edit Payment Info',
    'billing.contactAdminPayment': 'Contact your administrator to update payment details.',
    'billing.addPlan': 'Add Plan',
    'billing.editPlan': 'Edit Plan',
    'billing.pendingAdminApproval': 'Pending admin approval',
    'billing.selectClient': 'Select Client',
    'billing.membershipPlan': 'Membership Plan',
    'billing.description': 'Description',
    'billing.recurringInvoice': 'Recurring Invoice',
    'billing.createAsSubscription': 'Create as Subscription',
    'billing.addSubscription': 'Add Subscription',
    'billing.manageRecurringMemberships': 'Manage recurring memberships with customizable billing',
    'billing.createSubscription': 'Create Subscription',
    'billing.editSubscription': 'Edit Subscription',
    'billing.updateSubscription': 'Update Subscription',
    'billing.planName': 'Plan Name',
    'billing.customPlanName': 'Custom plan name...',
    'billing.paymentSchedule': 'Payment Schedule',
    'billing.firstPaymentDeposit': '1st Payment / Deposit ($)',
    'billing.optionalPlaceholder': '0.00 (optional)',
    'billing.customizableInitialPayment': 'Customizable initial payment',
    'billing.regularPaymentRequired': 'Regular Payment ($) *',
    'billing.billingFrequency': 'Billing Frequency',
    'billing.startDate': 'Start Date',
    'billing.trialPeriodDays': 'Trial Period (Days)',
    'billing.totalBillingCycles': 'Total Billing Cycles',
    'billing.serviceFeePaypal': 'Service Fee (PayPal)',
    'billing.feePercentage': 'Fee Percentage (%)',
    'billing.fixedFee': 'Fixed Fee ($)',
    'billing.commissionPoints': 'Commission & Points',
    'billing.commissionRate': 'Commission Rate (%)',
    'billing.pointsPerPaymentLabel': 'Points per Payment',
    'billing.paymentSummary': 'Payment Summary',
    'billing.firstPaymentDepositSummary': '1st Payment (Deposit):',
    'billing.regularPaymentSummary': 'Regular Payment:',
    'billing.billingCyclesSummary': 'Billing Cycles:',
    'billing.commissionPerPaymentSummary': 'Commission per Payment:',
    'billing.pointsPerPaymentSummary': 'Points per Payment:',
    'billing.notes': 'Notes',
    'billing.optionalNotes': 'Optional notes...',
    'billing.serviceFeeNote': 'Commissions and points are calculated on amount BEFORE service fee deduction',
    'billing.payments': 'payments',
    'billing.unlimited': 'Unlimited',
    'billing.fee': 'fee',
    'billing.net': 'net',
    'billing.trialEnds': 'Trial ends:',
    'billing.pause': 'Pause',
    'billing.noInvoices': 'No invoices found',

    // Dashboard additional
    'dashboard.affiliateProgram': 'Affiliate Program',
    'dashboard.upgradeProgress': 'Upgrade Progress',
    'dashboard.viewDetails': 'View Details',
    'dashboard.basicPartner': 'Basic Partner',
    'dashboard.individualPartner': 'Individual Partner',
    'dashboard.ptsTo': 'pts to',
    'dashboard.referrals': 'Referrals',
    'dashboard.totalEarned': 'Total Earned',
    'dashboard.commissionRate': 'Commission Rate',
    'dashboard.createMatch': 'Create Match',
    'dashboard.matched': 'Matched',
    'dashboard.points': 'points',
    'dashboard.dailyTasksReminder': 'Daily Tasks Reminder',
    'dashboard.tasksNeedAttention': 'These tasks need your attention today',
    'dashboard.noTasksScheduled': 'No tasks scheduled for today!',
    'dashboard.addTaskSuggestion': 'Consider adding tasks to make your schedule productive.',
    'dashboard.addNewTask': 'Add New Task',
    'dashboard.gotIt': 'Got it',
    'dashboard.pendingApprovals': 'Pending Approvals',
    'dashboard.stagesAwaitingReview': 'stages/deliverables awaiting review',
    'dashboard.reviewApprovals': 'Review Approvals',
    'dashboard.overdue': 'Overdue',
    'dashboard.dueToday': 'Due Today',
    'dashboard.incompleteDeliverables': 'Incomplete Deliverables',
    'dashboard.allDeliverablesComplete': 'All Caught Up!',
    'dashboard.noIncompleteDeliverables': 'No incomplete deliverables at the moment.',
    'dashboard.rejected': 'Rejected',

    // Profiles additional
    'profiles.selectAll': 'Select All',
    'profiles.deselectAll': 'Deselect All',
    'profiles.client': 'CLIENT',
    'profiles.basicMembership': 'Basic Membership',
    'profiles.coach': 'Coach',
    'profiles.yearsOld': 'years old',
    'profiles.filterConditions': 'Filter Conditions',

    // Directory additional
    'directory.profilesFound': 'profiles found',
    'directory.active': 'ACTIVE',
    'directory.noFavorites': 'No favorites yet',
    'directory.backToDirectory': 'Back to Directory',
    'directory.selectProfileToMatch': 'Select a profile to match with',
    'directory.matchRequestSent': 'A matching request will be sent if the profile is managed by another matchmaker.',
    'directory.selectBelow': 'Select below',
    'directory.sendMatchRequest': 'Send Match Request',

    // Profile Detail
    'profileDetail.profileInfo': 'Profile Information',
    'profileDetail.contactInfo': 'Contact Information',
    'profileDetail.location': 'Location',
    'profileDetail.birthday': 'Birthday',
    'profileDetail.gender': 'Gender',
    'profileDetail.age': 'Age',
    'profileDetail.managedBy': 'Managed By',
    'profileDetail.assignedCoach': 'Assigned Coach',
    'profileDetail.about': 'About',
    'profileDetail.details': 'Details',
    'profileDetail.occupation': 'Occupation',
    'profileDetail.education': 'Education',
    'profileDetail.height': 'Height',
    'profileDetail.religion': 'Religion',
    'profileDetail.company': 'Company',
    'profileDetail.assignment': 'Assignment',
    'profileDetail.matchmaker': 'Matchmaker',
    'profileDetail.coach': 'Coach',
    'profileDetail.additionalDetails': 'Additional Details',
    'profileDetail.categoryGeneral': 'General Information',
    'profileDetail.categoryPersonal': 'Personal Details',
    'profileDetail.categoryFamily': 'Family Information',
    'profileDetail.categoryPreferences': 'Preferences',
    'profileDetail.categoryLifestyle': 'Lifestyle',
    'profileDetail.categoryBackground': 'Background',
    'profileDetail.categoryContact': 'Contact Information',
    'profileDetail.categoryCRM': 'CRM Management',
    'profileDetail.categoryOther': 'Other',
    'profileDetail.notSpecified': 'Not specified',
    'profileDetail.activity': 'Activity',
    'profileDetail.profileCreated': 'Profile created',
    'profileDetail.lastUpdated': 'Last updated',
    'profileDetail.noMatchesYet': 'No matches yet',
    'profileDetail.createMatchToConnect': 'Create a match to connect this profile with compatible partners.',
    'profileDetail.matched': 'Matched',
    'profileDetail.profileNotFound': 'Profile not found',
    'profileDetail.createMatchFor': 'Create Match for',
    'profileDetail.loadingProfiles': 'Loading profiles...',
    'profileDetail.matchSentSuccess': 'Match request sent successfully',
    'profileDetail.searchProfiles': 'Search profiles...',
    'profileDetail.tasks': 'Tasks',
    'profileDetail.noTasks': 'No tasks linked to this profile',
    'profileDetail.overdue': 'Overdue',
    'profileDetail.customInfo': 'Custom Information',

    // Profile Form (Add/Edit Profile Wizard)
    'profileForm.addNewProfile': 'Add New Profile',
    'profileForm.editProfile': 'Edit Profile',
    'profileForm.stepXofY': 'Step',
    'profileForm.of': 'of',
    'profileForm.next': 'Next',
    'profileForm.previous': 'Previous',
    'profileForm.saveProfile': 'Save Profile',
    'profileForm.saving': 'Saving...',
    'profileForm.loadingProfile': 'Loading profile...',
    'profileForm.confirmSubmit': 'Confirm & Submit',
    // Step Names
    'profileForm.stepInterviewer': 'Interviewer',
    'profileForm.stepBasicInfo': 'Basic Info',
    'profileForm.stepLocationBio': 'Location & Bio',
    'profileForm.stepLocation': 'Location',
    'profileForm.stepAdditionalDetails': 'Additional Details',
    'profileForm.stepBioGallery': 'About / Bio',
    'profileForm.stepPhotoID': 'Photo ID',
    'profileForm.stepMembershipPlan': 'Membership Plan',
    'profileForm.stepPaymentPlan': 'Payment Plan',
    'profileForm.stepContractSign': 'Contract & Sign',
    'profileForm.stepRecruiter': 'Recruiter',
    // Photo ID Step
    'profileForm.photoIDUpload': 'Photo ID Upload',
    'profileForm.photoIDRequired': 'Photo ID Upload (Optional)',
    'profileForm.photoIDNote': 'Please upload a clear photo of your government-issued ID for verification',
    'profileForm.errPhotoIDRequired': 'Photo ID is required',
    'profileForm.changePhotoID': 'Click to change',
    'profileForm.clickToUploadPhotoID': 'Click to upload Photo ID',
    'profileForm.acceptedFormats': 'Accepted formats: JPG, PNG, GIF',
    // Step 1: Interviewer Assignment
    'profileForm.interviewerAssignment': 'Interviewer Assignment',
    'profileForm.whoConductedInterview': 'Who Conducted the Interview?',
    'profileForm.selectMatchmakerInterview': 'Select the matchmaker who conducted the client interview',
    'profileForm.interviewer': 'Interviewer',
    'profileForm.selectInterviewer': 'Select interviewer...',
    'profileForm.myself': 'Myself',
    'profileForm.selfInterviewNote': 'You are assigned as the interviewer. The profile will be linked to your account.',
    // Step 2: Basic Information
    'profileForm.basicInformation': 'Basic Information',
    'profileForm.profilePhoto': 'Profile Photo',
    'profileForm.galleryPhotos': 'Gallery Photos',
    'profileForm.upload': 'Upload',
    'profileForm.firstName': 'First Name',
    'profileForm.lastName': 'Last Name',
    'profileForm.emailLabel': 'Email',
    'profileForm.phoneLabel': 'Phone',
    'profileForm.dateOfBirth': 'Date of Birth',
    'profileForm.birthTime': 'Birth Time (24-hour)',
    'profileForm.starSign': 'Star Sign',
    'profileForm.selectStarSign': 'Select...',
    'profileForm.aries': 'Aries ♈',
    'profileForm.taurus': 'Taurus ♉',
    'profileForm.gemini': 'Gemini ♊',
    'profileForm.cancer': 'Cancer ♋',
    'profileForm.leo': 'Leo ♌',
    'profileForm.virgo': 'Virgo ♍',
    'profileForm.libra': 'Libra ♎',
    'profileForm.scorpio': 'Scorpio ♏',
    'profileForm.sagittarius': 'Sagittarius ♐',
    'profileForm.capricorn': 'Capricorn ♑',
    'profileForm.aquarius': 'Aquarius ♒',
    'profileForm.pisces': 'Pisces ♓',
    'profileForm.showPhotos': 'Show photos on public profile',
    'profileForm.showAge': 'Show age on public profile',
    'profileForm.sharePublicProfile': 'Share Public Profile',
    'profileForm.showOnDirectory': 'Show on Directory',
    'profileForm.visibilitySettings': 'Visibility Settings',
    'profileForm.gender': 'Gender',
    'profileForm.selectGender': 'Select gender...',
    'profileForm.male': 'Male',
    'profileForm.female': 'Female',
    'profileForm.other': 'Other',
    'profileForm.profileType': 'Profile Type',
    'profileForm.lead': 'Lead',
    'profileForm.client': 'Client',
    'profileForm.member': 'Member',
    'profileForm.errFirstNameRequired': 'First name is required',
    'profileForm.errLastNameRequired': 'Last name is required',
    'profileForm.errInvalidEmail': 'Invalid email format',
    'profileForm.errEmailRequired': 'Email is required',
    'profileForm.errGenderRequired': 'Gender is required',
    'profileForm.errBirthDateRequired': 'Date of birth is required',
    'profileForm.errInvalidBirthDate': 'Please enter a valid date',
    'profileForm.errInvalidBirthTime': 'Please enter valid time (HH:MM)',
    'profileForm.errContractFirstNameRequired': 'First name is required',
    'profileForm.errContractLastNameRequired': 'Last name is required',
    'profileForm.errPleaseAgreeToTerms': 'Please agree to the contract terms',
    'profileForm.failedToLoadProfile': 'Failed to load profile',
    'profileForm.profileUpdatedSuccess': 'Profile updated successfully',
    'profileForm.profileCreatedSuccess': 'Profile created successfully',
    'profileForm.failedToSaveProfile': 'Failed to save profile',
    // Step 3: Location & Bio
    'profileForm.locationBio': 'Location & Bio',
    'profileForm.location': 'Location',
    'profileForm.city': 'City',
    'profileForm.stateProvince': 'State / Province',
    'profileForm.country': 'Country',
    'profileForm.bioAbout': 'About / Bio',
    'profileForm.bioPlaceholder': 'Tell us about yourself, your interests, and what you are looking for...',
    // Step 4: Additional Details (Custom Fields)
    'profileForm.additionalDetails': 'Additional Details',
    'profileForm.otherInformation': 'Other Information',
    'profileForm.noFieldsConfigured': 'No custom fields configured',
    'profileForm.noFieldsConfiguredDesc': 'Contact your administrator to set up custom profile fields.',
    // Step 5: Membership Plan
    'profileForm.selectMembershipPlan': 'Select Membership Plan',
    'profileForm.noPlansAvailable': 'No membership plans available',
    'profileForm.skipStepOrContactAdmin': 'You can skip this step or contact admin to create plans.',
    'profileForm.selected': 'Selected',
    'profileForm.createPlans': 'Please ask an administrator to create membership plans.',
    'profileForm.selectedPlanLabel': 'Selected Plan',
    'profileForm.planPrice': 'Plan Price',
    // Step 6: Payment Plan
    'profileForm.paymentPlan': 'Payment Plan',
    'profileForm.paymentType': 'Payment Type',
    'profileForm.payFullNow': 'Pay Full Amount Now',
    'profileForm.oneTimePayment': 'One-time payment of the full membership amount',
    'profileForm.deposit': 'Deposit + Monthly Installments',
    'profileForm.depositMonthly': 'Pay a deposit now and the rest in monthly installments',
    'profileForm.customDeposit': 'Custom Deposit Amount',
    'profileForm.depositAmount': 'Deposit Amount',
    'profileForm.errDepositRequired': 'Deposit amount is required',
    'profileForm.errDepositTooHigh': 'Deposit cannot exceed plan price',
    'profileForm.numMonthlyPayments': 'Number of Monthly Payments',
    'profileForm.monthlyLabel': 'monthly',
    'profileForm.months': 'months',
    'profileForm.firstPaymentDate': 'First Payment Date',
    'profileForm.includeServiceFee': 'Include Service Fee',
    'profileForm.paymentSummary': 'Payment Summary',
    'profileForm.paymentSchedule': 'Payment Schedule',
    'profileForm.installmentDetails': 'Installment Details',
    'profileForm.dueDate': 'Due Date',
    'profileForm.amount': 'Amount',
    'profileForm.typeCol': 'Type',
    'profileForm.total': 'Total',
    'profileForm.totalDueToday': 'Total Due Today',
    'profileForm.paymentsSavedNote': 'Payment schedule will be saved when the profile is submitted.',
    // Payment Stages
    'profileForm.stagesPayment': 'Payment by Stages',
    'profileForm.stagesPaymentDesc': 'Custom payment stages with tracking',
    'profileForm.paymentStages': 'Payment Stages',
    'profileForm.addStage': 'Add Stage',
    'profileForm.stage': 'Stage',
    'profileForm.stageName': 'Stage Name',
    'profileForm.status': 'Status',
    'profileForm.paid': 'Paid',
    'profileForm.unpaid': 'Unpaid',
    'profileForm.paidOn': 'Paid on',
    'profileForm.noStagesAdded': 'No payment stages added',
    'profileForm.clickAddStage': 'Click "Add Stage" to create custom payment milestones',
    'profileForm.stagesTotal': 'Stages Total',
    'profileForm.stagesPaid': 'stages paid',
    'profileForm.stagesCommissionNote': 'Commissions and points will be calculated when each stage is marked as paid by an admin or coach.',
    // Payment Method
    'profileForm.paymentMethod': 'Payment Method',
    'profileForm.onlinePayment': 'Auto Payment',
    'profileForm.onlinePaymentDesc': 'Automatic payment via PayPal',
    'profileForm.offlinePayment': 'Pay Manually',
    'profileForm.offlinePaymentDesc': 'Zelle, Bank Transfer, Check, or Cash',
    'profileForm.payWithPaypal': 'Pay with PayPal',
    'profileForm.paypalOneTimeNote': 'You will be redirected to PayPal to complete your one-time payment after submitting the profile.',
    'profileForm.paypalSubscriptionNote': 'You will be redirected to PayPal to set up recurring payments after submitting the profile.',
    'profileForm.paypalProcessAfterSubmit': 'PayPal payment will be processed after you submit the profile and sign the contract.',
    'profileForm.offlinePaymentType': 'Select Payment Type',
    'profileForm.bankTransfer': 'Bank Transfer',
    'profileForm.check': 'Check',
    'profileForm.cash': 'Cash',
    'profileForm.uploadPaymentProof': 'Upload Payment Proof',
    'profileForm.uploadPaymentProofDesc': 'Please upload photos or screenshots of your payment confirmation (receipt, transaction confirmation, etc.)',
    'profileForm.addPhoto': 'Add Photo',
    'profileForm.offlineVerificationNote': 'Offline payments require verification by an admin or coach. Your profile will be approved once payment is verified.',
    'profileForm.paymentPending': 'Payment Pending',
    'profileForm.paymentVerified': 'Payment Verified',
    'profileForm.awaitingVerification': 'Awaiting Verification',
    'profileForm.markAsPaid': 'Mark as Paid',
    'profileForm.confirmMarkPaid': 'Mark this stage as paid? This will calculate commissions for the interviewer and recruiter.',
    'profileForm.stageMarkedAsPaid': 'Payment stage marked as paid successfully',
    'profileForm.failedToMarkPaid': 'Failed to mark payment stage as paid',
    // Step 7: Recruiter
    'profileForm.recruiterAssignment': 'Recruiter Assignment',
    'profileForm.recruiterInfo': 'Recruiter Information',
    'profileForm.selectRecruiter': 'Select recruiter...',
    'profileForm.selectMatchmaker': 'Select matchmaker...',
    'profileForm.commissionSplit': 'Commission Split',
    'profileForm.commissionSplitDesc': 'Set how commission is split between interviewer and recruiter',
    'profileForm.commissionTier': 'Commission Tier',
    'profileForm.commissionTierDesc': 'The commission tier determines the total commission percentage',
    'profileForm.commissionRateByTier': 'Commission rate determined by tier',
    'profileForm.tierBased': 'Tier Based',
    'profileForm.interviewerCommissionPct': 'Interviewer Commission %',
    'profileForm.interviewerGets': 'Interviewer gets',
    'profileForm.youGet': 'You get',
    'profileForm.from': 'from',
    'profileForm.to': 'to',
    'profileForm.approvalNotice': 'Commission split changes may require admin approval.',
    // Step 8: Contract & Sign (Service Agreement)
    'profileForm.serviceAgreement': 'Service Agreement',
    'profileForm.downloadContract': 'Download Contract',
    'profileForm.downloadPdf': 'Download PDF',
    'profileForm.agreementTitle': 'Matchmaking Service Agreement',
    'profileForm.agreementIntro': 'This agreement is entered into between the client and the matchmaking service provider.',
    'profileForm.agreementS1Title': '1. Services',
    'profileForm.agreementS1': 'The Service Provider agrees to provide personalized matchmaking services to the Client, including profile creation, partner search, and introduction arrangements.',
    'profileForm.agreementS2Title': '2. Term',
    'profileForm.agreementS2': 'This agreement shall commence on the date of signing and continue for the duration of the selected membership plan.',
    'profileForm.agreementS3Title': '3. Fees',
    'profileForm.agreementS3': 'The Client agrees to pay the membership fees as outlined in the selected plan, according to the agreed payment schedule.',
    'profileForm.agreementS4Title': '4. Confidentiality',
    'profileForm.agreementS4': 'Both parties agree to maintain strict confidentiality regarding all personal information shared during the matchmaking process.',
    'profileForm.agreementS5Title': '5. Code of Conduct',
    'profileForm.agreementS5': 'The Client agrees to conduct themselves respectfully during all introductions and communications facilitated by the Service Provider.',
    'profileForm.agreementS6Title': '6. Limitation of Liability',
    'profileForm.agreementS6': 'The Service Provider makes no guarantees regarding specific outcomes. The service is one of introduction only.',
    'profileForm.agreementS7Title': '7. Termination',
    'profileForm.agreementS7': 'Either party may terminate this agreement with 30 days written notice. Refund policies apply as per the selected membership terms.',
    'profileForm.agreeToTerms': 'I agree to the terms and conditions above',
    'profileForm.agreeConfirm': 'By checking this box, you confirm that you have read and agree to the service agreement.',
    'profileForm.errAgreeToTerms': 'You must agree to the terms before proceeding',
    'profileForm.clickToSign': 'Click to Sign',
    'profileForm.signDate': 'Sign Date',
    'profileForm.errSignDate': 'Sign date is required',
    'profileForm.signatureRecorded': 'Signature recorded',
    'profileForm.agreementSigned': 'Agreement has been signed',
    'profileForm.signedAgreementAvailable': 'A signed agreement is available for this profile',

    // Match Detail
    'matchDetail.backToMatches': 'Back to Matches',
    'matchDetail.matchNotFound': 'Match not found',
    'matchDetail.youSentRequest': 'You sent this match request to',
    'matchDetail.requestFrom': 'Match request from',
    'matchDetail.anotherMatchmaker': 'another matchmaker',
    'matchDetail.viewFullProfile': 'View Full Profile',
    'matchDetail.matchTimeline': 'Match Timeline',
    'matchDetail.matchCreated': 'Match Created',
    'matchDetail.matchNotes': 'Match Notes',
    'matchDetail.noNotesYet': 'No notes added yet. Click edit to add notes about this match.',

    // Common additional
    'common.share': 'Share',
    'common.tryAgain': 'Try Again',

    // Matches additional
    'matches.total': 'Total',
    'matches.match': 'Match',
    'matches.requestFrom': 'Request from',
    'matches.requestSentTo': 'Request Sent to',
    'matches.dateCreated': 'Date Created',
    'matches.matchScore': 'Match Score',
    'matches.statusPending': 'Pending',
    'matches.statusApproved': 'Approved',
    'matches.statusSucceeded': 'Met',
    'matches.statusInRelationship': 'Dating',
    'matches.statusInContact': 'In contact',
    'matches.statusRejected': 'Rejected',
    'matches.statusCancelled': 'Cancelled',
    'matches.statusFailed': 'Failed',
    'matches.statusCompleted': 'Completed',
    // Matches Modal
    'matches.createNewMatch': 'Create New Match',
    'matches.editMatch': 'Edit Match',
    'matches.modalInstruction': 'First, select one of your clients. Then select a match candidate from the directory.',
    'matches.yourClient': 'YOUR CLIENT',
    'matches.matchCandidate': 'MATCH CANDIDATE',
    'matches.selectBelow': 'Select below',
    'matches.selectClientFirst': 'Select client first',
    'matches.selectYourClient': 'Select Your Client:',
    'matches.searchYourClients': 'Search your clients...',
    'matches.sendMatchRequest': 'Send Match Request',
    'matches.male': 'Male',
    'matches.female': 'Female',

    // Dating Schedules additional
    'datingSchedules.past': 'Past',
    'datingSchedules.all': 'All',
    'datingSchedules.searchSchedules': 'Search schedules...',
    'datingSchedules.allStatus': 'All Status',
    'datingSchedules.date': 'Date',
    'datingSchedules.noSchedules': 'No schedules found',
    'datingSchedules.noSchedulesDesc': 'There are no scheduled dates to display yet.',
    'datingSchedules.inRelationship': 'In Relationship',
    'datingSchedules.engaged': 'Engaged',
    'datingSchedules.statusPending': 'Pending',
    'datingSchedules.statusConfirmed': 'Confirmed',
    'datingSchedules.statusAccepted': 'Accepted',
    'datingSchedules.statusRejected': 'Rejected',
    'datingSchedules.statusMeeting': 'Meeting',
    'datingSchedules.statusChatting': 'Chatting',
    'datingSchedules.statusInRelationship': 'In Relationship',
    'datingSchedules.statusEngaged': 'Engaged',
    'datingSchedules.statusCompleted': 'Completed',
    'datingSchedules.statusCancelled': 'Cancelled',
    'datingSchedules.statusNoShow': 'No Show',
    'datingSchedules.defaultScheduleTitle': 'Dating Schedule',
    'datingSchedules.requestFromMatchmaker': 'Request from',
    'datingSchedules.yourRequest': 'Your request',
    'datingSchedules.datingRequestNote': 'Note: This is a dating request from another matchmaker. You can accept, modify, or reject this request.',
    'datingSchedules.scheduleStatus': 'Schedule Status',
    'datingSchedules.updateStatus': 'Update Status',
    'datingSchedules.currentStatusFor': 'Current status for:',
    'datingSchedules.selectNewStatusFor': 'Select new status for:',
    'datingSchedules.sortTitleAZ': 'Title A-Z',
    'datingSchedules.sortTitleZA': 'Title Z-A',
    'datingSchedules.sortClient1AZ': 'Client 1 A-Z',
    'datingSchedules.sortClient1ZA': 'Client 1 Z-A',
    'datingSchedules.sortClient2AZ': 'Client 2 A-Z',
    'datingSchedules.sortClient2ZA': 'Client 2 Z-A',
    'datingSchedules.sortLocationAZ': 'Location A-Z',
    'datingSchedules.sortLocationZA': 'Location Z-A',
    'datingSchedules.sortStatusAZ': 'Status A-Z',
    'datingSchedules.sortStatusZA': 'Status Z-A',
    'datingSchedules.virtual': 'Virtual',
    'matches.loadingMatches': 'Loading matches...',
    'matches.loadingProfiles': 'Loading profiles...',
    'matches.noProfiles': 'No profiles found',
    'matches.noDetails': 'No details',

    // Connections additional
    'connections.matchmakers': 'Matchmakers',
    'connections.companies': 'Companies',
    'connections.searchMatchmakers': 'Search matchmakers by name or company...',
    'connections.filterByLocation': 'Filter by location...',
    'connections.filterByCountry': 'Country',
    'connections.filterByState': 'State / Province',
    'connections.filterByCity': 'City',
    'connections.self': 'Self',
    'connections.thisIsYou': 'This is you',
    'connections.independentMatchmaker': 'Independent Matchmaker',
    'connections.profiles': 'profiles',
    'connections.message': 'Message',

    // Messages additional
    'messages.sentImage': 'Sent an image',
    'messages.sharedProfile': 'Shared a profile',
    'messages.selectConversation': 'Select a conversation',
    'messages.chooseFromList': 'Choose a conversation from the list to start messaging',

    // Notifications additional
    'notifications.notification': 'notification',
    'notifications.notifications': 'notifications',
    'notifications.matchUpdated': 'Match Updated',

    // Tasks additional
    'tasks.overdue': 'Overdue',
    'tasks.filterConditions': 'Filter Conditions',

    // Affiliate
    'affiliate.title': 'Affiliate Program',
    'affiliate.subtitle': 'Earn commissions by referring new client memberships',
    'affiliate.referrals': 'Referrals',
    'affiliate.earnings': 'Earnings',
    'affiliate.commission': 'Commission',
    'affiliate.progressToNext': 'Progress to Next Tier',
    'affiliate.currentTier': 'Current Tier',
    'affiliate.totalEarnings': 'Total Earnings',
    'affiliate.totalPoints': 'Total Points',
    'affiliate.totalEarned': 'Total Earned',
    'affiliate.pendingBalance': 'Balance',
    'affiliate.requestPayout': 'Request Payout',
    'affiliate.points': 'Points',
    'affiliate.overview': 'Overview',
    'affiliate.commissions': 'Commissions',
    'affiliate.payouts': 'Payouts',
    'affiliate.tiers': 'Tiers',
    'affiliate.loadingData': 'Loading affiliate data...',
    'affiliate.small': 'Small (150px)',
    'affiliate.medium': 'Medium (200px)',
    'affiliate.large': 'Large (256px)',
    'affiliate.extraLarge': 'Extra Large (350px)',
    'affiliate.huge': 'Huge (500px)',
    'affiliate.inactive': 'Inactive',
    'affiliate.noTeamMembers': 'No team members yet. Start referring matchmakers!',
    'affiliate.noTeamMembersYet': 'No team members yet',
    'affiliate.name': 'Name',
    'affiliate.email': 'Email',
    'affiliate.level': 'Level',
    'affiliate.status': 'Status',
    'affiliate.joined': 'Joined',
    'affiliate.tier': 'Tier',
    'affiliate.noReferrals': 'No referrals yet. Share your link to get started!',
    'affiliate.date': 'Date',
    'affiliate.description': 'Description',
    'affiliate.amount': 'Amount',
    'affiliate.noCommissions': 'No commissions yet. Start referring to earn!',
    'affiliate.payoutHistory': 'Payout History',
    'affiliate.net': 'Net',
    'affiliate.method': 'Method',
    'affiliate.noPayouts': 'No payouts yet',
    'affiliate.progressTo': 'Progress to',
    'affiliate.earnPoints': 'Earn total points',
    'affiliate.maintainActive': 'Maintain active status',
    'affiliate.noTierInfo': 'No tier information available',
    'affiliate.searchTeamMembers': 'Search team members...',
    'affiliate.notEligible': 'Not Eligible for Payout',
    'affiliate.addPaymentInfo': 'Add Payment Info',
    'affiliate.uploadW9': 'Upload W-9',
    'affiliate.availableBalance': 'Available Balance',
    'affiliate.payoutAmount': 'Payout Amount ($)',
    'affiliate.minAmount': 'Min',
    'affiliate.maxAmount': 'Max',
    'affiliate.paymentMethod': 'Payment Method',
    'affiliate.bankTransfer': 'Bank Transfer',
    'affiliate.teamTotalPoints': 'Team Total Points',
    'affiliate.totalTeamMembers': 'Total Team Members',
    'affiliate.directReferrals': 'Direct Referrals',
    'affiliate.indirectReferrals': 'Indirect Referrals',
    'affiliate.teamOrgChart': 'Team Organization Chart',
    'affiliate.expand': 'Expand',
    'affiliate.collapse': 'Collapse',
    'affiliate.expandAll': 'Expand All',
    'affiliate.collapseAll': 'Collapse All',
    'affiliate.dragToPan': 'Drag to pan',
    'affiliate.zoomIn': 'Zoom In',
    'affiliate.zoomOut': 'Zoom Out',
    'affiliate.resetView': 'Reset View',
    'affiliate.underperformingMembers': 'Underperforming Team Members',
    'affiliate.underperformingDesc': 'Team members with less than 200 points or inactive status need attention',
    'affiliate.noTeamMemberYet': "You don't have your team member yet! You need to work harder!",
    'affiliate.allPerformingWell': 'Great job! All team members are performing well.',
    'affiliate.allTeamMembers': 'All Team Members',
    'affiliate.you': 'You',
    'affiliate.yourReferrals': 'Your Referrals',
    'affiliate.commissionHistory': 'Commission History',
    'affiliate.paymentInformation': 'Payment Information',
    'affiliate.completePaymentDetails': 'Complete your payment details and W-9 to request payouts',
    'affiliate.paymentInfoLabel': 'Payment Info',
    'affiliate.complete': 'Complete',
    'affiliate.required': 'Required',
    'affiliate.w9Form': 'W-9 Form',
    'affiliate.verified': 'Verified',
    'affiliate.pendingVerification': 'Pending Verification',
    'affiliate.check': 'Check',
    'affiliate.bankName': 'Bank Name',
    'affiliate.accountHolderName': 'Account Holder Name',
    'affiliate.accountNumber': 'Account Number',
    'affiliate.routingNumber': 'Routing Number',
    'affiliate.accountType': 'Account Type',
    'affiliate.checking': 'Checking',
    'affiliate.savings': 'Savings',
    'affiliate.paypalEmail': 'PayPal Email',
    'affiliate.zelleEmail': 'Zelle Email',
    'affiliate.zellePhone': 'Zelle Phone (Optional)',
    'affiliate.checkPayableTo': 'Make Check Payable To',
    'affiliate.mailingAddress': 'Mailing Address',
    'affiliate.taxInformation': 'Tax Information',
    'affiliate.legalName': 'Legal Name',
    'affiliate.businessName': 'Business Name (Optional)',
    'affiliate.addressLine1': 'Address Line 1',
    'affiliate.addressLine2': 'Address Line 2',
    'affiliate.city': 'City',
    'affiliate.state': 'State',
    'affiliate.zipCode': 'ZIP Code',
    'affiliate.savePaymentInfo': 'Save Payment Information',
    'affiliate.w9Description': 'Upload a completed W-9 form to receive payouts. Required by IRS for payments over $600/year.',
    'affiliate.w9Uploaded': 'W-9 Uploaded',
    'affiliate.replaceW9': 'Replace W-9',
    'affiliate.uploadW9Form': 'Upload W-9 Form',
    'affiliate.pdfOnly': 'PDF format only, max 5MB',
    'affiliate.selectFile': 'Select File',
    'affiliate.downloadW9': 'Download blank W-9 form from IRS website',
    'affiliate.minPayout': 'Min Payout',
    'affiliate.processingFee': 'Processing Fee',
    'affiliate.payoutSchedule': 'Payout Schedule',
    'affiliate.monthly': 'Monthly',
    'affiliate.affiliateTiers': 'Affiliate Tiers',
    'affiliate.tiersDescription': 'Progress through tiers to unlock higher commission rates and exclusive rewards',
    'affiliate.yourCurrentTier': 'Your Current Tier',
    'affiliate.morePointsNeeded': '{count} more points needed',
    'affiliate.maxTierReached': "Congratulations! You've reached the highest tier!",
    'affiliate.current': 'Current',
    'affiliate.pointsToUnlock': 'points to unlock',
    'affiliate.commissionRate': 'commission rate',
    'affiliate.requirements': 'Requirements',
    'affiliate.benefits': 'Benefits',
    'affiliate.totalPointsLabel': 'total points',
    'affiliate.commissionOnReferrals': 'commission on all referrals',
    'affiliate.bonusPointsOnEarnings': 'bonus points on earnings',
    'affiliate.quarterlyBonus': 'quarterly bonus',

    // Admin
    'admin.dashboard': 'Admin Dashboard',
    'admin.dashboardSubtitle': 'System overview and statistics',
    'admin.users': 'Users',
    'admin.usersSubtitle': 'Manage user accounts and permissions',
    'admin.profiles': 'All Profiles',
    'admin.profilesSubtitle': 'View and manage all system profiles',
    'admin.matches': 'Matches',
    'admin.matchesSubtitle': 'View and manage all matches',
    'admin.coaches': 'Coaches',
    'admin.coachesSubtitle': 'Manage coach assignments',
    'admin.plans': 'Plans',
    'admin.plansSubtitle': 'Manage membership plans',
    'admin.customFields': 'Custom Fields',
    'admin.customFieldsSubtitle': 'Configure profile custom fields',
    'admin.roles': 'Roles',
    'admin.rolesSubtitle': 'Manage user roles and permissions',
    'admin.settings': 'Platform Settings',
    'admin.settingsSubtitle': 'Configure platform settings',
    'admin.logs': 'Activity Logs',
    'admin.logsSubtitle': 'View system activity logs',
    'admin.trends': 'Trends',
    'admin.trendsSubtitle': 'View analytics and trends',
    'admin.affiliates': 'Affiliates',
    'admin.affiliatesSubtitle': 'Manage affiliate program',
    'admin.billing': 'Billing',
    'admin.billingSubtitle': 'View all billing and invoices',
    'admin.datingSchedules': 'Dating Schedules',
    'admin.datingSchedulesSubtitle': 'Manage all dating schedules',
    'admin.translations': 'Translations',
    'admin.translationsSubtitle': 'Manage language translations',
    'admin.totalUsers': 'Total Users',
    'admin.totalProfiles': 'Total Profiles',
    'admin.totalMatches': 'Total Matches',
    'admin.totalRevenue': 'Total Revenue',
    'admin.recentActivity': 'Recent Activity',
    'admin.addUser': 'Add User',
    'admin.addPlan': 'Add Plan',
    'admin.addField': 'Add Field',

    // Admin Dashboard
    'adminDashboard.activeMatchmakers': 'Active Matchmakers',
    'adminDashboard.matchesMade': 'Matches Made',
    'adminDashboard.monthlyRevenue': 'Monthly Revenue',
    'adminDashboard.activeSubscriptions': 'Active Subscriptions',
    'adminDashboard.revenueOverview': 'Revenue Overview',
    'adminDashboard.userGrowth': 'User Growth',
    'adminDashboard.last7Days': 'Last 7 days',
    'adminDashboard.last30Days': 'Last 30 days',
    'adminDashboard.last90Days': 'Last 90 days',
    'adminDashboard.revenueChartPlaceholder': 'Revenue chart would render here',
    'adminDashboard.userGrowthChartPlaceholder': 'User growth chart would render here',
    'adminDashboard.quickActions': 'Quick Actions',
    'adminDashboard.manageUsers': 'Manage Users',
    'adminDashboard.reviewProfiles': 'Review Profiles',
    'adminDashboard.managePlans': 'Manage Plans',
    'adminDashboard.affiliates': 'Affiliates',
    'adminDashboard.viewAll': 'View All',
    'adminDashboard.noRecentActivity': 'No recent activity',
    'adminDashboard.systemAlerts': 'System Alerts',
    'adminDashboard.profilesPendingReview': 'profiles pending review',
    'adminDashboard.newProfilesRequireApproval': 'New profiles require admin approval',
    'adminDashboard.systemUpdateAvailable': 'System update available',
    'adminDashboard.versionReadyToInstall': 'Version 2.1.0 is ready to install',
    'adminDashboard.planDistribution': 'Plan Distribution',
    'adminDashboard.planDistributionChartPlaceholder': 'Plan distribution chart would render here',

    // Activity Logs
    'logs.title': 'System Logs',
    'logs.subtitle': 'Monitor system activity and audit trail',
    'logs.exportCsv': 'Export CSV',
    'logs.clearLogs': 'Clear Logs',
    'logs.clearConfirm': 'This will backup all logs as CSV and then clear them. Continue?',
    'logs.clearedSuccess': 'Logs backed up and cleared successfully',
    'logs.clearFailed': 'Failed to clear logs',
    'logs.totalLogs': 'Total Logs',
    'logs.info': 'Info',
    'logs.warnings': 'Warnings',
    'logs.errors': 'Errors',
    'logs.critical': 'Critical',
    'logs.logType': 'Log Type',
    'logs.allTypes': 'All Types',
    'logs.level': 'Level',
    'logs.allLevels': 'All Levels',
    'logs.fromDate': 'From Date',
    'logs.toDate': 'To Date',
    'logs.searchLogs': 'Search logs...',
    'logs.noLogsFound': 'No logs found matching your filters',
    'logs.user': 'User',
    'logs.action': 'Action',
    'logs.details': 'Details',
    'logs.ipAddress': 'IP Address',
    'logs.timestamp': 'Timestamp',

    // Platform Settings
    'platformSettings.title': 'Platform Settings',
    'platformSettings.subtitle': 'Configure payment instructions and integrations for internal matchmakers (Powered by FindU)',
    'platformSettings.loadingSettings': 'Loading settings...',
    'platformSettings.payment': 'Payment',
    'platformSettings.paypal': 'PayPal',
    'platformSettings.plans': 'Plans',
    'platformSettings.agreement': 'Agreement',
    'platformSettings.twilio': 'Twilio',
    'platformSettings.smtp': 'SMTP',
    'platformSettings.companyRep': 'Company Rep',
    'platformSettings.paymentInstructionsForClients': 'Payment Instructions for Clients',
    'platformSettings.configurePaymentDetails': 'Configure payment details shown to clients of internal matchmakers (Powered by FindU)',
    'platformSettings.saveChanges': 'Save Changes',
    'platformSettings.enabled': 'Enabled',
    'platformSettings.secureOnlinePayments': 'Secure online payments',
    'platformSettings.paypalEmail': 'PayPal Email',
    'platformSettings.businessName': 'Business Name',
    'platformSettings.instructions': 'Instructions',
    'platformSettings.zelle': 'Zelle',
    'platformSettings.instantBankTransfer': 'Instant bank transfer',
    'platformSettings.zelleEmailPhone': 'Zelle Email/Phone',
    'platformSettings.memoInstruction': 'Memo Instruction',
    'platformSettings.bankTransferWire': 'Bank Transfer / Wire',
    'platformSettings.directBankDeposit': 'Direct bank deposit',
    'platformSettings.bankName': 'Bank Name',
    'platformSettings.accountName': 'Account Name',
    'platformSettings.accountNumber': 'Account Number',
    'platformSettings.routingNumber': 'Routing Number',
    'platformSettings.swiftCode': 'SWIFT Code (Optional)',
    'platformSettings.reference': 'Reference',
    'platformSettings.check': 'Check',
    'platformSettings.mailPayment': 'Mail payment',
    'platformSettings.makePayableTo': 'Make Payable To',
    // PayPal Integration
    'platformSettings.paypalIntegration': 'PayPal Integration',
    'platformSettings.paypalDesc': 'Configure PayPal API for one-time payments and subscriptions (Internal matchmakers only)',
    'platformSettings.testConnection': 'Test Connection',
    'platformSettings.saveSettings': 'Save Settings',
    'platformSettings.saving': 'Saving...',
    'platformSettings.environment': 'Environment',
    'platformSettings.sandboxTesting': '🧪 Sandbox (Testing)',
    'platformSettings.sandboxDesc': 'Use for development and testing',
    'platformSettings.liveProduction': '🚀 Live (Production)',
    'platformSettings.liveDesc': 'Real transactions',
    'platformSettings.apiCredentials': 'API Credentials',
    'platformSettings.clientId': 'Client ID',
    'platformSettings.enterClientId': 'Enter PayPal Client ID',
    'platformSettings.clientSecret': 'Client Secret',
    'platformSettings.enterClientSecret': 'Enter PayPal Client Secret',
    'platformSettings.webhookId': 'Webhook ID (Optional)',
    'platformSettings.webhookIdPlaceholder': 'For automatic payment notifications',
    'platformSettings.currency': 'Currency',
    'platformSettings.paymentTypes': 'Payment Types',
    'platformSettings.oneTimePayments': 'One-Time Payments',
    'platformSettings.oneTimeDesc': 'Accept single payments for invoices and one-time membership purchases',
    'platformSettings.subscriptionPayments': 'Subscription Payments',
    'platformSettings.subscriptionDesc': 'Enable recurring billing for client membership subscriptions',
    'platformSettings.redirectUrls': 'Redirect URLs',
    'platformSettings.returnUrl': 'Return URL (Success)',
    'platformSettings.cancelUrl': 'Cancel URL',
    'platformSettings.paypalNote': 'PayPal integration is only available for internal matchmakers (Powered by FindU). External (White Label) matchmakers must configure their own payment processing.',
    'platformSettings.note': 'Note:',
    // Client Membership Plans
    'platformSettings.clientMembershipPlans': 'Client Membership Plans',
    'platformSettings.clientPlansDesc': 'Manage membership plans offered to clients (used by internal matchmakers)',
    'platformSettings.addPlan': 'Add Plan',
    'platformSettings.totalPlans': 'Total Plans',
    'platformSettings.activePlans': 'Active Plans',
    'platformSettings.totalSubscribers': 'Total Subscribers',
    'platformSettings.monthlyRevenue': 'Monthly Revenue',
    'platformSettings.planName': 'Plan Name',
    'platformSettings.price': 'Price',
    'platformSettings.duration': 'Duration',
    'platformSettings.subscribers': 'Subscribers',
    'platformSettings.status': 'Status',
    'platformSettings.actions': 'Actions',
    'platformSettings.active': 'Active',
    'platformSettings.inactive': 'Inactive',
    'platformSettings.deactivate': 'Deactivate',
    'platformSettings.activate': 'Activate',
    'platformSettings.editPlan': 'Edit Plan',
    'platformSettings.createPlan': 'Create Plan',
    'platformSettings.planNameRequired': 'Plan Name *',
    'platformSettings.description': 'Description',
    'platformSettings.priceRequired': 'Price ($) *',
    'platformSettings.durationRequired': 'Duration *',
    'platformSettings.months': 'Month(s)',
    'platformSettings.years': 'Year(s)',
    'platformSettings.lifetime': 'Lifetime',
    'platformSettings.durationExample': 'e.g., 4 months = "$499/4x months", 2 years = "$999/2x years"',
    'platformSettings.affiliateNote': 'Commission rates and points earnings for matchmakers are configured in Affiliate Management → Tiers. When a client pays for this membership, the matchmaker will earn commission and points based on their affiliate tier settings.',
    'platformSettings.features': 'Features',
    'platformSettings.addFeature': 'Add a feature...',
    'platformSettings.planNameZh': 'Plan Name (Chinese)',
    'platformSettings.descriptionZh': 'Description (Chinese)',
    'platformSettings.featuresZh': 'Features (Chinese)',
    'platformSettings.addFeatureZh': 'Add feature in Chinese...',
    'platformSettings.planActiveLabel': 'Plan is active and available for purchase',
    'platformSettings.cancel': 'Cancel',
    'platformSettings.updatePlan': 'Update Plan',
    // Twilio SMS Settings
    'platformSettings.twilioSmsSettings': 'Twilio SMS Settings',
    'platformSettings.twilioDesc': 'Configure Twilio for SMS notifications and OTP verification',
    'platformSettings.smsService': 'SMS Service',
    'platformSettings.smsEnabled': 'SMS notifications are enabled',
    'platformSettings.smsDisabled': 'SMS notifications are disabled',
    'platformSettings.disabled': 'Disabled',
    'platformSettings.accountSid': 'Account SID',
    'platformSettings.accountSidHelp': 'Found in your Twilio Console Dashboard',
    'platformSettings.authToken': 'Auth Token',
    'platformSettings.authTokenHelp': 'Keep this secret!',
    'platformSettings.phoneConfig': 'Phone Configuration',
    'platformSettings.twilioPhone': 'Twilio Phone Number',
    'platformSettings.twilioPhoneHelp': 'Your Twilio phone number for sending SMS',
    'platformSettings.messagingServiceSid': 'Messaging Service SID (Optional)',
    'platformSettings.messagingServiceHelp': 'For high-volume messaging',
    'platformSettings.verifyService': 'Verify Service (OTP)',
    'platformSettings.verifyServiceSid': 'Verify Service SID',
    'platformSettings.verifyServiceHelp': 'For OTP verification. Create a Verify Service in Twilio Console.',
    'platformSettings.twilioGettingStarted': 'Getting Started with Twilio:',
    'platformSettings.twilioStep1': 'Sign up at',
    'platformSettings.twilioStep2': 'Get your Account SID and Auth Token from the Console Dashboard',
    'platformSettings.twilioStep3': 'Purchase a phone number for sending SMS',
    'platformSettings.twilioStep4': 'Create a Verify Service for OTP functionality',
    // Email / SMTP Settings
    'platformSettings.emailSmtpSettings': 'Email / SMTP Settings',
    'platformSettings.emailSmtpDesc': 'Configure email settings for notifications and communications',
    'platformSettings.sendTestEmail': 'Send Test Email',
    'platformSettings.testEmailDesc': 'Compose and send a test email to verify your email configuration is working correctly.',
    'platformSettings.testEmailTo': 'Recipient Email',
    'platformSettings.testEmailToPlaceholder': 'recipient@example.com',
    'platformSettings.testEmailSubject': 'Subject',
    'platformSettings.testEmailSubjectPlaceholder': 'Enter email subject',
    'platformSettings.testEmailBody': 'Email Body (HTML)',
    'platformSettings.testEmailBodyPlaceholder': 'Enter email body content...',
    'platformSettings.htmlSupported': 'HTML supported',
    'platformSettings.testEmailPreview': 'Preview',
    'platformSettings.sendingTestEmail': 'Sending...',
    'platformSettings.testEmailSuccess': 'Test email sent successfully! Check inbox.',
    'platformSettings.testEmailFailed': 'Email test failed. Please check your settings.',
    'platformSettings.enterRecipientEmail': 'Please enter a recipient email address',
    'platformSettings.emailService': 'Email Service',
    'platformSettings.emailEnabled': 'Email notifications are enabled',
    'platformSettings.emailDisabled': 'Email notifications are disabled',
    'platformSettings.emailProvider': 'Email Provider',
    'platformSettings.smtpServer': 'SMTP Server',
    'platformSettings.smtpConfig': 'SMTP Configuration',
    'platformSettings.smtpHost': 'SMTP Host',
    'platformSettings.port': 'Port',
    'platformSettings.secure': 'Secure',
    'platformSettings.username': 'Username',
    'platformSettings.passwordAppPassword': 'Password / App Password',
    'platformSettings.sendgridConfig': 'SendGrid Configuration',
    'platformSettings.apiKey': 'API Key',
    'platformSettings.sendgridHelp': 'Get your API key from SendGrid Dashboard → Settings → API Keys',
    'platformSettings.mailgunConfig': 'Mailgun Configuration',
    'platformSettings.domain': 'Domain',
    'platformSettings.awsSesConfig': 'AWS SES Configuration',
    'platformSettings.accessKeyId': 'Access Key ID',
    'platformSettings.secretAccessKey': 'Secret Access Key',
    'platformSettings.region': 'Region',
    'platformSettings.senderConfig': 'Sender Configuration',
    'platformSettings.fromEmail': 'From Email',
    'platformSettings.fromName': 'From Name',
    'platformSettings.replyToEmail': 'Reply-To Email (Optional)',
    'platformSettings.replyToHelp': 'Where replies will be sent',
    'platformSettings.gmailImportant': 'For Gmail SMTP, you need to use an App Password instead of your regular password. Enable 2-factor authentication and generate an App Password in your Google Account settings.',
    'platformSettings.important': 'Important:',
    // Membership Service Agreement
    'platformSettings.membershipServiceAgreement': 'Partner Agreement',
    'platformSettings.agreementDesc': 'Configure the partner agreement template that matchmakers/partners must sign during registration',
    'platformSettings.enableAgreement': 'Enable Partner Agreement',
    'platformSettings.enableAgreementHelp': 'When enabled, matchmakers/partners must sign the agreement during registration',
    'platformSettings.agreementTitle': 'Agreement Title',
    'platformSettings.agreementContent': 'Agreement Content',
    'platformSettings.placeholdersHelp': 'Use the following placeholders:',
    'platformSettings.requireSignature': 'Require Electronic Signature',
    'platformSettings.requireSignatureHelp': 'Clients must provide their electronic signature to accept the agreement',
    'platformSettings.lastUpdated': 'Last updated:',
    // Automation & Webhooks
    'automation.selectWebhook': 'Select Webhook',
    'automation.selectWebhookDesc': 'Choose a webhook to trigger for this automation task',
    'automation.webhookConfiguration': 'Webhook Configuration',
    'automation.subscriptionPaymentSuccess': 'Subscription Payment Success',
    'automation.subscriptionPaymentFailed': 'Subscription Payment Failed',
    'automation.subscriptionRenewal': 'Subscription Renewal',
    'automation.subscriptionCancelled': 'Subscription Cancelled',
    'automation.paymentMethodUpdated': 'Payment Method Updated',
    'automation.refundProcessed': 'Refund Processed',
    // Company Representative
    'platformSettings.companyRepresentative': 'Company Representative',
    'platformSettings.companyRepDesc': 'Assign an internal matchmaker to represent the company for external communications',
    'platformSettings.selectCompanyRep': 'Select Company Representative',
    'platformSettings.noRepresentative': '-- No Representative --',
    'platformSettings.companyRepHelp': 'Only admins and internal matchmakers can be assigned as company representative',
    'platformSettings.companyRepPermissions': 'Company Representative Permissions:',
    'platformSettings.companyRepPerm1': 'Receive and send messages to external matchmakers on behalf of all internal matchmakers',
    'platformSettings.companyRepPerm2': 'Create and manage matches for all internal matchmakers\' profiles',
    'platformSettings.companyRepPerm3': 'Approve or reject match requests from external matchmakers',
    'platformSettings.companyRepPerm4': 'Represent the company (FINDU) in the Connections directory',
    // Email Templates
    'platformSettings.emailTemplates': 'Templates',
    'platformSettings.emailTemplatesTitle': 'Email Templates',
    'platformSettings.emailTemplatesDesc': 'Customize automated email templates in English and Chinese. Use variables like {{client_name}} for dynamic content.',
    'platformSettings.templatesSaved': 'Email templates saved successfully',
    'platformSettings.templatesSaveFailed': 'Failed to save email templates',
    'platformSettings.tplPaymentConfirmation': 'Payment Confirmation',
    'platformSettings.tplPaymentConfirmationDesc': 'Sent to clients when payment is received',
    'platformSettings.tplReferralRegistration': 'Referral Registration Complete',
    'platformSettings.tplReferralRegistrationDesc': 'Sent to both parties when a referral registers',
    'platformSettings.tplBillingReminder': 'Billing Reminder',
    'platformSettings.tplBillingReminderDesc': 'Sent to clients when membership payment is due',
    'platformSettings.tplTeamActive': 'Team Active - Points Earned',
    'platformSettings.tplTeamActiveDesc': 'Sent to team members who are active and earned points',
    'platformSettings.tplTeamInactive': 'Team Inactive - Needs More Activity',
    'platformSettings.tplTeamInactiveDesc': 'Sent to team members who are inactive to encourage engagement',
    'platformSettings.availableVariables': 'Available Variables',
    'platformSettings.templateInfoTitle': 'How Templates Work:',
    'platformSettings.templateInfoDesc': 'Templates are sent automatically when their trigger events occur. Variables like {{client_name}} are replaced with actual data. Make sure SMTP settings are configured in the SMTP tab. Click on any variable to copy it.',

    // Social Login Settings
    'platformSettings.socialLogin': 'Social Login',
    'platformSettings.socialLoginSettings': 'Social Login Settings',
    'platformSettings.socialLoginSubtitle': 'Configure social login providers for the client portal',
    'platformSettings.googleLogin': 'Google Login',
    'platformSettings.appleLogin': 'Apple Login',
    'platformSettings.wechatLogin': 'WeChat Login',
    'platformSettings.socialEnabled': 'Enabled',
    'platformSettings.socialDisabled': 'Disabled',
    'platformSettings.socialClientId': 'Client ID',
    'platformSettings.enterGoogleClientId': 'Enter Google Client ID',
    'platformSettings.socialClientSecret': 'Client Secret',
    'platformSettings.enterGoogleClientSecret': 'Enter Google Client Secret',
    'platformSettings.appleServiceId': 'Client ID (Service ID)',
    'platformSettings.enterAppleServiceId': 'Enter Apple Service ID',
    'platformSettings.teamId': 'Team ID',
    'platformSettings.enterAppleTeamId': 'Enter Apple Team ID',
    'platformSettings.keyId': 'Key ID',
    'platformSettings.enterAppleKeyId': 'Enter Apple Key ID',
    'platformSettings.appId': 'App ID',
    'platformSettings.enterWechatAppId': 'Enter WeChat App ID',
    'platformSettings.appSecret': 'App Secret',
    'platformSettings.enterWechatAppSecret': 'Enter WeChat App Secret',
    'platformSettings.socialLoginNote': 'Enabling a provider here will show its login button on the client login page. Make sure you have configured valid credentials from each provider\'s developer console before enabling.',

    // Partner Agreement
    'platformSettings.partnerAgreement': 'Partner Agreement',
    'platformSettings.partnerAgreementDesc': 'Configure the partner agreement content displayed during matchmaker registration.',
    'platformSettings.enablePartnerAgreement': 'Enable Partner Agreement',
    'platformSettings.enablePartnerAgreementHelp': 'When enabled, custom partner agreement content will be shown in the matchmaker registration sign partner agreement modal.',
    'platformSettings.partnerAgreementTitle': 'Agreement Title',
    'platformSettings.partnerAgreementContent': 'Agreement Content',
    'platformSettings.partnerAgreementContentHelp': 'Enter the full partner agreement text. Supports HTML for formatting. This content will be displayed in the "Sign Partner Agreement" modal during matchmaker registration.',
    'platformSettings.partnerAgreementSaved': 'Partner agreement saved successfully',
    'platformSettings.partnerAgreementSaveFailed': 'Failed to save partner agreement',

    // Company Branding & Super Admin Settings
    'platformSettings.companyBranding': 'Company Branding',
    'platformSettings.superAdmin': 'Super Admin',

    'roles.title': 'Roles',
    'roles.subtitle': 'Manage user roles and permissions',
    'roles.createRole': 'Create Role',
    'roles.editRole': 'Edit Role',
    'roles.system': 'System',
    'roles.custom': 'Custom',
    'roles.users': 'Users',
    'roles.permissions': 'Permissions',
    'roles.fullSystemAccess': 'Full system access with all permissions',
    'roles.administrativeAccess': 'Administrative access with limited system settings',
    'roles.canManageMatchmakers': 'Can manage matchmakers and view reports',
    'roles.standardMatchmaker': 'Standard matchmaker agent account',
    'roles.supportStaff': 'Support staff with read access and limited actions',
    'roles.matchmakingCoach': 'Matchmaking coach with assigned client management',
    'roles.clientProfile': 'Client profile - Can view matches, messages and own profile',

    // Affiliate Page
    'affiliate.title': 'Affiliate Program',
    'affiliate.subtitle': 'Earn commissions by referring new clients and matchmakers',
    'affiliate.requestPayout': 'Request Payout',
    'affiliate.totalPoints': 'Total Points',
    'affiliate.earned': 'Earned',
    'affiliate.pendingBalance': 'Balance',
    'affiliate.commission': 'Commission',
    'affiliate.yourReferralLinks': 'Your Referral Links',
    'affiliate.shareLinksToEarn': 'Share these links to earn commissions on every signup',
    'affiliate.clientSignupLink': 'Client Signup Link',
    'affiliate.matchmakerSignupLink': 'Matchmaker Signup Link',
    'affiliate.copy': 'Copy',
    'affiliate.qrCode': 'QR Code',
    'affiliate.shareOnTwitter': 'Share on Twitter',
    'affiliate.shareOnLinkedIn': 'Share on LinkedIn',
    'affiliate.progressToNextTier': 'Progress to Next Tier',
    'affiliate.commissionAtNextTier': 'COMMISSION AT NEXT TIER',
    'affiliate.points': 'points',
    'affiliate.morePointsTo': 'more points to',
    'affiliate.overview': 'Overview',
    'affiliate.myTeam': 'My Team',
    'affiliate.referrals': 'Referrals',
    'affiliate.commissions': 'Commissions',
    'affiliate.paymentInfo': 'Payment Info',
    'affiliate.payouts': 'Payouts',
    'affiliate.tiersRewards': 'Tiers & Rewards',
    'affiliate.tax1099': '1099 Forms',
    'affiliate.tax1099Documents': '1099 Tax Documents',
    'affiliate.tax1099Description': 'Download your 1099 tax forms for reporting your affiliate earnings.',
    'affiliate.form1099': 'Form 1099',
    'affiliate.taxDocumentFor': 'Tax document for',
    'affiliate.uploadedOn': 'Uploaded on',
    'affiliate.no1099Documents': 'No 1099 documents available',
    'affiliate.no1099DocumentsDesc': '1099 forms will be uploaded by the admin when available for download.',
    'affiliate.documentNotAvailable': 'Document not available',
    'affiliate.downloadFailed': 'Failed to download document',
    'affiliate.howItWorks': 'How It Works',
    'affiliate.shareYourLink': 'Share Your Link',
    'affiliate.shareYourLinkDesc': 'Share your unique referral link with potential matchmakers',
    'affiliate.theySignUp': 'They Sign Up',
    'affiliate.theySignUpDesc': 'When they register using your link, they become your referral',
    'affiliate.earnCommissions': 'Earn Commissions',
    'affiliate.earnCommissionsDesc': 'Earn 40.00% on their subscription payments',
    'affiliate.getPaid': 'Get Paid',
    'affiliate.getPaidDesc': 'Request payouts when your balance reaches $50',

    // Admin Plans
    'plans.title': 'Subscription Plans',
    'plans.subtitle': 'Manage pricing plans and features',
    'plans.createPlan': 'Create Plan',
    'plans.editPlan': 'Edit Plan',
    'plans.activePlans': 'Active Plans',
    'plans.totalSubscribers': 'Total Subscribers',
    'plans.monthlyRevenue': 'Monthly Revenue',
    'plans.avgRevenueUser': 'Avg. Revenue/User',
    'plans.planCards': 'Plan Cards',
    'plans.comparisonTable': 'Comparison Table',
    'plans.month': 'month',
    'plans.year': 'year',
    'plans.save': 'save',
    'plans.profiles': 'Profiles',
    'plans.matchesPerMonth': 'Matches/mo',
    'plans.subscribers': 'Subscribers',
    'plans.disable': 'Disable',
    'plans.enable': 'Enable',
    'plans.mostPopular': 'MOST POPULAR',
    'plans.moreFeatures': 'more features',
    'plans.unlimited': 'Unlimited',
    'plans.unlimitedMessaging': 'Unlimited messaging',
    'plans.prioritySupport': 'Priority support',
    'plans.advancedAnalytics': 'Advanced analytics',
    'plans.customBranding': 'Custom branding',
    'plans.apiAccess': 'API access',
    'plans.bulkImportExport': 'Bulk import/export',
    'plans.teamCollaboration': 'Team collaboration',
    'plans.videoConsultations': 'Video consultations',
    'plans.dedicatedAccountManager': 'Dedicated account manager',
    'plans.whiteLabelSolution': 'White-label solution',
    'plans.customIntegrations': 'Custom integrations',
    'plans.slaGuarantee': 'SLA guarantee',
    'plans.perfectForNew': 'Perfect for new matchmakers getting started',
    'plans.forEstablished': 'For established matchmakers growing their business',
    'plans.forAgencies': 'For matchmaking agencies with multiple matchmakers',
    'plans.customSolutions': 'Custom solutions for large organizations',
    'plans.maxProfiles': 'Max Profiles',
    'plans.maxMatchesMonth': 'Max Matches/month',
    'plans.planName': 'Plan Name',
    'plans.description': 'Description',
    'plans.monthlyPrice': 'Monthly Price',
    'plans.yearlyPrice': 'Yearly Price',
    'plans.features': 'Features',
    'plans.onePerLine': 'One feature per line',
    'plans.deletePlan': 'Delete Plan',
    'plans.confirmDelete': 'Are you sure you want to delete this plan?',
    'plans.trialDays': 'Free Trial Days',
    'plans.billingCycle': 'Billing Cycle',
    'plans.slug': 'URL Slug',
    'plans.price': 'Price',
    'plans.monthly': 'Monthly',
    'plans.quarterly': 'Quarterly',
    'plans.yearly': 'Yearly',
    'plans.popular': 'Popular',
    'plans.freeTrialDays': 'day free trial',
    'plans.hasActiveSubscribers': 'Cannot delete plan with active subscribers',
    'plans.active': 'Active',
    'plans.inactive': 'Inactive',

    // Register - External Matchmaker
    'register.choosePlan': 'Choose your subscription plan',
    'register.choosePlanSubtitle': 'Select a plan to get started with your matchmaking business',
    'register.mostPopular': 'Most Popular',
    'register.selectPlan': 'Please select a plan to continue',
    'register.perMonth': '/month',
    'register.perQuarter': '/quarter',
    'register.perYear': '/year',

    // Matchmaker Registration
    'matchmakerReg.title': 'Matchmaker Registration',
    'matchmakerReg.externalSubtitle': 'External Matchmaker',
    'matchmakerReg.internalSubtitle': 'Matchmaker',
    'matchmakerReg.firstName': "What's your first name?",
    'matchmakerReg.lastName': "And your last name?",
    'matchmakerReg.email': "What's your email?",
    'matchmakerReg.password': 'Create a password',
    'matchmakerReg.confirmPassword': 'Confirm your password',
    'matchmakerReg.companyContact': 'Company & Contact Info',
    'matchmakerReg.companyContactSubtitle': 'Tell us about your business',
    'matchmakerReg.companyName': 'Company Name (optional)',
    'matchmakerReg.phone': 'Phone Number',
    'matchmakerReg.location': 'Where are you located?',
    'matchmakerReg.locationSubtitle': 'Your business location',
    'matchmakerReg.choosePlan': 'Choose your subscription plan',
    'matchmakerReg.choosePlanSubtitle': 'Select a plan to get started',
    'matchmakerReg.payment': 'Complete Payment',
    'matchmakerReg.paymentSubtitle': 'Choose a payment method and send payment',
    'matchmakerReg.planSummary': 'Plan Summary',
    'matchmakerReg.amountDue': 'Amount due',
    'matchmakerReg.paymentMethods': 'Payment Methods',
    'matchmakerReg.pendingNote': 'Your subscription will be activated once payment is confirmed by the administrator. Please send payment using one of the methods above.',
    'matchmakerReg.trialNote': 'Your free trial starts immediately',
    'matchmakerReg.trialPaymentNote': 'Payment will be due at the end of your trial period.',
    'matchmakerReg.terms': 'Review and accept our terms',
    'matchmakerReg.alreadyHaveAccount': 'Already have an account?',
    'matchmakerReg.noPlans': 'No plans available at the moment. Please try again later.',
    'matchmakerReg.noPaymentMethods': 'No payment methods configured yet. Please contact the administrator.',
    'matchmakerReg.loadingPlans': 'Loading plans...',
    'matchmakerReg.loadingPayment': 'Loading payment methods...',

    // Messages additional
    'messages.sentImage': 'Sent an image',
    'messages.sentProfile': 'Shared a profile',
    'messages.online': 'Online',
    'messages.offline': 'Offline',
    'messages.typeMessage': 'Type a message...',
    'messages.yearsOld': 'years old',

    // Notifications additional
    'notifications.loadingNotifications': 'Loading notifications...',
    'notifications.noNotificationsYet': 'No notifications yet',
    'notifications.notificationsAppearHere': 'When you receive notifications, they\'ll appear here.',
    'notifications.selected': 'selected',
    'notifications.selectAll': 'Select all',
    'notifications.deselectAll': 'Deselect all',
    'notifications.justNow': 'Just now',
    'notifications.minAgo': 'min ago',
    'notifications.minsAgo': 'mins ago',
    'notifications.hourAgo': 'hour ago',
    'notifications.hoursAgo': 'hours ago',
    'notifications.dayAgo': 'day ago',
    'notifications.daysAgo': 'days ago',

    // Auth
    'auth.login': 'Login',
    'auth.logout': 'Logout',
    'auth.register': 'Register',
    'auth.forgotPassword': 'Forgot Password',
    'auth.resetPassword': 'Reset Password',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.confirmPassword': 'Confirm Password',
    'auth.rememberMe': 'Remember Me',
    'auth.dashboardTitle': 'Matchmaker Dashboard',
    'auth.signInSubtitle': 'Sign in to your account',
    'auth.signIn': 'Sign In',
    'auth.phoneFormatHint': 'Format',
    'auth.signingIn': 'Signing in...',
    'auth.emailAddress': 'Email Address',
    'auth.emailPlaceholder': 'you@example.com',
    'auth.passwordPlaceholder': 'Enter your password',
    'auth.phoneOtp': 'Phone OTP',
    'auth.phoneNumber': 'Phone Number',
    'auth.change': 'Change',
    'auth.sendOtp': 'Send OTP',
    'auth.sendingOtp': 'Sending OTP...',
    'auth.enterOtp': 'Enter OTP',
    'auth.otpPlaceholder': 'Enter 6-digit OTP',
    'auth.didntReceiveOtp': "Didn't receive OTP?",
    'auth.resendIn': 'Resend in',
    'auth.resendOtp': 'Resend OTP',
    'auth.verifying': 'Verifying...',
    'auth.verifySignIn': 'Verify & Sign In',
    'auth.enterValidPhone': 'Please enter a valid phone number',
    'auth.otpSentSuccess': 'OTP sent to your phone number',
    'auth.otpSendFailed': 'Failed to send OTP. Please try again.',
    'auth.enterValidOtp': 'Please enter a valid OTP',
    'auth.otpInvalid': 'Invalid OTP. Please try again.',
    'auth.backToSignIn': 'Back to Sign In',
    'auth.forgotPasswordDesc': 'Enter the email address associated with your account and we will send you a password reset code.',
    'auth.enterEmail': 'Please enter your email address',
    'auth.sending': 'Sending...',
    'auth.sendResetLink': 'Send Reset Code',
    'auth.resetLinkSent': 'If an account with that email exists, a reset code has been sent.',
    'auth.resetCodeSentEmail': 'A 6-digit reset code has been sent to your email.',
    'auth.resetRequestFailed': 'Failed to send reset code. Please try again.',
    'auth.enterCodeDesc': 'Enter the 6-digit code sent to your email and choose a new password.',
    'auth.resetCodeLabel': 'Reset Code',
    'auth.enterResetCode': '000000',
    'auth.resetTokenLabel': 'Reset Token',
    'auth.pasteToken': 'Paste your reset token here',
    'auth.newPassword': 'New Password',
    'auth.newPasswordPlaceholder': 'Enter new password (min 6 characters)',
    'auth.confirmPasswordPlaceholder': 'Confirm your new password',
    'auth.enterResetToken': 'Please enter the 6-digit reset code',
    'auth.passwordMinLength': 'Password must be at least 6 characters',
    'auth.passwordsNoMatch': 'Passwords do not match',
    'auth.resetting': 'Resetting...',
    'auth.passwordResetSuccess': 'Password has been reset successfully! Redirecting to sign in...',
    'auth.resetFailed': 'Failed to reset password. Please try again.',
    'auth.loginFailed': 'Login failed. Please try again.',
    'auth.emailPasswordRequired': 'Please enter your email and password.',
    'auth.invalidCredentials': 'Email or password does not match. Please try again.',
    'auth.accountSuspended': 'Your account is inactive or suspended. Please contact support.',
    'auth.serverError': 'Unable to connect to server. Please try again later.',
    'auth.networkError': 'Network error. Please check your internet connection.',

    // Language
    'language.title': 'Language',
    'language.english': 'English',
    'language.chinese': '中文',

    // Admin Coaches
    'coaches.title': 'Matchmaking Coaches',
    'coaches.subtitle': 'Manage coaches and assign them to client profiles',
    'coaches.addCoach': 'Add Coach',
    'coaches.editCoach': 'Edit Coach',
    'coaches.assignProfile': 'Assign Profile',
    'coaches.coaches': 'Coaches',
    'coaches.assignments': 'Assignments',
    'coaches.searchCoaches': 'Search coaches...',
    'coaches.searchAssignments': 'Search assignments...',
    'coaches.coach': 'Coach',
    'coaches.specialization': 'Specialization',
    'coaches.experience': 'Experience',
    'coaches.clients': 'Clients',
    'coaches.permissions': 'Permissions',
    'coaches.noCoaches': 'No coaches created yet. Click "Add Coach" to create one.',
    'coaches.noCoachesMatch': 'No coaches match your search.',
    'coaches.matches': 'Matches',
    'coaches.dating': 'Dating',
    'coaches.onLeave': 'On Leave',
    'coaches.selectUser': 'Select User',
    'coaches.experienceYears': 'Experience (Years)',
    'coaches.certification': 'Certification',
    'coaches.bio': 'Bio',
    'coaches.maxClients': 'Max Clients',
    'coaches.hourlyRate': 'Hourly Rate ($)',
    'coaches.canManageMatches': 'Can Manage Matches',
    'coaches.canManageDating': 'Can Manage Dating Schedules',
    'coaches.updateCoach': 'Update Coach',
    'coaches.years': 'years',
    'coaches.selectUserPlaceholder': 'Select a user...',
    'coaches.allUsersAreCoaches': 'All users are already coaches',
    'coaches.specializationPlaceholder': 'e.g., Executive Matchmaking, International Clients',
    'coaches.certificationPlaceholder': 'e.g., Certified Matchmaker, Relationship Coach',
    'coaches.bioPlaceholder': "Brief description of the coach's background and expertise...",
    'coaches.canManageMatchesDesc': 'Create, view, and manage client matches',
    'coaches.canManageDatingDesc': 'Schedule and manage dates for clients',

    // Admin Custom Fields
    'customFields.title': 'Custom Fields',
    'customFields.subtitle': 'Manage custom fields and categories for client profiles',
    'customFields.addCustomField': 'Add Custom Field',
    'customFields.editCustomField': 'Edit Custom Field',
    'customFields.editField': 'Edit Field',
    'customFields.customFields': 'Custom Fields',
    'customFields.categories': 'Categories',
    'customFields.searchFields': 'Search fields...',
    'customFields.allTypes': 'All Types',
    'customFields.allCategories': 'All Categories',
    'customFields.order': 'Order',
    'customFields.fieldLabel': 'Field Label',
    'customFields.fieldName': 'Field Name',
    'customFields.category': 'Category',
    'customFields.required': 'Required',
    'customFields.radioSingle': 'Radio (Single)',
    'customFields.dropdownSelect': 'Dropdown Select',
    'customFields.checkboxMultiple': 'Checkbox (Multiple)',
    'customFields.number': 'Number',
    'customFields.datePicker': 'Date Picker',
    'customFields.background': 'Background',
    'customFields.lifestyle': 'Lifestyle',
    'customFields.personalDetails': 'Personal Details',
    'customFields.noFieldsYet': 'No custom fields created yet. Click "Add Custom Field" to create one.',
    'customFields.noFieldsMatch': 'No fields match your search criteria.',
    'customFields.fieldLabelPlaceholder': 'e.g., Favorite Hobbies',
    'customFields.fieldNameDb': 'Field Name (for database)',
    'customFields.fieldNamePlaceholder': 'e.g., favorite_hobbies',
    'customFields.autoGeneratedFromLabel': 'Auto-generated from label. Used for internal reference.',
    'customFields.fieldType': 'Field Type',
    'customFields.options': 'Options',
    'customFields.enterOption': 'Enter option and press Add',
    'customFields.noOptionsYet': 'No options added yet. Add at least one option above.',
    'customFields.placeholderText': 'Placeholder Text',
    'customFields.helpText': 'Help Text',
    'customFields.defaultValue': 'Default Value',
    'customFields.noDefaultValue': 'No default value',
    'customFields.editCategory': 'Edit Category',
    'customFields.addCategory': 'Add Category',
    'customFields.categoryLabel': 'Category Label',
    'customFields.description': 'Description',
    'customFields.color': 'Color',
    'customFields.displayOrder': 'Display Order',
    'customFields.genderVisibility': 'Gender Visibility',
    'customFields.genderVisibilityDesc': 'Choose which gender profiles this field appears on',
    'customFields.male': 'Male',
    'customFields.female': 'Female',
    'customFields.gender': 'Gender',
    'customFields.genderWarning': 'Warning: This field will not be visible on any profile if both are unchecked.',
    'customFields.english': 'English',
    'customFields.chinese': '中文',
    'customFields.requiredField': 'Required Field',
    'customFields.visibilityOptions': 'Visibility Options',
    'customFields.showOnPublicProfile': 'Show on Public Profile',
    'customFields.showOnPublicProfileDesc': 'Display this field on shared public profiles',
    'customFields.showOnDirectory': 'Show on Directory',
    'customFields.showOnDirectoryDesc': 'Display this field on directory profile pages',
    'customFields.preview': 'Preview:',
    'customFields.updateField': 'Update Field',
    'customFields.createField': 'Create Field',
    'customFields.updateCategory': 'Update Category',
    'customFields.createCategory': 'Create Category',
    'customFields.defaultValueHint': 'This value will be pre-filled when creating new profiles.',
    'customFields.noCategoriesYet': 'No categories created yet. Click "Add Category" to create one.',
    'customFields.fieldsCount': 'fields',
    'customFields.confirmDeleteField': 'Are you sure you want to delete this custom field? This will also remove all saved values for this field.',
    'customFields.confirmDeleteCategory': 'Are you sure you want to delete this category?',
    'customFields.cannotDeleteCategory': 'Cannot delete category with {count} field(s). Move or delete fields first.',
    'customFields.enterOptionEn': 'Enter option in English...',
    'customFields.enterOptionZh': '输入中文选项...',
    'customFields.enterDefaultValue': 'Enter default value...',
    'customFields.enterDefaultText': 'Enter default text...',
    'customFields.enterDefaultNumber': 'Enter default number...',
    'customFields.enterText': 'Enter text...',
    'customFields.enterNumber': 'Enter a number',
    'customFields.selectOption': 'Select an option',
    'customFields.fieldLabelPreview': 'Field Label',
    'customFields.min': 'Min',
    'customFields.max': 'Max',
    'customFields.to': 'to',
    'customFields.textInput': 'Text Input',
    'customFields.textArea': 'Text Area',
    'customFields.rangMinMax': 'Range (Min-Max)',
    'customFields.time24h': 'Time (24h)',
    'customFields.placeholderEnExample': 'e.g., Enter your hobbies...',
    'customFields.placeholderZhExample': '例如：输入您的爱好...',
    'customFields.helpTextEnExample': 'e.g., List your favorite activities...',
    'customFields.helpTextZhExample': '例如：列出您最喜欢的活动...',
    'customFields.categoryLabelEnExample': 'e.g., Lifestyle Preferences',
    'customFields.categoryLabelZhExample': '例如：生活方式偏好',
    'customFields.categoryDescEnExample': 'Optional description for this category...',
    'customFields.categoryDescZhExample': '可选的分类描述...',
    'common.add': 'Add',
    'common.type': 'Type',
    'common.status': 'Status',
    'common.actions': 'Actions',
    'common.active': 'Active',
    'common.inactive': 'Inactive',
    'common.optional': 'optional',

    // Admin Dating Schedules
    'adminDating.title': 'Dating Schedules Management',
    'adminDating.subtitle': 'Manage all matchmaker client dating schedules',
    'adminDating.createSchedule': 'Create Schedule',
    'adminDating.searchSchedules': 'Search schedules...',
    'adminDating.allMatchmakers': 'All Matchmakers',
    'adminDating.dateNewest': 'Date ↓ (Newest)',
    'adminDating.dateOldest': 'Date ↑ (Oldest)',
    'adminDating.dateTime': 'Date & Time',
    'adminDating.clients': 'Clients',
    'adminDating.location': 'Location',
    'adminDating.matchmaker': 'Matchmaker',
    'adminDating.requestBy': 'Request by:',
    'adminDating.virtual': 'Virtual',
    'adminDating.clientAZ': 'Client A-Z',
    'adminDating.clientZA': 'Client Z-A',
    'adminDating.locationAZ': 'Location A-Z',
    'adminDating.locationZA': 'Location Z-A',
    'adminDating.matchmakerAZ': 'Matchmaker A-Z',
    'adminDating.matchmakerZA': 'Matchmaker Z-A',
    'adminDating.statusAZ': 'Status A-Z',
    'adminDating.statusZA': 'Status Z-A',
    'adminDating.loadingSchedules': 'Loading schedules...',
    'adminDating.noSchedules': 'No schedules found',
    'adminDating.viewDetails': 'View Details',
    'adminDating.editSchedule': 'Edit Schedule',
    'adminDating.client1': 'Client 1',
    'adminDating.client2': 'Client 2',
    'adminDating.selectMatchmaker': 'Select matchmaker...',
    'adminDating.clientLabel': 'Client',
    'adminDating.selectClient': 'Select client...',
    'adminDating.selectMatchmakerFirst': 'Select matchmaker first',
    'adminDating.titleLabel': 'Title',
    'adminDating.titlePlaceholder': 'e.g., Coffee Date',
    'adminDating.date': 'Date',
    'adminDating.time': 'Time',
    'adminDating.duration': 'Duration',
    'adminDating.locationType': 'Location Type',
    'adminDating.venueName': 'Venue name',
    'adminDating.address': 'Address',
    'adminDating.fullAddress': 'Full address',
    'adminDating.notifications': 'Notifications',
    'adminDating.email': 'Email',
    'adminDating.sms': 'SMS',
    'adminDating.emailAndSms': 'Email & SMS',
    'adminDating.none': 'None',
    'adminDating.notes': 'Notes',
    'adminDating.internalNotes': 'Internal notes...',
    'adminDating.updateStatus': 'Update Status',
    'adminDating.selectNewStatus': 'Select new status for:',
    'adminDating.datingSchedule': 'Dating Schedule',
    'adminDating.statusPending': 'Pending',
    'adminDating.statusConfirmed': 'Confirmed',
    'adminDating.statusAccepted': 'Accepted',
    'adminDating.statusRejected': 'Rejected',
    'adminDating.statusMeeting': 'Meeting',
    'adminDating.statusChatting': 'Chatting',
    'adminDating.statusInRelationship': 'In Relationship',
    'adminDating.statusEngaged': 'Engaged',
    'adminDating.statusCompleted': 'Completed',
    'adminDating.statusCancelled': 'Cancelled',
    'adminDating.statusNoShow': 'No Show',
    'adminDating.locRestaurant': '🍽️ Restaurant',
    'adminDating.locCafe': '☕ Cafe',
    'adminDating.locBar': '🍸 Bar',
    'adminDating.locPark': '🌳 Park',
    'adminDating.locVirtual': '💻 Virtual',
    'adminDating.locOther': '📍 Other',

    // Admin Billing
    'adminBilling.title': 'Billing Management',
    'adminBilling.subtitle': 'Manage client and matchmaker memberships',
    'adminBilling.createPayment': 'Create Payment',
    'adminBilling.createClientMembership': 'Create Client Membership',
    'adminBilling.createMatchmakerMembership': 'Create Matchmaker Membership',
    'adminBilling.clientMemberships': 'Client Memberships',
    'adminBilling.matchmakerMemberships': 'Matchmaker Memberships',
    'adminBilling.clientMembershipSingle': 'Client Membership',
    'adminBilling.matchmakerMembershipSingle': 'Matchmaker Membership',
    'adminBilling.searchClients': 'Search clients...',
    'adminBilling.searchMatchmakers': 'Search matchmakers...',
    'adminBilling.allStatus': 'All Status',
    'adminBilling.allMatchmakers': 'All Matchmakers',
    'adminBilling.client': 'Client',
    'adminBilling.matchmaker': 'Matchmaker',
    'adminBilling.invoiceNumber': 'Invoice #',
    'adminBilling.amount': 'Amount',
    'adminBilling.commission': 'Commission',
    'adminBilling.dueDate': 'Due Date',
    'adminBilling.plan': 'Plan',
    'adminBilling.price': 'Price',
    'adminBilling.billing': 'Billing',
    'adminBilling.startDate': 'Start Date',
    'adminBilling.endDate': 'End Date',
    'adminBilling.noClientMemberships': 'No client memberships found',
    'adminBilling.pending': 'Pending',
    'adminBilling.awaitingApproval': 'Awaiting Approval',
    'adminBilling.paid': 'Paid',
    'adminBilling.overdue': 'Overdue',
    'adminBilling.cancelled': 'Cancelled',
    'adminBilling.pastDue': 'Past Due',
    'adminBilling.trialing': 'Trialing',
    'adminBilling.clientProfile': 'Client Profile',
    'adminBilling.selectClient': 'Select client...',
    'adminBilling.selectPlan': 'Select plan...',
    'adminBilling.selectMatchmaker': 'Select matchmaker...',
    'adminBilling.notes': 'Notes',
    'adminBilling.pendingByDefault': 'Status: Pending',
    'adminBilling.pendingByDefaultDesc': 'This membership will be created with "Pending" status. Commissions and points will not be processed until the status is changed to complete (approved/paid).',
    'adminBilling.commissionInfo': 'Commission Info',
    'adminBilling.commissionInfoDesc': 'When this membership is approved/completed, the matchmaker who manages this client will automatically receive a commission and points based on their affiliate tier rate.',
    'adminBilling.membership': 'Membership',
    'adminBilling.membershipDetails': 'Membership Details',
    'adminBilling.rejectMembership': 'Reject Membership',
    'adminBilling.rejectMembershipDesc': 'Please provide a reason for rejecting this membership payment. The matchmaker will be able to see this note.',
    'adminBilling.rejectionReason': 'Rejection Reason',
    'adminBilling.rejectionReasonPlaceholder': 'Enter the reason for rejection...',
    'adminBilling.rejectionReasonRequired': 'Please provide a rejection reason',
    'adminBilling.confirmReject': 'Reject Membership',
    'adminBilling.membershipRejected': 'Membership rejected successfully',
    'adminBilling.rejectionNotes': 'Rejection Notes',
    'adminBilling.commissionEarned': 'Commission Earned',
    'adminBilling.paymentMethod': 'Payment Method',
    'adminBilling.paymentMethodRequired': 'Please select a payment method',
    'adminBilling.proofOfPayment': 'Proof of Payment',
    'adminBilling.selectPaymentMethod': 'Select payment method',
    'adminBilling.selectSubscriptionAccount': 'Select subscription account',
    'adminBilling.subscriptionAccountRequired': 'Subscription account is required for this payment method',
    'adminBilling.paypal': 'PayPal',
    'adminBilling.square': 'Square',
    'adminBilling.zelle': 'Zelle',
    'adminBilling.cash': 'Cash',
    'adminBilling.bankTransfer': 'Bank Transfer',
    'common.loading': 'Loading...',
    'common.loadMore': 'Load More',
    'common.showingOf': 'Showing {showing} of {total}',
    'common.delete': 'Delete',
    'common.save': 'Save',
    'common.update': 'Update',
    'common.create': 'Create',

    // Share Modal
    'share.title': 'Share Profile',
    'share.share': 'Share',
    'share.shareProfile': 'Share Profile',
    'share.shareDesc': 'Share this public link with potential matches:',
    'share.openLink': 'Open in new tab',
    'share.cardFallback': 'Opened public profile - you can screenshot it as a profile card',
    'share.sharePublicProfile': 'Share Public Profile',
    'share.profilePrivate': 'Profile is Private',
    'share.profilePrivateDesc': 'This profile is currently set to private. Enable "Public Profile" in Edit Profile to allow others to view it via the shareable link.',
    'share.sharePublicLink': 'Share this public link with potential matches:',
    'share.linkNotPublicYet': 'Public link (will only work once profile is set to public):',
    'share.copyLink': 'Copy Link',
    'share.openInNewTab': 'Open in New Tab',
    'share.linkCopied': 'Public link copied to clipboard',
    'share.profileCard': 'Profile Card',
    'share.profileCardDesc': 'Generate a beautiful profile card with QR code that links to the public profile.',
    'share.generateCard': 'Generate Profile Card',
    'share.previewCard': 'Preview Profile Card',
    'share.cardLanguage': 'Card Language',
    'share.generateAndDownload': 'Generate & Download',
    'share.previewNote': 'Preview of your profile card. Select language and click below to generate.',
    'share.generating': 'Generating...',
    'share.cardGenerated': 'Profile card downloaded successfully!',
    'share.cardError': 'Failed to generate profile card',
    'share.noPublicLink': 'No public link available',
    'share.noPublicLinkYet': 'This profile doesn\'t have a public link yet. Enable "Public Profile" in Edit Profile to generate one.',
    'share.scanToView': 'Scan to view profile',
    'share.yearsOld': 'years old',
    'share.poweredBy': 'FINDU Matchmaking',

    // Database Synchronization
    'dbSync.title': 'Database Synchronization',
    'dbSync.subtitle': 'Connect, map, and synchronize data between remote and local databases',
    'dbSync.remoteConnection': 'Remote Database Connection',
    'dbSync.remoteConnectionDesc': 'Connect to the source database for synchronization',
    'dbSync.connected': 'Connected',
    'dbSync.host': 'Host',
    'dbSync.port': 'Port',
    'dbSync.database': 'Database',
    'dbSync.username': 'Username',
    'dbSync.password': 'Password',
    'dbSync.testing': 'Testing...',
    'dbSync.testConnection': 'Test Connection',
    'dbSync.remoteDatabase': 'Remote Database',
    'dbSync.localDatabase': 'Local Database',
    'dbSync.searchTables': 'Search tables...',
    'dbSync.connectFirst': 'Connect to remote database first',
    'dbSync.noTablesFound': 'No tables found',
    'dbSync.dragToConnect': 'Drag fields to connect',
    'dbSync.fieldMapping': 'Field Mapping',
    'dbSync.schedules': 'Schedules',
    'dbSync.logs': 'Sync Logs',
    'dbSync.savedMappings': 'Saved Mappings',
    'dbSync.saveMapping': 'Save Current Mapping',
    'dbSync.noMappings': 'No saved mappings yet',
    'dbSync.fieldsMapped': 'fields mapped',
    'dbSync.lastSync': 'Last sync',
    'dbSync.runNow': 'Run Now',
    'dbSync.schedule': 'Schedule',
    'dbSync.syncSchedules': 'Sync Schedules',
    'dbSync.schedulesDesc': 'Automate your database synchronization with scheduled jobs',
    'dbSync.addSchedule': 'Add Schedule',
    'dbSync.editSchedule': 'Edit Schedule',
    'dbSync.noSchedules': 'No Schedules Yet',
    'dbSync.noSchedulesDesc': 'Create a schedule to automate database synchronization',
    'dbSync.nextRun': 'Next run',
    'dbSync.scheduleName': 'Schedule Name',
    'dbSync.selectMapping': 'Select Mapping',
    'dbSync.selectMappingPlaceholder': 'Select a mapping...',
    'dbSync.frequency': 'Frequency',
    'dbSync.hourly': 'Hourly',
    'dbSync.daily': 'Daily',
    'dbSync.weekly': 'Weekly',
    'dbSync.monthly': 'Monthly',
    'dbSync.time': 'Time',
    'dbSync.dayOfWeek': 'Day of Week',
    'dbSync.dayOfMonth': 'Day of Month',
    'dbSync.syncMode': 'Sync Mode',
    'dbSync.incremental': 'Incremental (new/changed only)',
    'dbSync.full': 'Full (all records)',
    'dbSync.conflictResolution': 'Conflict Resolution',
    'dbSync.remoteWins': 'Remote wins',
    'dbSync.localWins': 'Local wins',
    'dbSync.newestWins': 'Newest wins',
    'dbSync.skipConflicts': 'Skip conflicts',
    'dbSync.enableSchedule': 'Enable Schedule',
    'dbSync.enableScheduleDesc': 'Schedule will run automatically when enabled',
    'dbSync.syncLogs': 'Sync Logs',
    'dbSync.logsDesc': 'View history of all synchronization operations',
    'dbSync.noLogs': 'No Sync History',
    'dbSync.noLogsDesc': 'Sync logs will appear here after you run synchronizations',
    'dbSync.status': 'Status',
    'dbSync.mapping': 'Mapping',
    'dbSync.records': 'Records',
    'dbSync.duration': 'Duration',
    'dbSync.timestamp': 'Timestamp',
    'dbSync.recordsSynced': 'synced',
    'dbSync.failed': 'failed',
    'dbSync.tableMode': 'Table Mode',
    'dbSync.queryMode': 'Query Mode',
    'dbSync.customQuery': 'Custom SQL Query',
    'dbSync.testQuery': 'Test Query (Preview)',
    'dbSync.queryPreview': 'Query Preview',
    'dbSync.runQueryFirst': 'Run a query to see available columns',
    'dbSync.onlySelectAllowed': 'SELECT queries only',
    'dbSync.queryError': 'Query Error',
    'dbSync.mergeRules': 'Field Merge Rules',
    'dbSync.addMergeRule': 'Add Merge Rule',
    'dbSync.editMergeRule': 'Edit Merge Rule',
    'dbSync.mergeName': 'Merge Name',
    'dbSync.sourceFields': 'Source Fields',
    'dbSync.outputFormat': 'Output Format',
    'dbSync.jsonTemplate': 'JSON Template',
    'dbSync.delimiterFormat': 'Delimiter',
    'dbSync.delimiter': 'Delimiter',
    'dbSync.fieldOrder': 'Field Order',
    'dbSync.targetLocalField': 'Target Local Field',
    'dbSync.mergeDescription': 'Merge multiple source fields into custom JSON or delimited strings',
    'dbSync.mergeRulesCount': 'merge rules',
    'dbSync.templateHelp': 'Use {{field_name}} placeholders for field values',
    'dbSync.queryResultColumns': 'Query Result Columns',

    // Commission Sync
    'dbSync.commissionSync': 'Commission Synchronization',
    'dbSync.commissionSyncDesc': 'Import & manage commission data by month/year. Bind matchmakers to affiliate tiers and track commission percentages.',
    'dbSync.selectMonth': 'Select Month & Year:',
    'dbSync.tableEditor': 'Table Editor',
    'dbSync.uploadExcel': 'Upload Excel',
    'dbSync.preview': 'Preview',
    'dbSync.commissionTableInfo': 'Drag columns and rows to rearrange, click cells to edit. Bind each row to client profiles and affiliate levels.',
    'dbSync.client': 'Client',
    'dbSync.affiliate': 'Affiliate/Tier',
    'dbSync.matchmaker': 'Matchmaker',
    'dbSync.recruiter': 'Recruiter',
    'dbSync.interviewer': 'Interviewer',
    'dbSync.amount': 'Amount',
    'dbSync.payment': 'Payment',
    'dbSync.noCommissionData': 'No commission data. Upload Excel or create entries manually.',
    'dbSync.selectClient': 'Select Client...',
    'dbSync.selectTier': 'Select Tier...',
    'dbSync.selectMatchmaker': 'Select Matchmaker...',
    'dbSync.selectRecruiter': 'Select Recruiter...',
    'dbSync.selectInterviewer': 'Select Interviewer...',
    'dbSync.selectPayment': 'Select Payment...',
    'dbSync.addRow': 'Add Row',
    'dbSync.dragDropExcel': 'Drop your Excel/CSV file here',
    'dbSync.supportedFormats': 'Supported: .xlsx, .xls, .csv',
    'dbSync.previewInfo': 'Preview of commission data to be imported. Review bindings and amounts before confirming.',
    'dbSync.syncCommissions': 'Sync Commission Data',
    'dbSync.clearAll': 'Clear All',
    'dbSync.rowsCount': 'Total rows',
    'dbSync.totalAmount': 'Total amount',
    'dbSync.dragToRearrange': 'Drag to rearrange: Columns and rows are draggable. Click cells to edit. Bind each row to affiliate tier and matchmaker.',
    'dbSync.browseFiles': 'Browse Files',
    'dbSync.syncing': 'Syncing...',
    'dbSync.tier1': 'Tier 1 (5%)',
    'dbSync.tier2': 'Tier 2 (10%)',
    'dbSync.tier3': 'Tier 3 (15%)',
    'dbSync.tier4': 'Tier 4 (20%)',

    // Admin Affiliates
    'adminAffiliates.title': 'Affiliate Management',
    'adminAffiliates.subtitle': 'Manage affiliates, commissions, payouts, and settings',
    'adminAffiliates.autoUpgrade': 'Auto Upgrade',
    'adminAffiliates.payoutSettings': 'Payout Settings',
    'adminAffiliates.affiliatesLabel': 'Affiliates',
    'adminAffiliates.totalReferrals': 'Total Referrals',
    'adminAffiliates.commission': 'Commission',
    'adminAffiliates.pendingPayouts': 'Pending Payouts',
    'adminAffiliates.teamRankings': 'Team Rankings',
    'adminAffiliates.tabAffiliates': 'Affiliates',
    'adminAffiliates.tabTeams': 'Teams',
    'adminAffiliates.tabCommissions': 'Commissions',
    'adminAffiliates.tabPayouts': 'Payouts',
    'adminAffiliates.tabTiers': 'Tiers',
    'adminAffiliates.tabProcessingFee': 'Processing Fee',
    'adminAffiliates.tabW9Review': 'W9 Review',
    'adminAffiliates.searchAffiliates': 'Search affiliates...',
    'adminAffiliates.allTiers': 'All Tiers',
    'adminAffiliates.suspended': 'Suspended',
    'adminAffiliates.filter': 'Filter',
    'adminAffiliates.affiliate': 'Affiliate',
    'adminAffiliates.code': 'Code',
    'adminAffiliates.referredBy': 'Referred By',
    'adminAffiliates.tier': 'Tier',
    'adminAffiliates.points': 'Points',
    'adminAffiliates.addPoints': 'Add Points',
    'adminAffiliates.clientQRCode': 'Client QR Code',
    'adminAffiliates.matchmakerQRCode': 'Matchmaker QR Code',
    'adminAffiliates.manage1099': 'Manage 1099',
    'adminAffiliates.manage1099Documents': 'Manage 1099 Documents',
    'adminAffiliates.uploadNew1099': 'Upload New 1099 Document',
    'adminAffiliates.taxYear': 'Tax Year',
    'adminAffiliates.selectFile': 'Select File',
    'adminAffiliates.allowedFileTypes': 'Allowed: PDF, JPG, PNG (max 10MB)',
    'adminAffiliates.uploadDocument': 'Upload Document',
    'adminAffiliates.existingDocuments': 'Existing Documents',
    'adminAffiliates.noDocumentsYet': 'No documents uploaded yet',
    'adminAffiliates.noDescription': 'No description',
    'adminAffiliates.uploadedAt': 'Uploaded',
    'adminAffiliates.documentUploaded': 'Document uploaded successfully',
    'adminAffiliates.documentDeleted': 'Document deleted successfully',
    'adminAffiliates.uploadFailed': 'Failed to upload document',
    'adminAffiliates.deleteFailed': 'Failed to delete document',
    'adminAffiliates.confirmDelete1099': 'Are you sure you want to delete this 1099 document?',
    'adminAffiliates.invalidFileType': 'Invalid file type. Please select PDF, JPG, or PNG.',
    'adminAffiliates.fileTooLarge': 'File is too large. Maximum size is 10MB.',
    'adminAffiliates.pleaseSelectFile': 'Please select a file to upload',
    'adminAffiliates.pleaseEnterTaxYear': 'Please enter a tax year',
    'adminAffiliates.descriptionPlaceholder': 'Optional description for this document',
    'adminAffiliates.affiliate': 'Affiliate',
    'adminAffiliates.suspend': 'Suspend',
    'adminAffiliates.activate': 'Activate',
    'adminAffiliates.noAffiliates': 'No affiliates',
    'adminAffiliates.teamRankingsByPoints': 'Team Rankings by Total Points',
    'adminAffiliates.teamRankingsDesc': 'Multi-level matchmaker teams ranked by combined team points',
    'adminAffiliates.rank': 'Rank',
    'adminAffiliates.teamLeader': 'Team Leader',
    'adminAffiliates.leaderTier': 'Leader Tier',
    'adminAffiliates.teamSize': 'Team Size',
    'adminAffiliates.totalTeamPoints': 'Total Team Points',
    'adminAffiliates.directIndirect': 'Direct / Indirect',
    'adminAffiliates.avgPointsMember': 'Avg Points/Member',
    'adminAffiliates.pending': 'Pending',
    'adminAffiliates.approved': 'Approved',
    'adminAffiliates.paid': 'Paid',
    'adminAffiliates.rejected': 'Rejected',
    'adminAffiliates.processing': 'Processing',
    'adminAffiliates.completed': 'Completed',
    'adminAffiliates.addCommission': 'Add Commission',
    'adminAffiliates.searchCommissions': 'Search commissions...',
    'adminAffiliates.date': 'Date',
    'adminAffiliates.amount': 'Amount',
    'adminAffiliates.description': 'Description',
    'adminAffiliates.noCommissions': 'No commissions',
    'adminAffiliates.createPayout': 'Create Payout',
    'adminAffiliates.searchPayouts': 'Search payouts...',
    'adminAffiliates.net': 'Net',
    'adminAffiliates.method': 'Method',
    'adminAffiliates.institutionalBonusSettings': 'Institutional Partner Bonus Settings',
    'adminAffiliates.addTier': 'Add Tier',
    'adminAffiliates.level': 'Level',
    'adminAffiliates.name': 'Name',
    'adminAffiliates.commPercent': 'Comm %',
    'adminAffiliates.coachingFee': 'Coaching Fee',
    'adminAffiliates.coachingFeePercent': 'Coaching Fee (%)',
    'adminAffiliates.coachingFeeDesc': 'Platform service fee percentage charged per tier',
    'adminAffiliates.pointsPercent': 'Points %',
    'adminAffiliates.ptsRequired': 'Pts Required',
    'adminAffiliates.recruiter': 'Bonus to Superior %',
    'adminAffiliates.requiredRecruits': 'Required Recruits',
    'adminAffiliates.indPartners': 'Ind',
    'adminAffiliates.senPartners': 'Sen',
    'adminAffiliates.instPartners': 'Inst',
    'adminAffiliates.none': 'None',
    'adminAffiliates.addDeductPoints': 'Add/Deduct Points',
    'adminAffiliates.pointsNegativeToDeduct': 'Points (negative to deduct)',
    'adminAffiliates.editCommission': 'Edit Commission',
    'adminAffiliates.amountDollar': 'Amount ($)',
    'adminAffiliates.editPayout': 'Edit Payout',
    'adminAffiliates.bankTransfer': 'Bank Transfer',
    'adminAffiliates.check': 'Check',
    'adminAffiliates.crypto': 'Crypto',
    'adminAffiliates.transactionReference': 'Transaction Reference',
    'adminAffiliates.transactionRefPlaceholder': 'e.g. TXN123456',
    'adminAffiliates.paymentProof': 'Payment Proof (Screenshot)',
    'adminAffiliates.clickOrDragUpload': 'Click or drag to upload screenshot',
    'adminAffiliates.pngJpgUpTo5MB': 'PNG, JPG up to 5MB',
    'adminAffiliates.editTier': 'Edit Tier',
    'adminAffiliates.pointsRequired': 'Points Required',
    'adminAffiliates.clientMembershipEarnings': 'Client Membership Payment Earnings',
    'adminAffiliates.clientMembershipEarningsDesc': 'When a matchmaker\'s client pays for membership, the matchmaker earns these percentages',
    'adminAffiliates.commissionRatePercent': 'Commission Rate (%)',
    'adminAffiliates.cashCommissionEarned': 'Cash commission earned',
    'adminAffiliates.pointsEarningPercent': 'Points Earning (%)',
    'adminAffiliates.pointsEarnedExample': 'Points earned (e.g., 5% of $1000 = 50 pts)',
    'adminAffiliates.recruiterBonusPercent': 'Bonus to Superior (%)',
    'adminAffiliates.recruiterBonusDesc': 'Percentage paid to the superior who recruited this partner when their client pays for membership',
    'adminAffiliates.requiredDirectRecruits': 'Required Direct Recruits',
    'adminAffiliates.requiredDirectRecruitsDesc': 'Minimum number of partners this tier must directly recruit to qualify',
    'adminAffiliates.individualPartners': 'Individual Partners',
    'adminAffiliates.seniorPartners': 'Senior Partners',
    'adminAffiliates.institutionalPartners': 'Institutional Partners',
    // Institutional Bonus Chain Modal
    'adminAffiliates.institutionalBonusChainTitle': 'Institutional Partner Bonus Chain',
    'adminAffiliates.howItWorks': 'How it works',
    'adminAffiliates.institutionalBonusDesc': 'When a client pays for membership, the system searches upward through the recruiter chain indefinitely. Each Institutional Partner found in the chain receives a bonus based on their level in the chain.',
    'adminAffiliates.bonusPercentagesByLevel': 'Bonus Percentages by Level',
    'adminAffiliates.level1st': '1st Level',
    'adminAffiliates.level2nd': '2nd Level',
    'adminAffiliates.level3rd': '3rd Level',
    'adminAffiliates.firstInstitutionalFound': 'First Institutional Partner Found',
    'adminAffiliates.secondInstitutionalFound': 'Second Institutional Partner Found',
    'adminAffiliates.thirdInstitutionalFound': 'Third Institutional Partner Found',
    'adminAffiliates.percentOfMembership': "% of client's paid membership",
    'adminAffiliates.example': 'Example',
    'adminAffiliates.institutionalBonusExample': 'If a client pays $1000 for membership and the recruiter chain has 3 Institutional Partners:',
    'adminAffiliates.institutionalPartnerReceives': 'Institutional Partner receives',
    'adminAffiliates.institutionalBonusSaved': 'Institutional partner bonus settings saved',
    'adminAffiliates.failedToSaveSettings': 'Failed to save settings',
    'adminAffiliates.saveSettings': 'Save Settings',
    // Level Difference Bonus
    'adminAffiliates.levelDiffBonusSettings': 'Level Difference Bonus',
    'adminAffiliates.levelDiffBonusTitle': 'Level Difference Bonus Commission',
    'adminAffiliates.levelDiffBonusDesc': 'When a matchmaker (level 2+) is recruited by someone at a higher tier, the recruiter receives a bonus commission equal to the difference between their commission rates. If the difference is zero or negative, no bonus is given.',
    'adminAffiliates.enableLevelDiffBonus': 'Enable Level Difference Bonus',
    'adminAffiliates.levelDiffBonusToggleDesc': 'Automatically award bonus commission based on tier commission rate difference',
    'adminAffiliates.minLevelRequired': 'Minimum Sales Matchmaker Level Required',
    'adminAffiliates.minLevelDesc': 'The sales matchmaker (the one who closed the sale) must be at this tier level or higher BEFORE this payment. If they cross the threshold via this payment\'s points, they qualify starting from the next payment.',
    'adminAffiliates.formula': 'Formula',
    'adminAffiliates.levelDiffFormula': 'Bonus = Upline Commission % − Sales Matchmaker Commission %. If ≤ 0, no bonus. The sales matchmaker must be at the minimum level (using their pre-payment tier) for this rule to fire; the bonus goes to their upline.',
    'adminAffiliates.addTiersToSeeExamples': 'Add at least 2 tiers with different commission rates to see examples',
    'adminAffiliates.exampleLabel': 'Example',
    'adminAffiliates.recruiterLabel': 'Recruiter',
    'adminAffiliates.youLabel': 'You',
    'adminAffiliates.bonusToRecruiter': 'bonus to recruiter',
    'adminAffiliates.noBonus': 'No bonus',
    'adminAffiliates.levelDiffBonusSaved': 'Level difference bonus settings saved',
    'adminAffiliates.createAffiliate': 'Create Affiliate',
    'adminAffiliates.selectMatchmaker': 'Select Matchmaker (Internal)',
    'adminAffiliates.searchMatchmaker': 'Search matchmaker by name or email...',
    'adminAffiliates.searchReferredBy': 'Search referrer by name or email...',
    'adminAffiliates.selectMatchmakerRequired': 'Please select an internal matchmaker',
    'adminAffiliates.defaultTier': 'Default (Starter)',
    'adminAffiliates.affiliateCreated': 'Affiliate created successfully',
    'adminAffiliates.emailPasswordRequired': 'Email and password are required',
    'adminAffiliates.pointsHistory': 'Points History',
    'adminAffiliates.totalEarnings': 'Total Earnings',
    'adminAffiliates.totalPoints': 'Total Points',
    'adminAffiliates.availableCommission': 'Available Commission ($)',
    'adminAffiliates.adminNotes': 'Admin Notes',
    'adminAffiliates.addNotesPlaceholder': 'Add notes about this affiliate...',
    'adminAffiliates.editAffiliateDetails': 'Edit Affiliate Details',
    'adminAffiliates.editSettings': 'Edit Settings',
    'adminAffiliates.recentReferrals': 'Recent Referrals',
    'adminAffiliates.noReferralsYet': 'No referrals yet',
    'adminAffiliates.affiliateUpdated': 'Affiliate updated successfully',
    'adminAffiliates.formType': 'Form Type',
    'adminAffiliates.uploadedDate': 'Uploaded Date',

    // Admin Matches
    'adminMatches.title': 'Matches Management',
    'adminMatches.subtitle': 'View and manage all matches across the platform',
    'adminMatches.refresh': 'Refresh',
    'adminMatches.searchProfiles': 'Search by profile names...',
    'adminMatches.allMatchmakers': 'All Matchmakers',
    'adminMatches.profileA': 'Profile A',
    'adminMatches.profileB': 'Profile B',
    'adminMatches.matchPercent': 'Match %',
    'adminMatches.noOccupation': 'No occupation',
    'adminMatches.matchDetails': 'Match Details',
    'adminMatches.na': 'N/A',
    'adminMatches.unknownMatchmaker': 'Unknown Matchmaker',
    'adminMatches.match': 'Match',
    'adminMatches.matchmaker': 'Matchmaker',
    'adminMatches.unknown': 'Unknown',
    'adminMatches.created': 'Created',
    'adminMatches.updated': 'Updated',
    'adminMatches.notes': 'Notes',
    'adminMatches.changeStatus': 'Change Status',
    'adminMatches.deleteMatch': 'Delete Match',
    'adminMatches.deleteConfirmText': 'Are you sure you want to delete this match between:',
    'adminMatches.cannotUndo': 'This action cannot be undone.',
    'adminMatches.deleting': 'Deleting...',
    'adminMatches.statusPending': 'Pending',
    'adminMatches.statusApproved': 'Approved',
    'adminMatches.statusSucceeded': 'Met',
    'adminMatches.statusInRelationship': 'Dating',
    'adminMatches.statusInContact': 'In contact',
    'adminMatches.statusRejected': 'Rejected',
    'adminMatches.statusFailed': 'Failed',
    'adminMatches.statusCancelled': 'Cancelled',
    'adminMatches.statusCompleted': 'Completed',

    // Admin Profiles
    'adminProfiles.title': 'Profile Management',
    'adminProfiles.subtitle': 'Review and moderate user profiles',
    'adminProfiles.gridView': 'Grid view',
    'adminProfiles.tableView': 'Table view',
    'adminProfiles.daysUntilDue': 'days until due',
    'adminProfiles.daysOverdue': 'days overdue',
    'adminProfiles.dueToday': 'Due today',
    'adminProfiles.addProfile': '+ Add Profile',
    'adminProfiles.searchProfiles': 'Search profiles...',
    'adminProfiles.allProfiles': 'All Profiles',
    'adminProfiles.selectAll': 'Select All',
    'adminProfiles.allGenders': 'All Genders',
    'adminProfiles.allAges': 'All Ages',
    'adminProfiles.allMemberships': 'All Memberships',
    'adminProfiles.allMatchmakers': 'All Matchmakers',
    'adminProfiles.allCoaches': 'All Coaches',
    'adminProfiles.viewDetails': 'View Details',
    'adminProfiles.active': 'Active',
    'adminProfiles.inactive': 'Inactive',
    'adminProfiles.created': 'Created:',
    'adminProfiles.matchmaker': 'Matchmaker:',
    'adminProfiles.coach': 'Coach:',
    'adminProfiles.client': 'Client',
    'adminProfiles.yrs': 'yrs',
    'adminProfiles.clearAndApprove': 'Clear & Approve',
    'adminProfiles.editProfile': 'Edit Profile',
    'adminProfiles.createProfile': 'Create Profile',
    'adminProfiles.updateProfile': 'Update Profile',
    'adminProfiles.name': 'Name',
    'adminProfiles.firstNameLabel': 'First Name',
    'adminProfiles.lastNameLabel': 'Last Name',
    'adminProfiles.age': 'Age',
    'adminProfiles.gender': 'Gender',
    'adminProfiles.city': 'City',
    'adminProfiles.type': 'Type',
    'adminProfiles.membership': 'Plan',
    'adminProfiles.male': 'Male',
    'adminProfiles.female': 'Female',
    'adminProfiles.other': 'Other',
    'adminProfiles.membershipPlan': 'Membership Plan',
    'adminProfiles.selectMembershipPlan': 'Select Membership Plan',
    'adminProfiles.assignedMatchmaker': 'Assigned Matchmaker',
    'adminProfiles.selectMatchmaker': 'Select Matchmaker',
    'adminProfiles.assignedCoach': 'Assigned Coach',
    'adminProfiles.noCoachAssigned': 'No Coach Assigned',
    'common.select': 'Select',
    'common.clear': 'Clear',
    'common.edit': 'Edit',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.all': 'All',

    // Admin Profiles - Edit Modal
    'adminProfiles.coachAssignDesc': "Assign a coach to help manage this client's matches and dating schedules",
    'adminProfiles.location': 'Location',
    'adminProfiles.locationPlaceholder': 'City, State',
    'adminProfiles.bio': 'Bio',
    'adminProfiles.photos': 'Photos',
    'adminProfiles.profilePhoto': 'Profile Photo (Avatar)',
    'adminProfiles.changePhoto': 'Change Photo',
    'adminProfiles.uploadPhoto': 'Upload Photo',
    'adminProfiles.squareImageMax': 'Square image, max 5MB',
    'adminProfiles.galleryPhotos': 'Gallery Photos',
    'adminProfiles.clickToAddPhotos': 'Click + to add photos. Max 10 photos, 5MB each.',
    'adminProfiles.profileSettings': 'Profile Settings',
    'adminProfiles.publicProfile': 'Public Profile',
    'adminProfiles.anyoneCanView': 'Anyone with the link can view',
    'adminProfiles.onlyMatchmakerSee': 'Only matchmaker can see',
    'adminProfiles.showOnDirectory': 'Show on Directory',
    'adminProfiles.visibleInDirectory': 'Visible in profile directory',
    'adminProfiles.hiddenFromDirectory': 'Hidden from directory',
    'adminProfiles.matchPricing': 'Match Pricing',
    'adminProfiles.setPriceForMatches': 'Set price for matches',
    'adminProfiles.noPricingSet': 'No pricing set',
    'adminProfiles.priceType': 'Price Type',
    'adminProfiles.offer': 'Offer',
    'adminProfiles.request': 'Request',
    'adminProfiles.amount': 'Amount ($)',
    'adminProfiles.generalInfo': 'General Information',
    'adminProfiles.personalDetailsLabel': 'Personal Details',
    'adminProfiles.preferences': 'Preferences',
    'adminProfiles.lifestyleLabel': 'Lifestyle',
    'adminProfiles.backgroundLabel': 'Background',
    'adminProfiles.otherLabel': 'Other',
    'common.notAssigned': 'Not assigned',
    'common.approve': 'Approve',
    'common.reject': 'Reject',

    // Admin Users
    'adminUsers.title': 'Users',
    'adminUsers.subtitle': 'Manage all users in the system',
    'adminUsers.export': 'Export',
    'adminUsers.addUser': 'Add User',
    'adminUsers.searchUsers': 'Search users...',
    'adminUsers.user': 'User',
    'adminUsers.role': 'Role',
    'adminUsers.editUser': 'Edit User',
    'adminUsers.createUser': 'Create User',
    'adminUsers.updateUser': 'Update User',
    'adminUsers.clickToUpload': 'Click to upload (max 5MB)',
    'adminUsers.firstName': 'First Name',
    'adminUsers.lastName': 'Last Name',
    'adminUsers.newPassword': 'New Password (leave blank to keep current)',
    'adminUsers.selectRole': 'Select "profile" or "client" role to allow this user to log in as a client',
    'adminUsers.matchmakerType': 'Matchmaker Type',
    'adminUsers.internal': 'Internal',
    'adminUsers.internalDesc': 'Powered by FindU. Membership plans controlled by Admin.',
    'adminUsers.external': 'External (White Label)',
    'adminUsers.externalDesc': 'Independent branding. Controls their own plans & payment info.',
    'adminUsers.affiliateTier': 'Affiliate Tier',
    'adminUsers.affiliateTierDesc': 'Sets the initial affiliate tier for this matchmaker (default: Starter)',
    'adminUsers.whiteLabel': 'White Label',
    'adminUsers.poweredByFindU': 'Powered by FindU',
    'adminUsers.type': 'Type',
    'adminUsers.status': 'Status',
    'adminUsers.created': 'Created',
    'adminUsers.actions': 'Actions',
    'adminUsers.loadingUsers': 'Loading users...',
    'adminUsers.allStatus': 'All Status',
    'adminUsers.active': 'Active',
    'adminUsers.inactive': 'Inactive',
    'adminUsers.suspended': 'Suspended',
    'adminUsers.edit': 'Edit',
    'adminUsers.suspend': 'Suspend',
    'adminUsers.activate': 'Activate',
    'adminUsers.password': 'Password *',
    'adminUsers.phone': 'Phone Number',
    'adminUsers.selectRoleOption': 'Select Role',
    'adminUsers.clientLogin': 'Client Login',
    'adminUsers.clientAccount': 'Client Account',
    'adminUsers.clientAccountDesc': 'This user will be able to log in to view their matches, messages, and profile.',
    'common.email': 'Email',

    // Admin Roles
    'adminRoles.title': 'Roles',
    'adminRoles.subtitle': 'Manage user roles and permissions',
    'adminRoles.createRole': 'Create Role',
    'adminRoles.createNewRole': 'Create New Role',
    'adminRoles.editRole': 'Edit Role',
    'adminRoles.roleName': 'Role Name',
    'adminRoles.enterRoleName': 'Enter role name',
    'adminRoles.description': 'Description',
    'adminRoles.describeRolePurpose': "Describe this role's purpose",
    'adminRoles.permissions': 'Permissions',
    'adminRoles.system': 'System',
    'adminRoles.users': 'Users',
    'adminRoles.permCount': 'Permissions',
    'adminRoles.profiles': 'Profiles',
    'adminRoles.viewProfiles': 'View Profiles',
    'adminRoles.createProfiles': 'Create Profiles',
    'adminRoles.editProfiles': 'Edit Profiles',
    'adminRoles.deleteProfiles': 'Delete Profiles',
    'adminRoles.approveProfiles': 'Approve Profiles',
    'adminRoles.matches': 'Matches',
    'adminRoles.viewMatches': 'View Matches',
    'adminRoles.createMatches': 'Create Matches',
    'adminRoles.editMatches': 'Edit Matches',
    'adminRoles.deleteMatches': 'Delete Matches',
    'adminRoles.messages': 'Messages',
    'adminRoles.viewMessages': 'View Messages',
    'adminRoles.sendMessages': 'Send Messages',
    'adminRoles.deleteMessages': 'Delete Messages',
    'adminRoles.tasks': 'Tasks',
    'adminRoles.viewTasks': 'View Tasks',
    'adminRoles.createTasks': 'Create Tasks',
    'adminRoles.editTasks': 'Edit Tasks',
    'adminRoles.deleteTasks': 'Delete Tasks',
    'adminRoles.calendar': 'Calendar',
    'adminRoles.viewCalendar': 'View Calendar',
    'adminRoles.createEvents': 'Create Events',
    'adminRoles.editEvents': 'Edit Events',
    'adminRoles.deleteEvents': 'Delete Events',
    'adminRoles.billing': 'Billing',
    'adminRoles.viewBilling': 'View Billing',
    'adminRoles.manageSubscriptions': 'Manage Subscriptions',
    'adminRoles.affiliates': 'Affiliates',
    'adminRoles.viewAffiliates': 'View Affiliates',
    'adminRoles.manageAffiliates': 'Manage Affiliates',
    'adminRoles.processPayouts': 'Process Payouts',
    'adminRoles.viewUsers': 'View Users',
    'adminRoles.createUsers': 'Create Users',
    'adminRoles.editUsers': 'Edit Users',
    'adminRoles.deleteUsers': 'Delete Users',
    'adminRoles.datingSchedules': 'Dating Schedules',
    'adminRoles.viewDatingSchedules': 'View Dating Schedules',
    'adminRoles.createDatingSchedules': 'Create Dating Schedules',
    'adminRoles.editDatingSchedules': 'Edit Dating Schedules',
    'adminRoles.deleteDatingSchedules': 'Delete Dating Schedules',
    'adminRoles.manageAssignedClientDating': 'Manage Assigned Client Dating',
    'adminRoles.coaches': 'Coaches',
    'adminRoles.viewCoaches': 'View Coaches',
    'adminRoles.manageCoaches': 'Manage Coaches',
    'adminRoles.assignClientsToCoaches': 'Assign Clients to Coaches',
    'adminRoles.manageAssignedClientMatches': 'Manage Assigned Client Matches',
    'adminRoles.kpiPerformance': 'KPI Performance',
    'adminRoles.viewKpi': 'View KPI Dashboard',
    'adminRoles.createKpi': 'Create Workflows & Assignments',
    'adminRoles.editKpi': 'Edit Workflows & Assignments',
    'adminRoles.deleteKpi': 'Delete Workflows & Assignments',
    'adminRoles.manageWorkflows': 'Manage Service Workflows',
    'adminRoles.manageAssignments': 'Manage Client Assignments',
    'adminRoles.reviewKpi': 'Review & Approve Stage Completions',
    'adminRoles.manageReminders': 'Manage Reminders & Warnings',
    'adminRoles.matchmakerType': 'Matchmaker Type',
    'adminRoles.internalMatchmaker': 'Internal Matchmaker (Powered by FindU)',
    'adminRoles.externalMatchmaker': 'External Matchmaker (White Label)',
    'adminRoles.manageOwnPlans': 'Manage Own Membership Plans',
    'adminRoles.manageOwnPaymentInfo': 'Manage Own Payment Instructions',
    'adminRoles.setPricePerMatch': 'Set Price per Match',
    'adminRoles.clientBilling': 'Client Billing',
    'adminRoles.viewClientInvoices': 'View Client Invoices',
    'adminRoles.createClientInvoices': 'Create Client Invoices',
    'adminRoles.editClientInvoices': 'Edit Client Invoices',
    'adminRoles.deleteClientInvoices': 'Delete Client Invoices',
    'adminRoles.approveClientPayments': 'Approve Client Payments',
    'adminRoles.manageClientSubscriptions': 'Manage Client Subscriptions',
    'adminRoles.settings': 'Settings',
    'adminRoles.viewSettings': 'View Settings',
    'adminRoles.editSettings': 'Edit Settings',
    'adminRoles.systemSettings': 'System Settings',
    'adminRoles.managePaymentInstructions': 'Manage Payment Instructions',
    'adminRoles.managePaypalIntegration': 'Manage PayPal Integration',
    'adminRoles.manageClientMembershipPlans': 'Manage Client Membership Plans',
    'adminRoles.directory': 'Directory',
    'adminRoles.viewProfileDirectory': 'View Profile Directory',
    'adminRoles.searchDirectory': 'Search Directory',
    'adminRoles.viewProfileDetails': 'View Profile Details',
    'adminRoles.favoriteProfiles': 'Favorite Profiles',
    'adminRoles.requestMatchesFromDirectory': 'Request Matches from Directory',
    'connections.viewConnections': 'View Connections',
    'connections.searchOtherMatchmakers': 'Search Other Matchmakers',
    'connections.messageOtherMatchmakers': 'Message Other Matchmakers',
    'connections.requestMatches': 'Request Matches',

    // Billing Page
    'billing.title': 'Client Billing',
    'billing.subtitle': 'Manage client memberships, invoices and payments',
    'billing.createInvoice': 'Create Invoice',
    'billing.totalRevenue': 'Total Revenue',
    'billing.pending': 'Pending',
    'billing.activeSubscriptions': 'Active Subscriptions',
    'billing.awaitingApproval': 'Awaiting Approval',
    'billing.cancelled': 'Cancelled',
    'billing.rejected': 'Rejected',
    'billing.membershipRejected': 'Membership Rejected',
    'billing.invoices': 'Invoices',
    'billing.subscriptions': 'Subscriptions',
    'billing.paymentByStages': 'Payment by Stages',
    'billing.installmentProgress': 'Installment Progress',
    'billing.stagesProgress': 'Stages Progress',
    'billing.amountPaid': 'Amount Paid',
    'billing.dueBalance': 'Due Balance',
    'billing.submitPayment': 'Submit Payment',
    'billing.itemsPaid': 'items paid',
    'billing.stagesPaid': 'stages paid',
    'billing.noInstallmentInvoices': 'No installment invoices found',
    'billing.noStagesInvoices': 'No stage-based invoices found',
    'billing.manageStagePayments': 'Manage stage-based payment invoices',
    'billing.membershipPlans': 'Membership Plans',
    'billing.paymentInstructions': 'Payment Instructions',
    'billing.plansManagedByAdmin': 'Plans are managed by Admin for internal matchmakers',
    'billing.monthly': 'monthly',
    'billing.yearly': 'yearly',
    'billing.noInvoices': 'No invoices found',

    // AI Assistant
    'aiAssistant.title': 'AI Matchmaker Assistant',
    'aiAssistant.subtitle': 'Voice & Text enabled',
    'aiAssistant.reminders': 'Reminders',
    'aiAssistant.myProfiles': 'My Profiles',
    'aiAssistant.myTasks': 'My Tasks',
    'aiAssistant.todaySchedule': "Today's Schedule",
    'aiAssistant.suggestions': 'Suggestions',
    'aiAssistant.typePlaceholder': 'Type a message or speak...',
    'aiAssistant.stopListening': 'Stop listening',
    'aiAssistant.startListening': 'Start listening',
    'aiAssistant.speechNotSupported': 'Speech recognition not supported in your browser',
    'aiAssistant.backgroundTaskStarted': 'Background task started',
    'aiAssistant.backgroundTasksRunning': 'background task(s) running...',
    'aiAssistant.greeting': "Hello! I'm your AI Assistant",
    'aiAssistant.greetingDesc': 'I can help you manage profiles, tasks, schedules, and more. Try speaking or typing!',
    'aiAssistant.errorProcessing': 'Sorry, I could not process your request. Please try again.',

    // Membership Due Info
    'membership.daysUntilDue': 'days until due',
    'membership.daysOverdue': 'days overdue',
    'membership.dueToday': 'Due today',
    'membership.noDueDate': 'No due date',
    'membership.nextPayment': 'Next payment',
    'membership.membershipDue': 'Membership Due',
    'membership.startDate': 'Membership Start Date',
    'membership.endDate': 'Membership End Date',
    'membership.nextPaymentDate': 'Next Payment Date',
    'membership.nextPaymentAmount': 'Next Payment Amount',

    // Category Sorting
    'customFields.moveUp': 'Move Up',
    'customFields.moveDown': 'Move Down',
    'customFields.reorderCategories': 'Reorder Categories',
    'customFields.dragToReorder': 'Drag to reorder',

    // Team Rankings - Affiliate
    'affiliate.teamRankings': 'Rankings',
    'affiliate.teamRankingsTitle': 'Team Rankings by Total Points',
    'affiliate.rank': 'Rank',
    'affiliate.rankName': 'Name',
    'affiliate.totalPoints': 'Total Points',
    'affiliate.you': 'You',
    'affiliate.loadingRankings': 'Loading rankings...',
    'affiliate.noRankings': 'No Rankings Available',
    'affiliate.noRankingsDesc': 'Team rankings will appear here once there are active affiliates with points.',
    'affiliate.pointsHistory': 'Points History',

    // 1099 Tax Forms - Affiliate
    'affiliate.tax1099Forms': '1099 Forms',
    'affiliate.tax1099Description': 'Download your 1099 tax forms for tax filing purposes.',
    'affiliate.taxYear': 'Tax Year',
    'affiliate.formType': 'Form Type',
    'affiliate.uploadedDate': 'Uploaded Date',
    'affiliate.totalEarnings': 'Total Earnings',
    'affiliate.no1099Forms': 'No 1099 Forms Available',
    'affiliate.no1099FormsDesc': '1099 forms will appear here when uploaded by the administrator.',
    'affiliate.tax1099Info': 'Important Tax Information',
    'affiliate.tax1099InfoDesc': '1099-NEC forms are typically issued by January 31st for the previous tax year if you earned $600 or more. Please consult a tax professional for advice.',
    'affiliate.downloadFailed': 'Failed to download the form. Please try again.',

    // 1099 Tax Forms - Admin
    'adminAffiliates.manage1099': 'Manage 1099',
    'adminAffiliates.manage1099Forms': '1099 Tax Forms Management',
    'adminAffiliates.upload1099Form': 'Upload New 1099 Form',
    'adminAffiliates.uploaded1099Forms': 'Uploaded 1099 Forms',
    'adminAffiliates.no1099Forms': 'No 1099 forms uploaded yet',
    'adminAffiliates.form1099Uploaded': '1099 form uploaded successfully',
    'adminAffiliates.form1099Deleted': '1099 form deleted successfully',
    'adminAffiliates.confirm1099Delete': 'Are you sure you want to delete this 1099 form?',
    'adminAffiliates.invalidFileType': 'Invalid file type. Please upload PDF, JPG, PNG, or GIF.',
    'adminAffiliates.fileTooLarge': 'File is too large. Maximum size is 10MB.',
    'adminAffiliates.selectFile': 'Select File',
    'adminAffiliates.dragOrClick': 'Drag & drop or click to select file',
    'adminAffiliates.uploadForm': 'Upload 1099 Form',
    'adminAffiliates.uploadFailed': 'Failed to upload 1099 form',
    'adminAffiliates.deleteFailed': 'Failed to delete 1099 form',
    'adminAffiliates.notes': 'Notes',
    'adminAffiliates.notesPlaceholder': 'Add any notes about this form...',

    // Admin Profiles Modal
    'adminProfiles.professional': 'Professional',
    'adminProfiles.noBio': 'No bio provided',
    'adminProfiles.assignment': 'Assignment',
    'adminProfiles.notAssigned': 'Not assigned',
    'adminProfiles.catGeneral': 'General Information',
    'adminProfiles.catPersonal': 'Personal Details',
    'adminProfiles.catPreferences': 'Preferences',
    'adminProfiles.catLifestyle': 'Lifestyle',
    'adminProfiles.catBackground': 'Background',
    'adminProfiles.catOther': 'Other',
    'adminProfiles.serviceAgreement': 'Service Agreement',
    'adminProfiles.agreementSigned': 'Agreement Signed',
    'adminProfiles.downloadAgreement': 'Download Agreement',
    'adminProfiles.signedOn': 'Signed on',
    'adminProfiles.noAgreement': 'No agreement signed',
    'adminProfiles.flags': 'Flags',
    'adminProfiles.reports': 'Reports',
    'adminProfiles.reason': 'Reason',
    'adminProfiles.reportedBy': 'Reported by',
    'adminProfiles.date': 'Date',
    'adminProfiles.country': 'Country',
    'adminProfiles.stateProvince': 'State/Province',
    'adminProfiles.searchCountry': 'Search country...',
    'adminProfiles.searchState': 'Search state/province...',
    'adminProfiles.searchCity': 'Search city...',
    'adminProfiles.password': 'Password',
    'adminProfiles.passwordPlaceholder': 'Enter password',
    'adminProfiles.passwordLeaveBlank': 'Leave blank to keep unchanged',
    'adminProfiles.dateOfBirth': 'Date of Birth',
    'adminProfiles.starSign': 'Star Sign',
    'adminProfiles.autoFromDOB': 'Auto from DOB',
    'adminProfiles.profileType': 'Profile Type',
    'adminProfiles.selectProfileType': 'Select Profile Type',
    'adminProfiles.lead': 'Lead',
    'adminProfiles.member': 'Member',
    'adminProfiles.noProfilesFound': 'No profiles found',
    'adminProfiles.offlineId': 'Offline ID',
    'adminProfiles.noProfilesAwaiting': 'No profiles awaiting review',
    'adminProfiles.addCustomField': 'Add Custom Field',
    'adminProfiles.editCustomField': 'Edit Custom Field',
    'adminProfiles.fieldName': 'Field Name (Internal)',
    'adminProfiles.displayLabel': 'Display Label',
    'adminProfiles.fieldType': 'Field Type',
    'adminProfiles.textInput': 'Text Input',
    'adminProfiles.textArea': 'Text Area (Multi-line)',
    'adminProfiles.dropdownSelect': 'Dropdown Select',
    'adminProfiles.radioButtons': 'Radio Buttons',
    'adminProfiles.checkboxes': 'Checkboxes (Multiple)',
    'adminProfiles.options': 'Options (comma separated)',
    'adminProfiles.displayOrder': 'Display Order',
    'adminProfiles.requiredField': 'Required Field',
    'adminProfiles.createField': 'Create Field',
    'adminProfiles.updateField': 'Update Field',
    'adminProfiles.occupation': 'Occupation',
    'adminProfiles.education': 'Education',
    'adminProfiles.email': 'Email',
    'adminProfiles.phone': 'Phone',
    'adminProfiles.generateCard': 'Generate Card',
    'adminProfiles.flagPhotoReview': 'Photo Review',
    'adminProfiles.flagPhotoMismatch': 'Photo Mismatch',
    'adminProfiles.flagIncomplete': 'Incomplete',
    'adminProfiles.flagSuspicious': 'Suspicious',
    'adminProfiles.flagReported': 'Reported',
    'adminProfiles.min': 'Min',
    'adminProfiles.max': 'Max',
    'adminProfiles.to': 'to',

    // KPI Performance Management
    'kpi.title': 'KPI Performance Management',
    'kpi.subtitle': 'Monitor and manage task performance for coaches and matchmakers',
    'kpi.totalTasks': 'Total Tasks',
    'kpi.completed': 'Completed',
    'kpi.inProgress': 'In Progress',
    'kpi.overdue': 'Overdue',
    'kpi.completionRate': 'Completion Rate',
    'kpi.pending': 'Pending',
    'kpi.cancelled': 'Cancelled',
    'kpi.staffPerformance': 'Staff Performance',
    'kpi.assignee': 'Assignee',
    'kpi.allAssignees': 'All Assignees',
    'kpi.allStatuses': 'All Statuses',
    'kpi.allPriorities': 'All Priorities',
    'kpi.allTypes': 'All Types',
    'kpi.searchTasks': 'Search tasks...',
    'kpi.createTask': 'Create Task',
    'kpi.editTask': 'Edit Task',
    'kpi.deleteTask': 'Delete Task',
    'kpi.deleteConfirm': 'Are you sure you want to delete this task?',
    'kpi.taskTitle': 'Task Title',
    'kpi.description': 'Description',
    'kpi.taskType': 'Task Type',
    'kpi.priority': 'Priority',
    'kpi.status': 'Status',
    'kpi.dueDate': 'Due Date',
    'kpi.dueTime': 'Due Time',
    'kpi.assignTo': 'Assign To',
    'kpi.selectAssignee': 'Select Assignee',
    'kpi.dateFrom': 'Date From',
    'kpi.dateTo': 'Date To',
    'kpi.noTasks': 'No tasks found',
    'kpi.noStaff': 'No staff data available',
    'kpi.taskCreated': 'Task created successfully',
    'kpi.taskUpdated': 'Task updated successfully',
    'kpi.taskDeleted': 'Task deleted successfully',
    'kpi.loadFailed': 'Failed to load KPI data',
    'kpi.saveFailed': 'Failed to save task',
    'kpi.tasks': 'Tasks',
    'kpi.overview': 'Overview',
    'kpi.role': 'Role',
    'kpi.coach': 'Coach',
    'kpi.matchmaker': 'Matchmaker',
    'kpi.daily': 'Daily',
    'kpi.weekly': 'Weekly',
    'kpi.monthly': 'Monthly',
    'kpi.oneTime': 'One Time',
    'kpi.low': 'Low',
    'kpi.medium': 'Medium',
    'kpi.high': 'High',
    'kpi.urgent': 'Urgent',
    'kpi.staffMember': 'Staff Member',
    'kpi.refresh': 'Refresh',

    // KPI - Status Labels
    'kpi.active': 'Active',
    'kpi.paused': 'Paused',
    'kpi.unnamed': 'Unnamed',
    'kpi.statusActive': 'Active',
    'kpi.statusCompleted': 'Completed',
    'kpi.statusPaused': 'Paused',
    'kpi.statusCancelled': 'Cancelled',
    'kpi.statusTransferred': 'Transferred',
    'kpi.statusPending': 'Pending',
    'kpi.statusInProgress': 'In Progress',
    'kpi.statusFailed': 'Failed',
    'kpi.statusSkipped': 'Skipped',
    'kpi.statusSubmitted': 'Submitted',
    'kpi.statusApproved': 'Approved',
    'kpi.statusRejected': 'Rejected',
    'kpi.statusSubmitted': 'Submitted',
    'kpi.statusNotStarted': 'Not Started',
    'kpi.statusOverdue': 'Overdue',
    'kpi.statusDraft': 'Draft',
    'kpi.statusScheduled': 'Scheduled',
    'kpi.pageNumber': 'Page {n}',
    'kpi.statusSent': 'Sent',
    'kpi.statusViewed': 'Viewed',
    'kpi.statusExpired': 'Expired',
    'kpi.statusDismissed': 'Dismissed',
    'kpi.statusInactive': 'Inactive',
    'kpi.statusReviewing': 'Reviewing',

    // KPI - Warning Labels
    'kpi.warningYellow': 'Yellow',
    'kpi.warningOrange': 'Orange',
    'kpi.warningRed': 'Red',

    // KPI - Overview Tab
    'kpi.loadingDashboard': 'Loading dashboard...',
    'kpi.activeClients': 'Active Clients',
    'kpi.totalRevenue': 'Total Revenue',
    'kpi.serviceValue': 'Service Value',
    'kpi.avgSatisfaction': 'Avg Satisfaction',
    'kpi.activeWarnings': 'Active Warnings',
    'kpi.coachPerformance': 'Coach Performance',
    'kpi.thCoach': 'Coach',
    'kpi.thActive': 'Active',
    'kpi.thCompleted': 'Completed',
    'kpi.thTotalValue': 'Total Value',
    'kpi.thEarned': 'Earned',
    'kpi.thWarnings': 'Warnings',
    'kpi.none': 'None',
    'kpi.noResults': 'No results found',
    'kpi.noCoachData': 'No coach data yet. Create a workflow and assign clients to get started.',
    'kpi.earnPercentage': 'Earn %',
    'kpi.earnOfBase': 'Earn % of Base Amount',
    'kpi.deductionAmount': 'Deduction (¥)',
    'kpi.moveUp': 'Move Up',
    'kpi.moveDown': 'Move Down',

    // Supervisor KPI
    'supervisor.title': 'Supervisor KPI Review',
    'supervisor.subtitle': 'Review and manage coach performance across all assignments',
    'supervisor.clientProgress': 'Client Progress',
    'supervisor.warningSystem': 'Warning System',
    'supervisor.feedbackRequired': 'Please enter feedback text',
    'supervisor.feedbackAdded': 'Feedback added successfully',
    'supervisor.viewAllFeedback': 'View All Feedback',
    'supervisor.submit': 'Submit',
    'supervisor.overview': 'Overview',
    'supervisor.progress': 'Progress',
    'supervisor.warnings': 'Warnings',
    'supervisor.reviewStatus': 'Review Status',
    'supervisor.reviewing': 'Reviewing',
    'supervisor.approved': 'Approved',
    'supervisor.rejected': 'Rejected',
    'supervisor.addFeedback': 'Add Feedback',
    'supervisor.feedback': 'Feedback',
    'supervisor.feedbackPlaceholder': 'Enter your feedback for this stage...',
    'supervisor.submitFeedback': 'Submit Feedback',
    'supervisor.previousFeedback': 'Previous Feedback',
    'supervisor.noFeedback': 'No feedback yet',
    'supervisor.activeClients': 'Active Clients',
    'supervisor.completed': 'Completed',
    'supervisor.avgSatisfaction': 'Avg Satisfaction',
    'supervisor.activeWarnings': 'Active Warnings',
    'supervisor.coachPerformance': 'Coach Performance',
    'supervisor.coach': 'Coach',
    'supervisor.active': 'Active',
    'supervisor.warningsCol': 'Warnings',
    'supervisor.assignmentList': 'Assignment List',
    'supervisor.allCoaches': 'All Coaches',
    'supervisor.allStatuses': 'All Statuses',
    'supervisor.viewDetail': 'View Detail',
    'supervisor.assignmentDetail': 'Assignment Detail',
    'supervisor.stages': 'Stages',
    'supervisor.action': 'Action',
    'supervisor.stage': 'Stage',
    'supervisor.status': 'Status',
    'supervisor.weight': 'Weight',
    'supervisor.earned': 'Earned',
    'supervisor.review': 'Review',
    'supervisor.deliverables': 'Deliverables',
    'supervisor.deduction': 'Deduction',
    'supervisor.feedbackSubmitted': 'Feedback submitted successfully',
    'supervisor.reviewUpdated': 'Review status updated',
    'supervisor.noAssignments': 'No assignments found',
    'supervisor.noWarnings': 'No warnings found',
    'supervisor.warningLevel': 'Warning Level',
    'supervisor.warningDescription': 'Description',
    'supervisor.warningDate': 'Date',
    'supervisor.questionnaireCompleted': 'Completed',
    'supervisor.requiredItems': 'Required Items',
    'supervisor.photoFiles': 'Photos/Files',
    'supervisor.appointmentLabel': 'Appointment',
    'supervisor.videoMeetingLabel': 'Video Meeting',
    'supervisor.matchingLabel': 'Matching',
    'supervisor.confirmationLabel': 'Confirmation',
    'supervisor.candidateLabel': 'Candidate',
    'supervisor.datingScheduleLabel': 'Dating Schedule',
    'supervisor.followupLabel': 'Follow-up',
    'supervisor.questionnaireLabel': 'Questionnaire',
    'supervisor.loadResultsFailed': 'Failed to load questionnaire results',
    'supervisor.questionnaireResults': 'Questionnaire Results',
    'supervisor.noResults': 'No results available',
    'supervisor.avgScore': 'Average Score',
    'supervisor.submittedAt': 'Submitted',
    'supervisor.primaryLoveLanguage': 'Primary Love Language',
    'supervisor.option': 'Option',
    'supervisor.timesSelected': 'times selected',
    'supervisor.optionLabel': 'Option',
    'supervisor.loveLanguage': 'Love Language',
    'supervisor.timesSelectedHeader': 'Times Selected',

    // Deliverable Info Panels
    'deliverableInfo.uploadedFiles': 'Uploaded Files',
    'deliverableInfo.videoMeeting': 'Video Meeting',
    'deliverableInfo.matches': 'Matches',
    'deliverableInfo.datingSchedules': 'Dating Schedules',
    'deliverableInfo.datingSchedule': 'Dating Schedule',
    'deliverableInfo.questionnaireResults': 'Questionnaire Results',
    'deliverableInfo.followUp': 'Follow-up',
    'deliverableInfo.appointment': 'Appointment',
    'deliverableInfo.viewRecording': 'View Recording',
    'deliverableInfo.viewResults': 'View Results',
    'deliverableInfo.scheduled': 'Scheduled',
    'deliverableInfo.attempt': 'Attempt',
    'deliverableInfo.interval': 'Interval',
    'deliverableInfo.days': 'days',
    'deliverableInfo.notes': 'Notes',
    'deliverableInfo.viewInfo': 'View Info',
    'deliverableInfo.status': 'Status',
    'deliverableInfo.confirmation': 'Confirmation',

    // Supervisor KPI - Additional strings
    'supervisor.progressStatus': 'Progress Status',
    'supervisor.noWarnings': 'No warnings',
    'supervisor.loadingRecording': 'Loading...',
    'supervisor.meetingLink': 'Meeting Link',
    'supervisor.typeLabel': 'Type',
    'supervisor.pendingStatus': 'pending',
    'supervisor.scheduledStatus': 'scheduled',
    'supervisor.completePercent': 'Complete',
    'supervisor.maxScore': 'Max',
    'supervisor.assessmentResults': 'Assessment Results',
    'supervisor.partnerPreferences': 'Partner Preferences',
    'supervisor.yourResults': 'Your Results',
    'supervisor.attachmentStyle': 'Attachment Style',
    'supervisor.primaryAttachmentStyle': 'Your Primary Attachment Style',
    'supervisor.notProvided': '(Not provided)',
    'supervisor.upTo': 'Up to',
    'supervisor.prevImage': '← Prev',
    'supervisor.nextImage': 'Next →',
    'supervisor.downloadImage': 'Download',
    'supervisor.closeVideo': 'Click outside or press ESC to close',
    'supervisor.navigationKeys': 'Press ESC or click outside to close • Use arrow keys to navigate',
    'supervisor.dateLabel': 'Date',

    // KPI - Workflows Tab
    'kpi.serviceWorkflows': 'Service Workflows',
    'kpi.createWorkflow': 'Create Workflow',
    'kpi.noWorkflows': 'No workflows yet. Click "Create Workflow" to set up service stages and commission.',
    'kpi.base': 'Base',
    'kpi.commission': 'Commission',
    'kpi.commissionPool': 'Commission Pool',
    'kpi.stagesCount': 'stages',
    'kpi.activeClientsCount': 'active clients',
    'kpi.edit': 'Edit',
    'kpi.editWorkflow': 'Edit Workflow',
    'kpi.createServiceWorkflow': 'Create Service Workflow',
    'kpi.workflowName': 'Workflow Name *',
    'kpi.workflowNamePlaceholder': 'e.g. Standard $3,980 Service',
    'kpi.baseServiceAmount': 'Base Service Amount ($)',
    'kpi.customAmount': 'Custom Amount',
    'kpi.approvedClientPayments': 'Approved Client Payments',
    'kpi.membershipPlans': 'Membership Plans',
    'kpi.enterCustomAmount': 'Enter custom amount...',
    'kpi.selectFromPayments': 'Select from approved payments or enter custom amount',
    'kpi.commissionRate': 'Commission Rate (%)',
    'kpi.serviceStages': 'Service Stages',
    'kpi.totalWeight': 'Total Weight',
    'kpi.shouldBe100': '(should be 100%)',
    'kpi.recalculate': 'Recalculate',
    'kpi.addStage': 'Add Stage',
    'kpi.unnamedStage': 'Unnamed Stage',
    'kpi.deliverables': 'deliverables',
    'kpi.stageCode': 'Code',
    'kpi.stageName': 'Stage Name',
    'kpi.allowPageCreation': 'Allow Page Creation',
    'kpi.allowPageCreationDesc': 'Allow coaches to add/edit multiple pages for this stage (different coaches can submit different content)',
    'kpi.enabled': 'Enabled',
    'kpi.workContent': 'Work Content',
    'kpi.weightPercent': 'Weight %',
    'kpi.commissionDollar': 'Commission ($)',
    'kpi.timeLimitDays': 'Time Limit (days)',
    'kpi.assessmentCriteria': 'Assessment Criteria',
    'kpi.requiredDeliverables': 'Required Deliverables (Photo Upload)',
    'kpi.add': 'Add',
    'kpi.deliverableName': 'Deliverable name...',
    'kpi.deliverableNameZh': 'Chinese deliverable name...',
    'kpi.photo': 'Photo',
    'kpi.file': 'File',
    'kpi.uploadFile': 'Upload File',
    'kpi.toastFileUploaded': 'File uploaded successfully',
    'kpi.toastFileUploadFailed': 'Failed to upload file',
    'kpi.videoMeeting': 'Video Meeting',
    'kpi.audioCall': 'Audio Call',
    'kpi.matching': 'Matching',
    'kpi.datingSchedule': 'Dating Schedule',
    'kpi.questionnaire': 'Questionnaire',
    'kpi.selectQuestionnaire': 'Select questionnaire...',
    'kpi.followUp': 'Follow-up',
    'kpi.editFollowUp': 'Edit',
    'kpi.createFollowUpConfig': 'Create Follow-up',
    'kpi.editFollowUpConfig': 'Edit Follow-up',
    'kpi.saveFollowUp': 'Save Follow-up',
    'kpi.followUpType': 'Follow-up Type',
    'kpi.followUpPostMeeting': 'Post Meeting',
    'kpi.followUpCheckIn': 'Check-in',
    'kpi.followUpProgressReview': 'Progress Review',
    'kpi.followUpCustom': 'Custom',
    'kpi.followUpIntervalDays': 'Reminder Interval (days)',
    'kpi.followUpIntervalHelp': 'Days between each follow-up attempt',
    'kpi.intervalDays': 'Interval',
    'kpi.maxAttempts': 'Max Attempts',
    'kpi.postMeeting': 'Post Meeting',
    'kpi.checkIn': 'Check-in',
    'kpi.progressReview': 'Progress Review',
    'kpi.custom': 'Custom',
    'kpi.followUpMaxAttempts': 'Max Attempts',
    'kpi.followUpMaxAttemptsHelp': 'Maximum number of follow-up attempts',
    'kpi.followUpDescription': 'Follow-up Instructions',
    'kpi.followUpDescPlaceholder': 'Describe what the coach should follow up on...',
    'kpi.followUpDescPlaceholderZh': 'Describe instructions (Chinese)...',
    'kpi.appointment': 'Appointment',
    'kpi.reminderDays': 'Reminder (days)',
    'kpi.confirmation': 'Confirmation',
    'kpi.matchCount': 'Match Count',
    'kpi.fileCount': 'File Count',
    'kpi.selectQuestionnaires': 'Select Questionnaires',
    'kpi.stage': 'Stage',
    'kpi.deliverable': 'Deliverable',
    'kpi.zhLabel': '🇨🇳 中文',
    'kpi.enLabel': '🇺🇸 EN',
    'kpi.workflowNameZh': 'Workflow Name (Chinese)',
    'kpi.descriptionZh': 'Description (Chinese)',
    'kpi.stageNameZh': 'Stage Name (Chinese)',
    'kpi.workContentZh': 'Work Content (Chinese)',
    'kpi.assessmentCriteriaZh': 'Assessment Criteria (Chinese)',
    'kpi.cancel': 'Cancel',
    'kpi.updateWorkflow': 'Update Workflow',
    'kpi.save': 'Save',

    // KPI - Client Progress Tab
    'kpi.clientProgressTracking': 'Client Progress Tracking',
    'kpi.assignClient': 'Assign Client',
    'kpi.allCoaches': 'All Coaches',
    'kpi.noAssignments': 'No assignments yet. Use workflows to assign clients to coaches.',
    'kpi.unknownClient': 'Unknown Client',
    'kpi.coachLabel': 'Coach',
    'kpi.workflowLabel': 'Workflow',
    'kpi.stageLabel': 'Stage',
    'kpi.earnedLabel': 'Earned',
    'kpi.stagesProgress': 'stages',
    'kpi.pageOf': 'Page {page} of {pages}',
    'kpi.assignClientToCoach': 'Assign Client to Coach',
    'kpi.serviceWorkflow': 'Service Workflow *',
    'kpi.selectWorkflow': 'Select workflow...',
    'kpi.coachRequired': 'Coach *',
    'kpi.selectCoach': 'Select coach...',
    'kpi.clientProfile': 'Client (Profile)',
    'kpi.selectProfile': 'Select profile (optional)...',
    'kpi.clientNameNoProfile': 'Client Name (no profile)',
    'kpi.enterClientName': 'Enter client name...',
    'kpi.serviceAmount': 'Service Amount ($)',
    'kpi.notes': 'Notes',
    'kpi.assign': 'Assign',
    'kpi.serviceFee': 'Service Fee',
    'kpi.complete': 'Complete',
    'kpi.deliverableNeedApproval': 'need approval',
    'kpi.failed': 'Failed',
    'kpi.uploadPhoto': 'Upload Photo',

    // KPI - Questionnaires Tab
    'kpi.questionnaires': 'Questionnaires',
    'kpi.createQuestionnaire': 'Create Questionnaire',
    'kpi.createSatisfactionSurvey': 'Create Satisfaction Survey',
    'kpi.noQuestionnaires': 'No questionnaires yet. Create an evaluation form to send to clients.',
    'kpi.questions': 'questions',
    'kpi.submissions': 'submissions',
    'kpi.avgScore': 'Avg Score',
    'kpi.send': 'Send',
    'kpi.editQuestionnaire': 'Edit Questionnaire',
    'kpi.titleRequired': 'Title *',
    'kpi.type': 'Type',
    'kpi.typeEvaluation': 'Evaluation',
    'kpi.typeFeedback': 'Feedback',
    'kpi.typeAssessment': 'Assessment',
    'kpi.typeLoveLanguage': 'Love Language',
    'kpi.typeProfilePreferences': 'Partner Preferences',
    'kpi.typeCustom': 'Custom',
    'kpi.profilePreferencesInfo': 'Partner Preferences Questionnaire',
    'kpi.profilePreferencesDesc': 'This questionnaire type automatically loads partner preference fields (mate_*). When a client completes this questionnaire, their responses will be saved directly to their profile preferences. No manual questions needed.',
    'kpi.questionsCount': 'Questions',
    'kpi.addQuestion': 'Add Question',
    'kpi.questionPlaceholder': 'Question content...',
    'kpi.qtRating': 'Rating',
    'kpi.qtText': 'Text',
    'kpi.qtMultipleChoice': 'Multiple Choice',
    'kpi.qtYesNo': 'Yes/No',
    'kpi.qtScale': 'Scale',
    'kpi.maxRating': 'Max Rating:',
    'kpi.update': 'Update',
    'kpi.create': 'Create',
    'kpi.sendQuestionnaire': 'Send Questionnaire',
    'kpi.clientAssignment': 'Client Assignment (optional)',
    'kpi.clientName': 'Client Name',
    'kpi.clientNamePlaceholder': 'Client name...',
    'kpi.clientEmail': 'Client Email',
    'kpi.linkGenerated': 'Questionnaire link generated!',
    'kpi.copy': 'Copy',
    'kpi.linkValidDays': 'Share this link with the client. Valid for 30 days.',
    'kpi.close': 'Close',
    'kpi.generateLink': 'Generate Link',
    'kpi.regenerateLink': 'Regenerate Link',
    'kpi.shareQuestionnaire': 'Share Questionnaire',
    'kpi.shareQuestionnaireNote': 'Generate a shareable questionnaire link for this deliverable. The link will be valid for 30 days.',
    'kpi.generating': 'Generating...',
    'kpi.questionnaireCompleted': 'Completed',
    'kpi.awaitingResponse': 'Awaiting Response',
    'kpi.loadResultsFailed': 'Failed to load questionnaire results',
    'kpi.questionnaireResults': 'Questionnaire Results',
    'kpi.noResults': 'No results available',
    'kpi.avgScore': 'Average Score',
    'kpi.submittedAt': 'Submitted',
    'kpi.primaryLoveLanguage': 'Primary Love Language',
    'kpi.option': 'Option',
    'kpi.timesSelected': 'times selected',
    'kpi.optionLabel': 'Option',
    'kpi.loveLanguage': 'Love Language',
    'kpi.timesSelectedHeader': 'Times Selected',

    // KPI - Reminders Tab
    'kpi.coachReminders': 'Coach Reminders',
    'kpi.createReminder': 'Create Reminder',
    'kpi.noReminders': 'No reminders yet. Create reminders to notify coaches about follow-ups.',
    'kpi.client': 'Client',
    'kpi.date': 'Date',
    'kpi.time': 'Time',
    'kpi.repeatEveryDays': 'Repeat every {days} days',
    'kpi.dismiss': 'Dismiss',
    'kpi.message': 'Message',
    'kpi.reminderTypeCustom': 'Custom',
    'kpi.reminderTypeStageDeadline': 'Stage Deadline',
    'kpi.reminderTypeFollowUp': 'Follow-up',
    'kpi.reminderTypeMeeting': 'Meeting',
    'kpi.triggerDate': 'Trigger Date',
    'kpi.triggerTime': 'Trigger Time',
    'kpi.recurring': 'Recurring',
    'kpi.every': 'Every',
    'kpi.days': 'days',
    'kpi.followUpClient': 'e.g. Follow up client',

    // KPI - Warnings Tab
    'kpi.warningSystem': 'Warning System',
    'kpi.yellowWarningDesc': '7 days without meeting — Assistant intervenes',
    'kpi.orangeWarningDesc': '8 days without meeting — QC specialist intervenes',
    'kpi.redWarningDesc': '10 days without meeting — Founder intervenes, consider coach change',
    'kpi.noActiveWarnings': 'No active warnings. All coaches are on track!',
    'kpi.daysOverdue': 'days overdue',
    'kpi.resolutionNotes': 'Resolution notes:',
    'kpi.resolve': 'Resolve',

    // KPI - Tasks Tab
    'kpi.taskManagement': 'Task Management',
    'kpi.total': 'Total',
    'kpi.thTitle': 'Title',
    'kpi.thAssignee': 'Assignee',
    'kpi.thType': 'Type',
    'kpi.thPriority': 'Priority',
    'kpi.thStatus': 'Status',
    'kpi.thDueDate': 'Due Date',
    'kpi.thActions': 'Actions',
    'kpi.assigneeRequired': 'Assignee *',
    'kpi.pleaseSelect': 'Please select...',

    // KPI - Toast Messages
    'kpi.toastEnterWorkflowName': 'Please enter workflow name',
    'kpi.toastWorkflowUpdated': 'Workflow updated',
    'kpi.toastWorkflowCreated': 'Workflow created',
    'kpi.toastSaveWorkflowFailed': 'Failed to save workflow',
    'kpi.toastLoadWorkflowFailed': 'Failed to load workflow',
    'kpi.toastDeleteWorkflowConfirm': 'Delete this workflow?',
    'kpi.toastWorkflowDeleted': 'Workflow deleted',
    'kpi.duplicate': 'Copy',
    'kpi.duplicateWorkflow': 'Duplicate Workflow',
    'kpi.toastWorkflowDuplicated': 'Workflow duplicated — review and save',
    'kpi.toastDeleteFailed': 'Failed to delete',
    'kpi.toastSelectWorkflowAndCoach': 'Please select workflow and coach',
    'kpi.toastClientAssigned': 'Client assigned to coach',
    'kpi.toastCreateAssignmentFailed': 'Failed to create assignment',
    'kpi.toastLoadAssignmentFailed': 'Failed to load assignment details',
    'kpi.toastStageUpdated': 'Stage updated',
    'kpi.toastUpdateStageFailed': 'Failed to update stage',
    'kpi.toastPhotoUploaded': 'Photo uploaded',
    'kpi.toastUploadPhotoFailed': 'Failed to upload photo',
    'kpi.toastEnterTitle': 'Please enter title',
    'kpi.toastQuestionnaireUpdated': 'Questionnaire updated',
    'kpi.toastQuestionnaireCreated': 'Questionnaire created',
    'kpi.toastSaveQuestionnaireFailed': 'Failed to save questionnaire',
    'kpi.toastLinkGenerated': 'Questionnaire link generated',
    'kpi.toastSendQuestionnaireFailed': 'Failed to send questionnaire',
    'kpi.toastLoadQuestionnaireFailed': 'Failed to load questionnaire',
    'kpi.toastDeleteQuestionnaireConfirm': 'Delete this questionnaire?',
    'kpi.toastDeleted': 'Deleted',
    'kpi.toastFailed': 'Failed',
    'kpi.toastLinkCopied': 'Link copied!',
    'kpi.toastSelectCoachAndTitle': 'Please select coach and enter title',
    'kpi.toastReminderCreated': 'Reminder created',
    'kpi.toastCreateReminderFailed': 'Failed to create reminder',
    'kpi.toastDeleteReminderConfirm': 'Delete this reminder?',
    'kpi.toastDismissed': 'Dismissed',
    'kpi.toastWarningResolved': 'Warning resolved',
    'kpi.toastSelectAssigneeAndTitle': 'Please select assignee and enter title',
    'kpi.toastDeleteTaskConfirm': 'Delete this task?',
    'kpi.toastSaveOrderFailed': 'Failed to save order',

    // KPI - Main
    'kpi.mainSubtitle': 'Service workflow management, coach performance tracking, and client satisfaction assessment',
    'kpi.tabOverview': 'Overview',
    'kpi.tabWorkflows': 'Workflows',
    'kpi.tabProgress': 'Progress',
    'kpi.tabQuestionnaires': 'Questionnaires',
    'kpi.tabReminders': 'Reminders',
    'kpi.tabWarnings': 'Warnings',
    'kpi.tabWeeklyContacts': 'Weekly Contacts',
    'kpi.weeklyContactsTitle': 'Weekly Contact Logs',
    'kpi.allCoaches': 'All Coaches',
    'kpi.allWeeks': 'All Weeks',
    'kpi.totalRecords': 'Total',
    'kpi.noWeeklyContacts': 'No weekly contact records found',
    'kpi.weekStart': 'Week',
    'kpi.coach': 'Coach',
    'kpi.client': 'Client',
    'kpi.contactType': 'Type',
    'kpi.matching': 'Matching',
    'kpi.datingSchedule': 'Dating',
    'kpi.submittedAt': 'Submitted',
    'kpi.refresh': 'Refresh',
    'kpi.allClientsContacted': 'All clients have been contacted this week!',
    'kpi.allStatus': 'All',
    'kpi.choiceOptions': 'Choice Options',
    'kpi.city': 'City',
    'kpi.contacted': 'Contacted',
    'kpi.done': 'Done',
    'kpi.editCoachPerformance': 'Edit Coach Performance',
    'kpi.existingLink': 'Existing Link',
    'kpi.lastContactDate': 'Last Contact',
    'kpi.never': 'Never',
    'kpi.noChoicesYet': 'No choices yet. Click "Add" to add options.',
    'kpi.notContacted': 'Not Contacted',
    'kpi.sentTo': 'Sent to',
    'kpi.tabTasks': 'Tasks',
    'kpi.tabFollowups': 'Follow-ups',
    'kpi.tabConfirmations': 'Confirmations',

    // KPI - Assignment filter statuses
    'kpi.filterActive': 'Active',
    'kpi.filterPaused': 'Paused',
    'kpi.filterCompleted': 'Completed',
    'kpi.filterCancelled': 'Cancelled',
    'kpi.filterTransferred': 'Transferred',

    // KPI - Detail labels
    'kpi.notApplicable': 'Not Applicable',

    // Coach KPI
    'coachKpi.title': 'KPI Performance',
    'coachKpi.subtitle': 'Manage client progress, meetings, and deliverables',
    'coachKpi.tabClients': 'My Clients',
    'coachKpi.tabStats': 'My Stats',
    'coachKpi.tabMeetings': 'Video Meetings',
    'coachKpi.tabQuestionnaires': 'Questionnaires',
    'coachKpi.tabConfirmations': 'Confirmations',
    'coachKpi.tabFollowups': 'Follow-ups',
    'coachKpi.tabReminders': 'Reminders',
    'coachKpi.tabAppointments': 'Appointments',
    'coachKpi.tabMatches': 'Matches',
    'coachKpi.tabSchedules': 'Dating Schedules',
    'coachKpi.createMatch': 'Create Match',
    // ── Create-match modal filter strip + pagination + chips ──
    'coachKpi.filter.age': 'Age',
    'coachKpi.filter.height': 'Height',
    'coachKpi.filter.matchPct': 'Match',
    'coachKpi.filter.income': 'Income',
    'coachKpi.filter.city': 'City',
    'coachKpi.filter.state': 'State',
    'coachKpi.filter.country': 'Country',
    'coachKpi.filter.marital': 'Marital',
    'coachKpi.filter.children': 'Children',
    'coachKpi.filter.property': 'Property',
    'coachKpi.filter.tier': 'Tier',
    'coachKpi.filter.photo': 'Photo',
    'coachKpi.filter.min': 'min',
    'coachKpi.filter.max': 'max',
    'coachKpi.filter.cm': 'cm',
    'coachKpi.filter.any': 'Any',
    'coachKpi.filter.withPhoto': 'With photo',
    'coachKpi.filter.clearAll': 'Clear all',
    'coachKpi.tier.all': 'All',
    'coachKpi.tier.ideal': 'Ideal',
    'coachKpi.tier.acceptable': 'Acceptable',
    'coachKpi.tier.bottomLine': 'Bottom',
    'coachKpi.page.first': 'First',
    'coachKpi.page.prev': 'Previous',
    'coachKpi.page.next': 'Next',
    'coachKpi.page.last': 'Last',
    'coachKpi.page.pageOf': 'Page',
    'coachKpi.page.pageSuffix': '',
    'coachKpi.page.totalCount': 'Total',
    'coachKpi.page.range': 'Items',
    'coachKpi.regularCandidate': 'Regular',
    'coachKpi.matchPctLabel': 'Match',
    'coachKpi.offlineId': 'Offline ID',
    'coachKpi.existingLink': 'Existing Link',
    'coachKpi.generateQuestionnaireFailed': 'Failed to generate questionnaire link',
    'coachKpi.status': 'Status',
    'common.copyFailed': 'Copy failed',
    'common.deliverable': 'Deliverable',
    'common.saved': 'Saved',
    'deliverableInfo.clientResponse': 'Client Response',
    'deliverableInfo.type': 'Type',
    'coachKpi.createSchedule': 'Create Schedule',
    'coachKpi.profileA': 'Profile A',
    'coachKpi.profileB': 'Profile B',
    'coachKpi.matchPercentage': 'Match %',
    'coachKpi.selectProfile': 'Select Profile',
    'coachKpi.searchSelectProfile': 'Search and select profile...',
    'coachKpi.client': 'Client',
    'coachKpi.matchNotesPlaceholder': 'Why is this a good match?',
    'coachKpi.scheduleTitle': 'Title',
    'coachKpi.scheduleTitlePlaceholder': 'e.g. First Date - Coffee',
    'coachKpi.time': 'Time',
    'coachKpi.locationPlaceholder': 'Venue name',
    'coachKpi.addressPlaceholder': 'Full address',
    'coachKpi.scheduleNotesPlaceholder': 'Special instructions or tips for the date',
    'coachKpi.matchNotes': 'Notes',
    'coachKpi.noMatches': 'No matches yet',
    'coachKpi.noSchedules': 'No dating schedules yet',
    'coachKpi.scheduledDate': 'Scheduled Date',
    'coachKpi.scheduledTime': 'Scheduled Time',
    'coachKpi.duration': 'Duration (min)',
    'coachKpi.location': 'Location',
    'coachKpi.locationType': 'Location Type',
    'coachKpi.locationAddress': 'Address',
    'coachKpi.matchmakerNotes': 'Notes',
    'coachKpi.updateStatus': 'Update Status',
    'coachKpi.restaurant': 'Restaurant',
    'coachKpi.cafe': 'Cafe',
    'coachKpi.bar': 'Bar',
    'coachKpi.park': 'Park',
    'coachKpi.virtual': 'Virtual',
    'coachKpi.other': 'Other',
    'coachKpi.client1': 'Client 1',
    'coachKpi.client2': 'Client 2',
    'coachKpi.matchCreated': 'Match created successfully',
    'coachKpi.matchesCreated': 'match(es) created successfully',
    'coachKpi.matchesCreatedTitle': 'Matches Created!',
    'coachKpi.reviewLinkReady': 'Share this link with the client to review and confirm their matches:',
    'coachKpi.reviewLinkCopied': 'Link copied!',
    'coachKpi.waitingToConfirm': 'Waiting to Confirm',
    'coachKpi.matchConfirmed': 'Confirmed',
    'coachKpi.matchRejected': 'Rejected - Rematch',
    'coachKpi.reviewLink': 'Review link',
    'coachKpi.copyLink': 'Copy',
    'coachKpi.selectMatches': 'Select Matching Profiles',
    'coachKpi.oppositeGender': 'Opposite Gender',
    'coachKpi.searchProfiles': 'Search by name, email, city...',
    'coachKpi.profilesSelected': 'profile(s) selected',
    'coachKpi.noMatchingProfiles': 'No matching profiles found',
    'coachKpi.loadingProfiles': 'Loading profiles...',
    'coachKpi.createMatchCount': 'Create Match',
    'coachKpi.selectAtLeastOneProfile': 'Please select at least one profile',
    'coachKpi.scheduleCreated': 'Dating schedule created successfully',
    'coachKpi.statusUpdated': 'Status updated successfully',
    'coachKpi.searchClients': 'Search clients...',
    'coachKpi.allStatuses': 'All Statuses',
    'coachKpi.refresh': 'Refresh',
    'coachKpi.noAssignments': 'No client assignments yet',
    'coachKpi.noWorkflow': 'No workflow',
    'coachKpi.currentStage': 'Current Stage',
    'coachKpi.startedAt': 'Started',
    'coachKpi.stages': 'stages',
    'coachKpi.overallProgress': 'Overall Progress',
    'coachKpi.stagesCompleted': 'stages completed',
    'coachKpi.requestComplete': 'Request Complete',
    'coachKpi.allDeliverablesRequired': 'All deliverables must be checked',
    'coachKpi.awaitingApproval': 'Awaiting Approval',
    'coachKpi.awaitingAdmin': 'awaiting admin approval',
    'coachKpi.uploadPhoto': 'Photo',
    'coachKpi.notes': 'Notes',
    'coachKpi.deliverableSubmitted': 'Deliverable marked as submitted',
    'coachKpi.deliverableUnsubmitted': 'Deliverable unmarked',
    'coachKpi.deliverableError': 'Failed to update deliverable',
    'coachKpi.confirmRequestStage': 'Request admin to mark this stage as complete?',
    'coachKpi.stageRequestSent': 'Stage completion request sent',
    'coachKpi.stageRequestError': 'Failed to request stage completion',
    'coachKpi.photoUploaded': 'Photo uploaded successfully',
    'coachKpi.photoUploadError': 'Failed to upload photo',
    'coachKpi.uploadFile': 'Upload File',
    'coachKpi.candidates': 'Candidates',
    'coachKpi.note': 'Note',
    'coachKpi.viewDetails': 'View Details',
    'coachKpi.fileUploaded': 'File uploaded successfully',
    'coachKpi.fileUploadError': 'Failed to upload file',
    'coachKpi.confirmDeleteFile': 'Are you sure you want to delete this file?',
    'coachKpi.fileDeleted': 'File deleted successfully',
    'coachKpi.fileDeleteError': 'Failed to delete file',
    'coachKpi.shareQuestionnaire': 'Share Questionnaire',
    'coachKpi.shareQuestionnaireNote': 'Generate a shareable questionnaire link for this client. The link will be valid for 30 days.',
    'coachKpi.questionnaireLinkGenerated': 'Questionnaire link generated!',
    'coachKpi.typeProfilePreferences': 'Partner Preferences',
    'coachKpi.typeLoveLanguage': 'Love Language',
    'coachKpi.generating': 'Generating...',
    'coachKpi.generateLink': 'Generate Link',
    'coachKpi.regenerateLink': 'Regenerate Link',
    'coachKpi.copy': 'Copy',
    'coachKpi.linkValidDays': 'Share this link with the client. Valid for 30 days.',
    'coachKpi.questionnaireLinkCopied': 'Questionnaire link copied to clipboard',
    'coachKpi.shareQuestionnaireFailed': 'Failed to generate questionnaire link',
    'coachKpi.awaitingResponse': 'Awaiting Response',
    'coachKpi.questionnaireCompleted': 'Completed',
    'coachKpi.loadResultsFailed': 'Failed to load questionnaire results',
    'coachKpi.questionnaireResults': 'Questionnaire Results',
    'coachKpi.noResults': 'No results available',
    'coachKpi.avgScore': 'Average Score',
    'coachKpi.submittedAt': 'Submitted',
    'coachKpi.primaryLoveLanguage': 'Primary Love Language',
    'coachKpi.option': 'Option',
    'coachKpi.timesSelected': 'times selected',
    'coachKpi.optionLabel': 'Option',
    'coachKpi.loveLanguage': 'Love Language',
    'coachKpi.timesSelectedHeader': 'Times Selected',
    'coachKpi.activeClients': 'Active Clients',
    'coachKpi.completedClients': 'Completed',
    'coachKpi.totalEarnings': 'Total Earnings',
    'coachKpi.pendingEarnings': 'Pending Earnings',
    'coachKpi.warnings': 'Warnings',
    'coachKpi.activeWarnings': 'Active Warnings',
    'coachKpi.yellow': 'Yellow',
    'coachKpi.orange': 'Orange',
    'coachKpi.red': 'Red',
    'coachKpi.availableQuestionnaires': 'Available Questionnaires',
    'coachKpi.noQuestionnaires': 'No questionnaires available',
    'coachKpi.sendToClient': 'Send to Client',
    'coachKpi.submissionHistory': 'Submission History',
    'coachKpi.noSubmissions': 'No submissions yet',
    'coachKpi.questionnaire': 'Questionnaire',
    'coachKpi.sentTo': 'Sent to',
    'coachKpi.score': 'Score',
    'coachKpi.sendQuestionnaire': 'Send Questionnaire',
    'coachKpi.selectClient': 'Select Client',
    'coachKpi.selectAssignment': 'Select assignment...',
    'coachKpi.clientName': 'Client Name',
    'coachKpi.clientEmail': 'Client Email',
    'coachKpi.generatedLink': 'Questionnaire Link',
    'coachKpi.send': 'Send',
    'coachKpi.questionnaireSent': 'Questionnaire sent successfully',
    'coachKpi.questionnaireSendError': 'Failed to send questionnaire',
    'coachKpi.confirmations': 'Confirmations',
    'coachKpi.createConfirmation': 'Create Confirmation',
    'coachKpi.noConfirmations': 'No confirmations yet',
    'coachKpi.clientResponse': 'Client response',
    'coachKpi.copyLink': 'Copy Link',
    'coachKpi.confirmationType': 'Type',
    'coachKpi.typeAgreement': 'Agreement',
    'coachKpi.typeDiscussion': 'Discussion',
    'coachKpi.typeMemo': 'Memo',
    'coachKpi.typeOther': 'Other',
    'coachKpi.expiresInDays': 'Expires in (days)',
    'coachKpi.content': 'Content',
    'coachKpi.confirmationLink': 'Confirmation Link',
    'coachKpi.confirmationCreated': 'Confirmation created',
    'coachKpi.confirmationUpdated': 'Confirmation updated',
    'coachKpi.confirmationCreateError': 'Failed to create confirmation',
    'coachKpi.confirmationAccepted': 'Confirmed',
    'coachKpi.confirmationRejected': 'Rejected',
    'coachKpi.typeBasic': 'Basic',
    'coachKpi.typeSchedule': 'Schedule',
    'coachKpi.confirmationTitlePlaceholder': 'Enter confirmation title...',
    'coachKpi.confirmationContentPlaceholder': 'Enter content for client to confirm...',
    'coachKpi.scheduleEventPlaceholder': 'e.g. Coaching Session',
    'coachKpi.editConfirmation': 'Edit Confirmation',
    'coachKpi.selectCandidates': 'Select Candidates',
    'coachKpi.editCandidates': 'Candidates',
    'coachKpi.candidatesSaved': 'Candidates saved',
    'coachKpi.searchProfiles': 'Search profiles by name, email, phone...',
    'coachKpi.selectedCandidates': 'Selected Candidates',
    'coachKpi.noCandidatesSelected': 'No candidates selected yet',
    'coachKpi.p1ConfirmedHint': 'Profiles confirmed in P1:',
    'coachKpi.previousCandidatesHint': 'Previously selected candidates:',
    'coachKpi.aiGenerate': 'AI Generate',
    'coachKpi.aiGenerating': 'Generating...',
    'coachKpi.aiContentGenerated': 'AI content generated',
    'coachKpi.nextFollowup': 'Next',
    'coachKpi.overdue': 'Overdue',
    'kpi.candidate': 'Candidate',
    'kpi.candidates': 'Candidates',
    'kpi.viewRecording': 'View Recording',
    'kpi.profileNumber': 'Profile #',
    'kpi.clickToView': 'Click to view',
    'kpi.status': 'Status',
    'kpi.matches': 'Matches',
    'kpi.datingSchedules': 'Dating Schedules',
    'kpi.appointmentTime': 'Appointment Time',
    'kpi.questionnaireResults': 'Questionnaire Results',
    'kpi.viewResults': 'View Results',
    'kpi.recordingLink': 'Recording Link',
    'kpi.photosFiles': 'Photos / Files',
    'kpi.matchedMembers': 'Matched Members',
    'coachKpi.followups': 'Follow-ups',
    'coachKpi.createFollowup': 'Create Follow-up',
    'coachKpi.noFollowups': 'No follow-ups yet',
    'coachKpi.attempt': 'Attempt',
    'coachKpi.unknownClient': 'Unknown client',
    'coachKpi.nextReminder': 'Next reminder',
    'coachKpi.retryOf': 'Retry of',
    'coachKpi.followupType': 'Type',
    'coachKpi.postMeeting': 'Post Meeting',
    'coachKpi.checkIn': 'Check In',
    'coachKpi.progressReview': 'Progress Review',
    'coachKpi.custom': 'Custom',
    'coachKpi.intervalDays': 'Interval (days)',
    'coachKpi.maxAttempts': 'Max Attempts',
    'coachKpi.followupCreated': 'Follow-up created',
    'coachKpi.followupUpdated': 'Follow-up updated',
    'coachKpi.followupCompleted': 'Follow-up Done',
    'coachKpi.editFollowup': 'Edit Follow-up',
    'coachKpi.followupCreateError': 'Failed to create follow-up',
    'coachKpi.followupSuccess': 'Follow-up marked as success',
    'coachKpi.followupFailed': 'Follow-up marked as failed, retry scheduled',
    'coachKpi.followupOutcomeError': 'Failed to update follow-up',
    'coachKpi.reminders': 'Reminders',
    'coachKpi.createReminder': 'Create Reminder',
    'coachKpi.editReminder': 'Edit Reminder',
    'coachKpi.noReminders': 'No reminders yet',
    'coachKpi.reminderCreated': 'Reminder created',
    'coachKpi.reminderUpdated': 'Reminder updated',
    'coachKpi.reminderDeleted': 'Reminder deleted',
    'coachKpi.reminderConfirmed': 'Reminder confirmed',
    'coachKpi.confirmReminder': 'Confirm',
    'coachKpi.deleteReminderConfirm': 'Delete this reminder?',
    'coachKpi.reminderTitlePlaceholder': 'Enter reminder title...',
    'coachKpi.reminderType': 'Type',
    'coachKpi.triggerDate': 'Date',
    'coachKpi.triggerTime': 'Time',
    'coachKpi.isRecurring': 'Recurring',
    'coachKpi.recurrenceDays': 'Repeat every (days)',
    'coachKpi.clientOptional': 'Client (optional)',
    'coachKpi.noClient': '— No client —',
    'coachKpi.recurring': 'Recurring',
    'coachKpi.every': 'Every',
    'coachKpi.days': 'days',
    'coachKpi.message': 'Message',
    'coachKpi.titleRequired': 'Title *',
    'coachKpi.followUp': 'Follow-up',
    'coachKpi.appointment': 'Appointment',
    'coachKpi.deadline': 'Deadline',
    'coachKpi.scheduleAppointment': 'Schedule Appointment',
    'coachKpi.editAppointment': 'Edit Appointment',
    'coachKpi.appointmentScheduled': 'Appointment Scheduled',
    'coachKpi.appointmentCompleted': 'Appointment Complete',
    'coachKpi.appointmentCreated': 'Appointment scheduled',
    'coachKpi.appointmentUpdated': 'Appointment updated',
    'coachKpi.appointmentDate': 'Date',
    'coachKpi.appointmentTime': 'Time',
    'coachKpi.appointmentNotes': 'Notes',
    'coachKpi.appointmentTimezone': 'Timezone',
    'coachKpi.videoMeetings': 'Video Meetings',
    'coachKpi.createMeeting': 'Create Meeting',
    'coachKpi.noMeetings': 'No video meetings yet',
    'coachKpi.meeting': 'Meeting',
    'coachKpi.notScheduled': 'Not scheduled',
    'coachKpi.duration': 'Duration',
    'coachKpi.minutes': 'min',
    'coachKpi.startMeeting': 'Start',
    'coachKpi.endMeeting': 'End',
    'coachKpi.meetingTitle': 'Meeting title...',
    'coachKpi.scheduledAt': 'Scheduled At',
    'coachKpi.meetingCreated': 'Meeting created',
    'coachKpi.meetingCreateError': 'Failed to create meeting',
    'coachKpi.generateMeetingLink': 'Generate Meeting Link',
    'coachKpi.meetingLinkCopied': 'Meeting link copied to clipboard',
    'coachKpi.meetingLink': 'Meeting Link',
    'coachKpi.viewMeetingLink': 'View Meeting Link',
    'coachKpi.openMeeting': 'Open Meeting',
    'coachKpi.viewRecording': 'View Recording',
    'coachKpi.generatingVideo': 'Generating Video...',
    'coachKpi.recordingFailed': 'Recording Failed',
    'coachKpi.addRecording': 'Add Recording',
    'coachKpi.recordingUrl': 'Recording URL',
    'coachKpi.recordingSaved': 'Recording saved',
    'coachKpi.recordingSaveError': 'Failed to save recording',
    'coachKpi.recordingUrlRequired': 'Recording URL is required',
    'coachKpi.meetingUpdated': 'Meeting updated',
    'coachKpi.meetingUpdateError': 'Failed to update meeting',
    'coachKpi.active': 'In Progress',
    'coachKpi.completed': 'Completed',
    'coachKpi.paused': 'Paused',
    'coachKpi.cancelled': 'Cancelled',
    'coachKpi.pending': 'Pending',
    'coachKpi.profileActive': 'Active',
    'coachKpi.profileInactive': 'Inactive',
    'coachKpi.profileArchived': 'Archived',
    'coachKpi.inProgress': 'In Progress',
    'coachKpi.failed': 'Failed',
    'coachKpi.submitted': 'Submitted',
    'coachKpi.approved': 'Approved',
    'coachKpi.rejected': 'Rejected',
    'coachKpi.sent': 'Sent',
    'coachKpi.viewed': 'Viewed',
    'coachKpi.expired': 'Expired',
    'coachKpi.scheduled': 'Scheduled',
    'coachKpi.success': 'Success',
    'coachKpi.accepted': 'Accepted',
    'coachKpi.succeeded': 'Succeeded',
    'coachKpi.matchSucceeded': 'Met',
    'coachKpi.matchInRelationship': 'Dating',
    'coachKpi.inContact': 'In contact',
    'coachKpi.confirmed': 'Confirmed',
    'coachKpi.meetingStatus': 'Meeting',
    'coachKpi.chatting': 'Chatting',
    'coachKpi.inRelationship': 'In Relationship',
    'coachKpi.engaged': 'Engaged',
    'coachKpi.noShow': 'No Show',
    'coachKpi.skipped': 'Skipped',
    'coachKpi.transferred': 'Transferred',
    'coachKpi.failedLoadAssignment': 'Failed to load assignment',
    'coachKpi.failedLoadPayment': 'Failed to load payment details',
    'coachKpi.failedLoadFeedback': 'Failed to load feedback',
    'coachKpi.failedCreateMatch': 'Failed to create match',
    'coachKpi.failedUpdateMatch': 'Failed to update match',
    'coachKpi.failedCreateSchedule': 'Failed to create schedule',
    'coachKpi.failedUpdateSchedule': 'Failed to update schedule',
    'coachKpi.scheduleCompleted': 'Schedule Done',
    'coachKpi.editSchedule': 'Edit Schedule',
    'coachKpi.scheduleUpdated': 'Dating schedule updated successfully',
    'coachKpi.videoMeetingFallback': 'Video Meeting',
    'coachKpi.followupSuffix': 'Follow-up',
    'coachKpi.datingSchedulePlaceholder': 'Dating Schedule',
    'coachKpi.loadingText': 'Loading...',
    'coachKpi.supervisorFallback': 'Supervisor',
    'coachKpi.allClients': 'All Clients',
    'coachKpi.kpiActive': 'KPI Active',
    'coachKpi.kpiCompleted': 'KPI Completed',
    'coachKpi.noKpi': 'No KPI',
    'coachKpi.startKpi': 'Start KPI',
    'coachKpi.noClients': 'No clients assigned yet',
    'coachKpi.assignedProfile': 'Assigned profile',
    'coachKpi.stagesInProgress': 'Stages In Progress',
    'coachKpi.progressStages': 'Progress Stages',
    'coachKpi.stageP1': 'Deep Analysis & Match Plan',
    'coachKpi.stageP2': 'Meeting Arrangement',
    'coachKpi.stageP3': 'Follow-up (No Success)',
    'coachKpi.stageP4': 'Follow-up (Success)',
    'coachKpi.stageP5': 'Customer Satisfaction',
    'coachKpi.stageP6': 'Final Review',
    'coachKpi.current': 'Current',
    'coachKpi.viewAllDetails': 'View All Details',
    'coachKpi.allMembers': 'All Members',
    'coachKpi.viewEditAssignment': 'View / Edit Assignment',
    'coachKpi.kpiAssignment': 'KPI Assignment',
    'coachKpi.createAssignment': 'Create Assignment',
    'coachKpi.startKpiForClient': 'Start KPI tracking for this client',
    'coachKpi.createAssignmentHint': 'Please create a KPI assignment from Admin KPI page',
    'coachKpi.clientPayment': "Client's Payment",
    'coachKpi.clientPaymentDesc': 'Payment stages & membership upgrade',
    'coachKpi.paymentFeatureHint': 'Client payment details',
    'coachKpi.serviceWorkflow': 'Service Workflow',
    'coachKpi.selectWorkflow': '-- Select Workflow --',
    'coachKpi.serviceAmount': 'Service Amount',
    'coachKpi.notes': 'Notes',
    'coachKpi.assignmentNotesPlaceholder': 'Optional notes for this assignment...',
    'coachKpi.selectWorkflowAndClient': 'Please select a workflow and client',
    'coachKpi.assignmentCreated': 'Assignment created successfully',
    'coachKpi.assignmentCreateFailed': 'Failed to create assignment',
    'coachKpi.currentPlan': 'Current Plan',
    'coachKpi.noPlan': 'No Plan',
    'coachKpi.totalPaid': 'Total Paid',
    'coachKpi.totalOutstanding': 'Outstanding',
    'coachKpi.totalInvoices': 'Total Invoices',
    'coachKpi.kpiPaymentStages': 'KPI Payment Stages',
    'coachKpi.commission': 'Commission',
    'coachKpi.membershipPlans': 'Membership Plans',
    'coachKpi.currentPlanLabel': 'CURRENT',
    'coachKpi.invoiceHistory': 'Invoice History',
    'coachKpi.noInvoices': 'No invoices found',
    'coachKpi.invoiceNo': 'Invoice #',
    'coachKpi.type': 'Type',
    'coachKpi.plan': 'Plan',
    'coachKpi.totalAmount': 'Total',
    'coachKpi.amountPaid': 'Paid',
    'coachKpi.paymentStatus': 'Status',
    'coachKpi.paymentType': 'Payment Type',
    'coachKpi.nextPayment': 'Next Payment',
    'coachKpi.nextPaymentDate': 'Next Pay Date',
    'coachKpi.progress': 'Progress',
    'coachKpi.paymentProgress': 'Payment Progress',
    'coachKpi.date': 'Date',
    'coachKpi.autoSelectedWorkflow': "Auto-selected based on client's membership plan",
    'coachKpi.noMatchingWorkflow': "No workflow matches client's membership plan",
    'kpi.membershipPlanOptional': 'Bind Membership Plan (Optional)',
    'kpi.noMembershipPlan': '-- None --',
    'kpi.bindPlanHint': 'Bind this workflow to a membership plan for auto-selection',

    // Coach KPI - Additional Messages
    'coachKpi.recordingNotAvailable': 'No recording URL available',
    'coachKpi.recordingLoadFailed': 'Failed to load video recording',
    'coachKpi.noteSaved': 'Note saved',
    'coachKpi.noteSaveFailed': 'Failed to save note',
    'coachKpi.assignmentNotFound': 'Assignment not found',
    'coachKpi.statusUpdated': 'Status updated',
    'coachKpi.statusUpdateFailed': 'Failed to update status',
    'coachKpi.appointmentCompletedPrompt': 'Mark this appointment as completed?',
    'coachKpi.appointmentCompletionFailed': 'Failed to update',
    'coachKpi.appointmentCancelPrompt': 'Appointment cancelled',
    'coachKpi.appointmentDeletePrompt': 'Delete this appointment?',
    'coachKpi.appointmentDeleted': 'Appointment deleted',
    'coachKpi.appointmentDeleteFailed': 'Failed to delete',
    'coachKpi.selectClientError': 'Please select a client',
    'coachKpi.matchFailedMessage': '{failCount} match(es) failed',
    'coachKpi.matchesUpdated': 'Matches updated',
    'coachKpi.reviewLinkGenerationFailed': 'Review link generation failed',
    'coachKpi.contentGenerationFailed': 'Failed to generate content',
    'coachKpi.saveCandidatesFailed': 'Failed to save candidates',
    'coachKpi.appointmentSaveFailed': 'Failed to save appointment',

    // Stage Pages (allow_page_creation feature)
    'coachKpi.pages': 'Pages',
    'coachKpi.originalPage': 'Original Page',
    'coachKpi.noPagesYet': 'No pages created yet',
    'coachKpi.addNewPage': 'Add New Page',
    'coachKpi.pageNavigation': 'Page {current} of {total}',
    'coachKpi.previous': 'Previous',
    'coachKpi.next': 'Next',
    'coachKpi.editPage': 'Edit Page',
    'coachKpi.deletePage': 'Delete Page',
    'coachKpi.addNote': 'Add Note',
    'coachKpi.enterNote': 'Enter note in English...',
    'coachKpi.enterNoteZh': 'Enter note in Chinese (输入中文注释)...',
    'coachKpi.noteRequired': 'Please enter a note',
    'coachKpi.confirmDeletePage': 'Are you sure you want to delete this page?',
    'coachKpi.pageDeleted': 'Page deleted successfully',
    'coachKpi.pageCreated': 'Page created successfully',
    'coachKpi.pageSaved': 'Page saved successfully',
    'coachKpi.pageDeliverables': 'Page Deliverables',
    'coachKpi.original': 'Original',
    'coachKpi.noPageDeliverables': 'No deliverables on this page',
    'coachKpi.titleRequired': 'Title is required',
    'coachKpi.saveFailed': 'Failed to save page',
    'coachKpi.deleteFailed': 'Failed to delete page',
    'coachKpi.addPageDeliverable': 'Add Deliverable',
    'coachKpi.pageMultipleDeliverables': 'This page can have multiple deliverables',

    // KPI Profile & Deductions
    'coachKpi.kpiProfile': 'KPI Profile',
    'coachKpi.enableKpiProfile': 'Enable KPI Profile',
    'coachKpi.kpiProfileDescription': 'When enabled, coach earns weight % and commission. When disabled, only deductions apply.',
    'coachKpi.deductions': 'Deductions',
    'coachKpi.deductionHistory': 'Deduction History',
    'coachKpi.deductionAmount': 'Deduction Amount',
    'coachKpi.deductionNumber': 'Deduction #{number}',
    'coachKpi.totalDeductions': 'Total Deductions',
    'coachKpi.noDeductions': 'No deductions yet',
    'coachKpi.warnings': 'Warnings',
    'coachKpi.deductionWarning': 'Deduction Penalty Applied',
    'coachKpi.deductionWarningMsg': 'KPI Profile is disabled for this client. Deduction of ${amount} applied.',
    'coachKpi.kpiProfileEnabled': 'KPI Profile Enabled - Coach earns normal commission',
    'coachKpi.kpiProfileDisabled': 'KPI Profile Disabled - No earnings, only deductions apply',
    'coachKpi.tabWeeklyContacts': 'Weekly Contacts',
    'coachKpi.tabRecommendations': 'Recommendations',
    'coachKpi.priorityAlerts': 'Priority Alerts',
    'coachKpi.weeklyContactLog': 'Weekly Contact Log',
    'coachKpi.weekOf': 'Week of',
    'coachKpi.user': 'User',
    'coachKpi.tabWarnings': 'Warnings',
    'coachKpi.markRead': 'Mark Read',
    'coachKpi.resolvedWarnings': 'Resolved / Read',
    'coachKpi.lastContact': 'Last Contact',
    'coachKpi.lastMatch': 'Last Match',
    'coachKpi.lastSchedule': 'Last Schedule',
    'coachKpi.priority': 'Priority',
    'coachKpi.daysAgo': 'days ago',
    'coachKpi.neverContacted': 'Never contacted',
    'coachKpi.noMeetingThisMonth': 'No meeting this month',
    'coachKpi.urgent': 'Urgent',
    'coachKpi.needsMeeting': 'Needs Meeting',
    'coachKpi.contactedThisWeek': 'Contacted this week',
    'coachKpi.notContactedThisWeek': 'Not contacted this week',
    'coachKpi.contactsThisMonth': 'contacts this month',
    'coachKpi.meetingsThisMonth': 'meetings this month',
    'coachKpi.logContact': 'Log Contact',
    'coachKpi.contactLogSaved': 'Contact logged successfully',
    'coachKpi.noAssignedClients': 'No assigned clients',
    'coachKpi.autoRecommendations': 'Auto Recommendations',
    'coachKpi.autoRecommendationsDesc': 'Select a client to see compatible candidate recommendations. Already matched profiles are excluded.',
    'coachKpi.selectClient': 'Select Client',
    'coachKpi.chooseClient': '-- Choose a client --',
    'coachKpi.noRecommendations': 'No compatible candidates found',
    'coachKpi.yearsOld': 'yrs',
    'coachKpi.matchCreated': 'Match created successfully',

    // 3-Dimension Partner Preferences
    'preferences.idealStandard': 'Ideal Standard',
    'preferences.acceptableRange': 'Acceptable Range',
    'preferences.absoluteBottomLine': 'Absolute Bottom Line',
    'preferences.dimensionHint': 'Fill in your preferences for each dimension. Default shows Acceptable Range.',

    // Leader Coach Review
    'leaderCoach.title': 'Leader Coach Reviews',
    'leaderCoach.description': 'Review submitted stages before supervisor approval',
    'leaderCoach.pending': 'Pending Review',
    'leaderCoach.approved': 'Approved',
    'leaderCoach.rejected': 'Rejected',
    'leaderCoach.reviewing': 'Reviewing',
    'leaderCoach.client': 'Client',
    'leaderCoach.coach': 'Coach',
    'leaderCoach.submittedOn': 'Submitted On',
    'leaderCoach.deliverables': 'Deliverables',
    'leaderCoach.reviewStage': 'Review Stage',
    'leaderCoach.reviewAction': 'Review Action',
    'leaderCoach.approve': 'Approve',
    'leaderCoach.reject': 'Reject',
    'leaderCoach.feedback': 'Feedback',
    'leaderCoach.rejectionReasonRequired': 'Please provide a reason for rejection',
    'leaderCoach.optionalFeedback': 'Provide feedback for the coach (optional)',
    'leaderCoach.submitReview': 'Submit Review',
    'leaderCoach.review': 'Review',
    'leaderCoach.feedbackRequired': 'Feedback is required for rejection',
    'leaderCoach.stageApprovedSuccess': 'Stage approved successfully',
    'leaderCoach.stageRejectedSuccess': 'Stage rejected successfully',
    'leaderCoach.noStagesToReview': 'No stages pending review',
    'leaderCoach.noStages': 'No stages',
    'leaderCoach.supervisorStatus': 'Supervisor Status',
    'leaderCoach.totalReviewed': 'Total Reviewed',
    'leaderCoach.coachesUnderReview': 'Coaches Under Review',
    'leaderCoach.avgReviewTime': 'Avg Review Time',
    'leaderCoach.noDataYet': 'No Reviews Yet',
    'leaderCoach.startReviewingStages': 'Start reviewing coach stages to see your performance metrics',

    // Leader Coach KPI Performance - Dashboard Stats
    'leaderCoach.kpiPerformance': 'KPI Performance',
    'leaderCoach.kpiPerformanceSubtitle': 'View your team performance and KPI metrics',
    'leaderCoach.totalAssignments': 'Total Assignments',
    'leaderCoach.activeAssignments': 'Active',
    'leaderCoach.completedAssignments': 'Completed',
    'leaderCoach.pausedAssignments': 'Paused',
    'leaderCoach.coachesUnderManagement': 'Coaches Under Management',
    'leaderCoach.avgSatisfaction': 'Avg Satisfaction',
    'leaderCoach.refresh': 'Refresh',
    'leaderCoach.approvalWorkflow': 'Approval Workflow',
    'leaderCoach.approvalPending': 'Pending Review',
    'leaderCoach.approvalLeaderCoach': 'Leader Coach Review',
    'leaderCoach.approvalSupervisor': 'Supervisor Approval',
    'leaderCoach.approvalCompleted': 'Approved',

    // Admin KPI - Messages
    'adminKpi.deliverableApproved': 'Deliverable approved',
    'adminKpi.deliverableUnapproved': 'Deliverable unapproved',
    'adminKpi.deliverableUpdateFailed': 'Failed to update deliverable',
    'adminKpi.copyLinkSuccess': 'Copied!',
    'adminKpi.copyLinkFailed': 'Copy failed, here is the text:',
    'adminKpi.followupDeletePrompt': 'Delete this follow-up?',
    'adminKpi.followupDeleted': 'Deleted',
    'adminKpi.followupDeleteFailed': 'Failed',
    'adminKpi.linkCopied': 'Link copied',
    'adminKpi.confirmationDeletePrompt': 'Delete this confirmation?',
    'adminKpi.confirmationDeleted': 'Deleted',
    'adminKpi.confirmationDeleteFailed': 'Failed',

    // Supervisor KPI - Messages
    'supervisorKpi.failedLoadAssignmentDetail': 'Failed to load assignment detail',
    'supervisorKpi.failedUpdateReview': 'Failed to update review',
    'supervisorKpi.deliverableApproved': 'Deliverable approved',
    'supervisorKpi.deliverableUnapproved': 'Deliverable unapproved',
    'supervisorKpi.deliverableUpdateFailed': 'Failed to update deliverable',
    'supervisorKpi.failedAddFeedback': 'Failed to add feedback',
    'supervisorKpi.recordingNotAvailable': 'No recording URL available',
    'supervisorKpi.recordingLoadFailed': 'Failed to load video recording',

    // Settings - Agora
    'settings.tabAgora': 'Video Meeting',
    'settings.agoraTitle': 'Video Meeting Settings',
    'settings.agoraDesc': 'Configure Agora video meeting integration for KPI Performance meetings.',
    'settings.agoraEnable': 'Enable Agora Video Meetings',
    'settings.agoraEnableDesc': 'Allow coaches to create video meetings with clients',
    'settings.agoraAppId': 'Agora App ID',
    'settings.agoraAppIdHint': 'Find this in your Agora Console > Project Management',
    'settings.agoraAppCert': 'App Certificate',
    'settings.agoraAppCertHint': 'Required for token-based authentication (recommended for production)',
    'settings.agoraTokenExpiry': 'Token Expiry (seconds)',
    'settings.agoraTokenExpiryHint': 'How long each meeting token is valid (default: 3600 = 1 hour)',
    'settings.agoraInfo': 'To use Agora video meetings, create a free account at agora.io and create a project to get your App ID and App Certificate. Coaches will be able to generate video meeting links when creating meetings in KPI Performance.',
    'settings.saveAgora': 'Save Video Meeting Settings',
    'settings.agoraSaved': 'Agora settings saved successfully',
    'settings.agoraSaveError': 'Failed to save Agora settings',
    'settings.agoraCustomerId': 'Agora Customer ID',
    'settings.agoraCustomerIdHint': 'From Agora Console > RESTful API (required for cloud recording)',
    'settings.agoraCustomerSecret': 'Agora Customer Secret',
    'settings.agoraCustomerSecretHint': 'From Agora Console > RESTful API (required for cloud recording)',

    // Settings - Agora RTC
    'settings.tabAgoraRTC': 'Agora RTC',
    'settings.agoraRTCTitle': 'Agora RTC Settings',
    'settings.agoraRTCDesc': 'Configure Agora RTC (Real-Time Communication) for KPI Performance video meetings.',
    'settings.agoraRTCEnable': 'Enable Agora RTC Video Meetings',
    'settings.agoraRTCEnableDesc': 'Allow coaches to create video meetings with clients using Agora RTC',
    'settings.agoraAppId': 'App ID',
    'settings.agoraAppIdHint': 'Find this in your Agora Console > Project Management',
    'settings.agoraAppCertificate': 'App Certificate',
    'settings.agoraAppCertificateHint': 'Required for generating RTC tokens',
    'settings.agoraTokenExpiry': 'Token Expiry (seconds)',
    'settings.agoraTokenExpiryHint': 'How long each meeting token is valid (default: 3600 = 1 hour, max: 604800 = 7 days)',
    'settings.agoraCloudRecording': 'Agora Cloud Recording (Optional)',
    'settings.agoraCloudRecordingDesc': 'Provide your Agora RESTful API credentials to enable cloud recording features.',

    'settings.awsS3Storage': 'AWS S3 Recording Storage',
    'settings.awsS3Desc': 'Configure AWS S3 for storing video recordings from Agora meetings.',
    'settings.enableAwsS3': 'Enable AWS S3 Storage',
    'settings.s3AccessKeyId': 'S3 Access Key ID',
    'settings.s3SecretAccessKey': 'S3 Secret Access Key',
    'settings.s3Bucket': 'S3 Bucket Name',
    'settings.s3Region': 'S3 Region',
    'settings.s3RegionHint': 'Select the AWS region where your S3 bucket is located',
    'settings.saveAgoraRTC': 'Save Agora RTC Settings',
    'settings.agoraRTCSaved': 'Agora RTC settings saved successfully',
    'settings.agoraRTCSaveError': 'Failed to save Agora RTC settings',
    'settings.agoraInfoTitle': 'Getting Started with Agora RTC',
    'settings.agoraChat': 'In-Meeting Chat',
    'settings.agoraChatDesc': 'Enable text chat during video meetings',
    'settings.agoraVirtualBg': 'Virtual Background',
    'settings.agoraVirtualBgDesc': 'Allow participants to use virtual backgrounds',
    'settings.agoraVirtualBgBlur': 'Enable Background Blur',
    'settings.agoraVirtualBgImages': 'Custom Background Images',
    'meeting.virtualBackground': 'Virtual Background',
    'meeting.bgNone': 'None',
    'meeting.bgBlur': 'Blur',
    'meeting.chat': 'Chat',

    // Admin Trends
    'trends.totalTrends': 'Total Trends',
    'trends.pendingApproval': 'Pending Approval',
    'trends.totalLikes': 'Total Likes',
    'trends.totalComments': 'Total Comments',
    'trends.trends': 'Trends',
    'trends.categories': 'Categories',
    'trends.settings': 'Settings',
    'trends.searchTrends': 'Search trends...',
    'trends.allStatus': 'All Status',
    'trends.published': 'Published',
    'trends.pending': 'Pending',
    'trends.rejected': 'Rejected',
    'trends.allCategories': 'All Categories',
    'trends.postNewTrend': 'Post New Trend',
    'trends.trend': 'Trend',
    'trends.type': 'Type',
    'trends.category': 'Category',
    'trends.author': 'Author',
    'trends.engagement': 'Engagement',
    'trends.status': 'Status',
    'trends.actions': 'Actions',
    'trends.approve': 'Approve',
    'trends.reject': 'Reject',
    'trends.noTrendsFound': 'No trends found',
    'trends.addCategory': 'Add Category',
    'trends.editCategory': 'Edit Category',
    'trends.active': 'Active',
    'trends.inactive': 'Inactive',
    'trends.contentModeration': 'Content Moderation Settings',
    'trends.blacklistWords': 'Blacklist Words',
    'trends.blacklistDesc': 'Posts containing these words will be automatically flagged for review',
    'trends.addWordPlaceholder': 'Add word to blacklist...',
    'trends.contentPermissions': 'Content Permissions',
    'trends.allowClientPosts': 'Allow clients to post trends',
    'trends.requireApproval': 'Require admin approval for client posts',
    'trends.allowPhotos': 'Allow photo uploads',
    'trends.allowVideos': 'Allow video uploads',
    'trends.allowPolls': 'Allow poll creation',
    'trends.maxContentLength': 'Maximum Content Length',
    'trends.characters': 'characters',
    'trends.contentPolicy': 'Content Policy',
    'trends.contentPolicyDesc': 'The system automatically blocks posts containing contact information (phone numbers, email addresses, URLs) and flags potentially inappropriate images for manual review.',
    'trends.categoryName': 'Category Name',
    'trends.description': 'Description',
    'trends.color': 'Color',
    'trends.editTrend': 'Edit Trend',
    'trends.title': 'Title',
    'trends.contentType': 'Content Type',
    'trends.text': 'Text',
    'trends.photo': 'Photo',
    'trends.audio': 'Audio',
    'trends.video': 'Video',
    'trends.poll': 'Poll',
    'trends.selectCategory': 'Select Category',
    'trends.content': 'Content',
    'trends.mediaUrl': 'Media URL',
    'trends.pollOptions': 'Poll Options',
    'trends.min2': 'min 2',
    'trends.addOption': 'Add option...',
    'trends.pinToTop': 'Pin this trend to top',
    'trends.publish': 'Publish',
    'trends.categoryNameRequired': 'Category name is required',
    'trends.categoryUpdated': 'Category updated',
    'trends.categoryCreated': 'Category created',
    'trends.cannotDeleteCategoryWithTrends': 'Cannot delete category with existing trends',
    'trends.confirmDeleteCategory': 'Delete this category?',
    'trends.categoryDeleted': 'Category deleted',
    'trends.fillAllFields': 'Please fill in all required fields',
    'trends.pollMinOptions': 'Poll must have at least 2 options',
    'trends.trendUpdated': 'Trend updated',
    'trends.trendPublished': 'Trend published',
    'trends.trendDeleted': 'Trend deleted',
    'trends.confirmDeleteTrend': 'Delete this trend?',
    'trends.trendApproved': 'Trend approved',
    'trends.trendRejected': 'Trend rejected',
    'trends.pinUpdated': 'Pin status updated',
    'trends.settingsSaved': 'Settings saved successfully',

    // Client Trends
    'clientTrends.justNow': 'Just now',
    'clientTrends.minutesAgo': '{n} min ago',
    'clientTrends.hoursAgo': '{n}h ago',
    'clientTrends.daysAgo': '{n}d ago',
    'clientTrends.weeksAgo': '{n}w ago',
    'clientTrends.monthsAgo': '{n}mo ago',
    'clientTrends.yearsAgo': '{n}y ago',
    'clientTrends.newest': 'Newest',
    'clientTrends.oldest': 'Oldest',
    'clientTrends.mostPopular': 'Most Popular',
    'clientTrends.mostComments': 'Most Comments',
    'clientTrends.latest': 'Latest',
    'clientTrends.filter': 'Filter',
    'clientTrends.search': 'Search',
    'clientTrends.sort': 'Sort',
    'clientTrends.searchPlaceholder': 'Search title, content...',
    'clientTrends.cancel': 'Cancel',
    'clientTrends.contentType': 'Content Type',
    'clientTrends.all': 'All',
    'clientTrends.text': 'Text',
    'clientTrends.photo': 'Photo',
    'clientTrends.video': 'Video',
    'clientTrends.poll': 'Poll',
    'clientTrends.reset': 'Reset',
    'clientTrends.applyFilters': 'Apply Filters',
    'clientTrends.categoryDatingTips': 'Dating Tips',
    'clientTrends.categoryEvents': 'Events',
    'clientTrends.categoryGeneral': 'General',
    'clientTrends.categoryLifestyle': 'Lifestyle',
    'clientTrends.categorySuccessStories': 'Success Stories',
    'clientTrends.foundPosts': 'Found {n} posts',
    'clientTrends.clearAll': 'Clear All',
    'clientTrends.noPosts': 'No Posts Yet',
    'clientTrends.noPostsDesc': 'Nothing here yet, check back later!',
    'clientTrends.endOfFeed': '--- End of feed ---',
    'clientTrends.comments': 'Comments',
    'clientTrends.noComments': 'No comments yet, be the first!',
    'clientTrends.loading': 'Loading...',
    'clientTrends.anonymousUser': 'Anonymous',
    'clientTrends.user': 'User',
    'clientTrends.underReview': '(Under review)',
    'clientTrends.commentPlaceholder': 'Write a comment...',
    'clientTrends.report': 'Report',
    'clientTrends.reportSubmitted': 'Report submitted',
    'clientTrends.collapse': 'Collapse',
    'clientTrends.readMore': 'Read more',
    'clientTrends.me': 'Me',
    'clientTrends.nVoted': '{n} voted',
    'clientTrends.nVotes': '{n} votes',

    // Client Profile Page
    'profile.loading': 'Loading...',
    'profile.notFound': 'Profile not found',
    'profile.loadFailed': 'Failed to load profile',
    'profile.goBack': 'Go Back',
    'profile.verified': 'Verified',
    'profile.yearsOld': '{n} yrs',
    'profile.noBio': 'No bio yet',
    'profile.sendMessage': 'Send Message',
    'profile.like': 'Like',
    'profile.liked': 'Liked',
    'profile.dislike': 'Dislike',
    'profile.matchPending': 'Match Pending',
    'profile.gallery': 'Gallery',
    'profile.noPhotos': 'No photos',
    'profile.details': 'Details',
    'profile.matePreferences': 'Partner Preferences',
    'profile.share': 'Share',
    'profile.report': 'Report',
    'profile.block': 'Block',
    'profile.cancel': 'Cancel',
    'profile.male': 'Male',
    'profile.female': 'Female',
    'profile.cm': 'CM',
    'profile.kg': 'KG',
    'profile.category.personal': 'Personal',
    'profile.category.background': 'Background',
    'profile.category.family': 'Family',
    'profile.category.general': 'General',
    'profile.category.lifestyle': 'Lifestyle',
    'profile.category.preferences': 'Preferences',
    'profile.zodiac.capricorn': 'Capricorn',
    'profile.zodiac.aquarius': 'Aquarius',
    'profile.zodiac.pisces': 'Pisces',
    'profile.zodiac.aries': 'Aries',
    'profile.zodiac.taurus': 'Taurus',
    'profile.zodiac.gemini': 'Gemini',
    'profile.zodiac.cancer': 'Cancer',
    'profile.zodiac.leo': 'Leo',
    'profile.zodiac.virgo': 'Virgo',
    'profile.zodiac.libra': 'Libra',
    'profile.zodiac.scorpio': 'Scorpio',
    'profile.zodiac.sagittarius': 'Sagittarius',

    // ── Client Home Page ──
    'clientHome.filter': 'Filter',
    'clientHome.sort': 'Sort',
    'clientHome.searchPlaceholder': 'Search name, city...',
    'clientHome.searchLabel': 'Search',
    'clientHome.searchNameIdPlaceholder': 'Name or offline ID',
    'clientHome.clear': 'Clear',
    'clientHome.cancel': 'Cancel',
    'clientHome.ageRange': 'Age Range',
    'clientHome.heightRange': 'Height Range (cm)',
    'clientHome.region': 'Region',
    'clientHome.allCountries': 'All Countries',
    'clientHome.allStates': 'All States/Provinces',
    'clientHome.allCities': 'All Cities',
    'clientHome.china': 'China',
    'clientHome.usa': 'USA',
    'clientHome.canada': 'Canada',
    'clientHome.australia': 'Australia',
    'clientHome.uk': 'UK',
    'clientHome.japan': 'Japan',
    'clientHome.southKorea': 'South Korea',
    'clientHome.singapore': 'Singapore',
    'clientHome.malaysia': 'Malaysia',
    'clientHome.taiwan': 'Taiwan',
    'clientHome.hongKong': 'Hong Kong',
    'clientHome.stateProvince': 'State/Province',
    'clientHome.city': 'City',
    'clientHome.hasPhoto': 'Has Photo',
    'clientHome.online': 'Online',
    'clientHome.reset': 'Reset',
    'clientHome.applyFilters': 'Apply Filters',
    'clientHome.foundMembers': 'Found {n} members',
    'clientHome.clearAll': 'Clear All',
    'clientHome.noMembers': 'No Members Found',
    'clientHome.adjustFilters': 'Try adjusting your filters or clearing search',
    'clientHome.noMembersYet': 'No members to browse yet',
    'clientHome.allShown': 'All members shown',
    'clientHome.defaultSort': 'Default',
    'clientHome.recentlyActive': 'Recently Active',
    'clientHome.ageAsc': 'Age ↑',
    'clientHome.ageDesc': 'Age ↓',
    'clientHome.heightAsc': 'Height ↑',
    'clientHome.heightDesc': 'Height ↓',
    'clientHome.member': 'Member',
    'clientHome.professional': 'Professional',
    'clientHome.yearsOld': '{n} yrs',
    'clientHome.filters': 'Filters',
    'clientHome.newest': 'Newest',
    'clientHome.hottest': 'Hottest',
    'clientHome.featured': 'Featured',
    'clientHome.sortBy': 'Sort By',
    'clientHome.apply': 'Apply',
    'clientHome.filtersSort': 'Filters & Sort',

    // ── Client Dashboard Page ──
    'clientDash.editProfile': 'Edit Profile',
    'clientDash.editProfileDesc': 'Complete your info',
    'clientDash.myAlbum': 'My Album',
    'clientDash.myAlbumDesc': 'Manage photos',
    'clientDash.myTrends': 'My Trends',
    'clientDash.myTrendsDesc': 'View your posts',
    'clientDash.whoViewedMe': 'Who Viewed Me',
    'clientDash.whoViewedMeDesc': 'View visitor records',
    'clientDash.followingFans': 'Following & Fans',
    'clientDash.followingFansDesc': 'Manage interactions',
    'clientDash.matchmaking': 'Matchmaking',
    'clientDash.matchmakingDesc': 'View matches',
    'clientDash.dateSchedule': 'Date Schedule',
    'clientDash.dateScheduleDesc': 'Manage dates',
    'clientDash.changePassword': 'Change Password',
    'clientDash.changePasswordDesc': 'Account security',
    'clientDash.settings': 'Settings',
    'clientDash.settingsDesc': 'Password, language & preferences',
    'clientDash.changeLanguage': 'Change Language',
    'clientDash.changeLanguageDesc': 'Switch display language',
    'clientDash.visitors': 'Visitors',
    'clientDash.following': 'Following',
    'clientDash.likes': 'Likes',
    'clientDash.myMatchmaker': 'My Matchmaker',
    'clientDash.inService': 'In Service',
    'clientDash.toBeAssigned': 'To Be Assigned',
    'clientDash.logout': 'Log Out',
    'clientDash.confirmLogout': 'Are you sure you want to log out?',
    'clientDash.user': 'User',
    'clientDash.yearsOld': ', {n} yrs',
    'clientDash.justNow': 'Just now',
    'clientDash.minutesAgo': '{n} min ago',
    'clientDash.hoursAgo': '{n}h ago',
    'clientDash.daysAgo': '{n}d ago',
    'clientDash.noPhotos': 'No Photos',
    'clientDash.uploadPhotos': 'Upload photos to let more people know you',
    'clientDash.totalPhotos': '{n} photos in total',
    'clientDash.noTrends': 'No Trends',
    'clientDash.postFirstTrend': 'Go to trends feed to post your first trend',
    'clientDash.totalTrends': '{n} trends in total',
    'clientDash.noVisitors': 'No Visitors',
    'clientDash.improveProfile': 'Complete your profile to increase visibility',
    'clientDash.totalVisitors': '{n} visitors in total',
    'clientDash.myFollowing': 'My Following',
    'clientDash.myFans': 'My Fans',
    'clientDash.noFollowing': 'No Following',
    'clientDash.browseToFollow': 'Browse users to follow people you like',
    'clientDash.noFans': 'No Fans',
    'clientDash.completeTips': 'Complete your profile to attract more fans',
    'clientDash.noMatches': 'No Matches',
    'clientDash.matchmakerSearching': 'Your matchmaker is finding matches for you',
    'clientDash.totalMatches': '{n} matches in total',
    'clientDash.unknown': 'Unknown',
    'clientDash.matchPercent': '{n}% Match',
    'clientDash.noDates': 'No Dates',
    'clientDash.matchmakerArranging': 'Your matchmaker will arrange suitable dates',
    'clientDash.totalDates': '{n} dates in total',
    'clientDash.fillAllFields': 'Please fill in all fields',
    'clientDash.minPasswordLength': 'New password must be at least 6 characters',
    'clientDash.passwordMismatch': 'Passwords do not match',
    'clientDash.passwordChanged': 'Password changed successfully!',
    'clientDash.passwordChangeFailed': 'Password change failed, please try again',
    'clientDash.currentPassword': 'Current Password',
    'clientDash.currentPasswordPlaceholder': 'Enter current password',
    'clientDash.newPassword': 'New Password',
    'clientDash.newPasswordPlaceholder': 'Enter new password (at least 6 characters)',
    'clientDash.confirmNewPassword': 'Confirm New Password',
    'clientDash.confirmPasswordPlaceholder': 'Enter new password again',
    'clientDash.saving': 'Saving...',
    'clientDash.confirmChange': 'Confirm Change',
    'clientDash.albumPhotoDesc': 'Your photos will be shown here for more people to see',
    'clientDash.trendsDesc': 'View all your posted trends',
    'clientDash.visitorsDesc': 'View recent visitors to your profile',
    'clientDash.followingFansFullDesc': 'Manage your following list and fans',
    'clientDash.matchmakingFullDesc': 'View matches recommended by your matchmaker',
    'clientDash.scheduleFullDesc': 'Manage your dating schedule and arrangements',
    'clientDash.statusPending': 'Pending',
    'clientDash.statusAccepted': 'Accepted',
    'clientDash.statusDeclined': 'Declined',
    'clientDash.statusConfirmed': 'Confirmed',
    'clientDash.statusCompleted': 'Completed',
    'clientDash.statusCancelled': 'Cancelled',
    'clientDash.statusRescheduled': 'Rescheduled',
    'clientDash.statusNoShow': 'No Show',
    'clientDash.typeText': 'Text',
    'clientDash.typeImage': 'Image',
    'clientDash.typeVideo': 'Video',
    'clientDash.typePoll': 'Poll',

    // ── Client Messages Page ──
    'clientMessages.inbox': 'Inbox',
    'clientMessages.sent': 'Sent',
    'clientMessages.notifications': 'Notifications',
    'clientMessages.selectConvo': 'Select a conversation',
    'clientMessages.selectFromLeft': 'Select from left to start chatting',
    'clientMessages.noMessages': 'No messages yet, send your first message to start chatting',
    'clientMessages.typeMessage': 'Type a message...',
    'clientMessages.me': 'Me',
    'clientMessages.noConversations': 'No Conversations',
    'clientMessages.noSentMessages': 'You haven\'t sent any messages yet',
    'clientMessages.noReceivedMessages': 'No messages yet. Your dedicated coach will reach out to you here.',
    'clientMessages.noNotifications': 'No Notifications',
    'clientMessages.notificationsInfo': 'You\'ll get notified here when there are new matches or system messages',

    // ── Client Edit Profile Page ──
    'clientEdit.editProfile': 'Edit Profile',
    'clientEdit.save': 'Save',
    'clientEdit.saving': 'Saving...',
    'clientEdit.photoHint': 'Tap camera icon to change photo',
    'clientEdit.photoUpdated': 'Photo updated!',
    'clientEdit.photoFailed': 'Photo upload failed, please try again',
    'clientEdit.profileUpdated': 'Profile updated!',
    'clientEdit.saveFailed': 'Save failed, please try again',
    'clientEdit.adminOnly': '*Admin only',
    'clientEdit.pleaseSelect': 'Select',
    'clientEdit.tab.basic': 'Basic Info',
    'clientEdit.tab.details': 'Details',
    'clientEdit.tab.preferences': 'Preferences',
    'clientEdit.tab.contact': 'Contact',
    'clientEdit.basicDesc': 'Complete your basic info to let more people know you',
    'clientEdit.detailsDesc': 'Add more details to improve match accuracy',
    'clientEdit.preferencesDesc': 'Set your preferences for precise matching',
    'clientEdit.contactDesc': 'Manage your contact info for matchmaker communication',
    // Basic tab
    'clientEdit.nickname': 'Nickname',
    'clientEdit.nicknamePlaceholder': 'Enter nickname',
    'clientEdit.gender': 'Gender',
    'clientEdit.male': 'Male',
    'clientEdit.female': 'Female',
    'clientEdit.birthday': 'Birthday',
    'clientEdit.height': 'Height',
    'clientEdit.weight': 'Weight',
    'clientEdit.workCity': 'Region',
    'clientEdit.cityPlaceholder': 'Your city',
    'clientEdit.maritalStatus': 'Marital Status',
    'clientEdit.education': 'Education',
    'clientEdit.monthlyIncome': 'Monthly Income',
    'clientEdit.jobType': 'Job Type',
    'clientEdit.housing': 'Housing',
    'clientEdit.car': 'Car',
    'clientEdit.marriageTimeline': 'Marriage Timeline',
    'clientEdit.bio': 'Bio',
    'clientEdit.bioPlaceholder': 'Tell us about yourself...',
    // Details tab - sections
    'clientEdit.familyStatus': 'Family Status',
    'clientEdit.children': 'Children',
    'clientEdit.wantKids': 'Want Kids',
    'clientEdit.parentsStatus': 'Parents Status',
    'clientEdit.siblings': 'Siblings',
    'clientEdit.personalTraits': 'Personal Traits',
    'clientEdit.zodiac': 'Zodiac',
    'clientEdit.ethnicity': 'Ethnicity',
    'clientEdit.religion': 'Religion',
    'clientEdit.hometown': 'Hometown',
    'clientEdit.hometownPlaceholder': 'Enter hometown',
    'clientEdit.lifestyle': 'Lifestyle',
    'clientEdit.smoking': 'Smoking',
    'clientEdit.drinking': 'Drinking',
    'clientEdit.sleep': 'Sleep',
    'clientEdit.exercise': 'Exercise',
    'clientEdit.hobbies': 'Hobbies',
    'clientEdit.languages': 'Languages',
    'clientEdit.other': 'Other',
    'clientEdit.relocation': 'Relocation',
    'clientEdit.purpose': 'Purpose',
    'clientEdit.lifePriority': 'Life Priority',
    // Preferences tab
    'clientEdit.basicRequirements': 'Basic Requirements',
    'clientEdit.prefAgeRange': 'Age Range',
    'clientEdit.prefHeightRange': 'Height Range',
    'clientEdit.prefWorkCity': 'Work City',
    'clientEdit.prefCityPlaceholder': 'Preferred city',
    'clientEdit.singleSelect': 'Single Select',
    'clientEdit.prefMinEducation': 'Min Education',
    'clientEdit.prefMinIncome': 'Min Income',
    'clientEdit.prefMarriageTimeline': 'Marriage Timeline',
    'clientEdit.multiSelect': 'Multi Select',
    'clientEdit.prefMarital': 'Marital Status',
    'clientEdit.prefHousing': 'Housing',
    'clientEdit.prefCar': 'Car',
    'clientEdit.prefSmoking': 'Smoking',
    'clientEdit.prefChildren': 'Children',
    'clientEdit.otherRequirements': 'Other Requirements',
    'clientEdit.prefOther': 'Other Requirements',
    'clientEdit.prefOtherPlaceholder': 'Other expectations for your partner...',
    // Contact tab
    'clientEdit.phone': 'Phone',
    'clientEdit.phonePlaceholder': 'Enter phone number',
    'clientEdit.wechat': 'WeChat',
    'clientEdit.wechatPlaceholder': 'Enter WeChat ID',
    'clientEdit.email': 'Email',
    // Options
    'clientEdit.marital.single': 'Single',
    'clientEdit.marital.divorcePending': 'Divorce (Pending)',
    'clientEdit.marital.divorced': 'Divorced',
    'clientEdit.marital.widowed': 'Widowed',
    'clientEdit.marital.separated': 'Separated',
    'clientEdit.edu.juniorHigh': 'Junior High',
    'clientEdit.edu.highSchool': 'High School',
    'clientEdit.edu.university': 'University',
    'clientEdit.edu.universityDropout': 'University (Incomplete)',
    'clientEdit.edu.masters': 'Masters',
    'clientEdit.edu.doctorate': 'Doctorate',
    'clientEdit.edu.preferNot': 'Prefer not to say',
    'clientEdit.income.below5k': 'Below 5k',
    'clientEdit.income.5kTo15k': '5k~15k',
    'clientEdit.income.15kTo30k': '15k~30k',
    'clientEdit.income.30kTo50k': '30k~50k',
    'clientEdit.income.above50k': 'Above 50k',
    'clientEdit.income.preferNot': 'Prefer not to say',
    'clientEdit.job.fullTime': 'Full-time',
    'clientEdit.job.partTime': 'Part-time',
    'clientEdit.job.freelance': 'Freelance',
    'clientEdit.job.businessOwner': 'Business Owner',
    'clientEdit.job.student': 'Student',
    'clientEdit.job.it': 'IT/Internet/Telecom',
    'clientEdit.job.finance': 'Finance/Banking/Insurance',
    'clientEdit.job.realEstate': 'Real Estate/Construction',
    'clientEdit.job.trade': 'Trade/Retail/Wholesale',
    'clientEdit.job.service': 'Service Industry',
    'clientEdit.job.media': 'Culture/Media/Entertainment',
    'clientEdit.job.medical': 'Medical/Nursing/Pharma',
    'clientEdit.job.education': 'Education/Training',
    'clientEdit.job.government': 'Government/Public Sector',
    'clientEdit.job.legal': 'Legal/Consulting',
    'clientEdit.job.manufacturing': 'Manufacturing',
    'clientEdit.job.transport': 'Transport/Logistics',
    'clientEdit.job.agriculture': 'Agriculture/Forestry',
    'clientEdit.job.military': 'Military/Police',
    'clientEdit.job.retired': 'Retired',
    'clientEdit.job.unemployed': 'Unemployed',
    'clientEdit.job.other': 'Other',
    'clientEdit.housing.own': 'Own Property',
    'clientEdit.housing.none': 'No Property',
    'clientEdit.housing.overseas': 'Property Overseas',
    'clientEdit.housing.withParents': 'Living with Parents',
    'clientEdit.housing.canAfford': 'Can Afford to Buy',
    'clientEdit.housing.preferNot': 'Prefer not to say',
    'clientEdit.car.own': 'Has Car',
    'clientEdit.car.none': 'No Car',
    'clientEdit.car.cantDrive': "Can't Drive",
    'clientEdit.marriage.anytime': 'Anytime',
    'clientEdit.marriage.halfYear': 'Within 6 months',
    'clientEdit.marriage.oneYear': 'Within 1 year',
    'clientEdit.marriage.twoYears': 'Within 2 years',
    'clientEdit.marriage.threeYears': 'Within 3 years',
    'clientEdit.children.none': 'None',
    'clientEdit.children.withSelf': 'Has (with self)',
    'clientEdit.children.withOther': 'Has (with other)',
    'clientEdit.children.independent': 'Has (independent)',
    'clientEdit.wantKids.yes': 'Want kids',
    'clientEdit.wantKids.no': 'No kids',
    'clientEdit.wantKids.either': "Doesn't matter",
    'clientEdit.parents.bothAlive': 'Both parents alive',
    'clientEdit.parents.motherOnly': 'Only mother alive',
    'clientEdit.parents.fatherOnly': 'Only father alive',
    'clientEdit.parents.bothPassed': 'Both passed away',
    'clientEdit.siblings.onlyChild': 'Only child',
    'clientEdit.zodiac.aries': 'Aries',
    'clientEdit.zodiac.taurus': 'Taurus',
    'clientEdit.zodiac.gemini': 'Gemini',
    'clientEdit.zodiac.cancer': 'Cancer',
    'clientEdit.zodiac.leo': 'Leo',
    'clientEdit.zodiac.virgo': 'Virgo',
    'clientEdit.zodiac.libra': 'Libra',
    'clientEdit.zodiac.scorpio': 'Scorpio',
    'clientEdit.zodiac.sagittarius': 'Sagittarius',
    'clientEdit.zodiac.capricorn': 'Capricorn',
    'clientEdit.zodiac.aquarius': 'Aquarius',
    'clientEdit.zodiac.pisces': 'Pisces',
    'clientEdit.ethnicity.asian': 'Asian',
    'clientEdit.ethnicity.white': 'White',
    'clientEdit.ethnicity.mexican': 'Mexican',
    'clientEdit.ethnicity.alaskan': 'Alaskan',
    'clientEdit.ethnicity.black': 'Black',
    'clientEdit.ethnicity.other': 'Other',
    'clientEdit.religion.christianity': 'Christianity',
    'clientEdit.religion.islam': 'Islam',
    'clientEdit.religion.hinduism': 'Hinduism',
    'clientEdit.religion.buddhism': 'Buddhism',
    'clientEdit.religion.judaism': 'Judaism',
    'clientEdit.religion.none': 'No Religion',
    'clientEdit.religion.catholic': 'Catholic',
    'clientEdit.smoking.no': "Don't smoke",
    'clientEdit.smoking.occasionally': 'Occasionally',
    'clientEdit.smoking.packADay': 'A pack a day',
    'clientEdit.smoking.whenever': 'Whenever available',
    'clientEdit.smoking.quitting': 'Quitting',
    'clientEdit.smoking.quit': 'Already quit',
    'clientEdit.drinking.never': 'Never drink',
    'clientEdit.drinking.sometimes': 'Sometimes',
    'clientEdit.drinking.enjoy': 'Enjoy drinking',
    'clientEdit.drinking.daily': 'Daily drinker',
    'clientEdit.drinking.quitting': 'Quitting',
    'clientEdit.drinking.quit': 'Already quit',
    'clientEdit.sleep.regular': 'Regular early sleeper',
    'clientEdit.sleep.nightOwl': 'Night owl',
    'clientEdit.sleep.earlyBird': 'Early bird',
    'clientEdit.sleep.occasional': 'Occasionally lazy',
    'clientEdit.sleep.irregular': 'No pattern',
    'clientEdit.exercise.daily': 'Daily exercise',
    'clientEdit.exercise.weekly': 'At least once a week',
    'clientEdit.exercise.monthly': 'A few times a month',
    'clientEdit.exercise.noTime': 'No time to exercise',
    'clientEdit.exercise.focused': 'Concentrated sessions',
    'clientEdit.exercise.none': "Don't like exercising",
    'clientEdit.hobby.sports': 'Sports',
    'clientEdit.hobby.food': 'Food',
    'clientEdit.hobby.music': 'Music',
    'clientEdit.hobby.movies': 'Movies',
    'clientEdit.hobby.entertainment': 'Entertainment',
    'clientEdit.hobby.shopping': 'Shopping',
    'clientEdit.hobby.hiking': 'Hiking',
    'clientEdit.hobby.travel': 'Travel',
    'clientEdit.hobby.fishing': 'Fishing',
    'clientEdit.hobby.golf': 'Golf',
    'clientEdit.hobby.yoga': 'Yoga',
    'clientEdit.hobby.swimming': 'Swimming',
    'clientEdit.hobby.reading': 'Reading',
    'clientEdit.hobby.climbing': 'Rock Climbing',
    'clientEdit.hobby.diving': 'Diving',
    'clientEdit.hobby.walking': 'Walking',
    'clientEdit.hobby.cooking': 'Cooking',
    'clientEdit.hobby.dancing': 'Dancing',
    'clientEdit.hobby.pets': 'Pets',
    'clientEdit.hobby.camping': 'Camping',
    'clientEdit.hobby.outdoor': 'Outdoor Sports',
    'clientEdit.hobby.offroad': 'Off-road',
    'clientEdit.hobby.shooting': 'Shooting',
    'clientEdit.hobby.boardGames': 'Board Games',
    'clientEdit.hobby.cycling': 'Cycling',
    'clientEdit.hobby.painting': 'Painting',
    'clientEdit.hobby.gaming': 'Gaming',
    'clientEdit.hobby.meditation': 'Meditation',
    'clientEdit.hobby.sailing': 'Sailing',
    'clientEdit.hobby.wine': 'Wine Tasting',
    'clientEdit.hobby.museum': 'Museums',
    'clientEdit.hobby.concerts': 'Concerts',
    'clientEdit.hobby.theater': 'Theater',
    'clientEdit.hobby.beach': 'Beach Vacation',
    'clientEdit.hobby.horseRiding': 'Horse Riding',
    'clientEdit.hobby.hunting': 'Hunting',
    'clientEdit.hobby.trekking': 'Trekking',
    'clientEdit.hobby.skiing': 'Skiing',
    'clientEdit.lang.mandarin': 'Mandarin',
    'clientEdit.lang.cantonese': 'Cantonese',
    'clientEdit.lang.englishBasic': 'English (Basic)',
    'clientEdit.lang.englishDaily': 'English (Daily)',
    'clientEdit.lang.englishFluent': 'English (Fluent)',
    'clientEdit.relocation.accept': 'Accept',
    'clientEdit.relocation.notNow': 'Not now',
    'clientEdit.relocation.partnerMove': 'Partner can relocate',
    'clientEdit.purpose.marriage': 'Marriage',
    'clientEdit.purpose.dating': 'Dating',
    'clientEdit.priority.familyFirst': 'Family First',
    'clientEdit.priority.careerFirst': 'Career First',
    'clientEdit.priority.looksFirst': 'Looks First',
    'clientEdit.priority.familyCareer': 'Family & Career',
    'clientEdit.priority.familyLooks': 'Family & Looks',
    'clientEdit.priority.careerLooks': 'Career & Looks',
    'clientEdit.priority.balanced': 'All Balanced',
    'clientEdit.rangeUnit.years': 'yrs',
    'clientEdit.rangeUnit.cm': 'CM',

    // Finance Page
    'finance.title': 'Finance',
    'finance.subtitle': 'Manage income, expenses, and financial statements',
    'finance.totalRevenue': 'Total Revenue',
    'finance.totalExpenses': 'Total Expenses',
    'finance.netProfit': 'Net Profit',
    'finance.outstanding': 'Outstanding',
    'finance.overview': 'Overview',
    'finance.income': 'Income',
    'finance.expenses': 'Expenses',
    'finance.statements': 'Statements',
    'finance.incomeVsExpenses': 'Income vs Expenses',
    'finance.recentTransactions': 'Recent Transactions',
    'finance.noTransactionsYet': 'No transactions yet',
    'finance.date': 'Date',
    'finance.description': 'Description',
    'finance.category': 'Category',
    'finance.type': 'Type',
    'finance.amount': 'Amount',
    'finance.source': 'Source',
    'finance.payee': 'Payee',
    'finance.paidBy': 'Paid By',
    'finance.affiliate': 'Affiliate',
    'finance.reference': 'Reference',
    'finance.actions': 'Actions',
    'finance.auto': 'Auto',
    'finance.manual': 'Manual',
    'finance.autoGenerated': 'Auto-generated',
    'finance.searchIncome': 'Search income transactions...',
    'finance.searchExpense': 'Search expense transactions...',
    'finance.allCategories': 'All Categories',
    'finance.from': 'From',
    'finance.to': 'To',
    'finance.addIncome': 'Add Income',
    'finance.addExpense': 'Add Expense',
    'finance.noIncomeFound': 'No income transactions found',
    'finance.noExpenseFound': 'No expense transactions found',
    'finance.adjustFilters': 'Try adjusting your filters or add a new entry',
    'finance.showing': 'Showing',
    'finance.of': 'of',
    'finance.prev': 'Prev',
    'finance.next': 'Next',
    'finance.financialStatement': 'Financial Statement',
    'finance.monthly': 'Monthly',
    'finance.annual': 'Annual',
    'finance.exportCsv': 'Export CSV',
    'finance.loadingStatement': 'Loading statement...',
    'finance.noStatementData': 'No statement data available',
    'finance.selectPeriod': 'Select a period above to view the statement',
    'finance.totalIncome': 'Total Income',
    'finance.transactions': 'Transactions',
    'finance.annualTransactions': 'Annual Transactions',
    'finance.noTransactionsForPeriod': 'No transactions for this period',
    'finance.netTotal': 'Net Total:',
    'finance.totalOutstanding': 'Total Outstanding',
    'finance.overdue': 'Overdue',
    'finance.pending': 'Pending',
    'finance.partial': 'Partial',
    'finance.awaitingApproval': 'Awaiting Approval',
    'finance.awaiting': 'Awaiting',
    'finance.outstandingInvoices': 'Outstanding Invoices',
    'finance.searchInvoices': 'Search invoices...',
    'finance.all': 'All',
    'finance.invoiceNumber': 'Invoice #',
    'finance.client': 'Client',
    'finance.plan': 'Plan',
    'finance.status': 'Status',
    'finance.total': 'Total',
    'finance.paid': 'Paid',
    'finance.due': 'Due',
    'finance.dueDate': 'Due Date',
    'finance.created': 'Created',
    'finance.noOutstandingInvoices': 'No outstanding invoices',
    'finance.allPaymentsUpToDate': 'All payments are up to date',
    'finance.daysOverdue': 'days overdue',
    'finance.dayOverdue': 'day overdue',
    'finance.invoices': 'invoice(s)',
    'finance.editIncome': 'Edit Income',
    'finance.editExpense': 'Edit Expense',
    'finance.updateIncome': 'Update Income',
    'finance.updateExpense': 'Update Expense',
    'finance.createIncome': 'Create Income',
    'finance.createExpense': 'Create Expense',
    'finance.dateLabel': 'Date *',
    'finance.amountLabel': 'Amount *',
    'finance.descriptionLabel': 'Description *',
    'finance.categoryLabel': 'Category',
    'finance.referenceLabel': 'Reference #',
    'finance.payeeLabel': 'Payee',
    'finance.notesLabel': 'Notes',
    'finance.enterDescription': 'Enter description...',
    'finance.optional': 'Optional',
    'finance.whoWasPaid': 'Who was paid?',
    'finance.additionalNotes': 'Additional notes...',
    'finance.statementExported': 'Statement exported',
    'finance.expense': 'Expense',
    'finance.viewAll': 'View All',
    'finance.matchmaker': 'Matchmaker',
    'finance.transactionDetails': 'Transaction Details',
    'finance.invoiceDetails': 'Invoice Details',
    'finance.paymentMethod': 'Payment Method',
    'leaderCoach.deliverableUpdated': 'Deliverable status updated',
    'leaderCoach.updateFailed': 'Failed to update deliverable',
    'leaderCoach.viewPages': 'View Pages',
    'leaderCoach.approveDeliverable': 'Approve Deliverable',
    'leaderCoach.rejectDeliverable': 'Reject Deliverable',
    'leaderCoach.pageNavigation': 'Page {current} of {total}',
    'leaderCoach.previousPage': 'Previous',
    'leaderCoach.nextPage': 'Next',
  },
  zh: {
    // Login Page
    'login.title': '红娘管理平台',
    'login.subtitle': '登录您的账户',
    'login.email': '邮箱',
    'login.phoneOtp': '手机验证码',
    'login.emailAddress': '邮箱地址',
    'login.enterEmail': '请输入邮箱',
    'login.password': '密码',
    'login.enterPassword': '请输入密码',
    'login.signingIn': '登录中...',
    'login.signIn': '登录',
    'login.phoneNumber': '手机号码',
    'login.enterPhoneNumber': '请输入手机号码',
    'login.phoneFormat': '请输入不含国家代码的手机号码',
    'login.searchCountry': '搜索国家...',
    'login.sendingOtp': '发送中...',
    'login.sendOtp': '发送验证码',
    'login.otpSent': '验证码已发送到您的手机',
    'login.otpFailed': '发送验证码失败，请重试',
    'login.enterOtp': '输入验证码',
    'login.otpPlaceholder': '请输入6位验证码',
    'login.resendIn': '重新发送倒计时',
    'login.resendOtp': '重新发送验证码',
    'login.verifying': '验证中...',
    'login.verifySignIn': '验证并登录',
    'login.invalidOtp': '验证码无效，请重试',
    'login.enterValidPhone': '请输入有效的手机号码',
    'login.enterValidOtp': '请输入有效的验证码',
    'login.change': '更改',
    'login.noAccount': '还没有账户？',
    'login.createOne': '立即注册',

    // Common
    'common.search': '搜索',
    'common.save': '保存',
    'common.saveChanges': '保存更改',
    'common.saving': '保存中...',
    'common.selected': '已选择',
    'common.cancel': '取消',
    'common.delete': '删除',
    'common.edit': '编辑',
    'common.view': '查看',
    'common.create': '创建',
    'common.add': '添加',
    'common.close': '关闭',
    'common.confirm': '确认',
    'common.back': '返回',
    'common.next': '下一步',
    'common.loading': '加载中...',
    'common.noData': '暂无数据',
    'common.actions': '操作',
    'common.status': '状态',
    'common.date': '日期',
    'common.name': '姓名',
    'common.email': '邮箱',
    'common.phone': '电话',
    'common.location': '位置',
    'common.total': '总计',
    'common.all': '全部',
    'common.active': '活跃',
    'common.inactive': '不活跃',
    'common.pending': '待处理',
    'common.approved': '已批准',
    'common.rejected': '已拒绝',
    'common.success': '成功',
    'common.error': '错误',
    'common.warning': '警告',
    'common.info': '信息',
    'common.viewAll': '查看全部',
    'common.type': '类型',
    'common.created': '创建时间',
    'common.updated': '更新时间',
    'common.details': '详情',
    'common.submit': '提交',
    'common.reset': '重置',
    'common.filter': '筛选',
    'common.filters': '筛选条件',
    'common.clearFilters': '清除筛选',
    'common.applyFilters': '应用筛选',
    'common.sortBy': '排序',
    'common.newestFirst': '最新优先',
    'common.oldestFirst': '最早优先',
    'common.nameAZ': '姓名 A-Z',
    'common.nameZA': '姓名 Z-A',
    'common.recentlyUpdated': '最近更新',
    'common.loadMore': '加载更多',
    'common.showingOf': '显示 {showing} / {total}',
    'common.noResults': '未找到结果',
    'common.noDetails': '无详细信息',
    'common.page': '页',
    'common.previous': '上一页',
    'common.remove': '移除',
    'common.yes': '是',
    'common.no': '否',
    'common.or': '或',
    'common.and': '和',
    'common.title': '标题',
    'common.description': '描述',
    'common.endDate': '结束日期',
    'common.copy': '复制',
    'common.copied': '已复制！',
    'common.saving': '保存中...',
    'common.selected': '已选择',
    'common.rows': '每页行数',
    'common.typeToSearch': '输入搜索...',
    'common.creating': '创建中...',
    'common.uploading': '上传中...',

    // Navigation / Sidebar
    'nav.dashboard': '仪表板',
    'nav.profiles': '档案',
    'nav.assignmentRequests': '分配请求',
    'nav.directory': '目录',
    'nav.matches': '匹配',
    'nav.datingSchedules': '约会安排',
    'nav.videoMeetings': '视频会议',
    'nav.audioCalls': '语音通话',
    'nav.connections': '连接',
    'nav.messages': '消息',
    'nav.notifications': '通知',
    'nav.tasks': '任务',
    'nav.calendar': '日历',
    'nav.affiliate': '联盟',
    'nav.training': '培训',
    'nav.billing': '账单',
    'nav.settings': '设置',
    'nav.admin': '管理',
    'nav.logout': '退出登录',
    'nav.mainMenu': '主菜单',
    'roles.coach': '教练',
    'roles.leaderCoach': '领队教练',
    'roles.admin': '管理员',
    'roles.supervisor': '督导',
    'roles.matchmaker': '红娘',
    'nav.collapse': '收起',
    'nav.expand': '展开',
    'nav.searchPages': '搜索页面...',
    
    // Admin Navigation
    'nav.adminDashboard': '管理仪表板',
    'nav.users': '用户',
    'nav.roles': '角色',
    'nav.allProfiles': '所有档案',
    'nav.profileEditReviews': '档案编辑审核',
    'nav.customFields': '自定义字段',
    'nav.coaches': '教练',
    'nav.adminDatingSchedules': '约会安排',
    'nav.adminMatches': '匹配管理',
    'nav.trends': '趋势',
    'nav.adminBilling': '账单管理',
    'nav.finance': '财务',
    'nav.upgradeRequests': '升级续费审核',
    'nav.affiliates': '联盟成员',
    'nav.plans': '方案',
    'nav.autoResponseManager': '自动回复管理器',
    'nav.automationSettings': '自动化设置',
    'nav.starsignSettings': '星座设置',
    'nav.starsignReports': '星座命盘报告',
    'nav.platformSettings': '平台设置',
    'nav.translations': '翻译管理',
    'nav.databaseSync': '数据库同步',
    'nav.apiTokens': 'API 接口',
    'nav.kpiManagement': 'KPI管理',
    'nav.coachKpi': 'KPI绩效',
    'nav.leaderCoachKpiPerformance': 'KPI绩效',
    'nav.payroll': '工资核算',
    'nav.payslip': '工资单',
    'nav.myPayroll': '我的工资单',
    'nav.leaderCoachReview': 'KPI审核',
    'nav.leaderCoachKpi': 'KPI审核',
    'nav.supervisorKpi': 'KPI审核',
    'nav.activityLogs': '活动日志',
    'nav.signedDocuments': '签署文件',
    'nav.home': '首页',
    'nav.profile': '我的',
    'nav.search': '搜索',
    'nav.searchPlaceholder': '搜索用户、动态、消息...',
    'nav.searchProfiles': '用户',
    'nav.searchTrends': '动态',
    'nav.searchMessages': '消息',
    'nav.searchNoResults': '未找到结果',
    'nav.searchViewAll': '查看全部结果',
    'nav.myAccount': '我的账户',

    // Weather
    'weather.clear': '晴朗',
    'weather.cloudy': '多云',
    'weather.foggy': '雾',
    'weather.drizzle': '毛毛雨',
    'weather.rain': '雨',
    'weather.snow': '雪',
    'weather.showers': '阵雨',
    'weather.snowfall': '降雪',
    'weather.thunderstorm': '雷暴',
    'weather.feelsLike': '体感',

    // Dashboard
    'dashboard.title': '仪表板',
    'dashboard.welcome': '欢迎回来',
    'dashboard.subtitle': '以下是您的红娘业务今日动态。',
    'dashboard.totalProfiles': '档案总数',
    'dashboard.activeMatches': '活跃匹配',
    'dashboard.successfulMatches': '成功匹配',
    'dashboard.pendingTasks': '待处理任务',
    'dashboard.upcomingEvents': '即将到来的事件',
    'dashboard.todayEvents': '今日事件',
    'dashboard.todaysTasks': '今日任务',
    'dashboard.recentActivity': '近期活动',
    'dashboard.recentProfiles': '最近档案',
    'dashboard.recentMatches': '最近匹配',
    'dashboard.pendingKpiApprovals': 'KPI 待审批',
    'dashboard.noApprovalsPending': '没有待审批项',
    'dashboard.allApprovalsUpToDate': '所有阶段完成请求已审核。',
    'dashboard.review': '审核',
    'dashboard.quickActions': '快捷操作',
    'dashboard.thisMonth': '本月',
    'dashboard.upcomingReminder': '即将开始的日程',
    'dashboard.itemsUpcoming': '即将开始的项目',
    'dashboard.viewKPI': '查看 KPI',
    'dashboard.noUpcomingItems': '没有即将开始的项目',
    'dashboard.affiliateProgram': '联盟计划',
    'dashboard.upgradeProgress': '升级进度',
    'dashboard.viewDetails': '查看详情',
    'dashboard.basicPartner': '基础合作伙伴',
    'dashboard.individualPartner': '个人合作伙伴',
    'dashboard.ptsTo': '分升级到',
    'dashboard.referrals': '推荐数',
    'dashboard.totalEarned': '总收益',
    'dashboard.commissionRate': '佣金比例',
    'dashboard.createMatch': '创建匹配',
    'dashboard.matched': '已匹配',
    'dashboard.points': '分',
    'dashboard.dailyTasksReminder': '每日任务提醒',
    'dashboard.tasksNeedAttention': '这些任务需要您今日关注',
    'dashboard.noTasksScheduled': '今天没有计划任务！',
    'dashboard.addTaskSuggestion': '建议添加任务，让您的日程更有价值。',
    'dashboard.addNewTask': '添加新任务',
    'dashboard.gotIt': '知道了',
    'dashboard.pendingApprovals': '待审批',
    'dashboard.stagesAwaitingReview': '个阶段/交付物待审核',
    'dashboard.reviewApprovals': '审核审批',
    'dashboard.overdue': '已逾期',
    'dashboard.dueToday': '今天截止',
    'dashboard.incompleteDeliverables': '未完成交付项',
    'dashboard.allDeliverablesComplete': '全部完成！',
    'dashboard.noIncompleteDeliverables': '目前没有未完成的交付项。',
    'dashboard.rejected': '已拒绝',

    // Profiles
    'profiles.title': '档案',
    'profiles.subtitle': '管理您的客户档案',
    'profiles.addProfile': '添加档案',
    'profiles.createProfile': '创建档案',
    'profiles.editProfile': '编辑档案',
    'profiles.deleteProfile': '删除档案',
    'profiles.viewProfile': '查看档案',
    'profiles.searchPlaceholder': '按姓名、邮箱、ID、离线ID搜索...',
    'profiles.noProfiles': '未找到档案',
    'profiles.noProfilesDesc': '暂无可显示的档案。',
    'profiles.startByAdding': '点击添加您的第一个档案。',
    'profiles.firstName': '名字',
    'profiles.lastName': '姓氏',
    'profiles.gender': '性别',
    'profiles.dateOfBirth': '出生日期',
    'profiles.age': '年龄',
    'profiles.bio': '简介',
    'profiles.occupation': '职业',
    'profiles.education': '教育',
    'profiles.male': '男',
    'profiles.female': '女',
    'profiles.other': '其他',
    'profiles.assignCoach': '分配教练',
    'profiles.layoutEditor': '布局编辑器',
    'profiles.profileType': '档案类型',
    'profiles.selectAll': '全选',
    'profiles.deselectAll': '取消全选',
    'profiles.client': '客户',
    'profiles.basicMembership': '基础会员',
    'profiles.coach': '教练',
    'profiles.myClient': '我的客户',
    'profiles.myAssignments': '我的分配',
    'profiles.requestAssignment': '请求分配档案',
    'profiles.requestAssignmentDesc': '提交请求以被分配此档案。管理员将审核并批准或拒绝您的请求。',
    'profiles.submitRequest': '提交请求',
    'profiles.submitting': '提交中...',
    'profiles.pending': '待处理',
    'profiles.request': '请求',
    'profiles.ownClientDisabled': '这是您自己的客户',
    'profiles.notesOptional': '备注（可选）',
    'profiles.notesPlaceholder': '请求此档案的原因...',
    'profiles.noEmail': '无邮箱',
    'profiles.backToProfiles': '返回档案',
    'profiles.myAssignmentRequests': '我的分配请求',
    'profiles.trackRequests': '跟踪您的档案分配请求及其状态',
    'profiles.sentRequests': '已发送的请求',
    'profiles.receivedRequests': '已接收的请求',
    'profiles.requestedBy': '请求者',
    'profiles.requestTo': '请求发送至',
    'profiles.requestFrom': '请求来自',
    'profiles.total': '总计',
    'profiles.accepted': '已接受',
    'profiles.rejected': '已拒绝',
    'profiles.searchByNameEmail': '按档案名称或邮箱搜索...',
    'profiles.requestCount': '个请求',
    'profiles.profile': '档案',
    'profiles.email': '邮箱',
    'profiles.phone': '电话',
    'profiles.height': '身高',
    'profiles.citizenship': '国籍身份',
    'profiles.columns': '字段',
    'profiles.customizeColumns': '自定义字段',
    'profiles.displayColumns': '显示字段',
    'profiles.notes': '备注',
    'profiles.requested': '请求时间',
    'profiles.responded': '回复时间',
    'profiles.adminResponse': '管理员回复',
    'profiles.noRequestsYet': '暂无分配请求。前往档案页面请求分配。',
    'profiles.noMatchFilter': '没有符合筛选条件的请求。',
    'profiles.unknown': '未知',
    'profiles.yearsOld': '岁',
    'profiles.filterConditions': '筛选条件',
    'profiles.chooseCoach': '选择教练...',
    'profiles.allTypes': '所有类型',
    'profiles.lead': '潜在客户',
    'profiles.member': '会员',
    'profiles.allGenders': '所有性别',
    'profiles.status': '状态',
    'profiles.allStatuses': '所有状态',
    'profiles.active': '活跃',
    'profiles.inactive': '未活跃',
    'profiles.archived': '已归档',
    'profiles.statusActive': '活跃',
    'profiles.statusInactive': '非活跃',
    'profileForm.profileStatus': '个人资料状态',
    'profiles.minAge': '最小年龄',
    'profiles.maxAge': '最大年龄',
    'profiles.membershipPlan': '会员计划',
    'profiles.allPlans': '所有计划',
    'profiles.approvalStatus': '审批状态',
    'profiles.allApprovalStatuses': '全部',
    'profiles.approvalPending': '待审批',
    'profiles.approvalApproved': '已批准',
    'profiles.approvalRejected': '已拒绝',
    'profiles.maritalStatus': '婚姻状况',
    'profiles.allMaritalStatuses': '全部',
    'profiles.neverMarried': '未婚',
    'profiles.divorced': '离婚',
    'profiles.divorcedPending': '离婚（处理中）',
    'profiles.widowed': '丧偶',
    'profiles.separated': '分居',
    'profiles.minHeight': '最低身高',
    'profiles.maxHeight': '最高身高',
    'profiles.heightRange': '身高范围（厘米）',
    'profiles.ageRange': '年龄范围',
    'profiles.country': '国家',
    'profiles.state': '省/州',
    'profiles.city': '城市',
    'directory.occupation': '职业',
    'directory.profileType': '档案类型',
    'directory.maritalStatus': '婚姻状况',
    'directory.country': '国家',
    'directory.state': '省/州',
    'directory.city': '城市',
    'directory.minHeight': '最低身高',
    'directory.maxHeight': '最高身高',
    'profiles.name': '姓名',
    'profiles.type': '类型',
    'profiles.membership': '会员资格',
    'profiles.location': '位置',
    'profiles.created': '创建时间',
    'profiles.photos': '照片',
    'profiles.yearsOld': '岁',
    'profiles.coach': '教练',

    // Matches
    'matches.title': '匹配',
    'matches.subtitle': '管理和跟踪档案匹配',
    'matches.viewOnly': '查看档案匹配及其状态',
    'matches.createMatch': '创建匹配',
    'matches.editMatch': '编辑匹配',
    'matches.deleteMatch': '删除匹配',
    'matches.viewMatch': '查看匹配',
    'matches.allMatches': '所有匹配',
    'matches.sentRequests': '已发送请求',
    'matches.receivedRequests': '已收到请求',
    'matches.matchPercentage': '匹配百分比',
    'matches.approve': '批准',
    'matches.reject': '拒绝',
    'matches.succeeded': '成功',
    'matches.failed': '失败',
    'matches.searchPlaceholder': '按姓名搜索...',
    'matches.allStatuses': '所有状态',
    'matches.noMatches': '未找到匹配',
    'matches.total': '共计',
    'matches.match': '匹配',
    'matches.requestFrom': '来自请求',
    'matches.requestSentTo': '请求已发送至',
    'matches.dateCreated': '创建日期',
    'matches.matchScore': '匹配分数',
    'matches.statusPending': '待处理',
    'matches.statusApproved': '已批准',
    'matches.statusSucceeded': '已约见',
    'matches.statusInRelationship': '已交往',
    'matches.statusInContact': '接触中',
    'matches.statusRejected': '已拒绝',
    'matches.statusCancelled': '已取消',
    'matches.statusFailed': '已失败',
    'matches.statusCompleted': '已完成',
    // Matches Modal
    'matches.createNewMatch': '创建新匹配',
    'matches.editMatch': '编辑匹配',
    'matches.modalInstruction': '首先选择您的一位客户，然后从目录中选择匹配候选人。',
    'matches.yourClient': '您的客户',
    'matches.matchCandidate': '匹配候选人',
    'matches.selectBelow': '在下方选择',
    'matches.selectClientFirst': '请先选择客户',
    'matches.selectYourClient': '选择您的客户：',
    'matches.searchYourClients': '搜索您的客户...',
    'matches.sendMatchRequest': '发送匹配请求',
    'matches.male': '男',
    'matches.female': '女',

    // Directory
    'directory.title': '档案目录',
    'directory.subtitle': '浏览发现网络中的档案',
    'directory.searchPlaceholder': '按姓名、职业搜索...',
    'directory.noResults': '目录中未找到档案',
    'directory.filters': '筛选',
    'directory.clearFilters': '清除筛选',
    'directory.myFavorites': '我的收藏',
    'directory.favorites': '收藏',
    'directory.favoriteProfiles': '个收藏档案',
    'directory.profilesFound': '个档案',
    'directory.active': '活跃',
    'directory.noFavorites': '暂无收藏',
    'directory.backToDirectory': '返回目录',
    'directory.selectProfileToMatch': '选择要匹配的档案',
    'directory.matchRequestSent': '如果档案由其他红娘管理，将发送匹配请求。',
    'directory.selectBelow': '在下方选择',
    'directory.sendMatchRequest': '发送匹配请求',

    // Profile Detail
    'profileDetail.profileInfo': '档案信息',
    'profileDetail.contactInfo': '联系信息',
    'profileDetail.location': '位置',
    'profileDetail.birthday': '生日',
    'profileDetail.gender': '性别',
    'profileDetail.age': '年龄',
    'profileDetail.managedBy': '管理者',
    'profileDetail.assignedCoach': '分配的教练',
    'profileDetail.about': '简介',
    'profileDetail.details': '详情',
    'profileDetail.occupation': '职业',
    'profileDetail.education': '学历',
    'profileDetail.height': '身高',
    'profileDetail.religion': '宗教',
    'profileDetail.company': '公司',
    'profileDetail.assignment': '分配',
    'profileDetail.matchmaker': '红娘',
    'profileDetail.coach': '教练',
    'profileDetail.additionalDetails': '附加详情',
    'profileDetail.categoryGeneral': '基本信息',
    'profileDetail.categoryPersonal': '个人详情',
    'profileDetail.categoryFamily': '家庭信息',
    'profileDetail.categoryPreferences': '择偶条件',
    'profileDetail.categoryLifestyle': '生活方式',
    'profileDetail.categoryBackground': '教育与职业',
    'profileDetail.categoryContact': '联系方式',
    'profileDetail.categoryCRM': 'CRM管理',
    'profileDetail.categoryOther': '其他',
    'profileDetail.notSpecified': '未填写',
    'profileDetail.activity': '活动',
    'profileDetail.profileCreated': '档案创建',
    'profileDetail.lastUpdated': '最后更新',
    'profileDetail.noMatchesYet': '暂无匹配',
    'profileDetail.createMatchToConnect': '创建匹配以连接此档案与兼容的对象。',
    'profileDetail.matched': '匹配于',
    'profileDetail.profileNotFound': '未找到档案',
    'profileDetail.createMatchFor': '为以下创建匹配',
    'profileDetail.loadingProfiles': '正在加载档案...',
    'profileDetail.matchSentSuccess': '匹配请求已成功发送',
    'profileDetail.searchProfiles': '搜索档案...',
    'profileDetail.tasks': '任务',
    'profileDetail.noTasks': '此档案没有关联的任务',
    'profileDetail.overdue': '已逾期',
    'profileDetail.customInfo': '自定义信息',

    // Profile Form (Add/Edit Profile Wizard)
    'profileForm.addNewProfile': '添加新档案',
    'profileForm.editProfile': '编辑档案',
    'profileForm.stepXofY': '步骤',
    'profileForm.of': '/',
    'profileForm.next': '下一步',
    'profileForm.previous': '上一步',
    'profileForm.saveProfile': '保存档案',
    'profileForm.saving': '保存中...',
    'profileForm.loadingProfile': '加载档案中...',
    'profileForm.confirmSubmit': '确认并提交',
    // Step Names
    'profileForm.stepInterviewer': '面谈人',
    'profileForm.stepBasicInfo': '基本信息',
    'profileForm.stepLocationBio': '位置与简介',
    'profileForm.stepLocation': '位置',
    'profileForm.stepAdditionalDetails': '附加信息',
    'profileForm.stepBioGallery': '关于 / 简介',
    'profileForm.stepPhotoID': '身份证照片',
    'profileForm.stepMembershipPlan': '会员计划',
    'profileForm.stepPaymentPlan': '付款方案',
    'profileForm.stepContractSign': '合同与签署',
    'profileForm.stepRecruiter': '推荐人',
    // Photo ID Step
    'profileForm.photoIDUpload': '身份证照片上传',
    'profileForm.photoIDRequired': '身份证照片上传（选填）',
    'profileForm.photoIDNote': '请上传您政府颁发的身份证的清晰照片以进行验证',
    'profileForm.errPhotoIDRequired': '身份证照片是必需的',
    'profileForm.changePhotoID': '点击更改',
    'profileForm.clickToUploadPhotoID': '点击上传身份证照片',
    'profileForm.acceptedFormats': '接受的格式：JPG、PNG、GIF',
    // Step 1: Interviewer Assignment
    'profileForm.interviewerAssignment': '面谈人分配',
    'profileForm.whoConductedInterview': '谁进行了面谈？',
    'profileForm.selectMatchmakerInterview': '选择进行客户面谈的红娘',
    'profileForm.interviewer': '面谈人',
    'profileForm.selectInterviewer': '选择面谈人...',
    'profileForm.myself': '我自己',
    'profileForm.selfInterviewNote': '您被指定为面谈人。该档案将关联到您的账户。',
    // Step 2: Basic Information
    'profileForm.basicInformation': '基本信息',
    'profileForm.profilePhoto': '个人头像',
    'profileForm.galleryPhotos': '相册照片',
    'profileForm.upload': '上传',
    'profileForm.firstName': '名',
    'profileForm.lastName': '姓',
    'profileForm.emailLabel': '邮箱',
    'profileForm.phoneLabel': '电话',
    'profileForm.dateOfBirth': '出生日期',
    'profileForm.birthTime': '出生时间（24小时制）',
    'profileForm.starSign': '星座',
    'profileForm.selectStarSign': '选择...',
    'profileForm.aries': '白羊座 ♈',
    'profileForm.taurus': '金牛座 ♉',
    'profileForm.gemini': '双子座 ♊',
    'profileForm.cancer': '巨蟹座 ♋',
    'profileForm.leo': '狮子座 ♌',
    'profileForm.virgo': '处女座 ♍',
    'profileForm.libra': '天秤座 ♎',
    'profileForm.scorpio': '天蝎座 ♏',
    'profileForm.sagittarius': '射手座 ♐',
    'profileForm.capricorn': '摩羯座 ♑',
    'profileForm.aquarius': '水瓶座 ♒',
    'profileForm.pisces': '双鱼座 ♓',
    'profileForm.showPhotos': '在公开资料中显示照片',
    'profileForm.showAge': '在公开资料中显示年龄',
    'profileForm.sharePublicProfile': '分享公开资料',
    'profileForm.showOnDirectory': '在目录中显示',
    'profileForm.visibilitySettings': '可见性设置',
    'profileForm.gender': '性别',
    'profileForm.selectGender': '选择性别...',
    'profileForm.male': '男',
    'profileForm.female': '女',
    'profileForm.other': '其他',
    'profileForm.profileType': '档案类型',
    'profileForm.lead': '潜在客户',
    'profileForm.client': '客户',
    'profileForm.member': '会员',
    'profileForm.errFirstNameRequired': '名是必填项',
    'profileForm.errLastNameRequired': '姓是必填项',
    'profileForm.errInvalidEmail': '邮箱格式无效',
    'profileForm.errEmailRequired': '邮箱为必填项',
    'profileForm.errGenderRequired': '性别为必填项',
    'profileForm.errBirthDateRequired': '出生日期为必填项',
    'profileForm.errInvalidBirthDate': '请输入有效日期',
    'profileForm.errInvalidBirthTime': '请输入有效时间（HH:MM）',
    'profileForm.errContractFirstNameRequired': '名是必填项',
    'profileForm.errContractLastNameRequired': '姓是必填项',
    'profileForm.errPleaseAgreeToTerms': '请同意合同条款',
    'profileForm.failedToLoadProfile': '加载档案失败',
    'profileForm.profileUpdatedSuccess': '档案更新成功',
    'profileForm.profileCreatedSuccess': '档案创建成功',
    'profileForm.failedToSaveProfile': '保存档案失败',
    // Step 3: Location & Bio
    'profileForm.locationBio': '位置与简介',
    'profileForm.location': '位置',
    'profileForm.city': '城市',
    'profileForm.stateProvince': '州/省',
    'profileForm.country': '国家',
    'profileForm.bioAbout': '关于/简介',
    'profileForm.bioPlaceholder': '介绍一下您自己、您的兴趣爱好以及您想寻找什么样的人...',
    // Step 4: Additional Details (Custom Fields)
    'profileForm.additionalDetails': '附加信息',
    'profileForm.otherInformation': '其他信息',
    'profileForm.noFieldsConfigured': '未配置自定义字段',
    'profileForm.noFieldsConfiguredDesc': '请联系管理员设置自定义档案字段。',
    // Step 5: Membership Plan
    'profileForm.selectMembershipPlan': '选择会员计划',
    'profileForm.noPlansAvailable': '暂无可用的会员计划',
    'profileForm.skipStepOrContactAdmin': '您可以跳过此步骤或联系管理员创建计划。',
    'profileForm.selected': '已选择',
    'profileForm.createPlans': '请联系管理员创建会员计划。',
    'profileForm.selectedPlanLabel': '已选计划',
    'profileForm.planPrice': '计划价格',
    // Step 6: Payment Plan
    'profileForm.paymentPlan': '付款方案',
    'profileForm.paymentType': '付款方式',
    'profileForm.payFullNow': '立即全额支付',
    'profileForm.oneTimePayment': '一次性支付全部会员费用',
    'profileForm.deposit': '预付款 + 月供',
    'profileForm.depositMonthly': '先支付预付款，剩余金额按月分期支付',
    'profileForm.customDeposit': '自定义预付款金额',
    'profileForm.depositAmount': '预付款金额',
    'profileForm.errDepositRequired': '预付款金额为必填项',
    'profileForm.errDepositTooHigh': '预付款不能超过计划价格',
    'profileForm.numMonthlyPayments': '月供期数',
    'profileForm.monthlyLabel': '每月',
    'profileForm.months': '个月',
    'profileForm.firstPaymentDate': '首次付款日期',
    'profileForm.includeServiceFee': '包含服务费',
    'profileForm.paymentSummary': '付款摘要',
    'profileForm.paymentSchedule': '付款计划',
    'profileForm.installmentDetails': '分期详情',
    'profileForm.dueDate': '到期日',
    'profileForm.amount': '金额',
    'profileForm.typeCol': '类型',
    'profileForm.total': '总计',
    'profileForm.totalDueToday': '今日应付总额',
    'profileForm.paymentsSavedNote': '付款计划将在提交档案时保存。',
    // Payment Stages
    'profileForm.stagesPayment': '阶段付款',
    'profileForm.stagesPaymentDesc': '自定义付款阶段及追踪',
    'profileForm.paymentStages': '付款阶段',
    'profileForm.addStage': '添加阶段',
    'profileForm.stage': '阶段',
    'profileForm.stageName': '阶段名称',
    'profileForm.status': '状态',
    'profileForm.paid': '已付',
    'profileForm.unpaid': '未付',
    'profileForm.paidOn': '付款日期',
    'profileForm.noStagesAdded': '尚未添加付款阶段',
    'profileForm.clickAddStage': '点击"添加阶段"创建自定义付款里程碑',
    'profileForm.stagesTotal': '阶段总计',
    'profileForm.stagesPaid': '个阶段已付',
    'profileForm.stagesCommissionNote': '当管理员或教练将每个阶段标记为已付款时，将计算佣金和积分。',
    // Payment Method
    'profileForm.paymentMethod': '付款方式',
    'profileForm.onlinePayment': '自动支付',
    'profileForm.onlinePaymentDesc': '通过PayPal自动支付',
    'profileForm.offlinePayment': '手动支付',
    'profileForm.offlinePaymentDesc': 'Zelle、银行转账、支票或现金',
    'profileForm.payWithPaypal': '使用PayPal支付',
    'profileForm.paypalOneTimeNote': '提交档案后，您将被重定向到PayPal完成一次性付款。',
    'profileForm.paypalSubscriptionNote': '提交档案后，您将被重定向到PayPal设置定期付款。',
    'profileForm.paypalProcessAfterSubmit': 'PayPal付款将在您提交档案并签署合同后处理。',
    'profileForm.offlinePaymentType': '选择付款类型',
    'profileForm.bankTransfer': '银行转账',
    'profileForm.check': '支票',
    'profileForm.cash': '现金',
    'profileForm.uploadPaymentProof': '上传付款凭证',
    'profileForm.uploadPaymentProofDesc': '请上传您的付款确认照片或截图（收据、交易确认等）',
    'profileForm.addPhoto': '添加照片',
    'profileForm.offlineVerificationNote': '线下付款需要管理员或教练验证。付款验证后，您的档案将被批准。',
    'profileForm.paymentPending': '待付款',
    'profileForm.paymentVerified': '已验证',
    'profileForm.awaitingVerification': '待验证',
    'profileForm.markAsPaid': '标记为已付',
    'profileForm.confirmMarkPaid': '确认将此阶段标记为已付？这将为面谈人和推荐人计算佣金。',
    'profileForm.stageMarkedAsPaid': '付款阶段已成功标记为已付',
    'profileForm.failedToMarkPaid': '标记付款阶段为已付失败',
    // Step 7: Recruiter
    'profileForm.recruiterAssignment': '推荐人分配',
    'profileForm.recruiterInfo': '推荐人信息',
    'profileForm.selectRecruiter': '选择推荐人...',
    'profileForm.selectMatchmaker': '选择红娘...',
    'profileForm.commissionSplit': '佣金分配',
    'profileForm.commissionSplitDesc': '设置面谈人和推荐人之间的佣金分配方式',
    'profileForm.commissionTier': '佣金等级',
    'profileForm.commissionTierDesc': '佣金等级决定总佣金百分比',
    'profileForm.commissionRateByTier': '佣金比例由等级决定',
    'profileForm.tierBased': '按等级',
    'profileForm.interviewerCommissionPct': '面谈人佣金%',
    'profileForm.interviewerGets': '面谈人获得',
    'profileForm.youGet': '您获得',
    'profileForm.from': '从',
    'profileForm.to': '到',
    'profileForm.approvalNotice': '佣金分配变更可能需要管理员审批。',
    // Step 8: Contract & Sign (Service Agreement)
    'profileForm.serviceAgreement': '服务协议',
    'profileForm.downloadContract': '下载合同',
    'profileForm.downloadPdf': '下载PDF',
    'profileForm.agreementTitle': '婚介服务协议',
    'profileForm.agreementIntro': '本协议由客户与婚介服务提供方之间签订。',
    'profileForm.agreementS1Title': '一、服务内容',
    'profileForm.agreementS1': '服务提供方同意为客户提供个性化的婚介服务，包括档案创建、对象搜索和介绍安排。',
    'profileForm.agreementS2Title': '二、期限',
    'profileForm.agreementS2': '本协议自签署之日起生效，有效期为所选会员计划的持续时间。',
    'profileForm.agreementS3Title': '三、费用',
    'profileForm.agreementS3': '客户同意按照所选计划和约定的付款方式支付会员费用。',
    'profileForm.agreementS4Title': '四、保密条款',
    'profileForm.agreementS4': '双方同意对婚介过程中分享的所有个人信息严格保密。',
    'profileForm.agreementS5Title': '五、行为准则',
    'profileForm.agreementS5': '客户同意在服务提供方安排的所有介绍和沟通中保持礼貌和尊重。',
    'profileForm.agreementS6Title': '六、责任限制',
    'profileForm.agreementS6': '服务提供方不对特定结果做出保证。本服务仅限于介绍。',
    'profileForm.agreementS7Title': '七、终止',
    'profileForm.agreementS7': '任何一方可在提前30天书面通知后终止本协议。退款政策按所选会员条款执行。',
    'profileForm.agreeToTerms': '我同意以上条款和条件',
    'profileForm.agreeConfirm': '勾选此框即表示您已阅读并同意服务协议。',
    'profileForm.errAgreeToTerms': '您必须同意条款才能继续',
    'profileForm.clickToSign': '点击签署',
    'profileForm.signDate': '签署日期',
    'profileForm.errSignDate': '签署日期为必填项',
    'profileForm.signatureRecorded': '签名已记录',
    'profileForm.agreementSigned': '协议已签署',
    'profileForm.signedAgreementAvailable': '此档案已有签署的协议',

    // Match Detail
    'matchDetail.backToMatches': '返回匹配',
    'matchDetail.matchNotFound': '未找到匹配',
    'matchDetail.youSentRequest': '您已发送匹配请求给',
    'matchDetail.requestFrom': '匹配请求来自',
    'matchDetail.anotherMatchmaker': '另一位红娘',
    'matchDetail.viewFullProfile': '查看完整档案',
    'matchDetail.matchTimeline': '匹配时间线',
    'matchDetail.matchCreated': '匹配创建',
    'matchDetail.matchNotes': '匹配备注',
    'matchDetail.noNotesYet': '暂无备注。点击编辑添加关于此匹配的备注。',

    // Common additional
    'common.share': '分享',
    'common.tryAgain': '重试',

    // Dating Schedules
    'datingSchedules.title': '约会安排',
    'datingSchedules.subtitle': '为客户管理约会',
    'datingSchedules.viewOnly': '查看客户预定约会',
    'datingSchedules.scheduleNew': '安排新约会',
    'datingSchedules.today': '今天',
    'datingSchedules.thisWeek': '本周',
    'datingSchedules.completed': '已完成',
    'datingSchedules.upcoming': '即将到来',
    'datingSchedules.past': '过去',
    'datingSchedules.all': '全部',
    'datingSchedules.clients': '客户',
    'datingSchedules.dateTime': '日期时间',
    'datingSchedules.location': '地点',
    'common.note': '备注',
    'common.prev': '上一页',
    'common.perPage': '每页',
    'datingSchedules.searchSchedules': '搜索安排...',
    'datingSchedules.allStatus': '所有状态',
    'datingSchedules.date': '日期',
    'datingSchedules.noSchedules': '未找到安排',
    'datingSchedules.noSchedulesDesc': '暂无可显示的约会安排。',
    'datingSchedules.inRelationship': '恋爱中',
    'datingSchedules.engaged': '已订婚',
    'datingSchedules.statusPending': '待定',
    'datingSchedules.statusConfirmed': '已确认',
    'datingSchedules.statusAccepted': '已接受',
    'datingSchedules.statusRejected': '已拒绝',
    'datingSchedules.statusMeeting': '见面中',
    'datingSchedules.statusChatting': '聊天中',
    'datingSchedules.statusInRelationship': '恋爱中',
    'datingSchedules.statusEngaged': '已订婚',
    'datingSchedules.statusCompleted': '已完成',
    'datingSchedules.statusCancelled': '已取消',
    'datingSchedules.statusNoShow': '未出现',
    'datingSchedules.defaultScheduleTitle': '约会安排',
    'datingSchedules.requestFromMatchmaker': '来自',
    'datingSchedules.yourRequest': '您的请求',
    'datingSchedules.datingRequestNote': '注意：这是来自另一位红娘的约会请求。您可以接受、修改或拒绝此请求。',
    'datingSchedules.scheduleStatus': '安排状态',
    'datingSchedules.updateStatus': '更新状态',
    'datingSchedules.currentStatusFor': '当前状态：',
    'datingSchedules.selectNewStatusFor': '为...选择新状态：',
    'datingSchedules.sortTitleAZ': '标题 A-Z',
    'datingSchedules.sortTitleZA': '标题 Z-A',
    'datingSchedules.sortClient1AZ': '客户 1 A-Z',
    'datingSchedules.sortClient1ZA': '客户 1 Z-A',
    'datingSchedules.sortClient2AZ': '客户 2 A-Z',
    'datingSchedules.sortClient2ZA': '客户 2 Z-A',
    'datingSchedules.sortLocationAZ': '地点 A-Z',
    'datingSchedules.sortLocationZA': '地点 Z-A',
    'datingSchedules.sortStatusAZ': '状态 A-Z',
    'datingSchedules.sortStatusZA': '状态 Z-A',
    'datingSchedules.virtual': '虚拟',
    'matches.loadingMatches': '正在加载匹配...',
    'matches.loadingProfiles': '正在加载档案...',
    'matches.noProfiles': '未找到档案',
    'matches.noDetails': '无详情',
    // Dating Schedules Modal
    'datingSchedules.modalTitle': '安排新约会',
    'datingSchedules.editModalTitle': '编辑安排',
    'datingSchedules.yourClient': '您的客户',
    'datingSchedules.selectYourClient': '选择您的客户',
    'datingSchedules.selectYourClientPlaceholder': '选择您的客户...',
    'datingSchedules.otherMatchmakerClient': '其他红娘的客户',
    'datingSchedules.matchmaker': '红娘',
    'datingSchedules.client': '客户',
    'datingSchedules.selectMatchmaker': '选择红娘...',
    'datingSchedules.selectMatchmakerFirst': '请先选择红娘',
    'datingSchedules.dateTitle': '标题',
    'datingSchedules.dateTitlePlaceholder': '例如：咖啡约会',
    'datingSchedules.time': '时间',
    'datingSchedules.duration': '时长（分钟）',
    'datingSchedules.locationType': '地点类型',
    'datingSchedules.locationName': '地点名称',
    'datingSchedules.locationNamePlaceholder': '例如：蓝瓶咖啡',
    'datingSchedules.address': '地址',
    'datingSchedules.addressPlaceholder': '完整地址',
    'datingSchedules.notificationPreference': '通知方式',
    'datingSchedules.notes': '备注',
    'datingSchedules.notesPlaceholder': '内部备注...',
    'datingSchedules.createSchedule': '创建安排',
    'datingSchedules.updateSchedule': '更新安排',
    'datingSchedules.restaurant': '餐厅',
    'datingSchedules.cafe': '咖啡馆',
    'datingSchedules.bar': '酒吧',
    'datingSchedules.park': '公园',
    'datingSchedules.other': '其他',
    'datingSchedules.none': '无',
    'datingSchedules.sms': '短信',

    // Connections
    'connections.title': '连接',
    'connections.subtitle': '查找并连接外部红娘和公司',
    'connections.searchPlaceholder': '搜索连接...',
    'connections.addConnection': '添加连接',
    'connections.noConnections': '未找到连接',
    'connections.matchmakers': '红娘',
    'connections.companies': '公司',
    'connections.searchMatchmakers': '按姓名或公司搜索红娘...',
    'connections.filterByLocation': '按位置筛选...',
    'connections.filterByCountry': '国家',
    'connections.filterByState': '州 / 省',
    'connections.filterByCity': '城市',
    'connections.self': '自己',
    'connections.thisIsYou': '这是您自己',
    'connections.independentMatchmaker': '独立红娘',
    'connections.profiles': '个档案',
    'connections.message': '发消息',

    // Common additional
    'common.refresh': '刷新',
    'common.of': '/',
    'common.update': '更新',

    // Messages
    'messages.title': '消息',
    'messages.subtitle': '与客户和团队沟通',
    'messages.newMessage': '新消息',
    'messages.noMessages': '暂无消息',
    'messages.noMessagesYet': '暂无消息',
    'messages.noConversations': '暂时没有消息',
    'messages.newChat': '新建聊天',
    'messages.typeMessage': '输入消息...',
    'messages.send': '发送',
    'messages.conversations': '对话',
    'messages.searchConversations': '搜索对话...',
    'messages.sentImage': '发送了图片',
    'messages.sentProfile': '分享了档案',
    'messages.sharedProfile': '分享了档案',
    'messages.selectConversation': '选择对话',
    'messages.chooseFromList': '从列表中选择对话开始聊天',
    'messages.online': '在线',
    'messages.offline': '离线',
    'messages.yearsOld': '岁',

    // Tasks
    'tasks.title': '任务',
    'tasks.subtitle': '管理您的任务和跟进',
    'tasks.createTask': '创建任务',
    'tasks.newTask': '新任务',
    'tasks.addTask': '添加任务',
    'tasks.noTasks': '未找到任务',
    'tasks.noTasksDesc': '暂无可显示的任务。',
    'tasks.dueDate': '截止日期',
    'tasks.priority': '优先级',
    'tasks.high': '高',
    'tasks.medium': '中',
    'tasks.low': '低',
    'tasks.completed': '已完成',
    'tasks.inProgress': '进行中',
    'tasks.allCaughtUp': '全部完成！',
    'tasks.noPendingToday': '今日无待处理任务。',
    'tasks.noDueTime': '无截止时间',
    'tasks.searchPlaceholder': '搜索任务...',
    'tasks.overdue': '已逾期',
    'tasks.filterConditions': '筛选条件',
    'tasks.allStatus': '所有状态',
    'tasks.pending': '待处理',
    'tasks.allPriorities': '所有优先级',
    'tasks.dueDateEarliest': '截止日期（最早优先）',
    'tasks.dueDateLatest': '截止日期（最晚优先）',
    'tasks.createdNewest': '创建时间（最新优先）',
    'tasks.createdOldest': '创建时间（最早优先）',
    'tasks.priorityHighToLow': '优先级（从高到低）',
    'tasks.priorityLowToHigh': '优先级（从低到高）',
    'tasks.loadingTasks': '加载任务中...',
    'tasks.recurringTask': '循环任务',
    'tasks.daily': '每天',
    'tasks.weekly': '每周',
    'tasks.monthly': '每月',
    'tasks.yearly': '每年',
    'tasks.linkedProfile': '关联档案',
    'tasks.searchProfile': '搜索档案...',
    'tasks.noProfile': '未关联档案',
    'tasks.taskType': '类型',
    'tasks.typeOneTime': '单次',
    'tasks.typePostMeeting': '会后跟进',
    'tasks.typeCheckIn': '签到回访',
    'tasks.typeProgressReview': '进度审查',
    'tasks.typeCustom': '自定义',

    // Calendar
    'calendar.title': '日历',
    'calendar.subtitle': '管理您的预约和事件',
    'calendar.createEvent': '创建事件',
    'calendar.newEvent': '新事件',
    'calendar.noEvents': '暂无预定事件',
    'calendar.noUpcomingEvents': '暂无即将到来的事件',
    'calendar.today': '今天',
    'calendar.month': '月',
    'calendar.week': '周',
    'calendar.day': '日',
    'calendar.noReminder': '无提醒',
    'calendar.addressOrLink': '地址或会议链接',
    'calendar.upcomingEvents': '即将到来的事件',
    'calendar.editEvent': '编辑事件',
    'calendar.sun': '周日',
    'calendar.mon': '周一',
    'calendar.tue': '周二',
    'calendar.wed': '周三',
    'calendar.thu': '周四',
    'calendar.fri': '周五',
    'calendar.sat': '周六',
    'calendar.eventTitle': '事件标题',
    'calendar.eventType': '事件类型',
    'calendar.date': '日期',
    'calendar.startTime': '开始时间',
    'calendar.endTime': '结束时间',
    'calendar.location': '地点',
    'calendar.description': '描述',
    'calendar.reminder': '提醒',
    'calendar.minutes': '分钟前',
    'calendar.hour': '小时前',
    'calendar.hours': '小时前',
    'calendar.dayBefore': '天前',
    'calendar.eventTypes.date': '约会',
    'calendar.eventTypes.meeting': '会议',
    'calendar.eventTypes.call': '电话',
    'calendar.eventTypes.followup': '跟进',
    'calendar.eventTypes.event': '活动',
    'calendar.eventTypes.other': '其他',
    'calendar.eventTypes.videoCall': '视频通话',
    'calendar.agoraVideoMeeting': 'Agora 视频会议',
    'calendar.generateLink': '生成链接',

    // Notifications
    'notifications.title': '通知',
    'notifications.subtitle': '保持更新',
    'notifications.markAllRead': '全部标为已读',
    'notifications.noNotifications': '暂无通知',
    'notifications.unread': '条未读通知',
    'notifications.allCaughtUp': '全部完成！',
    'notifications.notification': '条通知',
    'notifications.notifications': '条通知',
    'notifications.matchUpdated': '匹配已更新',

    // Settings
    'settings.title': '设置',
    'settings.subtitle': '管理您的账户设置和偏好',
    'settings.profile': '档案设置',
    'settings.account': '账户设置',
    'settings.notifications': '通知设置',
    'settings.privacy': '隐私设置',
    'settings.companySettings': '公司设置',
    'settings.profileInfo': '个人资料',
    'settings.securitySettings': '安全设置',
    'settings.changePassword': '修改密码',
    'settings.twoFactorAuth': '双重认证',
    'settings.authenticatorApp': '认证器应用',
    'settings.sessions': '会话',
    'settings.currentSession': '当前会话',
    'settings.notificationSettings': '通知设置',
    'settings.newMatchAlerts': '新匹配提醒',
    'settings.newMatchAlertsDesc': '发现新的潜在匹配时获得通知',
    'settings.messagesNotif': '消息',
    'settings.messagesNotifDesc': '收到新消息时获得通知',
    'settings.taskReminders': '任务提醒',
    'settings.taskRemindersDesc': '获取即将到来的任务和截止日期提醒',
    'settings.newsletter': '新闻通讯',
    'settings.newsletterDesc': '接收包含提示和更新的月度新闻通讯',
    'settings.instantMatchAlerts': '新匹配即时通知',
    'settings.instantMessages': '新消息即时通知',
    'settings.pushTaskReminders': '获取任务提醒推送通知',
    'settings.preferences': '偏好设置',
    'settings.theme': '主题',
    'settings.light': '浅色',
    'settings.dark': '深色',
    'settings.system': '系统',
    'settings.language': '语言',
    'settings.timezone': '时区',
    'settings.tabProfile': '个人资料',
    'settings.tabSecurity': '安全',
    'settings.tabNotifications': '通知',
    'settings.tabPreferences': '偏好设置',
    'settings.tabCompany': '公司',
    'settings.companySettingsDesc': '配置您的公司信息和品牌',
    'settings.companyLogo': '公司标志',
    'settings.uploadLogo': '上传标志',
    'settings.logoRecommended': '推荐：200x200像素，PNG或SVG格式',
    'settings.companyName': '公司名称',
    'settings.enterCompanyName': '输入您的公司名称',
    'settings.phoneNumber': '电话号码',
    'settings.emailAddress': '邮箱地址',
    'settings.locationAddress': '地址',
    'settings.businessHours': '营业时间',
    'settings.to': '至',
    'settings.closed': '休息',
    'settings.features': '功能',
    'settings.meetingScheduler': '会议安排',
    'settings.meetingSchedulerDesc': '允许客户与您预约会议',
    'settings.customBranding': '自定义品牌',
    'settings.customBrandingDesc': '向客户展示您的公司标志和品牌',
    'settings.saving': '保存中...',
    'settings.saveCompanySettings': '保存公司设置',
    'settings.profileInformation': '个人资料',
    'settings.profileInformationDesc': '更新您的个人信息和公开资料',
    'settings.changePhoto': '更换照片',
    'settings.uploadPhoto': '上传照片',
    'settings.newPhotoSelected': '已选择新照片',
    'settings.photoRequirements': 'JPG、PNG或GIF格式，最大2MB',
    'settings.firstName': '名',
    'settings.lastName': '姓',
    'settings.email': '邮箱',
    'settings.phone': '电话',
    'settings.bio': '简介',
    'settings.bioPlaceholder': '介绍一下您自己...',
    'settings.saveChanges': '保存更改',
    'settings.securitySettingsDesc': '管理您的密码和安全偏好',
    'settings.currentPassword': '当前密码',
    'settings.newPassword': '新密码',
    'settings.confirmNewPassword': '确认新密码',
    'settings.updating': '更新中...',
    'settings.updatePassword': '更新密码',
    'settings.authenticatorAppDesc': '使用认证器应用增强安全性',
    'settings.enable': '启用',
    'settings.active': '活跃',
    'settings.notificationSettingsDesc': '选择您接收的通知类型',
    'settings.emailNotifications': '邮件通知',
    'settings.messages': '消息',
    'settings.messagesDesc': '收到新消息时获得通知',
    'settings.pushNotifications': '推送通知',
    'settings.pushNewMatchDesc': '新匹配即时通知',
    'settings.pushMessagesDesc': '新消息即时通知',
    'settings.pushTaskRemindersDesc': '获取任务提醒推送通知',
    'settings.savePreferences': '保存偏好设置',
    'settings.preferencesDesc': '自定义您的体验',
    'settings.themeLight': '浅色',
    'settings.themeDark': '深色',
    'settings.themeSystem': '系统',
    'settings.dateFormat': '日期格式',
    'settings.currency': '货币',

    // Billing
    'billing.title': '客户会员账单',
    'billing.subtitle': '管理客户会员、发票和付款',
    'billing.invoices': '发票',
    'billing.payments': '付款',
    'billing.balance': '余额',
    'billing.createInvoice': '创建发票',
    'billing.loadingBilling': '加载账单信息中...',
    'billing.allStatus': '所有状态',
    'billing.pending': '待处理',
    'billing.paid': '已支付',
    'billing.overdue': '已逾期',
    'billing.awaitingApproval': '待审批',
    'billing.cancelled': '已取消',
    'billing.rejected': '已拒绝',
    'billing.membershipRejected': '会员已被拒绝',
    'billing.invoiceNumber': '发票编号',
    'billing.client': '客户',
    'billing.plan': '方案',
    'billing.amount': '金额',
    'billing.dueDate': '截止日期',
    'billing.status': '状态',
    'billing.actions': '操作',
    'billing.deposit': '定金',
    'billing.regular': '常规',
    'billing.frequency': '频率',
    'billing.progress': '进度',
    'billing.nextBilling': '下次账单',
    'billing.paymentByStages': '按阶段付款',
    'billing.installmentProgress': '分期进度',
    'billing.stagesProgress': '阶段进度',
    'billing.amountPaid': '已付金额',
    'billing.dueBalance': '待付余额',
    'billing.submitPayment': '提交付款',
    'billing.itemsPaid': '已付项',
    'billing.stagesPaid': '已完成阶段',
    'billing.noInstallmentInvoices': '未找到分期付款发票',
    'billing.noStagesInvoices': '未找到按阶段付款发票',
    'billing.manageStagePayments': '管理按阶段付款发票',
    'billing.paypalEmail': 'PayPal邮箱',
    'billing.businessName': '企业名称',
    'billing.notConfigured': '未配置',
    'billing.zelleEmail': 'Zelle邮箱',
    'billing.memo': '备注',
    'billing.includeInvoiceNumber': '请包含您的发票号',
    'billing.bankName': '银行名称',
    'billing.accountName': '账户名称',
    'billing.accountNumber': '账户号码',
    'billing.routingNumber': '路由号码',
    'billing.makePayableTo': '收款人',
    'billing.instruction': '说明',
    'billing.internalMatchmaker': '内部红娘：付款说明由管理员管理。',
    'billing.createClientInvoice': '创建客户发票',
    'billing.chooseClient': '选择客户...',
    'billing.choosePlan': '选择方案...',
    'billing.customSelectPlan': '自定义/选择方案',
    'billing.weekly': '每周',
    'billing.biWeekly': '每两周',
    'billing.monthly': '每月',
    'billing.quarterly': '每季度',
    'billing.yearly': '每年',
    'billing.unlimitedCycles': '无限周期（直到客户取消订阅）',
    'billing.includeServiceFee': '在付款计算中包含服务费',
    'billing.regularPayment': '常规付款',
    'billing.billingCycles': '账单周期',
    'billing.commissionPerPayment': '每次付款佣金',
    'billing.pointsPerPayment': '每次付款积分',
    'billing.editPaymentInstructions': '编辑付款说明',
    'billing.checking': '支票账户',
    'billing.savings': '储蓄账户',
    'billing.createInvoice': '创建发票',
    'billing.totalRevenue': '总收入',
    'billing.activeSubscriptions': '活跃订阅',
    'billing.subscriptions': '订阅',
    'billing.membershipPlans': '会员计划',
    'billing.paymentInstructions': '付款说明',
    'billing.plansManagedByAdmin': '内部红娘的计划由管理员管理',
    'billing.searchByClient': '按客户或发票号搜索...',
    'billing.whiteLabel': '白标',
    'billing.clientSubscriptions': '客户订阅',
    'billing.paySecurelyOnline': '安全在线支付',
    'billing.instantBankTransfer': '即时银行转账',
    'billing.directBankDeposit': '直接银行存款',
    'billing.mailPayment': '邮寄支付',
    'billing.bankTransferWire': '银行转账/电汇',
    'billing.check': '支票',
    'billing.paymentDetailsManagedByAdmin': '付款详情由管理员管理（只读）',
    'billing.configureOwnPayment': '为客户发票配置您自己的付款详情',
    'billing.editPaymentInfo': '编辑付款信息',
    'billing.contactAdminPayment': '请联系管理员更新付款详情。',
    'billing.addPlan': '添加计划',
    'billing.editPlan': '编辑计划',
    'billing.pendingAdminApproval': '等待管理员审批',
    'billing.selectClient': '选择客户',
    'billing.membershipPlan': '会员计划',
    'billing.description': '描述',
    'billing.recurringInvoice': '循环发票',
    'billing.createAsSubscription': '创建为订阅',
    'billing.addSubscription': '添加订阅',
    'billing.manageRecurringMemberships': '管理可自定义账单的循环会员',
    'billing.createSubscription': '创建订阅',
    'billing.editSubscription': '编辑订阅',
    'billing.updateSubscription': '更新订阅',
    'billing.planName': '计划名称',
    'billing.customPlanName': '自定义计划名称...',
    'billing.paymentSchedule': '付款计划',
    'billing.firstPaymentDeposit': '首付/押金 ($)',
    'billing.optionalPlaceholder': '0.00（可选）',
    'billing.customizableInitialPayment': '可自定义的首次付款',
    'billing.regularPaymentRequired': '常规付款 ($) *',
    'billing.billingFrequency': '账单频率',
    'billing.startDate': '开始日期',
    'billing.trialPeriodDays': '试用期（天）',
    'billing.totalBillingCycles': '总账单周期',
    'billing.serviceFeePaypal': '服务费（PayPal）',
    'billing.feePercentage': '费用百分比 (%)',
    'billing.fixedFee': '固定费用 ($)',
    'billing.commissionPoints': '佣金和积分',
    'billing.commissionRate': '佣金比例 (%)',
    'billing.pointsPerPaymentLabel': '每次付款积分',
    'billing.paymentSummary': '付款摘要',
    'billing.firstPaymentDepositSummary': '首付（押金）：',
    'billing.regularPaymentSummary': '常规付款：',
    'billing.billingCyclesSummary': '账单周期：',
    'billing.commissionPerPaymentSummary': '每次付款佣金：',
    'billing.pointsPerPaymentSummary': '每次付款积分：',
    'billing.notes': '备注',
    'billing.optionalNotes': '可选备注...',
    'billing.serviceFeeNote': '佣金和积分按扣除服务费前的金额计算',
    'billing.unlimited': '无限',
    'billing.fee': '费用',
    'billing.net': '净额',
    'billing.trialEnds': '试用结束：',
    'billing.pause': '暂停',
    'billing.noInvoices': '暂无发票',

    // Affiliate
    'affiliate.title': '联盟计划',
    'affiliate.subtitle': '通过推荐新客户会员赚取佣金',
    'affiliate.referrals': '推荐',
    'affiliate.earnings': '收益',
    'affiliate.commission': '佣金',
    'affiliate.progressToNext': '升级进度',
    'affiliate.currentTier': '当前等级',
    'affiliate.totalEarnings': '总收益',
    'affiliate.totalPoints': '总积分',
    'affiliate.totalEarned': '已赚取',
    'affiliate.pendingBalance': '余额',
    'affiliate.requestPayout': '申请提现',
    'affiliate.points': '积分',
    'affiliate.overview': '概览',
    'affiliate.commissions': '佣金',
    'affiliate.payouts': '提现',
    'affiliate.tiers': '等级',
    'affiliate.loadingData': '加载联盟数据中...',
    'affiliate.small': '小 (150px)',
    'affiliate.medium': '中 (200px)',
    'affiliate.large': '大 (256px)',
    'affiliate.extraLarge': '超大 (350px)',
    'affiliate.huge': '巨大 (500px)',
    'affiliate.inactive': '未激活',
    'affiliate.noTeamMembers': '暂无团队成员。开始推荐红娘吧！',
    'affiliate.noTeamMembersYet': '暂无团队成员',
    'affiliate.name': '姓名',
    'affiliate.email': '邮箱',
    'affiliate.level': '等级',
    'affiliate.status': '状态',
    'affiliate.joined': '加入时间',
    'affiliate.tier': '层级',
    'affiliate.noReferrals': '暂无推荐。分享您的链接开始吧！',
    'affiliate.date': '日期',
    'affiliate.description': '描述',
    'affiliate.amount': '金额',
    'affiliate.noCommissions': '暂无佣金。开始推荐来赚取吧！',
    'affiliate.payoutHistory': '提现历史',
    'affiliate.net': '净额',
    'affiliate.method': '方式',
    'affiliate.noPayouts': '暂无提现',
    'affiliate.progressTo': '升级到',
    'affiliate.earnPoints': '赚取总积分',
    'affiliate.maintainActive': '保持活跃状态',
    'affiliate.noTierInfo': '暂无等级信息',
    'affiliate.searchTeamMembers': '搜索团队成员...',
    'affiliate.notEligible': '不符合提现条件',
    'affiliate.addPaymentInfo': '添加付款信息',
    'affiliate.uploadW9': '上传 W-9',
    'affiliate.availableBalance': '可用余额',
    'affiliate.payoutAmount': '提现金额 ($)',
    'affiliate.minAmount': '最低',
    'affiliate.maxAmount': '最高',
    'affiliate.paymentMethod': '付款方式',
    'affiliate.bankTransfer': '银行转账',
    'affiliate.teamTotalPoints': '团队总积分',
    'affiliate.totalTeamMembers': '团队总人数',
    'affiliate.directReferrals': '直接推荐',
    'affiliate.indirectReferrals': '间接推荐',
    'affiliate.teamOrgChart': '团队组织架构',
    'affiliate.expand': '展开',
    'affiliate.collapse': '收起',
    'affiliate.expandAll': '全部展开',
    'affiliate.collapseAll': '全部收起',
    'affiliate.dragToPan': '拖拽平移',
    'affiliate.zoomIn': '放大',
    'affiliate.zoomOut': '缩小',
    'affiliate.resetView': '重置视图',
    'affiliate.underperformingMembers': '表现不佳的团队成员',
    'affiliate.underperformingDesc': '积分低于200或处于非活跃状态的团队成员需要关注',
    'affiliate.noTeamMemberYet': '您还没有团队成员！需要更加努力！',
    'affiliate.allPerformingWell': '做得好！所有团队成员表现良好。',
    'affiliate.allTeamMembers': '所有团队成员',
    'affiliate.you': '您',
    'affiliate.yourReferrals': '您的推荐',
    'affiliate.commissionHistory': '佣金历史',
    'affiliate.paymentInformation': '付款信息',
    'affiliate.completePaymentDetails': '填写付款信息和 W-9 表格以请求提现',
    'affiliate.paymentInfoLabel': '付款信息',
    'affiliate.complete': '已完成',
    'affiliate.required': '必填',
    'affiliate.w9Form': 'W-9 表格',
    'affiliate.verified': '已验证',
    'affiliate.pendingVerification': '等待验证',
    'affiliate.check': '支票',
    'affiliate.bankName': '银行名称',
    'affiliate.accountHolderName': '账户持有人姓名',
    'affiliate.accountNumber': '账户号码',
    'affiliate.routingNumber': '路由号码',
    'affiliate.accountType': '账户类型',
    'affiliate.checking': '支票账户',
    'affiliate.savings': '储蓄账户',
    'affiliate.paypalEmail': 'PayPal 邮箱',
    'affiliate.zelleEmail': 'Zelle 邮箱',
    'affiliate.zellePhone': 'Zelle 电话（可选）',
    'affiliate.checkPayableTo': '支票收款人',
    'affiliate.mailingAddress': '邮寄地址',
    'affiliate.taxInformation': '税务信息',
    'affiliate.legalName': '法定姓名',
    'affiliate.businessName': '公司名称（可选）',
    'affiliate.addressLine1': '地址行 1',
    'affiliate.addressLine2': '地址行 2',
    'affiliate.city': '城市',
    'affiliate.state': '州',
    'affiliate.zipCode': '邮政编码',
    'affiliate.savePaymentInfo': '保存付款信息',
    'affiliate.w9Description': '上传已填写的 W-9 表格以接收提现。IRS 要求年付款超过 $600 时提交。',
    'affiliate.w9Uploaded': 'W-9 已上传',
    'affiliate.replaceW9': '替换 W-9',
    'affiliate.uploadW9Form': '上传 W-9 表格',
    'affiliate.pdfOnly': '仅限 PDF 格式，最大 5MB',
    'affiliate.selectFile': '选择文件',
    'affiliate.downloadW9': '从 IRS 网站下载空白 W-9 表格',
    'affiliate.minPayout': '最低提现',
    'affiliate.processingFee': '手续费',
    'affiliate.payoutSchedule': '提现周期',
    'affiliate.monthly': '每月',
    'affiliate.affiliateTiers': '联盟等级',
    'affiliate.tiersDescription': '通过升级等级解锁更高佣金率和专属奖励',
    'affiliate.yourCurrentTier': '您当前的等级',
    'affiliate.morePointsNeeded': '还需 {count} 积分',
    'affiliate.maxTierReached': '恭喜！您已达到最高等级！',
    'affiliate.current': '当前',
    'affiliate.pointsToUnlock': '积分解锁',
    'affiliate.commissionRate': '佣金率',
    'affiliate.requirements': '要求',
    'affiliate.benefits': '权益',
    'affiliate.totalPointsLabel': '总积分',
    'affiliate.commissionOnReferrals': '所有推荐佣金',
    'affiliate.bonusPointsOnEarnings': '额外积分奖励',
    'affiliate.quarterlyBonus': '季度奖金',

    // Admin
    'admin.dashboard': '管理仪表板',
    'admin.dashboardSubtitle': '系统概览和统计',
    'admin.users': '用户',
    'admin.usersSubtitle': '管理用户账户和权限',
    'admin.profiles': '所有档案',
    'admin.profilesSubtitle': '查看和管理所有系统档案',
    'admin.matches': '匹配',
    'admin.matchesSubtitle': '查看和管理所有匹配',
    'admin.coaches': '教练',
    'admin.coachesSubtitle': '管理教练分配',
    'admin.plans': '方案',
    'admin.plansSubtitle': '管理会员方案',
    'admin.customFields': '自定义字段',
    'admin.customFieldsSubtitle': '配置档案自定义字段',
    'admin.roles': '角色',
    'admin.rolesSubtitle': '管理用户角色和权限',
    'admin.settings': '平台设置',
    'admin.settingsSubtitle': '配置平台设置',
    'admin.logs': '活动日志',
    'admin.logsSubtitle': '查看系统活动日志',
    'admin.trends': '趋势',
    'admin.trendsSubtitle': '查看分析和趋势',
    'admin.affiliates': '联盟成员',
    'admin.affiliatesSubtitle': '管理联盟计划',
    'admin.billing': '账单管理',
    'admin.billingSubtitle': '查看所有账单和发票',
    'admin.datingSchedules': '约会安排',
    'admin.datingSchedulesSubtitle': '管理所有约会安排',
    'admin.translations': '翻译管理',
    'admin.translationsSubtitle': '管理语言翻译',
    'admin.totalUsers': '用户总数',
    'admin.totalProfiles': '档案总数',
    'admin.totalMatches': '匹配总数',
    'admin.totalRevenue': '总收入',
    'admin.recentActivity': '近期活动',
    'admin.addUser': '添加用户',
    'admin.addPlan': '添加方案',
    'admin.addField': '添加字段',

    // Admin Dashboard
    'adminDashboard.activeMatchmakers': '活跃红娘',
    'adminDashboard.matchesMade': '已匹配数',
    'adminDashboard.monthlyRevenue': '月收入',
    'adminDashboard.activeSubscriptions': '活跃订阅',
    'adminDashboard.revenueOverview': '收入概览',
    'adminDashboard.userGrowth': '用户增长',
    'adminDashboard.last7Days': '最近7天',
    'adminDashboard.last30Days': '最近30天',
    'adminDashboard.last90Days': '最近90天',
    'adminDashboard.revenueChartPlaceholder': '收入图表将在此显示',
    'adminDashboard.userGrowthChartPlaceholder': '用户增长图表将在此显示',
    'adminDashboard.quickActions': '快捷操作',
    'adminDashboard.manageUsers': '管理用户',
    'adminDashboard.reviewProfiles': '审核档案',
    'adminDashboard.managePlans': '管理方案',
    'adminDashboard.affiliates': '联盟成员',
    'adminDashboard.viewAll': '查看全部',
    'adminDashboard.noRecentActivity': '暂无近期活动',
    'adminDashboard.systemAlerts': '系统提醒',
    'adminDashboard.profilesPendingReview': '个档案待审核',
    'adminDashboard.newProfilesRequireApproval': '新档案需要管理员批准',
    'adminDashboard.systemUpdateAvailable': '系统更新可用',
    'adminDashboard.versionReadyToInstall': '版本2.1.0已准备好安装',
    'adminDashboard.planDistribution': '方案分布',
    'adminDashboard.planDistributionChartPlaceholder': '方案分布图表将在此显示',

    // Activity Logs
    'logs.title': '系统日志',
    'logs.subtitle': '监控系统活动和审计追踪',
    'logs.exportCsv': '导出CSV',
    'logs.clearLogs': '清除日志',
    'logs.clearConfirm': '这将备份所有日志为CSV文件，然后清除它们。是否继续？',
    'logs.clearedSuccess': '日志已备份并清除成功',
    'logs.clearFailed': '清除日志失败',
    'logs.totalLogs': '日志总数',
    'logs.info': '信息',
    'logs.warnings': '警告',
    'logs.errors': '错误',
    'logs.critical': '严重',
    'logs.logType': '日志类型',
    'logs.allTypes': '所有类型',
    'logs.level': '级别',
    'logs.allLevels': '所有级别',
    'logs.fromDate': '开始日期',
    'logs.toDate': '结束日期',
    'logs.searchLogs': '搜索日志...',
    'logs.noLogsFound': '未找到符合筛选条件的日志',
    'logs.user': '用户',
    'logs.action': '操作',
    'logs.details': '详情',
    'logs.ipAddress': 'IP地址',
    'logs.timestamp': '时间戳',

    // Platform Settings
    'platformSettings.title': '平台设置',
    'platformSettings.subtitle': '为内部红娘配置支付说明和集成（由FindU提供）',
    'platformSettings.loadingSettings': '加载设置中...',
    'platformSettings.payment': '支付',
    'platformSettings.paypal': 'PayPal',
    'platformSettings.plans': '方案',
    'platformSettings.agreement': '协议',
    'platformSettings.twilio': 'Twilio',
    'platformSettings.smtp': 'SMTP',
    'platformSettings.companyRep': '公司代表',
    'platformSettings.paymentInstructionsForClients': '客户支付说明',
    'platformSettings.configurePaymentDetails': '配置向内部红娘客户显示的支付详情（由FindU提供）',
    'platformSettings.saveChanges': '保存更改',
    'platformSettings.enabled': '已启用',
    'platformSettings.secureOnlinePayments': '安全在线支付',
    'platformSettings.paypalEmail': 'PayPal邮箱',
    'platformSettings.businessName': '企业名称',
    'platformSettings.instructions': '说明',
    'platformSettings.zelle': 'Zelle',
    'platformSettings.instantBankTransfer': '即时银行转账',
    'platformSettings.zelleEmailPhone': 'Zelle邮箱/电话',
    'platformSettings.memoInstruction': '备注说明',
    'platformSettings.bankTransferWire': '银行转账/电汇',
    'platformSettings.directBankDeposit': '直接银行存款',
    'platformSettings.bankName': '银行名称',
    'platformSettings.accountName': '账户名称',
    'platformSettings.accountNumber': '账户号码',
    'platformSettings.routingNumber': '路由号码',
    'platformSettings.swiftCode': 'SWIFT代码（可选）',
    'platformSettings.reference': '参考',
    'platformSettings.check': '支票',
    'platformSettings.mailPayment': '邮寄支付',
    'platformSettings.makePayableTo': '收款人',
    // PayPal集成
    'platformSettings.paypalIntegration': 'PayPal 集成',
    'platformSettings.paypalDesc': '为内部红娘配置 PayPal API 的一次性支付和订阅功能',
    'platformSettings.testConnection': '测试连接',
    'platformSettings.saveSettings': '保存设置',
    'platformSettings.saving': '保存中...',
    'platformSettings.environment': '环境',
    'platformSettings.sandboxTesting': '🧪 沙盒（测试）',
    'platformSettings.sandboxDesc': '用于开发和测试',
    'platformSettings.liveProduction': '🚀 正式环境（生产）',
    'platformSettings.liveDesc': '真实交易',
    'platformSettings.apiCredentials': 'API 凭证',
    'platformSettings.clientId': '客户端 ID',
    'platformSettings.enterClientId': '输入 PayPal 客户端 ID',
    'platformSettings.clientSecret': '客户端密钥',
    'platformSettings.enterClientSecret': '输入 PayPal 客户端密钥',
    'platformSettings.webhookId': 'Webhook ID（可选）',
    'platformSettings.webhookIdPlaceholder': '用于自动支付通知',
    'platformSettings.currency': '货币',
    'platformSettings.paymentTypes': '支付类型',
    'platformSettings.oneTimePayments': '一次性支付',
    'platformSettings.oneTimeDesc': '接受发票和一次性会员购买的单次付款',
    'platformSettings.subscriptionPayments': '订阅支付',
    'platformSettings.subscriptionDesc': '为客户会员订阅启用定期计费',
    'platformSettings.redirectUrls': '重定向 URL',
    'platformSettings.returnUrl': '返回 URL（成功）',
    'platformSettings.cancelUrl': '取消 URL',
    'platformSettings.paypalNote': 'PayPal 集成仅适用于内部红娘（由 FindU 提供）。外部（白标）红娘需自行配置支付处理。',
    'platformSettings.note': '注意：',
    // 客户会员方案
    'platformSettings.clientMembershipPlans': '客户会员方案',
    'platformSettings.clientPlansDesc': '管理提供给客户的会员方案（由内部红娘使用）',
    'platformSettings.addPlan': '添加方案',
    'platformSettings.totalPlans': '方案总数',
    'platformSettings.activePlans': '活跃方案',
    'platformSettings.totalSubscribers': '总订阅数',
    'platformSettings.monthlyRevenue': '月收入',
    'platformSettings.planName': '方案名称',
    'platformSettings.price': '价格',
    'platformSettings.duration': '时长',
    'platformSettings.subscribers': '订阅数',
    'platformSettings.status': '状态',
    'platformSettings.actions': '操作',
    'platformSettings.active': '活跃',
    'platformSettings.inactive': '未活跃',
    'platformSettings.deactivate': '停用',
    'platformSettings.activate': '启用',
    'platformSettings.editPlan': '编辑方案',
    'platformSettings.createPlan': '创建方案',
    'platformSettings.planNameRequired': '方案名称 *',
    'platformSettings.description': '描述',
    'platformSettings.priceRequired': '价格 ($) *',
    'platformSettings.durationRequired': '时长 *',
    'platformSettings.months': '月',
    'platformSettings.years': '年',
    'platformSettings.lifetime': '终身',
    'platformSettings.durationExample': '例如：4个月 = "$499/4x 月"，2年 = "$999/2x 年"',
    'platformSettings.affiliateNote': '红娘的佣金率和积分收益在联盟管理→等级中配置。当客户为此会员付费时，红娘将根据其联盟等级设置赚取佣金和积分。',
    'platformSettings.features': '功能特点',
    'platformSettings.addFeature': '添加功能特点...',
    'platformSettings.planNameZh': '方案名称（中文）',
    'platformSettings.descriptionZh': '描述（中文）',
    'platformSettings.featuresZh': '功能特点（中文）',
    'platformSettings.addFeatureZh': '添加中文功能特点...',
    'platformSettings.planActiveLabel': '方案已激活，可供购买',
    'platformSettings.cancel': '取消',
    'platformSettings.updatePlan': '更新方案',
    // Twilio 短信设置
    'platformSettings.twilioSmsSettings': 'Twilio 短信设置',
    'platformSettings.twilioDesc': '配置 Twilio 用于短信通知和验证码验证',
    'platformSettings.smsService': '短信服务',
    'platformSettings.smsEnabled': '短信通知已启用',
    'platformSettings.smsDisabled': '短信通知已禁用',
    'platformSettings.disabled': '已禁用',
    'platformSettings.accountSid': 'Account SID',
    'platformSettings.accountSidHelp': '在 Twilio 控制台仪表板中查找',
    'platformSettings.authToken': 'Auth Token',
    'platformSettings.authTokenHelp': '请保密！',
    'platformSettings.phoneConfig': '电话配置',
    'platformSettings.twilioPhone': 'Twilio 电话号码',
    'platformSettings.twilioPhoneHelp': '用于发送短信的 Twilio 电话号码',
    'platformSettings.messagingServiceSid': '消息服务 SID（可选）',
    'platformSettings.messagingServiceHelp': '用于大量消息发送',
    'platformSettings.verifyService': '验证服务（OTP）',
    'platformSettings.verifyServiceSid': '验证服务 SID',
    'platformSettings.verifyServiceHelp': '用于验证码验证。在 Twilio 控制台创建验证服务。',
    'platformSettings.twilioGettingStarted': 'Twilio 入门指南：',
    'platformSettings.twilioStep1': '注册',
    'platformSettings.twilioStep2': '从控制台仪表板获取 Account SID 和 Auth Token',
    'platformSettings.twilioStep3': '购买用于发送短信的电话号码',
    'platformSettings.twilioStep4': '创建用于验证码功能的验证服务',
    // 邮件 / SMTP 设置
    'platformSettings.emailSmtpSettings': '邮件 / SMTP 设置',
    'platformSettings.emailSmtpDesc': '配置通知和通信的邮件设置',
    'platformSettings.sendTestEmail': '发送测试邮件',
    'platformSettings.testEmailDesc': '编写并发送测试邮件，以验证您的邮件配置是否正常工作。',
    'platformSettings.testEmailTo': '收件人邮箱',
    'platformSettings.testEmailToPlaceholder': 'recipient@example.com',
    'platformSettings.testEmailSubject': '主题',
    'platformSettings.testEmailSubjectPlaceholder': '输入邮件主题',
    'platformSettings.testEmailBody': '邮件正文（HTML）',
    'platformSettings.testEmailBodyPlaceholder': '输入邮件正文内容...',
    'platformSettings.htmlSupported': '支持HTML',
    'platformSettings.testEmailPreview': '预览',
    'platformSettings.sendingTestEmail': '发送中...',
    'platformSettings.testEmailSuccess': '测试邮件发送成功！请检查收件箱。',
    'platformSettings.testEmailFailed': '邮件测试失败，请检查您的设置。',
    'platformSettings.enterRecipientEmail': '请输入收件人邮箱地址',
    'platformSettings.emailService': '邮件服务',
    'platformSettings.emailEnabled': '邮件通知已启用',
    'platformSettings.emailDisabled': '邮件通知已禁用',
    'platformSettings.emailProvider': '邮件提供商',
    'platformSettings.smtpServer': 'SMTP 服务器',
    'platformSettings.smtpConfig': 'SMTP 配置',
    'platformSettings.smtpHost': 'SMTP 主机',
    'platformSettings.port': '端口',
    'platformSettings.secure': '安全',
    'platformSettings.username': '用户名',
    'platformSettings.passwordAppPassword': '密码 / 应用密码',
    'platformSettings.sendgridConfig': 'SendGrid 配置',
    'platformSettings.apiKey': 'API 密钥',
    'platformSettings.sendgridHelp': '从 SendGrid 仪表板→设置→API 密钥获取',
    'platformSettings.mailgunConfig': 'Mailgun 配置',
    'platformSettings.domain': '域名',
    'platformSettings.awsSesConfig': 'AWS SES 配置',
    'platformSettings.accessKeyId': '访问密钥 ID',
    'platformSettings.secretAccessKey': '秘密访问密钥',
    'platformSettings.region': '区域',
    'platformSettings.senderConfig': '发件人配置',
    'platformSettings.fromEmail': '发件邮箱',
    'platformSettings.fromName': '发件人名称',
    'platformSettings.replyToEmail': '回复邮箱（可选）',
    'platformSettings.replyToHelp': '回复将发送到此邮箱',
    'platformSettings.gmailImportant': '对于 Gmail SMTP，您需要使用应用密码而不是普通密码。请在 Google 账户设置中启用两步验证并生成应用密码。',
    'platformSettings.important': '重要：',
    // 合伙人协议
    'platformSettings.membershipServiceAgreement': '合伙人协议',
    'platformSettings.agreementDesc': '配置红娘/合伙人注册时必须签署的合作协议模板',
    'platformSettings.enableAgreement': '启用合伙人协议',
    'platformSettings.enableAgreementHelp': '启用后，红娘/合伙人注册时必须签署协议',
    'platformSettings.agreementTitle': '协议标题',
    'platformSettings.agreementContent': '协议内容',
    'platformSettings.placeholdersHelp': '使用以下占位符：',
    'platformSettings.requireSignature': '要求电子签名',
    'platformSettings.requireSignatureHelp': '客户必须提供电子签名以接受协议',
    'platformSettings.lastUpdated': '最后更新：',
    // 自动化与 Webhook
    'automation.selectWebhook': '选择 Webhook',
    'automation.selectWebhookDesc': '为此自动化任务选择要触发的 Webhook',
    'automation.webhookConfiguration': 'Webhook 配置',
    'automation.subscriptionPaymentSuccess': '订阅支付成功',
    'automation.subscriptionPaymentFailed': '订阅支付失败',
    'automation.subscriptionRenewal': '订阅续费',
    'automation.subscriptionCancelled': '订阅已取消',
    'automation.paymentMethodUpdated': '支付方式已更新',
    'automation.refundProcessed': '退款已处理',
    // 公司代表
    'platformSettings.companyRepresentative': '公司代表',
    'platformSettings.companyRepDesc': '指定一名内部红娘代表公司进行外部沟通',
    'platformSettings.selectCompanyRep': '选择公司代表',
    'platformSettings.noRepresentative': '-- 无代表 --',
    'platformSettings.companyRepHelp': '只有管理员和内部红娘可以被指定为公司代表',
    'platformSettings.companyRepPermissions': '公司代表权限：',
    'platformSettings.companyRepPerm1': '代表所有内部红娘接收和发送消息给外部红娘',
    'platformSettings.companyRepPerm2': '为所有内部红娘的档案创建和管理匹配',
    'platformSettings.companyRepPerm3': '批准或拒绝外部红娘的匹配请求',
    'platformSettings.companyRepPerm4': '在连接目录中代表公司（FINDU）',
    // Email Templates
    'platformSettings.emailTemplates': '模板',
    'platformSettings.emailTemplatesTitle': '邮件模板',
    'platformSettings.emailTemplatesDesc': '自定义中英文自动邮件模板。使用 {{client_name}} 等变量实现动态内容。',
    'platformSettings.templatesSaved': '邮件模板保存成功',
    'platformSettings.templatesSaveFailed': '保存邮件模板失败',
    'platformSettings.tplPaymentConfirmation': '付款确认',
    'platformSettings.tplPaymentConfirmationDesc': '收到付款后发送给客户',
    'platformSettings.tplReferralRegistration': '推荐注册完成',
    'platformSettings.tplReferralRegistrationDesc': '推荐人注册后发送给双方',
    'platformSettings.tplBillingReminder': '账单提醒',
    'platformSettings.tplBillingReminderDesc': '会员付款到期时发送给客户',
    'platformSettings.tplTeamActive': '团队活跃 - 获得积分',
    'platformSettings.tplTeamActiveDesc': '发送给活跃并获得积分的团队成员',
    'platformSettings.tplTeamInactive': '团队不活跃 - 需要更多活动',
    'platformSettings.tplTeamInactiveDesc': '发送给不活跃的团队成员以鼓励参与',
    'platformSettings.availableVariables': '可用变量',
    'platformSettings.templateInfoTitle': '模板工作原理：',
    'platformSettings.templateInfoDesc': '模板在触发事件发生时自动发送。{{client_name}} 等变量会被替换为实际数据。请确保在SMTP标签页中配置了SMTP设置。点击任意变量可复制。',

    // Social Login Settings
    'platformSettings.socialLogin': '社交登录',
    'platformSettings.socialLoginSettings': '社交登录设置',
    'platformSettings.socialLoginSubtitle': '配置客户端门户的社交登录提供商',
    'platformSettings.googleLogin': 'Google 登录',
    'platformSettings.appleLogin': 'Apple 登录',
    'platformSettings.wechatLogin': '微信登录',
    'platformSettings.socialEnabled': '已启用',
    'platformSettings.socialDisabled': '已禁用',
    'platformSettings.socialClientId': '客户端 ID',
    'platformSettings.enterGoogleClientId': '输入 Google 客户端 ID',
    'platformSettings.socialClientSecret': '客户端密钥',
    'platformSettings.enterGoogleClientSecret': '输入 Google 客户端密钥',
    'platformSettings.appleServiceId': '客户端 ID（服务 ID）',
    'platformSettings.enterAppleServiceId': '输入 Apple 服务 ID',
    'platformSettings.teamId': '团队 ID',
    'platformSettings.enterAppleTeamId': '输入 Apple 团队 ID',
    'platformSettings.keyId': '密钥 ID',
    'platformSettings.enterAppleKeyId': '输入 Apple 密钥 ID',
    'platformSettings.appId': '应用 ID',
    'platformSettings.enterWechatAppId': '输入微信应用 ID',
    'platformSettings.appSecret': '应用密钥',
    'platformSettings.enterWechatAppSecret': '输入微信应用密钥',
    'platformSettings.socialLoginNote': '在此启用提供商将在客户登录页面显示其登录按钮。请确保在启用之前已在各提供商的开发者控制台中配置了有效的凭据。',

    // 合作协议
    'platformSettings.partnerAgreement': '合作协议',
    'platformSettings.partnerAgreementDesc': '配置红娘注册时显示的合作协议内容。',
    'platformSettings.enablePartnerAgreement': '启用合作协议',
    'platformSettings.enablePartnerAgreementHelp': '启用后，自定义合作协议内容将在红娘注册签署合作协议模态框中显示。',
    'platformSettings.partnerAgreementTitle': '协议标题',
    'platformSettings.partnerAgreementContent': '协议内容',
    'platformSettings.partnerAgreementContentHelp': '输入完整的合作协议文本。支持HTML格式。此内容将在红娘注册时的"签署合作协议"模态框中显示。',
    'platformSettings.partnerAgreementSaved': '合作协议保存成功',
    'platformSettings.partnerAgreementSaveFailed': '保存合作协议失败',

    // Company Branding & Super Admin Settings
    'platformSettings.companyBranding': '公司品牌',
    'platformSettings.superAdmin': '超级管理员',

    // Roles
    'roles.title': '角色',
    'roles.subtitle': '管理用户角色和权限',
    'roles.createRole': '创建角色',
    'roles.editRole': '编辑角色',
    'roles.system': '系统',
    'roles.custom': '自定义',
    'roles.users': '用户',
    'roles.permissions': '权限',
    'roles.fullSystemAccess': '拥有所有权限的完整系统访问',
    'roles.administrativeAccess': '有限系统设置的管理访问',
    'roles.canManageMatchmakers': '可以管理红娘和查看报告',
    'roles.standardMatchmaker': '标准红娘代理账户',
    'roles.supportStaff': '具有读取权限和有限操作的支持人员',
    'roles.matchmakingCoach': '具有分配客户管理的婚恋教练',
    'roles.clientProfile': '客户档案 - 可查看匹配、消息和自己的档案',

    // Affiliate Page
    'affiliate.title': '联盟计划',
    'affiliate.subtitle': '通过推荐新客户和红娘赚取佣金',
    'affiliate.requestPayout': '申请提现',
    'affiliate.totalPoints': '总积分',
    'affiliate.earned': '已赚取',
    'affiliate.pendingBalance': '余额',
    'affiliate.commission': '佣金',
    'affiliate.yourReferralLinks': '您的推荐链接',
    'affiliate.shareLinksToEarn': '分享这些链接，每次注册都能赚取佣金',
    'affiliate.clientSignupLink': '客户注册链接',
    'affiliate.matchmakerSignupLink': '红娘注册链接',
    'affiliate.copy': '复制',
    'affiliate.qrCode': '二维码',
    'affiliate.shareOnTwitter': '分享到Twitter',
    'affiliate.shareOnLinkedIn': '分享到LinkedIn',
    'affiliate.progressToNextTier': '升级到下一等级',
    'affiliate.commissionAtNextTier': '下一等级佣金',
    'affiliate.points': '积分',
    'affiliate.morePointsTo': '更多积分升级到',
    'affiliate.overview': '概览',
    'affiliate.myTeam': '我的团队',
    'affiliate.referrals': '推荐',
    'affiliate.commissions': '佣金',
    'affiliate.paymentInfo': '支付信息',
    'affiliate.payouts': '提现',
    'affiliate.tiersRewards': '等级与奖励',
    'affiliate.tax1099': '1099表格',
    'affiliate.tax1099Documents': '1099税务文件',
    'affiliate.tax1099Description': '下载您的1099税表以报告您的联盟收入。',
    'affiliate.form1099': '1099表格',
    'affiliate.taxDocumentFor': '税务文件用于',
    'affiliate.uploadedOn': '上传日期',
    'affiliate.no1099Documents': '暂无1099文件',
    'affiliate.no1099DocumentsDesc': '管理员会在1099表格可用时上传供您下载。',
    'affiliate.documentNotAvailable': '文件不可用',
    'affiliate.downloadFailed': '下载文件失败',
    'affiliate.howItWorks': '如何运作',
    'affiliate.shareYourLink': '分享您的链接',
    'affiliate.shareYourLinkDesc': '与潜在红娘分享您的专属推荐链接',
    'affiliate.theySignUp': '他们注册',
    'affiliate.theySignUpDesc': '当他们使用您的链接注册时，他们成为您的推荐',
    'affiliate.earnCommissions': '赚取佣金',
    'affiliate.earnCommissionsDesc': '赚取他们订阅付款的40.00%',
    'affiliate.getPaid': '获得付款',
    'affiliate.getPaidDesc': '当余额达到$50时申请提现',

    // Admin Plans
    'plans.title': '订阅方案',
    'plans.subtitle': '管理定价方案和功能',
    'plans.createPlan': '创建方案',
    'plans.editPlan': '编辑方案',
    'plans.activePlans': '活跃方案',
    'plans.totalSubscribers': '总订阅者',
    'plans.monthlyRevenue': '月收入',
    'plans.avgRevenueUser': '平均用户收入',
    'plans.planCards': '方案卡片',
    'plans.comparisonTable': '对比表格',
    'plans.month': '月',
    'plans.year': '年',
    'plans.save': '节省',
    'plans.profiles': '档案数',
    'plans.matchesPerMonth': '每月匹配数',
    'plans.subscribers': '订阅者',
    'plans.disable': '禁用',
    'plans.enable': '启用',
    'plans.mostPopular': '最受欢迎',
    'plans.moreFeatures': '更多功能',
    'plans.unlimited': '无限',
    'plans.unlimitedMessaging': '无限消息',
    'plans.prioritySupport': '优先支持',
    'plans.advancedAnalytics': '高级分析',
    'plans.customBranding': '自定义品牌',
    'plans.apiAccess': 'API访问',
    'plans.bulkImportExport': '批量导入/导出',
    'plans.teamCollaboration': '团队协作',
    'plans.videoConsultations': '视频咨询',
    'plans.dedicatedAccountManager': '专属客户经理',
    'plans.whiteLabelSolution': '白标解决方案',
    'plans.customIntegrations': '自定义集成',
    'plans.slaGuarantee': 'SLA保障',
    'plans.perfectForNew': '适合刚入门的新红娘',
    'plans.forEstablished': '适合发展业务的成熟红娘',
    'plans.forAgencies': '适合拥有多位红娘的婚介机构',
    'plans.customSolutions': '适合大型组织的定制解决方案',
    'plans.maxProfiles': '最大档案数',
    'plans.maxMatchesMonth': '每月最大匹配数',
    'plans.planName': '方案名称',
    'plans.description': '描述',
    'plans.monthlyPrice': '月价格',
    'plans.yearlyPrice': '年价格',
    'plans.features': '功能特性',
    'plans.onePerLine': '每行一个功能',
    'plans.deletePlan': '删除方案',
    'plans.confirmDelete': '确定要删除此方案吗？',
    'plans.trialDays': '免费试用天数',
    'plans.billingCycle': '计费周期',
    'plans.slug': 'URL别名',
    'plans.price': '价格',
    'plans.monthly': '月付',
    'plans.quarterly': '季付',
    'plans.yearly': '年付',
    'plans.popular': '热门',
    'plans.freeTrialDays': '天免费试用',
    'plans.hasActiveSubscribers': '无法删除有活跃订阅者的方案',
    'plans.active': '活跃',
    'plans.inactive': '未启用',

    // Register - External Matchmaker
    'register.choosePlan': '选择您的订阅方案',
    'register.choosePlanSubtitle': '选择一个方案来开始您的红娘业务',
    'register.mostPopular': '最受欢迎',
    'register.selectPlan': '请选择一个方案以继续',
    'register.perMonth': '/月',
    'register.perQuarter': '/季',
    'register.perYear': '/年',

    // Matchmaker Registration
    'matchmakerReg.title': '红娘注册',
    'matchmakerReg.externalSubtitle': '外部红娘',
    'matchmakerReg.internalSubtitle': '红娘',
    'matchmakerReg.firstName': '您的名字是？',
    'matchmakerReg.lastName': '您的姓氏是？',
    'matchmakerReg.email': '您的邮箱是？',
    'matchmakerReg.password': '创建密码',
    'matchmakerReg.confirmPassword': '确认密码',
    'matchmakerReg.companyContact': '公司与联系方式',
    'matchmakerReg.companyContactSubtitle': '告诉我们您的业务信息',
    'matchmakerReg.companyName': '公司名称（可选）',
    'matchmakerReg.phone': '电话号码',
    'matchmakerReg.location': '您的位置在哪里？',
    'matchmakerReg.locationSubtitle': '您的业务所在地',
    'matchmakerReg.choosePlan': '选择您的订阅方案',
    'matchmakerReg.choosePlanSubtitle': '选择一个方案开始使用',
    'matchmakerReg.payment': '完成支付',
    'matchmakerReg.paymentSubtitle': '选择支付方式并完成支付',
    'matchmakerReg.planSummary': '方案概要',
    'matchmakerReg.amountDue': '应付金额',
    'matchmakerReg.paymentMethods': '支付方式',
    'matchmakerReg.pendingNote': '您的订阅将在管理员确认支付后激活。请使用以上方式之一完成支付。',
    'matchmakerReg.trialNote': '您的免费试用立即开始',
    'matchmakerReg.trialPaymentNote': '试用期结束后需要支付费用。',
    'matchmakerReg.terms': '审阅并接受条款',
    'matchmakerReg.alreadyHaveAccount': '已有账户？',
    'matchmakerReg.noPlans': '目前没有可用的方案，请稍后再试。',
    'matchmakerReg.noPaymentMethods': '尚未配置支付方式，请联系管理员。',
    'matchmakerReg.loadingPlans': '加载方案中...',
    'matchmakerReg.loadingPayment': '加载支付方式中...',

    // Messages additional
    'messages.sentImage': '发送了图片',
    'messages.sentProfile': '分享了档案',
    'messages.online': '在线',
    'messages.offline': '离线',
    'messages.typeMessage': '输入消息...',
    'messages.yearsOld': '岁',

    // Notifications additional
    'notifications.loadingNotifications': '加载通知中...',
    'notifications.noNotificationsYet': '暂无通知',
    'notifications.notificationsAppearHere': '收到通知后，将显示在此处。',
    'notifications.selected': '已选择',
    'notifications.selectAll': '全选',
    'notifications.deselectAll': '取消全选',
    'notifications.justNow': '刚刚',
    'notifications.minAgo': '分钟前',
    'notifications.minsAgo': '分钟前',
    'notifications.hourAgo': '小时前',
    'notifications.hoursAgo': '小时前',
    'notifications.dayAgo': '天前',
    'notifications.daysAgo': '天前',

    // Admin Coaches
    'coaches.title': '婚恋教练',
    'coaches.subtitle': '管理教练并将其分配给客户档案',
    'coaches.addCoach': '添加教练',
    'coaches.editCoach': '编辑教练',
    'coaches.assignProfile': '分配档案',
    'coaches.coaches': '教练',
    'coaches.assignments': '分配',
    'coaches.searchCoaches': '搜索教练...',
    'coaches.searchAssignments': '搜索分配...',
    'coaches.coach': '教练',
    'coaches.specialization': '专业领域',
    'coaches.experience': '经验',
    'coaches.clients': '客户',
    'coaches.permissions': '权限',
    'coaches.noCoaches': '暂无教练。点击"添加教练"创建。',
    'coaches.noCoachesMatch': '没有匹配的教练。',
    'coaches.matches': '匹配',
    'coaches.dating': '约会',
    'coaches.onLeave': '休假中',
    'coaches.selectUser': '选择用户',
    'coaches.experienceYears': '经验（年）',
    'coaches.certification': '认证',
    'coaches.bio': '简介',
    'coaches.maxClients': '最大客户数',
    'coaches.hourlyRate': '时薪（$）',
    'coaches.canManageMatches': '可管理匹配',
    'coaches.canManageDating': '可管理约会安排',
    'coaches.updateCoach': '更新教练',
    'coaches.years': '年',
    'coaches.selectUserPlaceholder': '选择用户...',
    'coaches.allUsersAreCoaches': '所有用户都已是教练',
    'coaches.specializationPlaceholder': '例如：高端婚恋、国际客户',
    'coaches.certificationPlaceholder': '例如：认证婚恋顾问、情感教练',
    'coaches.bioPlaceholder': '简要描述教练的背景和专长...',
    'coaches.canManageMatchesDesc': '创建、查看和管理客户匹配',
    'coaches.canManageDatingDesc': '为客户安排和管理约会',

    // Admin Custom Fields
    'customFields.title': '自定义字段',
    'customFields.subtitle': '管理客户档案的自定义字段和分类',
    'customFields.addCustomField': '添加自定义字段',
    'customFields.editCustomField': '编辑自定义字段',
    'customFields.editField': '编辑字段',
    'customFields.customFields': '自定义字段',
    'customFields.categories': '分类',
    'customFields.searchFields': '搜索字段...',
    'customFields.allTypes': '所有类型',
    'customFields.allCategories': '所有分类',
    'customFields.order': '顺序',
    'customFields.fieldLabel': '字段标签',
    'customFields.fieldName': '字段名称',
    'customFields.category': '分类',
    'customFields.required': '必填',
    'customFields.radioSingle': '单选',
    'customFields.dropdownSelect': '下拉选择',
    'customFields.checkboxMultiple': '多选',
    'customFields.number': '数字',
    'customFields.datePicker': '日期选择',
    'customFields.background': '背景',
    'customFields.lifestyle': '生活方式',
    'customFields.personalDetails': '个人信息',
    'customFields.noFieldsYet': '暂无自定义字段。点击"添加自定义字段"创建。',
    'customFields.noFieldsMatch': '没有匹配的字段。',
    'customFields.fieldLabelPlaceholder': '例如：兴趣爱好',
    'customFields.fieldNameDb': '字段名称（数据库用）',
    'customFields.fieldNamePlaceholder': '例如：favorite_hobbies',
    'customFields.autoGeneratedFromLabel': '根据标签自动生成。用于内部引用。',
    'customFields.fieldType': '字段类型',
    'customFields.options': '选项',
    'customFields.enterOption': '输入选项后点击添加',
    'customFields.noOptionsYet': '暂未添加选项。请在上方添加至少一个选项。',
    'customFields.placeholderText': '占位符文本',
    'customFields.helpText': '帮助文本',
    'customFields.defaultValue': '默认值',
    'customFields.noDefaultValue': '无默认值',
    'customFields.editCategory': '编辑分类',
    'customFields.addCategory': '添加分类',
    'customFields.categoryLabel': '分类标签',
    'customFields.description': '描述',
    'customFields.color': '颜色',
    'customFields.displayOrder': '显示顺序',
    'customFields.genderVisibility': '性别可见性',
    'customFields.genderVisibilityDesc': '选择此字段显示在哪个性别的个人资料上',
    'customFields.male': '男性',
    'customFields.female': '女性',
    'customFields.gender': '性别',
    'customFields.genderWarning': '警告：如果两者都未勾选，此字段将不会显示在任何个人资料上。',
    'customFields.english': 'English',
    'customFields.chinese': '中文',
    'customFields.requiredField': '必填字段',
    'customFields.visibilityOptions': '可见性选项',
    'customFields.showOnPublicProfile': '在公开资料中显示',
    'customFields.showOnPublicProfileDesc': '在共享的公开资料中显示此字段',
    'customFields.showOnDirectory': '在目录中显示',
    'customFields.showOnDirectoryDesc': '在目录个人资料页面中显示此字段',
    'customFields.preview': '预览：',
    'customFields.updateField': '更新字段',
    'customFields.createField': '创建字段',
    'customFields.updateCategory': '更新分类',
    'customFields.createCategory': '创建分类',
    'customFields.defaultValueHint': '创建新档案时，此值将自动填充。',
    'customFields.noCategoriesYet': '暂无分类。点击"添加分类"创建。',
    'customFields.fieldsCount': '个字段',
    'customFields.confirmDeleteField': '确定要删除此自定义字段吗？这也将删除该字段的所有已保存值。',
    'customFields.confirmDeleteCategory': '确定要删除此分类吗？',
    'customFields.cannotDeleteCategory': '无法删除包含 {count} 个字段的分类。请先移动或删除字段。',
    'customFields.enterOptionEn': 'Enter option in English...',
    'customFields.enterOptionZh': '输入中文选项...',
    'customFields.enterDefaultValue': '输入默认值...',
    'customFields.enterDefaultText': '输入默认文本...',
    'customFields.enterDefaultNumber': '输入默认数字...',
    'customFields.enterText': '输入文本...',
    'customFields.enterNumber': '输入数字',
    'customFields.selectOption': '请选择...',
    'customFields.fieldLabelPreview': '字段标签',
    'customFields.min': '最小值',
    'customFields.max': '最大值',
    'customFields.to': '至',
    'customFields.textInput': '文本输入',
    'customFields.textArea': '文本区域',
    'customFields.rangMinMax': '范围（最小-最大）',
    'customFields.time24h': '时间（24小时制）',
    'customFields.placeholderEnExample': 'e.g., Enter your hobbies...',
    'customFields.placeholderZhExample': '例如：输入您的爱好...',
    'customFields.helpTextEnExample': 'e.g., List your favorite activities...',
    'customFields.helpTextZhExample': '例如：列出您最喜欢的活动...',
    'customFields.categoryLabelEnExample': 'e.g., Lifestyle Preferences',
    'customFields.categoryLabelZhExample': '例如：生活方式偏好',
    'customFields.categoryDescEnExample': '可选的分类描述...',
    'customFields.categoryDescZhExample': '可选的分类描述...',
    'common.add': '添加',
    'common.type': '类型',
    'common.status': '状态',
    'common.actions': '操作',
    'common.active': '活跃',
    'common.inactive': '未激活',
    'common.optional': '可选',

    // Admin Dating Schedules
    'adminDating.title': '约会安排管理',
    'adminDating.subtitle': '管理所有红娘客户的约会安排',
    'adminDating.createSchedule': '创建安排',
    'adminDating.searchSchedules': '搜索安排...',
    'adminDating.allMatchmakers': '所有红娘',
    'adminDating.dateNewest': '日期 ↓ (最新)',
    'adminDating.dateOldest': '日期 ↑ (最旧)',
    'adminDating.dateTime': '日期和时间',
    'adminDating.clients': '客户',
    'adminDating.location': '地点',
    'adminDating.matchmaker': '红娘',
    'adminDating.requestBy': '请求来自：',
    'adminDating.virtual': '线上',
    'adminDating.clientAZ': '客户 A-Z',
    'adminDating.clientZA': '客户 Z-A',
    'adminDating.locationAZ': '地点 A-Z',
    'adminDating.locationZA': '地点 Z-A',
    'adminDating.matchmakerAZ': '红娘 A-Z',
    'adminDating.matchmakerZA': '红娘 Z-A',
    'adminDating.statusAZ': '状态 A-Z',
    'adminDating.statusZA': '状态 Z-A',
    'adminDating.loadingSchedules': '正在加载安排...',
    'adminDating.noSchedules': '未找到安排',
    'adminDating.viewDetails': '查看详情',
    'adminDating.editSchedule': '编辑安排',
    'adminDating.client1': '客户1',
    'adminDating.client2': '客户2',
    'adminDating.selectMatchmaker': '选择红娘...',
    'adminDating.clientLabel': '客户',
    'adminDating.selectClient': '选择客户...',
    'adminDating.selectMatchmakerFirst': '请先选择红娘',
    'adminDating.titleLabel': '标题',
    'adminDating.titlePlaceholder': '例如：咖啡约会',
    'adminDating.date': '日期',
    'adminDating.time': '时间',
    'adminDating.duration': '时长',
    'adminDating.locationType': '地点类型',
    'adminDating.venueName': '场地名称',
    'adminDating.address': '地址',
    'adminDating.fullAddress': '详细地址',
    'adminDating.notifications': '通知方式',
    'adminDating.email': '邮件',
    'adminDating.sms': '短信',
    'adminDating.emailAndSms': '邮件和短信',
    'adminDating.none': '无',
    'adminDating.notes': '备注',
    'adminDating.internalNotes': '内部备注...',
    'adminDating.updateStatus': '更新状态',
    'adminDating.selectNewStatus': '为以下安排选择新状态：',
    'adminDating.datingSchedule': '约会安排',
    'adminDating.statusPending': '待定',
    'adminDating.statusConfirmed': '已确认',
    'adminDating.statusAccepted': '已接受',
    'adminDating.statusRejected': '已拒绝',
    'adminDating.statusMeeting': '会面中',
    'adminDating.statusChatting': '聊天中',
    'adminDating.statusInRelationship': '恋爱中',
    'adminDating.statusEngaged': '已订婚',
    'adminDating.statusCompleted': '已完成',
    'adminDating.statusCancelled': '已取消',
    'adminDating.statusNoShow': '未出席',
    'adminDating.locRestaurant': '🍽️ 餐厅',
    'adminDating.locCafe': '☕ 咖啡厅',
    'adminDating.locBar': '🍸 酒吧',
    'adminDating.locPark': '🌳 公园',
    'adminDating.locVirtual': '💻 线上',
    'adminDating.locOther': '📍 其他',

    // Admin Billing
    'adminBilling.title': '账单管理',
    'adminBilling.subtitle': '管理客户和红娘的会员订阅',
    'adminBilling.createPayment': '创建付款',
    'adminBilling.createClientMembership': '创建客户会员',
    'adminBilling.createMatchmakerMembership': '创建红娘会员',
    'adminBilling.clientMemberships': '客户会员',
    'adminBilling.matchmakerMemberships': '红娘会员',
    'adminBilling.clientMembershipSingle': '客户会员',
    'adminBilling.matchmakerMembershipSingle': '红娘会员',
    'adminBilling.searchClients': '搜索客户...',
    'adminBilling.searchMatchmakers': '搜索红娘...',
    'adminBilling.allStatus': '所有状态',
    'adminBilling.allMatchmakers': '所有红娘',
    'adminBilling.client': '客户',
    'adminBilling.matchmaker': '红娘',
    'adminBilling.invoiceNumber': '发票编号',
    'adminBilling.amount': '金额',
    'adminBilling.commission': '佣金',
    'adminBilling.dueDate': '到期日',
    'adminBilling.plan': '方案',
    'adminBilling.price': '价格',
    'adminBilling.billing': '账单周期',
    'adminBilling.startDate': '开始日期',
    'adminBilling.endDate': '结束日期',
    'adminBilling.noClientMemberships': '未找到客户会员',
    'adminBilling.pending': '待处理',
    'adminBilling.awaitingApproval': '待审批',
    'adminBilling.paid': '已支付',
    'adminBilling.overdue': '已逾期',
    'adminBilling.cancelled': '已取消',
    'adminBilling.pastDue': '逾期未付',
    'adminBilling.trialing': '试用中',
    'adminBilling.clientProfile': '客户档案',
    'adminBilling.selectClient': '选择客户...',
    'adminBilling.selectPlan': '选择方案...',
    'adminBilling.selectMatchmaker': '选择红娘...',
    'adminBilling.notes': '备注',
    'adminBilling.pendingByDefault': '状态：待处理',
    'adminBilling.pendingByDefaultDesc': '此会员将以"待处理"状态创建。佣金和积分将在状态更改为完成（已批准/已支付）后才会处理。',
    'adminBilling.commissionInfo': '佣金信息',
    'adminBilling.commissionInfoDesc': '当此会员被批准/完成时，管理该客户的红娘将根据其联盟等级费率自动获得佣金和积分。',
    'adminBilling.membership': '会员',
    'adminBilling.membershipDetails': '会员详情',
    'adminBilling.rejectMembership': '拒绝会员',
    'adminBilling.rejectMembershipDesc': '请提供拒绝此会员付款的原因。红娘将能够看到此备注。',
    'adminBilling.rejectionReason': '拒绝原因',
    'adminBilling.rejectionReasonPlaceholder': '输入拒绝原因...',
    'adminBilling.rejectionReasonRequired': '请提供拒绝原因',
    'adminBilling.confirmReject': '确认拒绝',
    'adminBilling.membershipRejected': '会员已成功拒绝',
    'adminBilling.rejectionNotes': '拒绝备注',
    'adminBilling.commissionEarned': '已赚佣金',
    'adminBilling.paymentMethod': '支付方式',
    'adminBilling.paymentMethodRequired': '请选择支付方式',
    'adminBilling.proofOfPayment': '支付证明',
    'adminBilling.selectPaymentMethod': '选择支付方式',
    'adminBilling.selectSubscriptionAccount': '选择订阅账户',
    'adminBilling.subscriptionAccountRequired': '此支付方式需要选择订阅账户',
    'adminBilling.paypal': 'PayPal',
    'adminBilling.square': 'Square',
    'adminBilling.zelle': 'Zelle',
    'adminBilling.cash': '现金',
    'adminBilling.bankTransfer': '银行转账',
    'common.loading': '加载中...',
    'common.loadMore': '加载更多',
    'common.showingOf': '显示 {showing} / {total}',
    'common.delete': '删除',
    'common.save': '保存',
    'common.update': '更新',
    'common.create': '创建',

    // Share Modal
    'share.title': '分享档案',
    'share.share': '分享',
    'share.shareProfile': '分享档案',
    'share.shareDesc': '将此公开链接分享给潜在匹配对象：',
    'share.openLink': '在新标签页打开',
    'share.cardFallback': '已打开公开档案 - 您可以截图作为档案卡片',
    'share.sharePublicProfile': '分享公开档案',
    'share.profilePrivate': '档案为私密状态',
    'share.profilePrivateDesc': '此档案目前设置为私密。在编辑档案中启用"公开档案"以允许他人通过分享链接查看。',
    'share.sharePublicLink': '与潜在匹配对象分享此公开链接：',
    'share.linkNotPublicYet': '公开链接（仅在档案设置为公开后才会生效）：',
    'share.copyLink': '复制链接',
    'share.openInNewTab': '在新标签页打开',
    'share.linkCopied': '公开链接已复制到剪贴板',
    'share.profileCard': '档案卡片',
    'share.profileCardDesc': '生成精美的档案卡片，包含二维码链接到公开档案。',
    'share.generateCard': '生成档案卡片',
    'share.previewCard': '预览档案卡片',
    'share.cardLanguage': '卡片语言',
    'share.generateAndDownload': '生成并下载',
    'share.previewNote': '档案卡片预览。选择语言后点击下方按钮生成。',
    'share.generating': '生成中...',
    'share.cardGenerated': '档案卡片下载成功！',
    'share.cardError': '生成档案卡片失败',
    'share.noPublicLink': '无可用的公开链接',
    'share.noPublicLinkYet': '此档案尚无公开链接。在编辑档案中启用"公开档案"以生成链接。',
    'share.scanToView': '扫码查看档案',
    'share.yearsOld': '岁',
    'share.poweredBy': 'FINDU 红娘服务',

    // Database Synchronization
    'dbSync.title': '数据库同步',
    'dbSync.subtitle': '连接、映射并同步远程和本地数据库之间的数据',
    'dbSync.remoteConnection': '远程数据库连接',
    'dbSync.remoteConnectionDesc': '连接到用于同步的源数据库',
    'dbSync.connected': '已连接',
    'dbSync.host': '主机',
    'dbSync.port': '端口',
    'dbSync.database': '数据库',
    'dbSync.username': '用户名',
    'dbSync.password': '密码',
    'dbSync.testing': '测试中...',
    'dbSync.testConnection': '测试连接',
    'dbSync.remoteDatabase': '远程数据库',
    'dbSync.localDatabase': '本地数据库',
    'dbSync.searchTables': '搜索表...',
    'dbSync.connectFirst': '请先连接远程数据库',
    'dbSync.noTablesFound': '未找到表',
    'dbSync.dragToConnect': '拖动字段以建立连接',
    'dbSync.fieldMapping': '字段映射',
    'dbSync.schedules': '计划任务',
    'dbSync.logs': '同步日志',
    'dbSync.savedMappings': '已保存的映射',
    'dbSync.saveMapping': '保存当前映射',
    'dbSync.noMappings': '暂无已保存的映射',
    'dbSync.fieldsMapped': '个字段已映射',
    'dbSync.lastSync': '上次同步',
    'dbSync.runNow': '立即运行',
    'dbSync.schedule': '计划',
    'dbSync.syncSchedules': '同步计划',
    'dbSync.schedulesDesc': '使用计划任务自动执行数据库同步',
    'dbSync.addSchedule': '添加计划',
    'dbSync.editSchedule': '编辑计划',
    'dbSync.noSchedules': '暂无计划任务',
    'dbSync.noSchedulesDesc': '创建计划以自动化数据库同步',
    'dbSync.nextRun': '下次运行',
    'dbSync.scheduleName': '计划名称',
    'dbSync.selectMapping': '选择映射',
    'dbSync.selectMappingPlaceholder': '选择一个映射...',
    'dbSync.frequency': '频率',
    'dbSync.hourly': '每小时',
    'dbSync.daily': '每天',
    'dbSync.weekly': '每周',
    'dbSync.monthly': '每月',
    'dbSync.time': '时间',
    'dbSync.dayOfWeek': '星期几',
    'dbSync.dayOfMonth': '每月几号',
    'dbSync.syncMode': '同步模式',
    'dbSync.incremental': '增量同步（仅新增/更改）',
    'dbSync.full': '全量同步（所有记录）',
    'dbSync.conflictResolution': '冲突解决',
    'dbSync.remoteWins': '远程优先',
    'dbSync.localWins': '本地优先',
    'dbSync.newestWins': '最新优先',
    'dbSync.skipConflicts': '跳过冲突',
    'dbSync.enableSchedule': '启用计划',
    'dbSync.enableScheduleDesc': '启用后计划将自动运行',
    'dbSync.syncLogs': '同步日志',
    'dbSync.logsDesc': '查看所有同步操作的历史记录',
    'dbSync.noLogs': '暂无同步记录',
    'dbSync.noLogsDesc': '运行同步后，日志将显示在这里',
    'dbSync.status': '状态',
    'dbSync.mapping': '映射',
    'dbSync.records': '记录',
    'dbSync.duration': '耗时',
    'dbSync.timestamp': '时间戳',
    'dbSync.recordsSynced': '已同步',
    'dbSync.failed': '失败',
    'dbSync.tableMode': '表模式',
    'dbSync.queryMode': '查询模式',
    'dbSync.customQuery': '自定义SQL查询',
    'dbSync.testQuery': '测试查询 (预览)',
    'dbSync.queryPreview': '查询结果预览',
    'dbSync.runQueryFirst': '运行查询以查看可用列',
    'dbSync.onlySelectAllowed': '仅允许SELECT查询',
    'dbSync.queryError': '查询错误',
    'dbSync.mergeRules': '字段合并规则',
    'dbSync.addMergeRule': '添加合并规则',
    'dbSync.editMergeRule': '编辑合并规则',
    'dbSync.mergeName': '合并名称',
    'dbSync.sourceFields': '源字段',
    'dbSync.outputFormat': '输出格式',
    'dbSync.jsonTemplate': 'JSON模板',
    'dbSync.delimiterFormat': '分隔符格式',
    'dbSync.delimiter': '分隔符',
    'dbSync.fieldOrder': '字段顺序',
    'dbSync.targetLocalField': '目标本地字段',
    'dbSync.mergeDescription': '将多个源字段合并为自定义JSON或分隔字符串',
    'dbSync.mergeRulesCount': '个合并规则',
    'dbSync.templateHelp': '使用 {{字段名}} 占位符替换字段值',
    'dbSync.queryResultColumns': '查询结果列',

    // Commission Sync
    'dbSync.commissionSync': '佣金同步',
    'dbSync.commissionSyncDesc': '按月/年导入和管理佣金数据。将推荐人与联盟级别绑定并跟踪佣金百分比。',
    'dbSync.selectMonth': '选择月份和年份：',
    'dbSync.tableEditor': '表编辑器',
    'dbSync.uploadExcel': '上传Excel',
    'dbSync.preview': '预览',
    'dbSync.commissionTableInfo': '拖动列和行可以重新排列，单击单元格进行编辑。将每一行绑定到客户资料和联盟级别。',
    'dbSync.client': '客户',
    'dbSync.affiliate': '联盟/级别',
    'dbSync.matchmaker': '介绍人',
    'dbSync.recruiter': '招募人员',
    'dbSync.interviewer': '面试官',
    'dbSync.amount': '金额',
    'dbSync.payment': '支付',
    'dbSync.noCommissionData': '没有佣金数据。上传Excel或手动创建条目。',
    'dbSync.selectClient': '选择客户...',
    'dbSync.selectTier': '选择级别...',
    'dbSync.selectMatchmaker': '选择介绍人...',
    'dbSync.selectRecruiter': '选择招募人员...',
    'dbSync.selectInterviewer': '选择面试官...',
    'dbSync.selectPayment': '选择支付...',
    'dbSync.addRow': '添加行',
    'dbSync.dragDropExcel': '将Excel/CSV文件放在此处',
    'dbSync.supportedFormats': '支持格式：.xlsx、.xls、.csv',
    'dbSync.previewInfo': '要导入的佣金数据预览。在确认前检查绑定和金额。',
    'dbSync.syncCommissions': '同步佣金数据',
    'dbSync.clearAll': '清除全部',
    'dbSync.rowsCount': '总行数',
    'dbSync.totalAmount': '总金额',
    'dbSync.dragToRearrange': '拖动以重新排列：列和行可拖动。单击单元格进行编辑。将每一行绑定到联盟级别和推荐人。',
    'dbSync.browseFiles': '浏览文件',
    'dbSync.syncing': '同步中...',
    'dbSync.tier1': '一级（5%）',
    'dbSync.tier2': '二级（10%）',
    'dbSync.tier3': '三级（15%）',
    'dbSync.tier4': '四级（20%）',

    // Admin Affiliates
    'adminAffiliates.title': '联盟成员管理',
    'adminAffiliates.subtitle': '管理联盟成员、佣金、提现和设置',
    'adminAffiliates.autoUpgrade': '自动升级',
    'adminAffiliates.payoutSettings': '提现设置',
    'adminAffiliates.affiliatesLabel': '联盟成员',
    'adminAffiliates.totalReferrals': '总推荐数',
    'adminAffiliates.commission': '佣金',
    'adminAffiliates.pendingPayouts': '待提现',
    'adminAffiliates.teamRankings': '团队排名',
    'adminAffiliates.tabAffiliates': '联盟成员',
    'adminAffiliates.tabTeams': '团队',
    'adminAffiliates.tabCommissions': '佣金',
    'adminAffiliates.tabPayouts': '提现',
    'adminAffiliates.tabTiers': '等级',
    'adminAffiliates.tabProcessingFee': '手续费',
    'adminAffiliates.tabW9Review': 'W9 审核',
    'adminAffiliates.searchAffiliates': '搜索联盟成员...',
    'adminAffiliates.allTiers': '所有等级',
    'adminAffiliates.suspended': '已暂停',
    'adminAffiliates.filter': '筛选',
    'adminAffiliates.affiliate': '联盟成员',
    'adminAffiliates.code': '推荐码',
    'adminAffiliates.referredBy': '推荐人',
    'adminAffiliates.tier': '等级',
    'adminAffiliates.points': '积分',
    'adminAffiliates.addPoints': '添加积分',
    'adminAffiliates.clientQRCode': '客户二维码',
    'adminAffiliates.matchmakerQRCode': '红娘二维码',
    'adminAffiliates.manage1099': '管理1099',
    'adminAffiliates.manage1099Documents': '管理1099文件',
    'adminAffiliates.uploadNew1099': '上传新的1099文件',
    'adminAffiliates.taxYear': '税务年度',
    'adminAffiliates.selectFile': '选择文件',
    'adminAffiliates.allowedFileTypes': '允许格式：PDF、JPG、PNG（最大10MB）',
    'adminAffiliates.uploadDocument': '上传文件',
    'adminAffiliates.existingDocuments': '现有文件',
    'adminAffiliates.noDocumentsYet': '暂无上传的文件',
    'adminAffiliates.noDescription': '无描述',
    'adminAffiliates.uploadedAt': '上传时间',
    'adminAffiliates.documentUploaded': '文件上传成功',
    'adminAffiliates.documentDeleted': '文件删除成功',
    'adminAffiliates.uploadFailed': '上传文件失败',
    'adminAffiliates.deleteFailed': '删除文件失败',
    'adminAffiliates.confirmDelete1099': '确定要删除这个1099文件吗？',
    'adminAffiliates.invalidFileType': '无效的文件类型。请选择PDF、JPG或PNG。',
    'adminAffiliates.fileTooLarge': '文件太大。最大允许10MB。',
    'adminAffiliates.pleaseSelectFile': '请选择要上传的文件',
    'adminAffiliates.pleaseEnterTaxYear': '请输入税务年度',
    'adminAffiliates.descriptionPlaceholder': '此文件的可选描述',
    'adminAffiliates.affiliate': '联盟成员',
    'adminAffiliates.suspend': '暂停',
    'adminAffiliates.activate': '激活',
    'adminAffiliates.noAffiliates': '暂无联盟成员',
    'adminAffiliates.teamRankingsByPoints': '按总积分排名的团队',
    'adminAffiliates.teamRankingsDesc': '多级红娘团队按团队总积分排名',
    'adminAffiliates.rank': '排名',
    'adminAffiliates.teamLeader': '团队领导',
    'adminAffiliates.leaderTier': '领导等级',
    'adminAffiliates.teamSize': '团队规模',
    'adminAffiliates.totalTeamPoints': '团队总积分',
    'adminAffiliates.directIndirect': '直接/间接',
    'adminAffiliates.avgPointsMember': '人均积分',
    'adminAffiliates.pending': '待处理',
    'adminAffiliates.approved': '已批准',
    'adminAffiliates.paid': '已支付',
    'adminAffiliates.rejected': '已拒绝',
    'adminAffiliates.processing': '处理中',
    'adminAffiliates.completed': '已完成',
    'adminAffiliates.addCommission': '添加佣金',
    'adminAffiliates.searchCommissions': '搜索佣金...',
    'adminAffiliates.date': '日期',
    'adminAffiliates.amount': '金额',
    'adminAffiliates.description': '描述',
    'adminAffiliates.noCommissions': '暂无佣金',
    'adminAffiliates.createPayout': '创建提现',
    'adminAffiliates.searchPayouts': '搜索提现...',
    'adminAffiliates.net': '净额',
    'adminAffiliates.method': '方式',
    'adminAffiliates.institutionalBonusSettings': '机构合作伙伴奖金设置',
    'adminAffiliates.addTier': '添加等级',
    'adminAffiliates.level': '级别',
    'adminAffiliates.name': '名称',
    'adminAffiliates.commPercent': '佣金%',
    'adminAffiliates.coachingFee': '辅导费',
    'adminAffiliates.coachingFeePercent': '辅导费（%）',
    'adminAffiliates.coachingFeeDesc': '按等级收取的平台服务费百分比',
    'adminAffiliates.pointsPercent': '积分%',
    'adminAffiliates.ptsRequired': '所需积分',
    'adminAffiliates.recruiter': '上级奖金 %',
    'adminAffiliates.requiredRecruits': '所需招募',
    'adminAffiliates.indPartners': '个人',
    'adminAffiliates.senPartners': '高级',
    'adminAffiliates.instPartners': '机构',
    'adminAffiliates.none': '无',
    'adminAffiliates.addDeductPoints': '添加/扣除积分',
    'adminAffiliates.pointsNegativeToDeduct': '积分（负数为扣除）',
    'adminAffiliates.editCommission': '编辑佣金',
    'adminAffiliates.amountDollar': '金额（$）',
    'adminAffiliates.editPayout': '编辑提现',
    'adminAffiliates.bankTransfer': '银行转账',
    'adminAffiliates.check': '支票',
    'adminAffiliates.crypto': '加密货币',
    'adminAffiliates.transactionReference': '交易参考号',
    'adminAffiliates.transactionRefPlaceholder': '例如：TXN123456',
    'adminAffiliates.paymentProof': '付款凭证（截图）',
    'adminAffiliates.clickOrDragUpload': '点击或拖拽上传截图',
    'adminAffiliates.pngJpgUpTo5MB': 'PNG、JPG，最大5MB',
    'adminAffiliates.editTier': '编辑等级',
    'adminAffiliates.pointsRequired': '所需积分',
    'adminAffiliates.clientMembershipEarnings': '客户会员付款收益',
    'adminAffiliates.clientMembershipEarningsDesc': '当红娘的客户支付会员费用时，红娘获得这些百分比',
    'adminAffiliates.commissionRatePercent': '佣金比例（%）',
    'adminAffiliates.cashCommissionEarned': '现金佣金收益',
    'adminAffiliates.pointsEarningPercent': '积分收益（%）',
    'adminAffiliates.pointsEarnedExample': '积分收益（例如：$1000的5% = 50积分）',
    'adminAffiliates.recruiterBonusPercent': '上级奖金（%）',
    'adminAffiliates.recruiterBonusDesc': '当被推荐合作伙伴的客户支付会员费用时，支付给上级的百分比',
    'adminAffiliates.requiredDirectRecruits': '所需直接招募',
    'adminAffiliates.requiredDirectRecruitsDesc': '此等级必须直接招募的最低合作伙伴数量才能获得资格',
    'adminAffiliates.individualPartners': '个人合作伙伴',
    'adminAffiliates.seniorPartners': '高级合作伙伴',
    'adminAffiliates.institutionalPartners': '机构合作伙伴',
    // Institutional Bonus Chain Modal
    'adminAffiliates.institutionalBonusChainTitle': '机构合作伙伴奖金链',
    'adminAffiliates.howItWorks': '运作方式',
    'adminAffiliates.institutionalBonusDesc': '当客户支付会员费用时，系统会沿推荐链向上无限搜索。链中找到的每个机构合作伙伴会根据其在链中的层级获得相应的奖金。',
    'adminAffiliates.bonusPercentagesByLevel': '按层级的奖金百分比',
    'adminAffiliates.level1st': '第1级',
    'adminAffiliates.level2nd': '第2级',
    'adminAffiliates.level3rd': '第3级',
    'adminAffiliates.firstInstitutionalFound': '链中找到的第一个机构合作伙伴',
    'adminAffiliates.secondInstitutionalFound': '链中找到的第二个机构合作伙伴',
    'adminAffiliates.thirdInstitutionalFound': '链中找到的第三个机构合作伙伴',
    'adminAffiliates.percentOfMembership': '% 客户支付会员费的比例',
    'adminAffiliates.example': '示例',
    'adminAffiliates.institutionalBonusExample': '如果客户支付$1000的会员费，且推荐链中有3个机构合作伙伴：',
    'adminAffiliates.institutionalPartnerReceives': '机构合作伙伴获得',
    'adminAffiliates.institutionalBonusSaved': '机构合作伙伴奖金设置已保存',
    'adminAffiliates.failedToSaveSettings': '保存设置失败',
    'adminAffiliates.saveSettings': '保存设置',
    // Level Difference Bonus
    'adminAffiliates.levelDiffBonusSettings': '级差奖金',
    'adminAffiliates.levelDiffBonusTitle': '级差奖金佣金',
    'adminAffiliates.levelDiffBonusDesc': '当红娘（2级或以上）被更高等级的人推荐时，推荐人将获得等于两者佣金比例之差的奖金佣金。如果差值为零或负数，则不发放奖金。',
    'adminAffiliates.enableLevelDiffBonus': '启用级差奖金',
    'adminAffiliates.levelDiffBonusToggleDesc': '根据等级佣金比例差自动发放奖金佣金',
    'adminAffiliates.minLevelRequired': '销售红娘最低等级要求',
    'adminAffiliates.minLevelDesc': '销售红娘（成交那位）在本次付款前必须已经达到此等级或更高，才会触发级差奖金。如果是因为本次付款的积分刚好升上来，则要等到下一笔付款才生效。',
    'adminAffiliates.formula': '计算公式',
    'adminAffiliates.levelDiffFormula': '级差奖金 = 上级佣金% − 销售红娘佣金%。差 ≤ 0 则不发放。销售红娘需在付款前已达最低等级（按付款前的 tier 判断），奖金发给销售红娘的上级。',
    'adminAffiliates.addTiersToSeeExamples': '请添加至少2个不同佣金比例的等级以查看示例',
    'adminAffiliates.exampleLabel': '示例',
    'adminAffiliates.recruiterLabel': '推荐人',
    'adminAffiliates.youLabel': '你',
    'adminAffiliates.bonusToRecruiter': '奖金给推荐人',
    'adminAffiliates.noBonus': '无奖金',
    'adminAffiliates.levelDiffBonusSaved': '级差奖金设置已保存',
    'adminAffiliates.createAffiliate': '创建联盟成员',
    'adminAffiliates.selectMatchmaker': '选择红娘（内部）',
    'adminAffiliates.searchMatchmaker': '按姓名或邮箱搜索红娘...',
    'adminAffiliates.searchReferredBy': '按姓名或邮箱搜索推荐人...',
    'adminAffiliates.selectMatchmakerRequired': '请选择一位内部红娘',
    'adminAffiliates.defaultTier': '默认（入门级）',
    'adminAffiliates.affiliateCreated': '联盟成员创建成功',
    'adminAffiliates.emailPasswordRequired': '邮箱和密码为必填项',
    'adminAffiliates.pointsHistory': '积分历史',
    'adminAffiliates.totalEarnings': '总收益',
    'adminAffiliates.totalPoints': '总积分',
    'adminAffiliates.availableCommission': '可用佣金（$）',
    'adminAffiliates.adminNotes': '管理员备注',
    'adminAffiliates.addNotesPlaceholder': '添加关于此联盟成员的备注...',
    'adminAffiliates.editAffiliateDetails': '编辑联盟成员详情',
    'adminAffiliates.editSettings': '编辑设置',
    'adminAffiliates.recentReferrals': '最近推荐',
    'adminAffiliates.noReferralsYet': '暂无推荐',
    'adminAffiliates.affiliateUpdated': '联盟成员更新成功',
    'adminAffiliates.formType': '表格类型',
    'adminAffiliates.uploadedDate': '上传日期',

    // Admin Matches
    'adminMatches.title': '匹配管理',
    'adminMatches.subtitle': '查看和管理平台上的所有匹配',
    'adminMatches.refresh': '刷新',
    'adminMatches.searchProfiles': '按档案名称搜索...',
    'adminMatches.allMatchmakers': '所有红娘',
    'adminMatches.profileA': '档案A',
    'adminMatches.profileB': '档案B',
    'adminMatches.matchPercent': '匹配度',
    'adminMatches.noOccupation': '无职业',
    'adminMatches.matchDetails': '匹配详情',
    'adminMatches.na': '无',
    'adminMatches.unknownMatchmaker': '未知红娘',
    'adminMatches.match': '匹配',
    'adminMatches.matchmaker': '红娘',
    'adminMatches.unknown': '未知',
    'adminMatches.created': '创建时间',
    'adminMatches.updated': '更新时间',
    'adminMatches.notes': '备注',
    'adminMatches.changeStatus': '更改状态',
    'adminMatches.deleteMatch': '删除匹配',
    'adminMatches.deleteConfirmText': '确定要删除以下两人之间的匹配吗：',
    'adminMatches.cannotUndo': '此操作无法撤销。',
    'adminMatches.deleting': '删除中...',
    'adminMatches.statusPending': '待处理',
    'adminMatches.statusApproved': '已批准',
    'adminMatches.statusSucceeded': '已约见',
    'adminMatches.statusInRelationship': '已交往',
    'adminMatches.statusInContact': '接触中',
    'adminMatches.statusRejected': '已拒绝',
    'adminMatches.statusFailed': '已失败',
    'adminMatches.statusCancelled': '已取消',
    'adminMatches.statusCompleted': '已完成',

    // Admin Profiles
    'adminProfiles.title': '档案管理',
    'adminProfiles.subtitle': '审核和管理用户档案',
    'adminProfiles.gridView': '网格视图',
    'adminProfiles.tableView': '表格视图',
    'adminProfiles.daysUntilDue': '天到期',
    'adminProfiles.daysOverdue': '天逾期',
    'adminProfiles.dueToday': '今天到期',
    'adminProfiles.addProfile': '+ 添加档案',
    'adminProfiles.searchProfiles': '搜索档案...',
    'adminProfiles.allProfiles': '所有档案',
    'adminProfiles.selectAll': '全选',
    'adminProfiles.allGenders': '所有性别',
    'adminProfiles.allAges': '所有年龄',
    'adminProfiles.allMemberships': '所有会员等级',
    'adminProfiles.allMatchmakers': '所有红娘',
    'adminProfiles.allCoaches': '所有教练',
    'adminProfiles.viewDetails': '查看详情',
    'adminProfiles.active': '活跃',
    'adminProfiles.inactive': '未激活',
    'adminProfiles.created': '创建时间：',
    'adminProfiles.matchmaker': '红娘：',
    'adminProfiles.coach': '教练：',
    'adminProfiles.client': '客户',
    'adminProfiles.yrs': '岁',
    'adminProfiles.clearAndApprove': '清除并批准',
    'adminProfiles.editProfile': '编辑档案',
    'adminProfiles.createProfile': '创建档案',
    'adminProfiles.updateProfile': '更新档案',
    'adminProfiles.name': '姓名',
    'adminProfiles.firstNameLabel': '名',
    'adminProfiles.lastNameLabel': '姓',
    'adminProfiles.age': '年龄',
    'adminProfiles.gender': '性别',
    'adminProfiles.city': '城市',
    'adminProfiles.type': '类型',
    'adminProfiles.membership': '方案',
    'adminProfiles.male': '男',
    'adminProfiles.female': '女',
    'adminProfiles.other': '其他',
    'adminProfiles.membershipPlan': '会员计划',
    'adminProfiles.selectMembershipPlan': '选择会员计划',
    'adminProfiles.assignedMatchmaker': '分配的红娘',
    'adminProfiles.selectMatchmaker': '选择红娘',
    'adminProfiles.assignedCoach': '分配的教练',
    'adminProfiles.noCoachAssigned': '未分配教练',
    'common.select': '选择',
    'common.clear': '清除',
    'common.edit': '编辑',
    'common.yes': '是',
    'common.no': '否',
    'common.all': '全部',

    // Admin Profiles - Edit Modal
    'adminProfiles.coachAssignDesc': '分配教练来帮助管理此客户的匹配和约会安排',
    'adminProfiles.location': '位置',
    'adminProfiles.locationPlaceholder': '城市, 州/省',
    'adminProfiles.bio': '简介',
    'adminProfiles.photos': '照片',
    'adminProfiles.profilePhoto': '头像照片',
    'adminProfiles.changePhoto': '更换照片',
    'adminProfiles.uploadPhoto': '上传照片',
    'adminProfiles.squareImageMax': '方形图片，最大5MB',
    'adminProfiles.galleryPhotos': '相册照片',
    'adminProfiles.clickToAddPhotos': '点击 + 添加照片。最多10张，每张最大5MB。',
    'adminProfiles.profileSettings': '档案设置',
    'adminProfiles.publicProfile': '公开档案',
    'adminProfiles.anyoneCanView': '任何有链接的人都可以查看',
    'adminProfiles.onlyMatchmakerSee': '仅红娘可见',
    'adminProfiles.showOnDirectory': '在目录中显示',
    'adminProfiles.visibleInDirectory': '在档案目录中可见',
    'adminProfiles.hiddenFromDirectory': '从目录中隐藏',
    'adminProfiles.matchPricing': '匹配定价',
    'adminProfiles.setPriceForMatches': '为匹配设置价格',
    'adminProfiles.noPricingSet': '未设置定价',
    'adminProfiles.priceType': '价格类型',
    'adminProfiles.offer': '报价',
    'adminProfiles.request': '请求',
    'adminProfiles.amount': '金额（$）',
    'adminProfiles.generalInfo': '基本信息',
    'adminProfiles.personalDetailsLabel': '个人信息',
    'adminProfiles.preferences': '偏好设置',
    'adminProfiles.lifestyleLabel': '生活方式',
    'adminProfiles.backgroundLabel': '背景',
    'adminProfiles.otherLabel': '其他',
    'common.notAssigned': '未分配',
    'common.approve': '批准',
    'common.reject': '拒绝',

    // Admin Users
    'adminUsers.title': '用户',
    'adminUsers.subtitle': '管理系统中的所有用户',
    'adminUsers.export': '导出',
    'adminUsers.addUser': '添加用户',
    'adminUsers.searchUsers': '搜索用户...',
    'adminUsers.user': '用户',
    'adminUsers.role': '角色',
    'adminUsers.editUser': '编辑用户',
    'adminUsers.createUser': '创建用户',
    'adminUsers.updateUser': '更新用户',
    'adminUsers.clickToUpload': '点击上传（最大5MB）',
    'adminUsers.firstName': '名',
    'adminUsers.lastName': '姓',
    'adminUsers.newPassword': '新密码（留空保持不变）',
    'adminUsers.selectRole': '选择"profile"或"client"角色允许此用户以客户身份登录',
    'adminUsers.matchmakerType': '红娘类型',
    'adminUsers.internal': '内部',
    'adminUsers.internalDesc': 'FindU提供支持。会员计划由管理员控制。',
    'adminUsers.external': '外部（白标）',
    'adminUsers.externalDesc': '独立品牌。自主控制计划和付款信息。',
    'adminUsers.affiliateTier': '联盟等级',
    'adminUsers.affiliateTierDesc': '设置此红娘的初始联盟等级（默认：入门）',
    'adminUsers.whiteLabel': '白标',
    'adminUsers.poweredByFindU': 'FindU提供支持',
    'adminUsers.type': '类型',
    'adminUsers.status': '状态',
    'adminUsers.created': '创建时间',
    'adminUsers.actions': '操作',
    'adminUsers.loadingUsers': '正在加载用户...',
    'adminUsers.allStatus': '所有状态',
    'adminUsers.active': '活跃',
    'adminUsers.inactive': '未激活',
    'adminUsers.suspended': '已暂停',
    'adminUsers.edit': '编辑',
    'adminUsers.suspend': '暂停',
    'adminUsers.activate': '激活',
    'adminUsers.password': '密码 *',
    'adminUsers.phone': '电话号码',
    'adminUsers.selectRoleOption': '选择角色',
    'adminUsers.clientLogin': '客户登录',
    'adminUsers.clientAccount': '客户账户',
    'adminUsers.clientAccountDesc': '此用户将能够登录查看其匹配、消息和档案。',
    'common.email': '邮箱',

    // Admin Roles
    'adminRoles.title': '角色',
    'adminRoles.subtitle': '管理用户角色和权限',
    'adminRoles.createRole': '创建角色',
    'adminRoles.createNewRole': '创建新角色',
    'adminRoles.editRole': '编辑角色',
    'adminRoles.roleName': '角色名称',
    'adminRoles.enterRoleName': '输入角色名称',
    'adminRoles.description': '描述',
    'adminRoles.describeRolePurpose': '描述此角色的用途',
    'adminRoles.permissions': '权限',
    'adminRoles.system': '系统',
    'adminRoles.users': '用户',
    'adminRoles.permCount': '权限',
    'adminRoles.profiles': '档案',
    'adminRoles.viewProfiles': '查看档案',
    'adminRoles.createProfiles': '创建档案',
    'adminRoles.editProfiles': '编辑档案',
    'adminRoles.deleteProfiles': '删除档案',
    'adminRoles.approveProfiles': '审批档案',
    'adminRoles.matches': '匹配',
    'adminRoles.viewMatches': '查看匹配',
    'adminRoles.createMatches': '创建匹配',
    'adminRoles.editMatches': '编辑匹配',
    'adminRoles.deleteMatches': '删除匹配',
    'adminRoles.messages': '消息',
    'adminRoles.viewMessages': '查看消息',
    'adminRoles.sendMessages': '发送消息',
    'adminRoles.deleteMessages': '删除消息',
    'adminRoles.tasks': '任务',
    'adminRoles.viewTasks': '查看任务',
    'adminRoles.createTasks': '创建任务',
    'adminRoles.editTasks': '编辑任务',
    'adminRoles.deleteTasks': '删除任务',
    'adminRoles.calendar': '日历',
    'adminRoles.viewCalendar': '查看日历',
    'adminRoles.createEvents': '创建事件',
    'adminRoles.editEvents': '编辑事件',
    'adminRoles.deleteEvents': '删除事件',
    'adminRoles.billing': '账单',
    'adminRoles.viewBilling': '查看账单',
    'adminRoles.manageSubscriptions': '管理订阅',
    'adminRoles.affiliates': '联盟',
    'adminRoles.viewAffiliates': '查看联盟',
    'adminRoles.manageAffiliates': '管理联盟',
    'adminRoles.processPayouts': '处理提现',
    'adminRoles.viewUsers': '查看用户',
    'adminRoles.createUsers': '创建用户',
    'adminRoles.editUsers': '编辑用户',
    'adminRoles.deleteUsers': '删除用户',
    'adminRoles.datingSchedules': '约会安排',
    'adminRoles.viewDatingSchedules': '查看约会安排',
    'adminRoles.createDatingSchedules': '创建约会安排',
    'adminRoles.editDatingSchedules': '编辑约会安排',
    'adminRoles.deleteDatingSchedules': '删除约会安排',
    'adminRoles.manageAssignedClientDating': '管理分配客户的约会',
    'adminRoles.coaches': '教练',
    'adminRoles.viewCoaches': '查看教练',
    'adminRoles.manageCoaches': '管理教练',
    'adminRoles.assignClientsToCoaches': '分配客户给教练',
    'adminRoles.manageAssignedClientMatches': '管理分配客户的匹配',
    'adminRoles.kpiPerformance': 'KPI绩效管理',
    'adminRoles.viewKpi': '查看KPI面板',
    'adminRoles.createKpi': '创建流程和分配',
    'adminRoles.editKpi': '编辑流程和分配',
    'adminRoles.deleteKpi': '删除流程和分配',
    'adminRoles.manageWorkflows': '管理服务流程',
    'adminRoles.manageAssignments': '管理客户分配',
    'adminRoles.reviewKpi': '审核和批准阶段完成',
    'adminRoles.manageReminders': '管理提醒和预警',
    'adminRoles.matchmakerType': '红娘类型',
    'adminRoles.internalMatchmaker': '内部红娘（FindU提供支持）',
    'adminRoles.externalMatchmaker': '外部红娘（白标）',
    'adminRoles.manageOwnPlans': '管理自有会员计划',
    'adminRoles.manageOwnPaymentInfo': '管理自有付款说明',
    'adminRoles.setPricePerMatch': '设置每次匹配价格',
    'adminRoles.clientBilling': '客户账单',
    'adminRoles.viewClientInvoices': '查看客户发票',
    'adminRoles.createClientInvoices': '创建客户发票',
    'adminRoles.editClientInvoices': '编辑客户发票',
    'adminRoles.deleteClientInvoices': '删除客户发票',
    'adminRoles.approveClientPayments': '审批客户付款',
    'adminRoles.manageClientSubscriptions': '管理客户订阅',
    'adminRoles.settings': '设置',
    'adminRoles.viewSettings': '查看设置',
    'adminRoles.editSettings': '编辑设置',
    'adminRoles.systemSettings': '系统设置',
    'adminRoles.managePaymentInstructions': '管理付款说明',
    'adminRoles.managePaypalIntegration': '管理PayPal集成',
    'adminRoles.manageClientMembershipPlans': '管理客户会员计划',
    'adminRoles.directory': '目录',
    'adminRoles.viewProfileDirectory': '查看档案目录',
    'adminRoles.searchDirectory': '搜索目录',
    'adminRoles.viewProfileDetails': '查看档案详情',
    'adminRoles.favoriteProfiles': '收藏档案',
    'adminRoles.requestMatchesFromDirectory': '从目录请求匹配',
    'connections.viewConnections': '查看连接',
    'connections.searchOtherMatchmakers': '搜索其他红娘',
    'connections.messageOtherMatchmakers': '给其他红娘发消息',
    'connections.requestMatches': '请求匹配',

    // Billing Page
    'billing.title': '客户会员账单',
    'billing.subtitle': '管理客户会员、发票和付款',
    'billing.createInvoice': '创建发票',
    'billing.totalRevenue': '总收入',
    'billing.pending': '待处理',
    'billing.activeSubscriptions': '活跃订阅',
    'billing.awaitingApproval': '待审批',
    'billing.cancelled': '已取消',
    'billing.rejected': '已拒绝',
    'billing.membershipRejected': '会员已被拒绝',
    'billing.invoices': '发票',
    'billing.subscriptions': '订阅',
    'billing.paymentByStages': '按阶段付款',
    'billing.installmentProgress': '分期进度',
    'billing.stagesProgress': '阶段进度',
    'billing.amountPaid': '已付金额',
    'billing.dueBalance': '待付余额',
    'billing.submitPayment': '提交付款',
    'billing.itemsPaid': '已付项',
    'billing.stagesPaid': '已完成阶段',
    'billing.noInstallmentInvoices': '未找到分期付款发票',
    'billing.noStagesInvoices': '未找到按阶段付款发票',
    'billing.manageStagePayments': '管理按阶段付款发票',
    'billing.membershipPlans': '会员计划',
    'billing.paymentInstructions': '付款说明',
    'billing.plansManagedByAdmin': '内部红娘的计划由管理员管理',
    'billing.monthly': '每月',
    'billing.yearly': '每年',
    'billing.invoiceNumber': '发票号',
    'billing.client': '客户',
    'billing.plan': '计划',
    'billing.amount': '金额',
    'billing.dueDate': '到期日',
    'billing.status': '状态',
    'billing.actions': '操作',
    'billing.paid': '已付款',
    'billing.overdue': '逾期',
    'billing.searchByClient': '按客户或发票号搜索...',
    'billing.whiteLabel': '白标',
    'billing.clientSubscriptions': '客户订阅',
    'billing.paySecurelyOnline': '安全在线支付',
    'billing.instantBankTransfer': '即时银行转账',
    'billing.directBankDeposit': '直接银行存款',
    'billing.mailPayment': '邮寄支付',
    'billing.bankTransferWire': '银行转账/电汇',
    'billing.check': '支票',
    'billing.paymentDetailsManagedByAdmin': '付款详情由管理员管理（只读）',
    'billing.configureOwnPayment': '为客户发票配置您自己的付款详情',
    'billing.editPaymentInfo': '编辑付款信息',
    'billing.paypalEmail': 'PayPal邮箱',
    'billing.businessName': '商业名称',
    'billing.notConfigured': '未配置',
    'billing.zelleEmail': 'Zelle邮箱',
    'billing.memo': '备注',
    'billing.includeInvoiceNumber': '请填写您的发票号码',
    'billing.bankName': '银行名称',
    'billing.accountName': '账户名称',
    'billing.accountNumber': '账户号码',
    'billing.routingNumber': '路由号码',
    'billing.makePayableTo': '收款人',
    'billing.instruction': '说明',
    'billing.internalMatchmaker': '内部红娘',
    'billing.contactAdminPayment': '付款说明由管理员管理。请联系管理员更新付款详情。',
    'billing.addPlan': '添加计划',
    'billing.editPlan': '编辑计划',
    'billing.pendingAdminApproval': '等待管理员审批',
    'billing.selectClient': '选择客户',
    'billing.membershipPlan': '会员计划',
    'billing.description': '描述',
    'billing.recurringInvoice': '循环发票',
    'billing.createAsSubscription': '创建为订阅',
    'billing.addSubscription': '添加订阅',
    'billing.manageRecurringMemberships': '管理可自定义账单的循环会员',
    'billing.createSubscription': '创建订阅',
    'billing.editSubscription': '编辑订阅',
    'billing.updateSubscription': '更新订阅',
    'billing.planName': '计划名称',
    'billing.customPlanName': '自定义计划名称...',
    'billing.paymentSchedule': '付款计划',
    'billing.firstPaymentDeposit': '首付/押金 ($)',
    'billing.optionalPlaceholder': '0.00（可选）',
    'billing.customizableInitialPayment': '可自定义的首次付款',
    'billing.regularPaymentRequired': '常规付款 ($) *',
    'billing.billingFrequency': '账单频率',
    'billing.startDate': '开始日期',
    'billing.trialPeriodDays': '试用期（天）',
    'billing.totalBillingCycles': '总账单周期',
    'billing.serviceFeePaypal': '服务费（PayPal）',
    'billing.feePercentage': '费用百分比 (%)',
    'billing.fixedFee': '固定费用 ($)',
    'billing.commissionPoints': '佣金和积分',
    'billing.commissionRate': '佣金比例 (%)',
    'billing.pointsPerPaymentLabel': '每次付款积分',
    'billing.paymentSummary': '付款摘要',
    'billing.firstPaymentDepositSummary': '首付（押金）：',
    'billing.regularPaymentSummary': '常规付款：',
    'billing.billingCyclesSummary': '账单周期：',
    'billing.commissionPerPaymentSummary': '每次付款佣金：',
    'billing.pointsPerPaymentSummary': '每次付款积分：',
    'billing.notes': '备注',
    'billing.optionalNotes': '可选备注...',
    'billing.serviceFeeNote': '佣金和积分按扣除服务费前的金额计算',
    'billing.payments': '次付款',
    'billing.unlimited': '无限',
    'billing.fee': '费用',
    'billing.net': '净额',
    'billing.trialEnds': '试用结束：',
    'billing.pause': '暂停',
    'billing.deposit': '押金',
    'billing.regular': '常规',
    'billing.frequency': '频率',
    'billing.progress': '进度',
    'billing.nextBilling': '下次账单',
    'billing.createClientInvoice': '创建客户发票',
    'billing.chooseClient': '选择客户...',
    'billing.choosePlan': '选择计划...',
    'billing.customSelectPlan': '自定义/选择方案',
    'billing.weekly': '每周',
    'billing.biWeekly': '每两周',
    'billing.quarterly': '每季度',
    'billing.unlimitedCycles': '无限周期（直到客户取消订阅）',
    'billing.includeServiceFee': '在付款计算中包含服务费',
    'billing.regularPayment': '常规付款',
    'billing.billingCycles': '账单周期',
    'billing.commissionPerPayment': '每次付款佣金',
    'billing.pointsPerPayment': '每次付款积分',
    'billing.editPaymentInstructions': '编辑付款说明',
    'billing.checking': '支票账户',
    'billing.savings': '储蓄账户',
    'billing.loadingBilling': '加载账单信息中...',
    'billing.allStatus': '所有状态',
    'billing.balance': '余额',
    'billing.noInvoices': '暂无发票',

    // Auth
    'auth.login': '登录',
    'auth.logout': '退出登录',
    'auth.register': '注册',
    'auth.forgotPassword': '忘记密码',
    'auth.resetPassword': '重置密码',
    'auth.email': '邮箱',
    'auth.password': '密码',
    'auth.confirmPassword': '确认密码',
    'auth.rememberMe': '记住我',
    'auth.dashboardTitle': '红娘管理平台',
    'auth.signInSubtitle': '登录您的账户',
    'auth.signIn': '登录',
    'auth.phoneFormatHint': '格式',
    'auth.signingIn': '登录中...',
    'auth.emailAddress': '邮箱地址',
    'auth.emailPlaceholder': 'you@example.com',
    'auth.passwordPlaceholder': '请输入密码',
    'auth.phoneOtp': '手机验证码',
    'auth.phoneNumber': '手机号码',
    'auth.change': '更改',
    'auth.sendOtp': '发送验证码',
    'auth.sendingOtp': '发送验证码中...',
    'auth.enterOtp': '输入验证码',
    'auth.otpPlaceholder': '输入6位验证码',
    'auth.didntReceiveOtp': '没有收到验证码？',
    'auth.resendIn': '重新发送倒计时',
    'auth.resendOtp': '重新发送验证码',
    'auth.verifying': '验证中...',
    'auth.verifySignIn': '验证并登录',
    'auth.enterValidPhone': '请输入有效的手机号码',
    'auth.otpSentSuccess': '验证码已发送到您的手机',
    'auth.otpSendFailed': '发送验证码失败，请重试。',
    'auth.enterValidOtp': '请输入有效的验证码',
    'auth.otpInvalid': '验证码无效，请重试。',
    'auth.backToSignIn': '返回登录',
    'auth.forgotPasswordDesc': '输入您的账户邮箱地址，我们将向您发送密码重置验证码。',
    'auth.enterEmail': '请输入您的邮箱地址',
    'auth.sending': '发送中...',
    'auth.sendResetLink': '发送重置验证码',
    'auth.resetLinkSent': '如果该邮箱存在账户，重置验证码已发送。',
    'auth.resetCodeSentEmail': '6位重置验证码已发送到您的邮箱。',
    'auth.resetRequestFailed': '发送重置验证码失败，请重试。',
    'auth.enterCodeDesc': '输入发送到您邮箱的6位验证码并选择新密码。',
    'auth.resetCodeLabel': '重置验证码',
    'auth.enterResetCode': '000000',
    'auth.resetTokenLabel': '重置令牌',
    'auth.pasteToken': '粘贴您的重置令牌',
    'auth.newPassword': '新密码',
    'auth.newPasswordPlaceholder': '输入新密码（至少6个字符）',
    'auth.confirmPasswordPlaceholder': '确认您的新密码',
    'auth.enterResetToken': '请输入6位重置验证码',
    'auth.passwordMinLength': '密码至少需要6个字符',
    'auth.passwordsNoMatch': '两次密码输入不一致',
    'auth.resetting': '重置中...',
    'auth.passwordResetSuccess': '密码重置成功！正在跳转到登录页面...',
    'auth.resetFailed': '密码重置失败，请重试。',
    'auth.loginFailed': '登录失败，请重试。',
    'auth.emailPasswordRequired': '请输入邮箱和密码。',
    'auth.invalidCredentials': '邮箱或密码不匹配，请重试。',
    'auth.accountSuspended': '您的账户已被停用或暂停，请联系客服。',
    'auth.serverError': '无法连接服务器，请稍后重试。',
    'auth.networkError': '网络错误，请检查您的网络连接。',

    // Language
    'language.title': '语言',
    'language.english': 'English',
    'language.chinese': '中文',

    // AI Assistant
    'aiAssistant.title': 'AI红娘助手',
    'aiAssistant.subtitle': '支持语音和文字',
    'aiAssistant.reminders': '提醒事项',
    'aiAssistant.myProfiles': '我的档案',
    'aiAssistant.myTasks': '我的任务',
    'aiAssistant.todaySchedule': '今日日程',
    'aiAssistant.suggestions': '建议',
    'aiAssistant.typePlaceholder': '输入消息或说话...',
    'aiAssistant.stopListening': '停止录音',
    'aiAssistant.startListening': '开始录音',
    'aiAssistant.speechNotSupported': '您的浏览器不支持语音识别',
    'aiAssistant.backgroundTaskStarted': '后台任务已启动',
    'aiAssistant.backgroundTasksRunning': '个后台任务运行中...',
    'aiAssistant.greeting': '您好！我是您的AI助手',
    'aiAssistant.greetingDesc': '我可以帮您管理档案、任务、约会安排等。试试说话或打字吧！',
    'aiAssistant.errorProcessing': '抱歉，我无法处理您的请求。请重试。',

    // Membership Due Info
    'membership.daysUntilDue': '天后到期',
    'membership.daysOverdue': '天已过期',
    'membership.dueToday': '今天到期',
    'membership.noDueDate': '无到期日',
    'membership.nextPayment': '下次付款',
    'membership.membershipDue': '会员到期',
    'membership.startDate': '会员开始日期',
    'membership.endDate': '会员结束日期',
    'membership.nextPaymentDate': '下次付款日期',
    'membership.nextPaymentAmount': '下次付款金额',

    // Category Sorting
    'customFields.moveUp': '上移',
    'customFields.moveDown': '下移',
    'customFields.reorderCategories': '重新排序分类',
    'customFields.dragToReorder': '拖动以重新排序',

    // Team Rankings - Affiliate
    'affiliate.teamRankings': '排行榜',
    'affiliate.teamRankingsTitle': '团队积分排行榜',
    'affiliate.rank': '排名',
    'affiliate.rankName': '姓名',
    'affiliate.totalPoints': '总积分',
    'affiliate.you': '你',
    'affiliate.loadingRankings': '加载排行榜...',
    'affiliate.noRankings': '暂无排名',
    'affiliate.noRankingsDesc': '当有活跃的联盟成员获得积分后，排行榜将显示在此处。',
    'affiliate.pointsHistory': '积分历史',

    // 1099 Tax Forms - Affiliate
    'affiliate.tax1099Forms': '1099表格',
    'affiliate.tax1099Description': '下载您的1099税务表格用于报税。',
    'affiliate.taxYear': '税务年度',
    'affiliate.formType': '表格类型',
    'affiliate.uploadedDate': '上传日期',
    'affiliate.totalEarnings': '总收入',
    'affiliate.no1099Forms': '暂无1099表格',
    'affiliate.no1099FormsDesc': '管理员上传后，1099表格将显示在此处。',
    'affiliate.tax1099Info': '重要税务信息',
    'affiliate.tax1099InfoDesc': '如果您的年收入达到$600或以上，1099-NEC表格通常在1月31日前发放，用于上一税务年度。请咨询税务专业人士获取建议。',
    'affiliate.downloadFailed': '下载表格失败，请重试。',

    // 1099 Tax Forms - Admin
    'adminAffiliates.manage1099': '管理1099',
    'adminAffiliates.manage1099Forms': '1099税务表格管理',
    'adminAffiliates.upload1099Form': '上传新1099表格',
    'adminAffiliates.uploaded1099Forms': '已上传的1099表格',
    'adminAffiliates.no1099Forms': '尚未上传1099表格',
    'adminAffiliates.form1099Uploaded': '1099表格上传成功',
    'adminAffiliates.form1099Deleted': '1099表格删除成功',
    'adminAffiliates.confirm1099Delete': '确定要删除此1099表格吗？',
    'adminAffiliates.invalidFileType': '无效的文件类型。请上传PDF、JPG、PNG或GIF文件。',
    'adminAffiliates.fileTooLarge': '文件过大。最大限制为10MB。',
    'adminAffiliates.selectFile': '选择文件',
    'adminAffiliates.dragOrClick': '拖放或点击选择文件',
    'adminAffiliates.uploadForm': '上传1099表格',
    'adminAffiliates.uploadFailed': '上传1099表格失败',
    'adminAffiliates.deleteFailed': '删除1099表格失败',
    'adminAffiliates.notes': '备注',
    'adminAffiliates.notesPlaceholder': '添加关于此表格的备注...',

    // Admin Profiles Modal
    'adminProfiles.professional': '职业信息',
    'adminProfiles.noBio': '暂无简介',
    'adminProfiles.assignment': '分配',
    'adminProfiles.notAssigned': '未分配',
    'adminProfiles.catGeneral': '基本信息',
    'adminProfiles.catPersonal': '个人详情',
    'adminProfiles.catPreferences': '偏好',
    'adminProfiles.catLifestyle': '生活方式',
    'adminProfiles.catBackground': '背景',
    'adminProfiles.catOther': '其他',
    'adminProfiles.serviceAgreement': '服务协议',
    'adminProfiles.agreementSigned': '协议已签署',
    'adminProfiles.downloadAgreement': '下载协议',
    'adminProfiles.signedOn': '签署于',
    'adminProfiles.noAgreement': '未签署协议',
    'adminProfiles.flags': '标记',
    'adminProfiles.reports': '举报',
    'adminProfiles.reason': '原因',
    'adminProfiles.reportedBy': '举报人',
    'adminProfiles.date': '日期',
    'adminProfiles.country': '国家',
    'adminProfiles.stateProvince': '州/省',
    'adminProfiles.searchCountry': '搜索国家...',
    'adminProfiles.searchState': '搜索州/省...',
    'adminProfiles.searchCity': '搜索城市...',
    'adminProfiles.password': '密码',
    'adminProfiles.passwordPlaceholder': '输入密码',
    'adminProfiles.passwordLeaveBlank': '留空则保持不变',
    'adminProfiles.dateOfBirth': '出生日期',
    'adminProfiles.starSign': '星座',
    'adminProfiles.autoFromDOB': '根据生日自动计算',
    'adminProfiles.profileType': '档案类型',
    'adminProfiles.selectProfileType': '选择档案类型',
    'adminProfiles.lead': '潜在客户',
    'adminProfiles.member': '会员',
    'adminProfiles.noProfilesFound': '未找到档案',
    'adminProfiles.offlineId': '线下ID',
    'adminProfiles.noProfilesAwaiting': '没有待审核的档案',
    'adminProfiles.addCustomField': '添加自定义字段',
    'adminProfiles.editCustomField': '编辑自定义字段',
    'adminProfiles.fieldName': '字段名称（内部）',
    'adminProfiles.displayLabel': '显示标签',
    'adminProfiles.fieldType': '字段类型',
    'adminProfiles.textInput': '文本输入',
    'adminProfiles.textArea': '多行文本',
    'adminProfiles.dropdownSelect': '下拉选择',
    'adminProfiles.radioButtons': '单选按钮',
    'adminProfiles.checkboxes': '多选框',
    'adminProfiles.options': '选项（逗号分隔）',
    'adminProfiles.displayOrder': '显示顺序',
    'adminProfiles.requiredField': '必填字段',
    'adminProfiles.createField': '创建字段',
    'adminProfiles.updateField': '更新字段',
    'adminProfiles.occupation': '职业',
    'adminProfiles.education': '学历',
    'adminProfiles.email': '邮箱',
    'adminProfiles.phone': '电话',
    'adminProfiles.generateCard': '生成卡片',
    'adminProfiles.flagPhotoReview': '照片审核',
    'adminProfiles.flagPhotoMismatch': '照片不符',
    'adminProfiles.flagIncomplete': '资料不完整',
    'adminProfiles.flagSuspicious': '可疑',
    'adminProfiles.flagReported': '被举报',
    'adminProfiles.min': '最小',
    'adminProfiles.max': '最大',
    'adminProfiles.to': '至',

    // KPI Performance Management
    'kpi.title': 'KPI绩效管理',
    'kpi.subtitle': '监控和管理教练及红娘的任务绩效',
    'kpi.totalTasks': '总任务',
    'kpi.completed': '已完成',
    'kpi.inProgress': '进行中',
    'kpi.overdue': '已逾期',
    'kpi.completionRate': '完成率',
    'kpi.pending': '待处理',
    'kpi.cancelled': '已取消',
    'kpi.staffPerformance': '员工绩效',
    'kpi.assignee': '负责人',
    'kpi.allAssignees': '所有负责人',
    'kpi.allStatuses': '所有状态',
    'kpi.allPriorities': '所有优先级',
    'kpi.allTypes': '所有类型',
    'kpi.searchTasks': '搜索任务...',
    'kpi.createTask': '创建任务',
    'kpi.editTask': '编辑任务',
    'kpi.deleteTask': '删除任务',
    'kpi.deleteConfirm': '确定要删除此任务吗？',
    'kpi.taskTitle': '任务标题',
    'kpi.description': '描述',
    'kpi.taskType': '任务类型',
    'kpi.priority': '优先级',
    'kpi.status': '状态',
    'kpi.dueDate': '截止日期',
    'kpi.dueTime': '截止时间',
    'kpi.assignTo': '分配给',
    'kpi.selectAssignee': '选择负责人',
    'kpi.dateFrom': '开始日期',
    'kpi.dateTo': '结束日期',
    'kpi.noTasks': '未找到任务',
    'kpi.noStaff': '暂无员工数据',
    'kpi.taskCreated': '任务创建成功',
    'kpi.taskUpdated': '任务更新成功',
    'kpi.taskDeleted': '任务删除成功',
    'kpi.loadFailed': '加载KPI数据失败',
    'kpi.saveFailed': '保存任务失败',
    'kpi.tasks': '任务',
    'kpi.overview': '概览',
    'kpi.role': '角色',
    'kpi.coach': '教练',
    'kpi.matchmaker': '红娘',
    'kpi.daily': '每日',
    'kpi.weekly': '每周',
    'kpi.monthly': '每月',
    'kpi.oneTime': '一次性',
    'kpi.low': '低',
    'kpi.medium': '中',
    'kpi.high': '高',
    'kpi.urgent': '紧急',
    'kpi.staffMember': '员工',
    'kpi.refresh': '刷新',

    'kpi.active': '活跃',
    'kpi.paused': '暂停',
    'kpi.unnamed': '未命名',
    // KPI - Status Labels
    'kpi.statusActive': '活跃',
    'kpi.statusCompleted': '已完成',
    'kpi.statusPaused': '暂停',
    'kpi.statusCancelled': '已取消',
    'kpi.statusTransferred': '已转移',
    'kpi.statusPending': '待处理',
    'kpi.statusInProgress': '进行中',
    'kpi.statusFailed': '未通过',
    'kpi.statusSkipped': '已跳过',
    'kpi.statusSubmitted': '已提交',
    'kpi.statusApproved': '已审批',
    'kpi.statusRejected': '已拒绝',
    'kpi.statusNotStarted': '未开始',
    'kpi.statusOverdue': '已逾期',
    'kpi.statusDraft': '草稿',
    'kpi.statusScheduled': '已预约',
    'kpi.pageNumber': '第 {n} 页',
    'kpi.statusSent': '已发送',
    'kpi.statusViewed': '已查看',
    'kpi.statusExpired': '已过期',
    'kpi.statusDismissed': '已忽略',
    'kpi.statusInactive': '停用',
    'kpi.statusReviewing': '审核中',

    // KPI - Warning Labels
    'kpi.warningYellow': '黄色',
    'kpi.warningOrange': '橙色',
    'kpi.warningRed': '红色',

    // KPI - Overview Tab
    'kpi.loadingDashboard': '加载仪表盘中...',
    'kpi.activeClients': '活跃客户',
    'kpi.totalRevenue': '总收入',
    'kpi.serviceValue': '服务价值',
    'kpi.avgSatisfaction': '平均满意度',
    'kpi.activeWarnings': '活跃预警',
    'kpi.coachPerformance': '红娘绩效',
    'kpi.thCoach': '红娘',
    'kpi.thActive': '活跃',
    'kpi.thCompleted': '已完成',
    'kpi.thTotalValue': '总价值',
    'kpi.thEarned': '已赚取',
    'kpi.thWarnings': '预警',
    'kpi.none': '无',
    'kpi.noResults': '未找到结果',
    'kpi.noCoachData': '暂无红娘数据。创建服务流程并分配客户以开始使用。',
    'kpi.earnPercentage': '收益%',
    'kpi.earnOfBase': '基础金额收益%',
    'kpi.deductionAmount': '扣除金额(¥)',
    'kpi.moveUp': '上移',
    'kpi.moveDown': '下移',

    // 审核专业 Supervisor KPI
    'supervisor.title': '审核专业 KPI审核',
    'supervisor.subtitle': '审核和管理所有分配中的红娘绩效',
    'supervisor.clientProgress': '客户进度',
    'supervisor.warningSystem': '预警系统',
    'supervisor.feedbackRequired': '请输入反馈内容',
    'supervisor.feedbackAdded': '反馈添加成功',
    'supervisor.viewAllFeedback': '查看全部反馈',
    'supervisor.submit': '提交',
    'supervisor.overview': '概览',
    'supervisor.progress': '进度',
    'supervisor.warnings': '警告',
    'supervisor.reviewStatus': '审核状态',
    'supervisor.reviewing': '审核中',
    'supervisor.approved': '已批准',
    'supervisor.rejected': '已拒绝',
    'supervisor.addFeedback': '添加反馈',
    'supervisor.feedback': '反馈',
    'supervisor.feedbackPlaceholder': '请输入该阶段的反馈意见...',
    'supervisor.submitFeedback': '提交反馈',
    'supervisor.previousFeedback': '历史反馈',
    'supervisor.noFeedback': '暂无反馈',
    'supervisor.activeClients': '活跃客户',
    'supervisor.completed': '已完成',
    'supervisor.avgSatisfaction': '平均满意度',
    'supervisor.activeWarnings': '活跃预警',
    'supervisor.coachPerformance': '红娘绩效',
    'supervisor.coach': '红娘',
    'supervisor.active': '活跃',
    'supervisor.warningsCol': '预警',
    'supervisor.assignmentList': '分配列表',
    'supervisor.allCoaches': '全部红娘',
    'supervisor.allStatuses': '全部状态',
    'supervisor.viewDetail': '查看详情',
    'supervisor.assignmentDetail': '分配详情',
    'supervisor.stages': '阶段',
    'supervisor.action': '操作',
    'supervisor.stage': '阶段',
    'supervisor.status': '状态',
    'supervisor.weight': '权重',
    'supervisor.earned': '收益',
    'supervisor.review': '审核',
    'supervisor.deliverables': '交付物',
    'supervisor.deduction': '扣除',
    'supervisor.feedbackSubmitted': '反馈提交成功',
    'supervisor.reviewUpdated': '审核状态已更新',
    'supervisor.noAssignments': '暂无分配',
    'supervisor.noWarnings': '暂无预警',
    'supervisor.warningLevel': '预警等级',
    'supervisor.warningDescription': '描述',
    'supervisor.warningDate': '日期',
    'supervisor.questionnaireCompleted': '已完成',
    'supervisor.requiredItems': '必须项目',
    'supervisor.photoFiles': '照片/文件',
    'supervisor.appointmentLabel': '预约',
    'supervisor.videoMeetingLabel': '视频会议',
    'supervisor.matchingLabel': '匹配',
    'supervisor.confirmationLabel': '确认',
    'supervisor.candidateLabel': '候选人',
    'supervisor.datingScheduleLabel': '约会安排',
    'supervisor.followupLabel': '跟进',
    'supervisor.questionnaireLabel': '问卷',
    'supervisor.loadResultsFailed': '加载问卷结果失败',
    'supervisor.questionnaireResults': '问卷结果',
    'supervisor.noResults': '暂无结果',
    'supervisor.avgScore': '平均分数',
    'supervisor.submittedAt': '提交时间',
    'supervisor.primaryLoveLanguage': '主要爱语',
    'supervisor.option': '选项',
    'supervisor.timesSelected': '次选中',
    'supervisor.optionLabel': '选项',
    'supervisor.loveLanguage': '爱语',
    'supervisor.timesSelectedHeader': '选中次数',

    // Deliverable Info Panels
    'deliverableInfo.uploadedFiles': '已上传文件',
    'deliverableInfo.videoMeeting': '视频会议',
    'deliverableInfo.matches': '匹配',
    'deliverableInfo.datingSchedules': '约会安排',
    'deliverableInfo.datingSchedule': '约会安排',
    'deliverableInfo.questionnaireResults': '问卷结果',
    'deliverableInfo.followUp': '跟进',
    'deliverableInfo.appointment': '预约',
    'deliverableInfo.viewRecording': '查看录像',
    'deliverableInfo.viewResults': '查看结果',
    'deliverableInfo.scheduled': '已安排',
    'deliverableInfo.attempt': '尝试',
    'deliverableInfo.interval': '间隔',
    'deliverableInfo.days': '天',
    'deliverableInfo.notes': '备注',
    'deliverableInfo.viewInfo': '查看详情',
    'deliverableInfo.status': '状态',
    'deliverableInfo.confirmation': '确认',

    // Supervisor KPI - Additional strings (Chinese)
    'supervisor.progressStatus': '进度状态',
    'supervisor.noWarnings': '无警告',
    'supervisor.loadingRecording': '加载中...',
    'supervisor.meetingLink': '会议链接',
    'supervisor.typeLabel': '类型',
    'supervisor.pendingStatus': '待处理',
    'supervisor.scheduledStatus': '已安排',
    'supervisor.completePercent': '完成',
    'supervisor.maxScore': '满分',
    'supervisor.assessmentResults': '评估结果',
    'supervisor.partnerPreferences': '择偶要求',
    'supervisor.yourResults': '您的结果',
    'supervisor.attachmentStyle': '依恋风格',
    'supervisor.primaryAttachmentStyle': '您的主要依恋风格',
    'supervisor.notProvided': '(未填写)',
    'supervisor.upTo': '最多',
    'supervisor.prevImage': '← 上一个',
    'supervisor.nextImage': '下一个 →',
    'supervisor.downloadImage': '下载',
    'supervisor.closeVideo': '点击外部或按 ESC 关闭',
    'supervisor.navigationKeys': '按 ESC 或点击外部关闭 • 使用箭头键导航',
    'supervisor.dateLabel': '日期',

    // KPI - Workflows Tab
    'kpi.serviceWorkflows': '服务流程',
    'kpi.createWorkflow': '创建流程',
    'kpi.noWorkflows': '暂无服务流程。点击「创建流程」设置服务阶段和佣金百分比。',
    'kpi.base': '基础',
    'kpi.commission': '佣金',
    'kpi.commissionPool': '佣金池',
    'kpi.stagesCount': '个阶段',
    'kpi.activeClientsCount': '位活跃客户',
    'kpi.edit': '编辑',
    'kpi.editWorkflow': '编辑流程',
    'kpi.createServiceWorkflow': '创建服务流程',
    'kpi.workflowName': '流程名称 *',
    'kpi.workflowNamePlaceholder': '例如：标准 $3,980 服务',
    'kpi.baseServiceAmount': '基础服务金额 ($)',
    'kpi.customAmount': '自定义金额',
    'kpi.approvedClientPayments': '已审批客户付款',
    'kpi.membershipPlans': '会员方案',
    'kpi.enterCustomAmount': '输入自定义金额...',
    'kpi.selectFromPayments': '从已审批付款中选择或输入自定义金额',
    'kpi.commissionRate': '佣金比例 (%)',
    'kpi.serviceStages': '服务阶段',
    'kpi.totalWeight': '总权重',
    'kpi.shouldBe100': '(应为100%)',
    'kpi.recalculate': '重新计算',
    'kpi.addStage': '添加阶段',
    'kpi.unnamedStage': '未命名阶段',
    'kpi.deliverables': '个交付物',
    'kpi.stageCode': '编码',
    'kpi.stageName': '阶段名称',
    'kpi.allowPageCreation': '允许创建页面',
    'kpi.allowPageCreationDesc': '允许教练为此阶段创建/编辑多个页面（不同教练可以提交不同内容）',
    'kpi.enabled': '已启用',
    'kpi.workContent': '工作内容',
    'kpi.weightPercent': '权重 %',
    'kpi.commissionDollar': '佣金 ($)',
    'kpi.timeLimitDays': '时限 (天)',
    'kpi.assessmentCriteria': '考核标准',
    'kpi.requiredDeliverables': '必需交付物（照片上传）',
    'kpi.add': '添加',
    'kpi.deliverableName': '交付物名称...',
    'kpi.deliverableNameZh': '中文交付物名称...',
    'kpi.photo': '照片',
    'kpi.file': '文件',
    'kpi.uploadFile': '上传文件',
    'kpi.toastFileUploaded': '文件上传成功',
    'kpi.toastFileUploadFailed': '文件上传失败',
    'kpi.videoMeeting': '视频会议',
    'kpi.audioCall': '语音通话',
    'kpi.matching': '匹配',
    'kpi.datingSchedule': '约会安排',
    'kpi.questionnaire': '问卷',
    'kpi.selectQuestionnaire': '选择问卷...',
    'kpi.followUp': '跟进',
    'kpi.editFollowUp': '编辑',
    'kpi.createFollowUpConfig': '创建跟进',
    'kpi.editFollowUpConfig': '编辑跟进',
    'kpi.saveFollowUp': '保存跟进',
    'kpi.followUpType': '跟进类型',
    'kpi.followUpPostMeeting': '会后跟进',
    'kpi.followUpCheckIn': '签到',
    'kpi.followUpProgressReview': '进度审查',
    'kpi.followUpCustom': '自定义',
    'kpi.followUpIntervalDays': '提醒间隔（天）',
    'kpi.followUpIntervalHelp': '每次跟进尝试之间的天数',
    'kpi.followUpMaxAttempts': '最大尝试次数',
    'kpi.followUpMaxAttemptsHelp': '最大跟进尝试次数',
    'kpi.followUpDescription': '跟进说明',
    'kpi.intervalDays': '间隔',
    'kpi.maxAttempts': '最大次数',
    'kpi.postMeeting': '会后跟进',
    'kpi.checkIn': '签到',
    'kpi.progressReview': '进度审查',
    'kpi.custom': '自定义',
    'kpi.followUpDescPlaceholder': '描述教练需要跟进的内容...',
    'kpi.followUpDescPlaceholderZh': '描述教练需要跟进的内容...',
    'kpi.appointment': '预约',
    'kpi.reminderDays': '提醒（天）',
    'kpi.confirmation': '确认书',
    'kpi.matchCount': '匹配数量',
    'kpi.fileCount': '文件数量',
    'kpi.selectQuestionnaires': '选择问卷',
    'kpi.stage': '阶段',
    'kpi.deliverable': '交付物',
    'kpi.zhLabel': '🇨🇳 中文',
    'kpi.enLabel': '🇺🇸 EN',
    'kpi.workflowNameZh': '流程名称（中文）',
    'kpi.descriptionZh': '描述（中文）',
    'kpi.stageNameZh': '阶段名称（中文）',
    'kpi.workContentZh': '工作内容（中文）',
    'kpi.assessmentCriteriaZh': '评估标准（中文）',
    'kpi.cancel': '取消',
    'kpi.updateWorkflow': '更新流程',
    'kpi.save': '保存',

    // KPI - Client Progress Tab
    'kpi.clientProgressTracking': '客户进度追踪',
    'kpi.assignClient': '分配客户',
    'kpi.allCoaches': '所有红娘',
    'kpi.noAssignments': '暂无客户分配。请使用服务流程将客户分配给红娘。',
    'kpi.unknownClient': '未知客户',
    'kpi.coachLabel': '红娘',
    'kpi.workflowLabel': '流程',
    'kpi.stageLabel': '阶段',
    'kpi.earnedLabel': '已赚取',
    'kpi.stagesProgress': '阶段',
    'kpi.pageOf': '第 {page} 页 / 共 {pages} 页',
    'kpi.assignClientToCoach': '分配客户给红娘',
    'kpi.serviceWorkflow': '服务流程 *',
    'kpi.selectWorkflow': '选择流程...',
    'kpi.coachRequired': '红娘 *',
    'kpi.selectCoach': '选择红娘...',
    'kpi.clientProfile': '客户（档案）',
    'kpi.selectProfile': '选择档案（可选）...',
    'kpi.clientNameNoProfile': '客户姓名（无档案时）',
    'kpi.enterClientName': '输入客户姓名...',
    'kpi.serviceAmount': '服务金额 ($)',
    'kpi.notes': '备注',
    'kpi.assign': '分配',
    'kpi.serviceFee': '服务费',
    'kpi.complete': '完成',
    'kpi.deliverableNeedApproval': '个待批准',
    'kpi.failed': '不合格',
    'kpi.uploadPhoto': '上传照片',

    // KPI - Questionnaires Tab
    'kpi.questionnaires': '问卷调查',
    'kpi.createQuestionnaire': '创建问卷',
    'kpi.createSatisfactionSurvey': '创建满意度调查',
    'kpi.noQuestionnaires': '暂无问卷。创建评估表发送给客户。',
    'kpi.questions': '题',
    'kpi.submissions': '份提交',
    'kpi.avgScore': '平均分',
    'kpi.send': '发送',
    'kpi.editQuestionnaire': '编辑问卷',
    'kpi.titleRequired': '标题 *',
    'kpi.type': '类型',
    'kpi.typeEvaluation': '评估',
    'kpi.typeFeedback': '反馈',
    'kpi.typeAssessment': '测评',
    'kpi.typeLoveLanguage': '爱的语言',
    'kpi.typeProfilePreferences': '择偶要求',
    'kpi.typeCustom': '自定义',
    'kpi.profilePreferencesInfo': '择偶要求问卷',
    'kpi.profilePreferencesDesc': '此问卷类型自动加载择偶要求字段（mate_*）。当客户填写完成后，回答将直接保存到其个人资料的择偶偏好中。无需手动添加题目。',
    'kpi.questionsCount': '题目',
    'kpi.addQuestion': '添加题目',
    'kpi.questionPlaceholder': '题目内容...',
    'kpi.qtRating': '评分',
    'kpi.qtText': '文本',
    'kpi.qtMultipleChoice': '多选',
    'kpi.qtYesNo': '是/否',
    'kpi.qtScale': '量表',
    'kpi.maxRating': '最高分:',
    'kpi.update': '更新',
    'kpi.create': '创建',
    'kpi.sendQuestionnaire': '发送问卷',
    'kpi.clientAssignment': '客户分配（可选）',
    'kpi.clientName': '客户姓名',
    'kpi.clientNamePlaceholder': '客户姓名...',
    'kpi.clientEmail': '客户邮箱',
    'kpi.linkGenerated': '问卷链接已生成！',
    'kpi.copy': '复制',
    'kpi.linkValidDays': '将此链接分享给客户。链接30天内有效。',
    'kpi.close': '关闭',
    'kpi.generateLink': '生成链接',
    'kpi.regenerateLink': '重新生成链接',
    'kpi.shareQuestionnaire': '分享问卷',
    'kpi.shareQuestionnaireNote': '为此交付项生成可分享的问卷链接。链接有效期为30天。',
    'kpi.generating': '生成中...',
    'kpi.questionnaireCompleted': '已完成',
    'kpi.awaitingResponse': '等待回复',
    'kpi.loadResultsFailed': '加载问卷结果失败',
    'kpi.questionnaireResults': '问卷结果',
    'kpi.noResults': '暂无结果',
    'kpi.avgScore': '平均分数',
    'kpi.submittedAt': '提交时间',
    'kpi.primaryLoveLanguage': '主要爱语',
    'kpi.option': '选项',
    'kpi.timesSelected': '次选中',
    'kpi.optionLabel': '选项',
    'kpi.loveLanguage': '爱语',
    'kpi.timesSelectedHeader': '选中次数',

    // KPI - Reminders Tab
    'kpi.coachReminders': '红娘提醒',
    'kpi.createReminder': '创建提醒',
    'kpi.noReminders': '暂无提醒。创建提醒通知红娘跟进任务。',
    'kpi.client': '客户',
    'kpi.date': '日期',
    'kpi.time': '时间',
    'kpi.repeatEveryDays': '每 {days} 天重复',
    'kpi.dismiss': '忽略',
    'kpi.message': '消息',
    'kpi.reminderTypeCustom': '自定义',
    'kpi.reminderTypeStageDeadline': '阶段截止',
    'kpi.reminderTypeFollowUp': '跟进',
    'kpi.reminderTypeMeeting': '约见',
    'kpi.triggerDate': '触发日期',
    'kpi.triggerTime': '触发时间',
    'kpi.recurring': '重复',
    'kpi.every': '每',
    'kpi.days': '天',
    'kpi.followUpClient': '例如 跟进客户',

    // KPI - Warnings Tab
    'kpi.warningSystem': '预警系统',
    'kpi.yellowWarningDesc': '7天未安排见面 — 助理介入',
    'kpi.orangeWarningDesc': '8天未安排见面 — 品控专员介入',
    'kpi.redWarningDesc': '10天未安排见面 — 创始人介入，考虑更换红娘',
    'kpi.noActiveWarnings': '无活跃预警。所有红娘进展正常！',
    'kpi.daysOverdue': '天',
    'kpi.resolutionNotes': '解决备注:',
    'kpi.resolve': '解决',

    // KPI - Tasks Tab
    'kpi.taskManagement': '任务管理',
    'kpi.total': '总数',
    'kpi.thTitle': '标题',
    'kpi.thAssignee': '负责人',
    'kpi.thType': '类型',
    'kpi.thPriority': '优先级',
    'kpi.thStatus': '状态',
    'kpi.thDueDate': '截止日期',
    'kpi.thActions': '操作',
    'kpi.assigneeRequired': '负责人 *',
    'kpi.pleaseSelect': '请选择...',

    // KPI - Toast Messages
    'kpi.toastEnterWorkflowName': '请输入流程名称',
    'kpi.toastWorkflowUpdated': '流程已更新',
    'kpi.toastWorkflowCreated': '流程已创建',
    'kpi.toastSaveWorkflowFailed': '保存流程失败',
    'kpi.toastLoadWorkflowFailed': '加载流程失败',
    'kpi.toastDeleteWorkflowConfirm': '确定删除此流程？',
    'kpi.toastWorkflowDeleted': '流程已删除',
    'kpi.duplicate': '副本',
    'kpi.duplicateWorkflow': '复制流程',
    'kpi.toastWorkflowDuplicated': '流程已复制 — 请检查并保存',
    'kpi.toastDeleteFailed': '删除失败',
    'kpi.toastSelectWorkflowAndCoach': '请选择流程和红娘',
    'kpi.toastClientAssigned': '客户已分配给红娘',
    'kpi.toastCreateAssignmentFailed': '创建分配失败',
    'kpi.toastLoadAssignmentFailed': '加载分配详情失败',
    'kpi.toastStageUpdated': '阶段已更新',
    'kpi.toastUpdateStageFailed': '更新阶段失败',
    'kpi.toastPhotoUploaded': '照片已上传',
    'kpi.toastUploadPhotoFailed': '上传照片失败',
    'kpi.toastEnterTitle': '请输入标题',
    'kpi.toastQuestionnaireUpdated': '问卷已更新',
    'kpi.toastQuestionnaireCreated': '问卷已创建',
    'kpi.toastSaveQuestionnaireFailed': '保存问卷失败',
    'kpi.toastLinkGenerated': '问卷链接已生成',
    'kpi.toastSendQuestionnaireFailed': '发送问卷失败',
    'kpi.toastLoadQuestionnaireFailed': '加载问卷失败',
    'kpi.toastDeleteQuestionnaireConfirm': '确定删除此问卷？',
    'kpi.toastDeleted': '已删除',
    'kpi.toastFailed': '失败',
    'kpi.toastLinkCopied': '链接已复制！',
    'kpi.toastSelectCoachAndTitle': '请选择红娘并输入标题',
    'kpi.toastReminderCreated': '提醒已创建',
    'kpi.toastCreateReminderFailed': '创建提醒失败',
    'kpi.toastDeleteReminderConfirm': '确定删除此提醒？',
    'kpi.toastDismissed': '已忽略',
    'kpi.toastWarningResolved': '预警已解决',
    'kpi.toastSelectAssigneeAndTitle': '请选择负责人并输入标题',
    'kpi.toastDeleteTaskConfirm': '确定删除此任务？',
    'kpi.toastSaveOrderFailed': '保存排序失败',

    // KPI - Main
    'kpi.mainSubtitle': '服务流程管理、红娘绩效追踪及客户满意度评估',
    'kpi.tabOverview': '总览',
    'kpi.tabWorkflows': '服务流程',
    'kpi.tabProgress': '客户进度',
    'kpi.tabQuestionnaires': '问卷调查',
    'kpi.tabReminders': '提醒',
    'kpi.tabWarnings': '预警',
    'kpi.tabWeeklyContacts': '每周联系',
    'kpi.weeklyContactsTitle': '每周联系记录',
    'kpi.allCoaches': '所有教练',
    'kpi.allWeeks': '所有周',
    'kpi.totalRecords': '总计',
    'kpi.noWeeklyContacts': '暂无每周联系记录',
    'kpi.weekStart': '周',
    'kpi.coach': '教练',
    'kpi.client': '客户',
    'kpi.contactType': '类型',
    'kpi.matching': '匹配',
    'kpi.datingSchedule': '约会',
    'kpi.submittedAt': '提交时间',
    'kpi.refresh': '刷新',
    'kpi.allClientsContacted': '本周所有客户已联系！',
    'kpi.allStatus': '全部',
    'kpi.choiceOptions': '选项列表',
    'kpi.city': '城市',
    'kpi.contacted': '已联系',
    'kpi.done': '完成',
    'kpi.editCoachPerformance': '编辑教练绩效',
    'kpi.existingLink': '已有链接',
    'kpi.lastContactDate': '上次联系',
    'kpi.never': '从未',
    'kpi.noChoicesYet': '暂无选项。点击"添加"来添加选项。',
    'kpi.notContacted': '未联系',
    'kpi.sentTo': '发送给',
    'kpi.tabTasks': '任务',
    'kpi.tabFollowups': '跟进',
    'kpi.tabConfirmations': '确认函',

    // KPI - Assignment filter statuses
    'kpi.filterActive': '活跃',
    'kpi.filterPaused': '暂停',
    'kpi.filterCompleted': '已完成',
    'kpi.filterCancelled': '已取消',
    'kpi.filterTransferred': '已转移',

    // KPI - Detail labels
    'kpi.notApplicable': '未达标',

    // Coach KPI
    'coachKpi.title': 'KPI绩效',
    'coachKpi.subtitle': '管理客户进度、会议和交付物',
    'coachKpi.tabClients': '我的客户',
    'coachKpi.tabStats': '我的统计',
    'coachKpi.tabMeetings': '视频会议',
    'coachKpi.tabQuestionnaires': '问卷调查',
    'coachKpi.tabConfirmations': '确认函',
    'coachKpi.tabFollowups': '跟进',
    'coachKpi.tabReminders': '提醒',
    'coachKpi.tabAppointments': '预约',
    'coachKpi.tabMatches': '匹配',
    'coachKpi.tabSchedules': '约会日程',
    'coachKpi.createMatch': '创建匹配',
    // ── 创建匹配弹窗：筛选条 + 分页 + 详情徽章 ──
    'coachKpi.filter.age': '年龄',
    'coachKpi.filter.height': '身高',
    'coachKpi.filter.matchPct': '匹配度',
    'coachKpi.filter.income': '月收入',
    'coachKpi.filter.city': '城市',
    'coachKpi.filter.state': '省/州',
    'coachKpi.filter.country': '国家',
    'coachKpi.filter.marital': '婚姻',
    'coachKpi.filter.children': '子女',
    'coachKpi.filter.property': '房产',
    'coachKpi.filter.tier': '层级',
    'coachKpi.filter.photo': '头像',
    'coachKpi.filter.min': '最小',
    'coachKpi.filter.max': '最大',
    'coachKpi.filter.cm': '厘米',
    'coachKpi.filter.any': '不限',
    'coachKpi.filter.withPhoto': '仅有头像',
    'coachKpi.filter.clearAll': '清除全部',
    'coachKpi.tier.all': '全部',
    'coachKpi.tier.ideal': '理想',
    'coachKpi.tier.acceptable': '可接受',
    'coachKpi.tier.bottomLine': '底线',
    'coachKpi.page.first': '首页',
    'coachKpi.page.prev': '上一页',
    'coachKpi.page.next': '下一页',
    'coachKpi.page.last': '末页',
    'coachKpi.page.pageOf': '第',
    'coachKpi.page.pageSuffix': '页',
    'coachKpi.page.totalCount': '共',
    'coachKpi.page.range': '第',
    'coachKpi.regularCandidate': '普通候选人',
    'coachKpi.matchPctLabel': '匹配度',
    'coachKpi.offlineId': '线下ID',
    'coachKpi.existingLink': '已有链接',
    'coachKpi.generateQuestionnaireFailed': '生成问卷链接失败',
    'coachKpi.status': '状态',
    'common.copyFailed': '复制失败',
    'common.deliverable': '交付项',
    'common.saved': '已保存',
    'deliverableInfo.clientResponse': '客户回复',
    'deliverableInfo.type': '类型',
    'coachKpi.createSchedule': '创建日程',
    'coachKpi.profileA': '档案A',
    'coachKpi.profileB': '档案B',
    'coachKpi.matchPercentage': '匹配度',
    'coachKpi.selectProfile': '选择档案',
    'coachKpi.searchSelectProfile': '搜索并选择档案...',
    'coachKpi.client': '客户',
    'coachKpi.matchNotesPlaceholder': '为什么这是一个好的匹配？',
    'coachKpi.scheduleTitle': '标题',
    'coachKpi.scheduleTitlePlaceholder': '例如：第一次约会 - 咖啡',
    'coachKpi.time': '时间',
    'coachKpi.locationPlaceholder': '场地名称',
    'coachKpi.addressPlaceholder': '详细地址',
    'coachKpi.scheduleNotesPlaceholder': '约会的特别说明或建议',
    'coachKpi.matchNotes': '备注',
    'coachKpi.noMatches': '暂无匹配',
    'coachKpi.noSchedules': '暂无约会日程',
    'coachKpi.scheduledDate': '预定日期',
    'coachKpi.scheduledTime': '预定时间',
    'coachKpi.duration': '时长（分钟）',
    'coachKpi.location': '地点',
    'coachKpi.locationType': '地点类型',
    'coachKpi.locationAddress': '地址',
    'coachKpi.matchmakerNotes': '备注',
    'coachKpi.updateStatus': '更新状态',
    'coachKpi.restaurant': '餐厅',
    'coachKpi.cafe': '咖啡厅',
    'coachKpi.bar': '酒吧',
    'coachKpi.park': '公园',
    'coachKpi.virtual': '线上',
    'coachKpi.other': '其他',
    'coachKpi.client1': '客户1',
    'coachKpi.client2': '客户2',
    'coachKpi.matchCreated': '匹配创建成功',
    'coachKpi.matchesCreated': '个匹配创建成功',
    'coachKpi.matchesCreatedTitle': '匹配已创建！',
    'coachKpi.reviewLinkReady': '将此链接分享给客户，以便审核确认匹配：',
    'coachKpi.reviewLinkCopied': '链接已复制！',
    'coachKpi.waitingToConfirm': '等待确认',
    'coachKpi.matchConfirmed': '已确认',
    'coachKpi.matchRejected': '已拒绝 - 重新匹配',
    'coachKpi.reviewLink': '审核链接',
    'coachKpi.copyLink': '复制',
    'coachKpi.selectMatches': '选择匹配对象',
    'coachKpi.oppositeGender': '异性',
    'coachKpi.searchProfiles': '搜索姓名、邮箱、城市...',
    'coachKpi.profilesSelected': '个档案已选择',
    'coachKpi.noMatchingProfiles': '未找到匹配档案',
    'coachKpi.loadingProfiles': '正在加载档案...',
    'coachKpi.createMatchCount': '创建匹配',
    'coachKpi.selectAtLeastOneProfile': '请至少选择一个档案',
    'coachKpi.scheduleCreated': '约会日程创建成功',
    'coachKpi.statusUpdated': '状态更新成功',
    'coachKpi.searchClients': '搜索客户...',
    'coachKpi.allStatuses': '所有状态',
    'coachKpi.refresh': '刷新',
    'coachKpi.noAssignments': '暂无客户分配',
    'coachKpi.noWorkflow': '无工作流',
    'coachKpi.currentStage': '当前阶段',
    'coachKpi.startedAt': '开始时间',
    'coachKpi.stages': '阶段',
    'coachKpi.overallProgress': '整体进度',
    'coachKpi.stagesCompleted': '阶段已完成',
    'coachKpi.requestComplete': '请求完成',
    'coachKpi.allDeliverablesRequired': '所有交付物必须先勾选',
    'coachKpi.awaitingApproval': '等待审批',
    'coachKpi.awaitingAdmin': '等待管理员审批',
    'coachKpi.uploadPhoto': '照片',
    'coachKpi.notes': '备注',
    'coachKpi.deliverableSubmitted': '交付物已标记为已提交',
    'coachKpi.deliverableUnsubmitted': '交付物已取消标记',
    'coachKpi.deliverableError': '更新交付物失败',
    'coachKpi.confirmRequestStage': '请求管理员标记此阶段为完成？',
    'coachKpi.stageRequestSent': '阶段完成请求已发送',
    'coachKpi.stageRequestError': '请求阶段完成失败',
    'coachKpi.photoUploaded': '照片上传成功',
    'coachKpi.photoUploadError': '照片上传失败',
    'coachKpi.uploadFile': '上传文件',
    'coachKpi.candidates': '候选人',
    'coachKpi.note': '备注',
    'coachKpi.viewDetails': '查看详情',
    'coachKpi.fileUploaded': '文件上传成功',
    'coachKpi.fileUploadError': '文件上传失败',
    'coachKpi.confirmDeleteFile': '确定要删除此文件吗？',
    'coachKpi.fileDeleted': '文件删除成功',
    'coachKpi.fileDeleteError': '文件删除失败',
    'coachKpi.shareQuestionnaire': '分享问卷',
    'coachKpi.shareQuestionnaireNote': '为此客户生成可分享的问卷链接。链接有效期为30天。',
    'coachKpi.questionnaireLinkGenerated': '问卷链接已生成！',
    'coachKpi.typeProfilePreferences': '择偶要求',
    'coachKpi.typeLoveLanguage': '爱的语言',
    'coachKpi.generating': '生成中...',
    'coachKpi.generateLink': '生成链接',
    'coachKpi.regenerateLink': '重新生成链接',
    'coachKpi.copy': '复制',
    'coachKpi.linkValidDays': '将此链接分享给客户。链接30天内有效。',
    'coachKpi.questionnaireLinkCopied': '问卷链接已复制到剪贴板',
    'coachKpi.shareQuestionnaireFailed': '生成问卷链接失败',
    'coachKpi.awaitingResponse': '等待回复',
    'coachKpi.questionnaireCompleted': '已完成',
    'coachKpi.loadResultsFailed': '加载问卷结果失败',
    'coachKpi.questionnaireResults': '问卷结果',
    'coachKpi.noResults': '暂无结果',
    'coachKpi.avgScore': '平均分数',
    'coachKpi.submittedAt': '提交时间',
    'coachKpi.primaryLoveLanguage': '主要爱语',
    'coachKpi.option': '选项',
    'coachKpi.timesSelected': '次选中',
    'coachKpi.optionLabel': '选项',
    'coachKpi.loveLanguage': '爱语',
    'coachKpi.timesSelectedHeader': '选中次数',
    'coachKpi.activeClients': '活跃客户',
    'coachKpi.completedClients': '已完成',
    'coachKpi.totalEarnings': '总收入',
    'coachKpi.pendingEarnings': '待结算收入',
    'coachKpi.warnings': '警告',
    'coachKpi.activeWarnings': '活跃警告',
    'coachKpi.yellow': '黄色',
    'coachKpi.orange': '橙色',
    'coachKpi.red': '红色',
    'coachKpi.availableQuestionnaires': '可用问卷',
    'coachKpi.noQuestionnaires': '暂无可用问卷',
    'coachKpi.sendToClient': '发送给客户',
    'coachKpi.submissionHistory': '提交历史',
    'coachKpi.noSubmissions': '暂无提交记录',
    'coachKpi.questionnaire': '问卷',
    'coachKpi.sentTo': '发送给',
    'coachKpi.score': '分数',
    'coachKpi.sendQuestionnaire': '发送问卷',
    'coachKpi.selectClient': '选择客户',
    'coachKpi.selectAssignment': '选择分配...',
    'coachKpi.clientName': '客户姓名',
    'coachKpi.clientEmail': '客户邮箱',
    'coachKpi.generatedLink': '问卷链接',
    'coachKpi.send': '发送',
    'coachKpi.questionnaireSent': '问卷发送成功',
    'coachKpi.questionnaireSendError': '问卷发送失败',
    'coachKpi.confirmations': '确认函',
    'coachKpi.createConfirmation': '创建确认函',
    'coachKpi.noConfirmations': '暂无确认函',
    'coachKpi.clientResponse': '客户回复',
    'coachKpi.copyLink': '复制链接',
    'coachKpi.confirmationType': '类型',
    'coachKpi.typeAgreement': '协议',
    'coachKpi.typeDiscussion': '讨论',
    'coachKpi.typeMemo': '备忘录',
    'coachKpi.typeOther': '其他',
    'coachKpi.expiresInDays': '有效期（天）',
    'coachKpi.content': '内容',
    'coachKpi.confirmationLink': '确认函链接',
    'coachKpi.confirmationCreated': '确认函已创建',
    'coachKpi.confirmationUpdated': '确认函已更新',
    'coachKpi.confirmationCreateError': '创建确认函失败',
    'coachKpi.confirmationAccepted': '已确认',
    'coachKpi.confirmationRejected': '已拒绝',
    'coachKpi.typeBasic': '基本',
    'coachKpi.typeSchedule': '日程',
    'coachKpi.confirmationTitlePlaceholder': '输入确认标题...',
    'coachKpi.confirmationContentPlaceholder': '输入需客户确认的内容...',
    'coachKpi.scheduleEventPlaceholder': '例如：辅导课程',
    'coachKpi.editConfirmation': '编辑确认',
    'coachKpi.selectCandidates': '选择候选人',
    'coachKpi.editCandidates': '候选人',
    'coachKpi.candidatesSaved': '候选人已保存',
    'coachKpi.searchProfiles': '按姓名、邮箱、电话搜索档案...',
    'coachKpi.selectedCandidates': '已选候选人',
    'coachKpi.noCandidatesSelected': '尚未选择候选人',
    'coachKpi.p1ConfirmedHint': 'P1阶段已确认的档案：',
    'coachKpi.previousCandidatesHint': '之前已选择的候选人：',
    'coachKpi.aiGenerate': 'AI生成',
    'coachKpi.aiGenerating': '生成中...',
    'coachKpi.aiContentGenerated': 'AI内容已生成',
    'coachKpi.nextFollowup': '下次',
    'coachKpi.overdue': '逾期',
    'kpi.candidate': '候选人',
    'kpi.candidates': '候选人',
    'kpi.viewRecording': '查看录制',
    'kpi.profileNumber': '档案 #',
    'kpi.clickToView': '点击查看',
    'kpi.status': '状态',
    'kpi.matches': '匹配',
    'kpi.datingSchedules': '约会安排',
    'kpi.appointmentTime': '预约时间',
    'kpi.questionnaireResults': '问答结果',
    'kpi.viewResults': '查看结果',
    'kpi.recordingLink': '录制链接',
    'kpi.photosFiles': '照片 / 文件',
    'kpi.matchedMembers': '匹配会员',
    'coachKpi.followups': '跟进',
    'coachKpi.createFollowup': '创建跟进',
    'coachKpi.noFollowups': '暂无跟进',
    'coachKpi.attempt': '尝试',
    'coachKpi.unknownClient': '未知客户',
    'coachKpi.nextReminder': '下次提醒',
    'coachKpi.retryOf': '重试自',
    'coachKpi.followupType': '类型',
    'coachKpi.postMeeting': '会后跟进',
    'coachKpi.checkIn': '签到',
    'coachKpi.progressReview': '进度审查',
    'coachKpi.custom': '自定义',
    'coachKpi.intervalDays': '间隔（天）',
    'coachKpi.maxAttempts': '最大尝试次数',
    'coachKpi.followupCreated': '跟进已创建',
    'coachKpi.followupUpdated': '跟进已更新',
    'coachKpi.followupCompleted': '跟进已完成',
    'coachKpi.editFollowup': '编辑跟进',
    'coachKpi.followupCreateError': '创建跟进失败',
    'coachKpi.followupSuccess': '跟进标记为成功',
    'coachKpi.followupFailed': '跟进标记为失败，已安排重试',
    'coachKpi.followupOutcomeError': '更新跟进失败',
    'coachKpi.reminders': '提醒',
    'coachKpi.createReminder': '创建提醒',
    'coachKpi.editReminder': '编辑提醒',
    'coachKpi.noReminders': '暂无提醒',
    'coachKpi.reminderCreated': '提醒已创建',
    'coachKpi.reminderUpdated': '提醒已更新',
    'coachKpi.reminderDeleted': '提醒已删除',
    'coachKpi.reminderConfirmed': '提醒已确认',
    'coachKpi.confirmReminder': '确认',
    'coachKpi.deleteReminderConfirm': '确定删除此提醒？',
    'coachKpi.reminderTitlePlaceholder': '输入提醒标题...',
    'coachKpi.reminderType': '类型',
    'coachKpi.triggerDate': '日期',
    'coachKpi.triggerTime': '时间',
    'coachKpi.isRecurring': '循环',
    'coachKpi.recurrenceDays': '重复间隔（天）',
    'coachKpi.clientOptional': '客户（可选）',
    'coachKpi.noClient': '— 无客户 —',
    'coachKpi.recurring': '循环',
    'coachKpi.every': '每',
    'coachKpi.days': '天',
    'coachKpi.message': '消息',
    'coachKpi.titleRequired': '标题 *',
    'coachKpi.followUp': '跟进',
    'coachKpi.appointment': '预约',
    'coachKpi.deadline': '截止',
    'coachKpi.scheduleAppointment': '预约安排',
    'coachKpi.editAppointment': '编辑预约',
    'coachKpi.appointmentScheduled': '已预约',
    'coachKpi.appointmentCompleted': '预约完成',
    'coachKpi.appointmentCreated': '预约已安排',
    'coachKpi.appointmentUpdated': '预约已更新',
    'coachKpi.appointmentDate': '预约日期',
    'coachKpi.appointmentTime': '预约时间',
    'coachKpi.appointmentNotes': '预约备注',
    'coachKpi.appointmentTimezone': '时区',
    'coachKpi.videoMeetings': '视频会议',
    'coachKpi.createMeeting': '创建会议',
    'coachKpi.noMeetings': '暂无视频会议',
    'coachKpi.meeting': '会议',
    'coachKpi.notScheduled': '未安排',
    'coachKpi.duration': '时长',
    'coachKpi.minutes': '分钟',
    'coachKpi.startMeeting': '开始',
    'coachKpi.endMeeting': '结束',
    'coachKpi.meetingTitle': '会议标题...',
    'coachKpi.scheduledAt': '计划时间',
    'coachKpi.meetingCreated': '会议已创建',
    'coachKpi.meetingCreateError': '创建会议失败',
    'coachKpi.generateMeetingLink': '生成会议链接',
    'coachKpi.meetingLinkCopied': '会议链接已复制到剪贴板',
    'coachKpi.meetingLink': '会议链接',
    'coachKpi.viewMeetingLink': '查看会议链接',
    'coachKpi.openMeeting': '打开会议',
    'coachKpi.viewRecording': '查看录像',
    'coachKpi.generatingVideo': '生成视频中...',
    'coachKpi.recordingFailed': '录制失败',
    'coachKpi.addRecording': '添加录像',
    'coachKpi.recordingUrl': '录像链接',
    'coachKpi.recordingSaved': '录像已保存',
    'coachKpi.recordingSaveError': '保存录像失败',
    'coachKpi.recordingUrlRequired': '录像链接不能为空',
    'coachKpi.meetingUpdated': '会议已更新',
    'coachKpi.meetingUpdateError': '更新会议失败',
    'coachKpi.active': '进行中',
    'coachKpi.completed': '已完成',
    'coachKpi.paused': '已暂停',
    'coachKpi.cancelled': '已取消',
    'coachKpi.pending': '待处理',
    'coachKpi.profileActive': '活跃',
    'coachKpi.profileInactive': '不活跃',
    'coachKpi.profileArchived': '已归档',
    'coachKpi.inProgress': '进行中',
    'coachKpi.failed': '失败',
    'coachKpi.submitted': '已提交',
    'coachKpi.approved': '已批准',
    'coachKpi.rejected': '已拒绝',
    'coachKpi.sent': '已发送',
    'coachKpi.viewed': '已查看',
    'coachKpi.expired': '已过期',
    'coachKpi.scheduled': '已安排',
    'coachKpi.success': '成功',
    'coachKpi.accepted': '已接受',
    'coachKpi.succeeded': '成功',
    'coachKpi.matchSucceeded': '已约见',
    'coachKpi.matchInRelationship': '已交往',
    'coachKpi.inContact': '接触中',
    'coachKpi.confirmed': '已确认',
    'coachKpi.meetingStatus': '会面中',
    'coachKpi.chatting': '聊天中',
    'coachKpi.inRelationship': '恋爱中',
    'coachKpi.engaged': '已订婚',
    'coachKpi.noShow': '未出席',
    'coachKpi.skipped': '已跳过',
    'coachKpi.transferred': '已转移',
    'coachKpi.failedLoadAssignment': '加载任务失败',
    'coachKpi.failedLoadPayment': '加载付款详情失败',
    'coachKpi.failedLoadFeedback': '加载反馈失败',
    'coachKpi.failedCreateMatch': '创建匹配失败',
    'coachKpi.failedUpdateMatch': '更新匹配失败',
    'coachKpi.failedCreateSchedule': '创建日程失败',
    'coachKpi.failedUpdateSchedule': '更新日程失败',
    'coachKpi.scheduleCompleted': '日程已完成',
    'coachKpi.editSchedule': '编辑日程',
    'coachKpi.scheduleUpdated': '约会日程更新成功',
    'coachKpi.videoMeetingFallback': '视频会议',
    'coachKpi.followupSuffix': '跟进',
    'coachKpi.datingSchedulePlaceholder': '约会日程',
    'coachKpi.loadingText': '加载中...',
    'coachKpi.supervisorFallback': '主管',
    'coachKpi.allClients': '所有客户',
    'coachKpi.kpiActive': 'KPI进行中',
    'coachKpi.kpiCompleted': 'KPI已完成',
    'coachKpi.noKpi': '无KPI',
    'coachKpi.startKpi': '开始KPI',
    'coachKpi.noClients': '暂无分配客户',
    'coachKpi.assignedProfile': '已分配档案',
    'coachKpi.stagesInProgress': '进行中阶段',
    'coachKpi.progressStages': '进度阶段',
    'coachKpi.stageP1': '深度需求分析 + 匹配方案',
    'coachKpi.stageP2': '见面安排',
    'coachKpi.stageP3': '见面后跟进（未成功）',
    'coachKpi.stageP4': '见面后跟进（成功）',
    'coachKpi.stageP5': '客户满意度',
    'coachKpi.stageP6': '最终审核',
    'coachKpi.current': '当前',
    'coachKpi.viewAllDetails': '查看全部详情',
    'coachKpi.allMembers': '所有会员',
    'coachKpi.viewEditAssignment': '查看/编辑分配',
    'coachKpi.kpiAssignment': 'KPI分配',
    'coachKpi.createAssignment': '创建分配',
    'coachKpi.startKpiForClient': '为该客户开始KPI跟踪',
    'coachKpi.createAssignmentHint': '请从管理员KPI页面创建KPI分配',
    'coachKpi.clientPayment': '客户付款',
    'coachKpi.clientPaymentDesc': '付款阶段和会员升级',
    'coachKpi.paymentFeatureHint': '客户付款详情',
    'coachKpi.serviceWorkflow': '服务流程',
    'coachKpi.selectWorkflow': '-- 选择流程 --',
    'coachKpi.serviceAmount': '服务金额',
    'coachKpi.notes': '备注',
    'coachKpi.assignmentNotesPlaceholder': '可选的分配备注...',
    'coachKpi.selectWorkflowAndClient': '请选择流程和客户',
    'coachKpi.assignmentCreated': '分配创建成功',
    'coachKpi.assignmentCreateFailed': '创建分配失败',
    'coachKpi.currentPlan': '当前套餐',
    'coachKpi.noPlan': '无套餐',
    'coachKpi.totalPaid': '已支付总额',
    'coachKpi.totalOutstanding': '待支付',
    'coachKpi.totalInvoices': '发票总数',
    'coachKpi.kpiPaymentStages': 'KPI付款阶段',
    'coachKpi.commission': '佣金',
    'coachKpi.membershipPlans': '会员套餐',
    'coachKpi.currentPlanLabel': '当前',
    'coachKpi.invoiceHistory': '发票历史',
    'coachKpi.noInvoices': '暂无发票',
    'coachKpi.invoiceNo': '发票号',
    'coachKpi.type': '类型',
    'coachKpi.plan': '套餐',
    'coachKpi.totalAmount': '总计',
    'coachKpi.amountPaid': '已付',
    'coachKpi.paymentStatus': '状态',
    'coachKpi.paymentType': '付款方式',
    'coachKpi.nextPayment': '下次付款',
    'coachKpi.nextPaymentDate': '下次付款日期',
    'coachKpi.progress': '进度',
    'coachKpi.paymentProgress': '付款进度',
    'coachKpi.date': '日期',
    'coachKpi.autoSelectedWorkflow': '已根据客户会员套餐自动选择',
    'coachKpi.noMatchingWorkflow': '没有匹配客户会员套餐的流程',
    'kpi.membershipPlanOptional': '绑定会员套餐（可选）',
    'kpi.noMembershipPlan': '-- 无 --',
    'kpi.bindPlanHint': '将此流程绑定到会员套餐，以便自动选择',

    // Coach KPI - Additional Messages
    'coachKpi.recordingNotAvailable': '无录制URL可用',
    'coachKpi.recordingLoadFailed': '加载视频记录失败',
    'coachKpi.noteSaved': '笔记已保存',
    'coachKpi.noteSaveFailed': '保存笔记失败',
    'coachKpi.assignmentNotFound': '未找到任务',
    'coachKpi.statusUpdated': '状态已更新',
    'coachKpi.statusUpdateFailed': '更新状态失败',
    'coachKpi.appointmentCompletedPrompt': '标记此任务为完成?',
    'coachKpi.appointmentCompletionFailed': '更新失败',
    'coachKpi.appointmentCancelPrompt': '任务已取消',
    'coachKpi.appointmentDeletePrompt': '删除此任务?',
    'coachKpi.appointmentDeleted': '任务已删除',
    'coachKpi.appointmentDeleteFailed': '删除失败',
    'coachKpi.selectClientError': '请选择客户端',
    'coachKpi.matchFailedMessage': '{failCount} 个匹配失败',
    'coachKpi.matchesUpdated': '匹配已更新',
    'coachKpi.reviewLinkGenerationFailed': '审查链接生成失败',
    'coachKpi.contentGenerationFailed': '生成内容失败',
    'coachKpi.saveCandidatesFailed': '保存候选人失败',
    'coachKpi.appointmentSaveFailed': '保存任务失败',

    // 阶段页面功能（allow_page_creation feature）
    'coachKpi.pages': '页面',
    'coachKpi.originalPage': '原始页面',
    'coachKpi.noPagesYet': '暂无页面',
    'coachKpi.addNewPage': '添加新页面',
    'coachKpi.pageNavigation': '第 {current} / {total} 页',
    'coachKpi.previous': '上一页',
    'coachKpi.next': '下一页',
    'coachKpi.editPage': '编辑页面',
    'coachKpi.deletePage': '删除页面',
    'coachKpi.addNote': '添加备注',
    'coachKpi.enterNote': '输入英文备注...',
    'coachKpi.enterNoteZh': '输入中文备注...',
    'coachKpi.noteRequired': '请输入备注',
    'coachKpi.confirmDeletePage': '确定要删除此页面吗？',
    'coachKpi.pageDeleted': '页面已删除',
    'coachKpi.pageCreated': '页面已创建',
    'coachKpi.pageDeliverables': '页面可交付物',
    'coachKpi.original': '原始版本',
    'coachKpi.pageSaved': '页面已保存',
    'coachKpi.noPageDeliverables': '此页面上没有可交付项',
    'coachKpi.titleRequired': '标题是必需的',
    'coachKpi.saveFailed': '保存页面失败',
    'coachKpi.deleteFailed': '删除页面失败',
    'coachKpi.addPageDeliverable': '添加可交付项',
    'coachKpi.pageMultipleDeliverables': '此页面可以有多个可交付项',

    // KPI Profile & Deductions
    'coachKpi.kpiProfile': 'KPI档案',
    'coachKpi.enableKpiProfile': '启用KPI档案',
    'coachKpi.kpiProfileDescription': '启用后，教练获得权重百分比和佣金。禁用后，仅适用扣款。',
    'coachKpi.deductions': '扣款',
    'coachKpi.deductionHistory': '扣款历史',
    'coachKpi.deductionAmount': '扣款金额',
    'coachKpi.deductionNumber': '扣款 #{number}',
    'coachKpi.totalDeductions': '总扣款',
    'coachKpi.noDeductions': '暂无扣款',
    'coachKpi.warnings': '警告',
    'coachKpi.deductionWarning': '扣款处罚已应用',
    'coachKpi.deductionWarningMsg': '此客户的KPI档案已禁用。已应用 ${amount} 的扣款。',
    'coachKpi.kpiProfileEnabled': 'KPI档案已启用 - 教练获得正常佣金',
    'coachKpi.kpiProfileDisabled': 'KPI档案已禁用 - 无收益，仅适用扣款',
    'coachKpi.tabWeeklyContacts': '每周联系',
    'coachKpi.tabRecommendations': '推荐匹配',
    'coachKpi.priorityAlerts': '优先提醒',
    'coachKpi.weeklyContactLog': '每周联系记录',
    'coachKpi.weekOf': '本周起始',
    'coachKpi.user': '用户',
    'coachKpi.tabWarnings': '警告',
    'coachKpi.markRead': '标记已读',
    'coachKpi.resolvedWarnings': '已处理 / 已读',
    'coachKpi.lastContact': '上次联系',
    'coachKpi.lastMatch': '最近匹配',
    'coachKpi.lastSchedule': '最近约会',
    'coachKpi.priority': '优先级',
    'coachKpi.daysAgo': '天前',
    'coachKpi.neverContacted': '从未联系',
    'coachKpi.noMeetingThisMonth': '本月无见面',
    'coachKpi.urgent': '紧急',
    'coachKpi.needsMeeting': '需要见面',
    'coachKpi.contactedThisWeek': '本周已联系',
    'coachKpi.notContactedThisWeek': '本周未联系',
    'coachKpi.contactsThisMonth': '次本月联系',
    'coachKpi.meetingsThisMonth': '次本月见面',
    'coachKpi.logContact': '记录联系',
    'coachKpi.contactLogSaved': '联系记录已保存',
    'coachKpi.noAssignedClients': '暂无分配客户',
    'coachKpi.autoRecommendations': '自动推荐',
    'coachKpi.autoRecommendationsDesc': '选择一位客户查看兼容的候选人推荐。已匹配过的档案会自动排除。',
    'coachKpi.selectClient': '选择客户',
    'coachKpi.chooseClient': '-- 选择客户 --',
    'coachKpi.noRecommendations': '未找到兼容的候选人',
    'coachKpi.yearsOld': '岁',
    'coachKpi.matchCreated': '匹配创建成功',

    // 3-Dimension Partner Preferences (Chinese)
    'preferences.idealStandard': '理想标准',
    'preferences.acceptableRange': '可接受范围',
    'preferences.absoluteBottomLine': '绝对底线',
    'preferences.dimensionHint': '请为每个维度填写您的偏好。默认显示可接受范围。',

    // Leader Coach Review (Chinese)
    'leaderCoach.title': '教练领导评审',
    'leaderCoach.description': '在主管批准前审核已提交的阶段',
    'leaderCoach.pending': '待审核',
    'leaderCoach.approved': '已批准',
    'leaderCoach.rejected': '已拒绝',
    'leaderCoach.reviewing': '审核中',
    'leaderCoach.client': '客户',
    'leaderCoach.coach': '教练',
    'leaderCoach.submittedOn': '提交时间',
    'leaderCoach.deliverables': '可交付项',
    'leaderCoach.reviewStage': '审核阶段',
    'leaderCoach.reviewAction': '审核操作',
    'leaderCoach.approve': '批准',
    'leaderCoach.reject': '拒绝',
    'leaderCoach.feedback': '反馈',
    'leaderCoach.rejectionReasonRequired': '请提供拒绝原因',
    'leaderCoach.optionalFeedback': '为教练提供反馈（可选）',
    'leaderCoach.submitReview': '提交审核',
    'leaderCoach.review': '审核',
    'leaderCoach.feedbackRequired': '拒绝时需要反馈',
    'leaderCoach.stageApprovedSuccess': '阶段批准成功',
    'leaderCoach.stageRejectedSuccess': '阶段拒绝成功',
    'leaderCoach.noStagesToReview': '没有待审核的阶段',
    'leaderCoach.noStages': '没有阶段',
    'leaderCoach.supervisorStatus': '主管状态',
    'leaderCoach.totalReviewed': '总审查数',
    'leaderCoach.coachesUnderReview': '审查中的教练',
    'leaderCoach.avgReviewTime': '平均审查时间',
    'leaderCoach.noDataYet': '暂无审查记录',
    'leaderCoach.startReviewingStages': '开始审查教练阶段以查看您的绩效指标',

    // Leader Coach KPI Performance - Dashboard Stats (Chinese)
    'leaderCoach.kpiPerformance': 'KPI绩效',
    'leaderCoach.kpiPerformanceSubtitle': '查看您的团队绩效和KPI指标',
    'leaderCoach.totalAssignments': '总分配数',
    'leaderCoach.activeAssignments': '活跃中',
    'leaderCoach.completedAssignments': '已完成',
    'leaderCoach.pausedAssignments': '已暂停',
    'leaderCoach.coachesUnderManagement': '管理中的教练',
    'leaderCoach.avgSatisfaction': '平均满意度',
    'leaderCoach.refresh': '刷新',
    'leaderCoach.approvalWorkflow': '批准流程',
    'leaderCoach.approvalPending': '待审核',
    'leaderCoach.approvalLeaderCoach': '领导教练审核',
    'leaderCoach.approvalSupervisor': '主管批准',
    'leaderCoach.approvalCompleted': '已批准',

    // Admin KPI - Messages
    'adminKpi.deliverableApproved': '可交付物已批准',
    'adminKpi.deliverableUnapproved': '可交付物未批准',
    'adminKpi.deliverableUpdateFailed': '更新可交付物失败',
    'adminKpi.copyLinkSuccess': '已复制!',
    'adminKpi.copyLinkFailed': '复制失败，这是文本:',
    'adminKpi.followupDeletePrompt': '删除此后续?',
    'adminKpi.followupDeleted': '已删除',
    'adminKpi.followupDeleteFailed': '失败',
    'adminKpi.linkCopied': '链接已复制',
    'adminKpi.confirmationDeletePrompt': '删除此确认?',
    'adminKpi.confirmationDeleted': '已删除',
    'adminKpi.confirmationDeleteFailed': '失败',

    // Supervisor KPI - Messages
    'supervisorKpi.failedLoadAssignmentDetail': '加载任务详情失败',
    'supervisorKpi.failedUpdateReview': '更新审查失败',
    'supervisorKpi.deliverableApproved': '可交付物已批准',
    'supervisorKpi.deliverableUnapproved': '可交付物未批准',
    'supervisorKpi.deliverableUpdateFailed': '更新可交付物失败',
    'supervisorKpi.failedAddFeedback': '添加反馈失败',
    'supervisorKpi.recordingNotAvailable': '无录制URL可用',
    'supervisorKpi.recordingLoadFailed': '加载视频记录失败',

    // Settings - Agora
    'settings.tabAgora': '视频会议',
    'settings.agoraTitle': '视频会议设置',
    'settings.agoraDesc': '配置声网视频会议集成，用于KPI绩效会议。',
    'settings.agoraEnable': '启用声网视频会议',
    'settings.agoraEnableDesc': '允许教练与客户创建视频会议',
    'settings.agoraAppId': '声网 App ID',
    'settings.agoraAppIdHint': '在声网控制台 > 项目管理中找到',
    'settings.agoraAppCert': 'App 证书',
    'settings.agoraAppCertHint': '用于令牌认证（生产环境推荐）',
    'settings.agoraTokenExpiry': '令牌有效期（秒）',
    'settings.agoraTokenExpiryHint': '每个会议令牌的有效时长（默认：3600 = 1小时）',
    'settings.agoraInfo': '要使用声网视频会议，请在 agora.io 创建免费账户并创建项目以获取 App ID 和 App 证书。教练将能够在KPI绩效中创建会议时生成视频会议链接。',
    'settings.saveAgora': '保存视频会议设置',
    'settings.agoraSaved': '声网设置已成功保存',
    'settings.agoraSaveError': '保存声网设置失败',
    'settings.agoraCustomerId': '声网客户 ID',
    'settings.agoraCustomerIdHint': '声网控制台 > RESTful API（云录制必需）',
    'settings.agoraCustomerSecret': '声网客户密钥',
    'settings.agoraCustomerSecretHint': '声网控制台 > RESTful API（云录制必需）',

    // Settings - Agora RTC (Chinese)
    'settings.tabAgoraRTC': '声网RTC',
    'settings.agoraRTCTitle': '声网RTC设置',
    'settings.agoraRTCDesc': '配置声网RTC（实时通信）用于KPI绩效视频会议。',
    'settings.agoraRTCEnable': '启用声网RTC视频会议',
    'settings.agoraRTCEnableDesc': '允许教练使用声网RTC与客户创建视频会议',
    'settings.agoraAppId': 'App ID',
    'settings.agoraAppIdHint': '在声网控制台 > 项目管理中找到',
    'settings.agoraAppCertificate': 'App Certificate',
    'settings.agoraAppCertificateHint': '用于生成RTC令牌',
    'settings.agoraTokenExpiry': '令牌有效期（秒）',
    'settings.agoraTokenExpiryHint': '每个会议令牌的有效时长（默认：3600 = 1小时，最长：604800 = 7天）',
    'settings.agoraCloudRecording': '声网云录制（可选）',
    'settings.agoraCloudRecordingDesc': '提供声网RESTful API凭证以启用云录制功能。',

    'settings.awsS3Storage': 'AWS S3录制存储',
    'settings.awsS3Desc': '配置AWS S3用于存储声网会议录像。',
    'settings.enableAwsS3': '启用AWS S3存储',
    'settings.s3AccessKeyId': 'S3 Access Key ID',
    'settings.s3SecretAccessKey': 'S3 Secret Access Key',
    'settings.s3Bucket': 'S3 存储桶名称',
    'settings.s3Region': 'S3 地域',
    'settings.s3RegionHint': '选择S3存储桶所在的AWS地域',
    'settings.saveAgoraRTC': '保存声网RTC设置',
    'settings.agoraRTCSaved': '声网RTC设置已成功保存',
    'settings.agoraRTCSaveError': '保存声网RTC设置失败',
    'settings.agoraInfoTitle': '声网RTC入门指南',
    'settings.agoraChat': '会议聊天',
    'settings.agoraChatDesc': '启用视频会议中的文字聊天',
    'settings.agoraVirtualBg': '虚拟背景',
    'settings.agoraVirtualBgDesc': '允许参与者使用虚拟背景',
    'settings.agoraVirtualBgBlur': '启用背景模糊',
    'settings.agoraVirtualBgImages': '自定义背景图片',
    'meeting.virtualBackground': '虚拟背景',
    'meeting.bgNone': '无',
    'meeting.bgBlur': '模糊',
    'meeting.chat': '聊天',

    // Admin Trends
    'trends.totalTrends': '总动态数',
    'trends.pendingApproval': '待审核',
    'trends.totalLikes': '总点赞数',
    'trends.totalComments': '总评论数',
    'trends.trends': '动态',
    'trends.categories': '分类',
    'trends.settings': '设置',
    'trends.searchTrends': '搜索动态...',
    'trends.allStatus': '所有状态',
    'trends.published': '已发布',
    'trends.pending': '待审核',
    'trends.rejected': '已拒绝',
    'trends.allCategories': '所有分类',
    'trends.postNewTrend': '发布新动态',
    'trends.trend': '动态',
    'trends.type': '类型',
    'trends.category': '分类',
    'trends.author': '作者',
    'trends.engagement': '互动',
    'trends.status': '状态',
    'trends.actions': '操作',
    'trends.approve': '批准',
    'trends.reject': '拒绝',
    'trends.noTrendsFound': '未找到动态',
    'trends.addCategory': '添加分类',
    'trends.editCategory': '编辑分类',
    'trends.active': '启用',
    'trends.inactive': '停用',
    'trends.contentModeration': '内容审核设置',
    'trends.blacklistWords': '黑名单词汇',
    'trends.blacklistDesc': '包含这些词汇的帖子将自动标记为待审核',
    'trends.addWordPlaceholder': '添加黑名单词汇...',
    'trends.contentPermissions': '内容权限',
    'trends.allowClientPosts': '允许客户发布动态',
    'trends.requireApproval': '客户发布需要管理员审核',
    'trends.allowPhotos': '允许上传图片',
    'trends.allowVideos': '允许上传视频',
    'trends.allowPolls': '允许创建投票',
    'trends.maxContentLength': '最大内容长度',
    'trends.characters': '字符',
    'trends.contentPolicy': '内容政策',
    'trends.contentPolicyDesc': '系统自动屏蔽包含联系信息（电话号码、邮箱地址、网址）的帖子，并标记可能不当的图片进行人工审核。',
    'trends.categoryName': '分类名称',
    'trends.description': '描述',
    'trends.color': '颜色',
    'trends.editTrend': '编辑动态',
    'trends.title': '标题',
    'trends.contentType': '内容类型',
    'trends.text': '文字',
    'trends.photo': '图片',
    'trends.audio': '音频',
    'trends.video': '视频',
    'trends.poll': '投票',
    'trends.selectCategory': '选择分类',
    'trends.content': '内容',
    'trends.mediaUrl': '媒体链接',
    'trends.pollOptions': '投票选项',
    'trends.min2': '至少2个',
    'trends.addOption': '添加选项...',
    'trends.pinToTop': '置顶此动态',
    'trends.publish': '发布',
    'trends.categoryNameRequired': '请填写分类名称',
    'trends.categoryUpdated': '分类已更新',
    'trends.categoryCreated': '分类已创建',
    'trends.cannotDeleteCategoryWithTrends': '无法删除有动态的分类',
    'trends.confirmDeleteCategory': '确定要删除此分类吗？',
    'trends.categoryDeleted': '分类已删除',
    'trends.fillAllFields': '请填写所有必填项',
    'trends.pollMinOptions': '投票至少需要2个选项',
    'trends.trendUpdated': '动态已更新',
    'trends.trendPublished': '动态已发布',
    'trends.trendDeleted': '动态已删除',
    'trends.confirmDeleteTrend': '确定要删除此动态吗？',
    'trends.trendApproved': '动态已批准',
    'trends.trendRejected': '动态已拒绝',
    'trends.pinUpdated': '置顶状态已更新',
    'trends.settingsSaved': '设置保存成功',

    // Client Trends
    'clientTrends.justNow': '刚刚',
    'clientTrends.minutesAgo': '{n}分钟前',
    'clientTrends.hoursAgo': '{n}小时前',
    'clientTrends.daysAgo': '{n}天前',
    'clientTrends.weeksAgo': '{n}周前',
    'clientTrends.monthsAgo': '{n}个月前',
    'clientTrends.yearsAgo': '{n}年前',
    'clientTrends.newest': '最新',
    'clientTrends.oldest': '最早',
    'clientTrends.mostPopular': '最热门',
    'clientTrends.mostComments': '最多评论',
    'clientTrends.latest': '最新',
    'clientTrends.filter': '筛选',
    'clientTrends.search': '搜索',
    'clientTrends.sort': '排序',
    'clientTrends.searchPlaceholder': '搜索标题、内容...',
    'clientTrends.cancel': '取消',
    'clientTrends.contentType': '内容类型',
    'clientTrends.all': '全部',
    'clientTrends.text': '文字',
    'clientTrends.photo': '图片',
    'clientTrends.video': '视频',
    'clientTrends.poll': '投票',
    'clientTrends.reset': '重置',
    'clientTrends.applyFilters': '应用筛选',
    'clientTrends.categoryDatingTips': '约会技巧',
    'clientTrends.categoryEvents': '活动',
    'clientTrends.categoryGeneral': '综合',
    'clientTrends.categoryLifestyle': '生活方式',
    'clientTrends.categorySuccessStories': '成功故事',
    'clientTrends.foundPosts': '找到 {n} 条动态',
    'clientTrends.clearAll': '清除全部',
    'clientTrends.noPosts': '暂无动态',
    'clientTrends.noPostsDesc': '这里还没有内容，过会儿再来看看吧',
    'clientTrends.endOfFeed': '--- 已经到底啦 ---',
    'clientTrends.comments': '评论',
    'clientTrends.noComments': '暂无评论，来说点什么吧',
    'clientTrends.loading': '加载中...',
    'clientTrends.anonymousUser': '匿名用户',
    'clientTrends.user': '用户',
    'clientTrends.underReview': '(审核中)',
    'clientTrends.commentPlaceholder': '写评论...',
    'clientTrends.report': '举报',
    'clientTrends.reportSubmitted': '举报已提交',
    'clientTrends.collapse': '收起',
    'clientTrends.readMore': '展开全文',
    'clientTrends.me': '我',
    'clientTrends.nVoted': '{n} 人已投票',
    'clientTrends.nVotes': '{n} 票',

    // Client Profile Page
    'profile.loading': '加载中...',
    'profile.notFound': '未找到资料',
    'profile.loadFailed': '加载资料失败',
    'profile.goBack': '返回',
    'profile.verified': '已认证',
    'profile.yearsOld': '{n}岁',
    'profile.noBio': '暂无简介',
    'profile.sendMessage': '发消息',
    'profile.like': '喜欢',
    'profile.liked': '已喜欢',
    'profile.dislike': '不喜欢',
    'profile.matchPending': '匹配中',
    'profile.gallery': '相册',
    'profile.noPhotos': '暂无照片',
    'profile.details': '详细资料',
    'profile.matePreferences': '择偶条件',
    'profile.share': '分享',
    'profile.report': '举报',
    'profile.block': '拉黑',
    'profile.cancel': '取消',
    'profile.male': '男',
    'profile.female': '女',
    'profile.cm': 'CM',
    'profile.kg': 'KG',
    'profile.category.personal': '个人信息',
    'profile.category.background': '背景',
    'profile.category.family': '家庭',
    'profile.category.general': '基本信息',
    'profile.category.lifestyle': '生活方式',
    'profile.category.preferences': '择偶要求',
    'profile.zodiac.capricorn': '摩羯座',
    'profile.zodiac.aquarius': '水瓶座',
    'profile.zodiac.pisces': '双鱼座',
    'profile.zodiac.aries': '白羊座',
    'profile.zodiac.taurus': '金牛座',
    'profile.zodiac.gemini': '双子座',
    'profile.zodiac.cancer': '巨蟹座',
    'profile.zodiac.leo': '狮子座',
    'profile.zodiac.virgo': '处女座',
    'profile.zodiac.libra': '天秤座',
    'profile.zodiac.scorpio': '天蝎座',
    'profile.zodiac.sagittarius': '射手座',

    // ── Client Home Page ──
    'clientHome.filter': '筛选',
    'clientHome.sort': '排序',
    'clientHome.searchPlaceholder': '搜索姓名、城市...',
    'clientHome.searchLabel': '搜索',
    'clientHome.searchNameIdPlaceholder': '姓名或线下ID',
    'clientHome.clear': '清除',
    'clientHome.cancel': '取消',
    'clientHome.ageRange': '年龄范围',
    'clientHome.heightRange': '身高范围 (cm)',
    'clientHome.region': '地区',
    'clientHome.allCountries': '全部国家',
    'clientHome.allStates': '全部省/州',
    'clientHome.allCities': '全部城市',
    'clientHome.china': '中国',
    'clientHome.usa': '美国',
    'clientHome.canada': '加拿大',
    'clientHome.australia': '澳大利亚',
    'clientHome.uk': '英国',
    'clientHome.japan': '日本',
    'clientHome.southKorea': '韩国',
    'clientHome.singapore': '新加坡',
    'clientHome.malaysia': '马来西亚',
    'clientHome.taiwan': '台湾',
    'clientHome.hongKong': '香港',
    'clientHome.stateProvince': '省/州',
    'clientHome.city': '城市',
    'clientHome.hasPhoto': '有照片',
    'clientHome.online': '在线',
    'clientHome.reset': '重置',
    'clientHome.applyFilters': '应用筛选',
    'clientHome.foundMembers': '找到 {n} 位会员',
    'clientHome.clearAll': '清除全部',
    'clientHome.noMembers': '未找到会员',
    'clientHome.adjustFilters': '尝试调整筛选条件或清除搜索',
    'clientHome.noMembersYet': '暂时没有可浏览的会员',
    'clientHome.allShown': '已显示全部会员',
    'clientHome.defaultSort': '默认',
    'clientHome.recentlyActive': '最近活跃',
    'clientHome.ageAsc': '年龄↑',
    'clientHome.ageDesc': '年龄↓',
    'clientHome.heightAsc': '身高↑',
    'clientHome.heightDesc': '身高↓',
    'clientHome.member': '会员',
    'clientHome.professional': '专业人士',
    'clientHome.yearsOld': '{n}岁',
    'clientHome.filters': '筛选',
    'clientHome.newest': '最新',
    'clientHome.hottest': '最热',
    'clientHome.featured': '精选',
    'clientHome.sortBy': '排序方式',
    'clientHome.apply': '应用',
    'clientHome.filtersSort': '筛选和排序',

    // ── Client Dashboard Page ──
    'clientDash.editProfile': '编辑资料',
    'clientDash.editProfileDesc': '完善个人信息',
    'clientDash.myAlbum': '我的相册',
    'clientDash.myAlbumDesc': '管理照片',
    'clientDash.myTrends': '我的动态',
    'clientDash.myTrendsDesc': '查看发布内容',
    'clientDash.whoViewedMe': '谁看过我',
    'clientDash.whoViewedMeDesc': '查看访客记录',
    'clientDash.followingFans': '关注与粉丝',
    'clientDash.followingFansDesc': '互动管理',
    'clientDash.matchmaking': '匹配牵线',
    'clientDash.matchmakingDesc': '查看匹配',
    'clientDash.dateSchedule': '约会日程',
    'clientDash.dateScheduleDesc': '管理约会',
    'clientDash.changePassword': '更改密码',
    'clientDash.changePasswordDesc': '账户安全',
    'clientDash.settings': '设置',
    'clientDash.settingsDesc': '密码、语言和偏好设置',
    'clientDash.changeLanguage': '切换语言',
    'clientDash.changeLanguageDesc': '切换显示语言',
    'clientDash.visitors': '访客',
    'clientDash.following': '关注',
    'clientDash.likes': '喜欢',
    'clientDash.myMatchmaker': '我的红娘',
    'clientDash.inService': '服务中',
    'clientDash.toBeAssigned': '待分配',
    'clientDash.logout': '退出登录',
    'clientDash.confirmLogout': '确定要退出登录吗？',
    'clientDash.user': '用户',
    'clientDash.yearsOld': '，{n}岁',
    'clientDash.justNow': '刚刚',
    'clientDash.minutesAgo': '{n}分钟前',
    'clientDash.hoursAgo': '{n}小时前',
    'clientDash.daysAgo': '{n}天前',
    'clientDash.noPhotos': '暂无照片',
    'clientDash.uploadPhotos': '上传照片让更多人认识你',
    'clientDash.totalPhotos': '共 {n} 张照片',
    'clientDash.noTrends': '暂无动态',
    'clientDash.postFirstTrend': '去动态广场发布你的第一条动态吧',
    'clientDash.totalTrends': '共 {n} 条动态',
    'clientDash.noVisitors': '暂无访客',
    'clientDash.improveProfile': '完善资料提高曝光率',
    'clientDash.totalVisitors': '共 {n} 位访客',
    'clientDash.myFollowing': '我的关注',
    'clientDash.myFans': '我的粉丝',
    'clientDash.noFollowing': '暂无关注',
    'clientDash.browseToFollow': '浏览用户列表关注感兴趣的人',
    'clientDash.noFans': '暂无粉丝',
    'clientDash.completeTips': '完善资料吸引更多粉丝',
    'clientDash.noMatches': '暂无匹配',
    'clientDash.matchmakerSearching': '红娘正在为您寻找合适的匹配',
    'clientDash.totalMatches': '共 {n} 个匹配',
    'clientDash.unknown': '未知',
    'clientDash.matchPercent': '{n}% 匹配',
    'clientDash.noDates': '暂无约会',
    'clientDash.matchmakerArranging': '红娘会为您安排合适的约会',
    'clientDash.totalDates': '共 {n} 个约会',
    'clientDash.fillAllFields': '请填写所有字段',
    'clientDash.minPasswordLength': '新密码至少6个字符',
    'clientDash.passwordMismatch': '两次输入的密码不一致',
    'clientDash.passwordChanged': '密码修改成功！',
    'clientDash.passwordChangeFailed': '密码修改失败，请重试',
    'clientDash.currentPassword': '当前密码',
    'clientDash.currentPasswordPlaceholder': '请输入当前密码',
    'clientDash.newPassword': '新密码',
    'clientDash.newPasswordPlaceholder': '请输入新密码（至少6位）',
    'clientDash.confirmNewPassword': '确认新密码',
    'clientDash.confirmPasswordPlaceholder': '请再次输入新密码',
    'clientDash.saving': '保存中...',
    'clientDash.confirmChange': '确认修改',
    'clientDash.albumPhotoDesc': '您的照片将展示在这里，让更多人看到你',
    'clientDash.trendsDesc': '查看您发布的所有动态内容',
    'clientDash.visitorsDesc': '查看最近浏览过您资料的访客',
    'clientDash.followingFansFullDesc': '管理您的关注列表和粉丝',
    'clientDash.matchmakingFullDesc': '查看红娘为您推荐的匹配',
    'clientDash.scheduleFullDesc': '管理您的约会安排和日程',
    'clientDash.statusPending': '待确认',
    'clientDash.statusAccepted': '已接受',
    'clientDash.statusDeclined': '已拒绝',
    'clientDash.statusConfirmed': '已确认',
    'clientDash.statusCompleted': '已完成',
    'clientDash.statusCancelled': '已取消',
    'clientDash.statusRescheduled': '已改期',
    'clientDash.statusNoShow': '未出席',
    'clientDash.typeText': '文字',
    'clientDash.typeImage': '图片',
    'clientDash.typeVideo': '视频',
    'clientDash.typePoll': '投票',

    // ── Client Messages Page ──
    'clientMessages.inbox': '收件箱',
    'clientMessages.sent': '已发送',
    'clientMessages.notifications': '通知',
    'clientMessages.selectConvo': '选择一个对话',
    'clientMessages.selectFromLeft': '从左侧选择对话开始聊天',
    'clientMessages.noMessages': '还没有消息，发送第一条消息开始聊天吧',
    'clientMessages.typeMessage': '输入消息...',
    'clientMessages.me': '我',
    'clientMessages.noConversations': '暂无消息',
    'clientMessages.noSentMessages': '你还没有发送过消息',
    'clientMessages.noReceivedMessages': '还没有收到消息，你的专属陪跑老师会在这里与你联系',
    'clientMessages.noNotifications': '暂无通知',
    'clientMessages.notificationsInfo': '有新的匹配或系统消息时会在这里通知你',

    // ── Client Edit Profile Page ──
    'clientEdit.editProfile': '编辑资料',
    'clientEdit.save': '保存',
    'clientEdit.saving': '保存中...',
    'clientEdit.photoHint': '点击相机图标更换照片',
    'clientEdit.photoUpdated': '照片已更新！',
    'clientEdit.photoFailed': '照片上传失败，请重试',
    'clientEdit.profileUpdated': '资料已更新！',
    'clientEdit.saveFailed': '保存失败，请重试',
    'clientEdit.adminOnly': '*仅管理员可改',
    'clientEdit.pleaseSelect': '请选择',
    'clientEdit.tab.basic': '基本资料',
    'clientEdit.tab.details': '详细资料',
    'clientEdit.tab.preferences': '择偶条件',
    'clientEdit.tab.contact': '联系方式',
    'clientEdit.basicDesc': '完善基本信息，让更多人了解你',
    'clientEdit.detailsDesc': '补充详细资料，提高匹配精准度',
    'clientEdit.preferencesDesc': '设置择偶条件，精准匹配心仪对象',
    'clientEdit.contactDesc': '管理联系方式，方便红娘联系你',
    'clientEdit.nickname': '昵称',
    'clientEdit.nicknamePlaceholder': '输入昵称',
    'clientEdit.gender': '性别',
    'clientEdit.male': '男',
    'clientEdit.female': '女',
    'clientEdit.birthday': '生日',
    'clientEdit.height': '身高',
    'clientEdit.weight': '体重',
    'clientEdit.workCity': '地区',
    'clientEdit.cityPlaceholder': '所在城市',
    'clientEdit.maritalStatus': '婚姻状况',
    'clientEdit.education': '学历',
    'clientEdit.monthlyIncome': '月薪',
    'clientEdit.jobType': '工作类型',
    'clientEdit.housing': '住房情况',
    'clientEdit.car': '购车情况',
    'clientEdit.marriageTimeline': '期望结婚时间',
    'clientEdit.bio': '个人简介',
    'clientEdit.bioPlaceholder': '简单介绍一下自己...',
    'clientEdit.familyStatus': '家庭状况',
    'clientEdit.children': '子女情况',
    'clientEdit.wantKids': '是否要小孩',
    'clientEdit.parentsStatus': '父母状况',
    'clientEdit.siblings': '兄弟姐妹',
    'clientEdit.personalTraits': '个人特征',
    'clientEdit.zodiac': '星座',
    'clientEdit.ethnicity': '民族',
    'clientEdit.religion': '宗教',
    'clientEdit.hometown': '籍贯',
    'clientEdit.hometownPlaceholder': '输入籍贯',
    'clientEdit.lifestyle': '生活习惯',
    'clientEdit.smoking': '吸烟',
    'clientEdit.drinking': '饮酒',
    'clientEdit.sleep': '作息',
    'clientEdit.exercise': '锻炼',
    'clientEdit.hobbies': '兴趣爱好',
    'clientEdit.languages': '语言',
    'clientEdit.other': '其他',
    'clientEdit.relocation': '考虑异地',
    'clientEdit.purpose': '报名目的',
    'clientEdit.lifePriority': '生活比重',
    'clientEdit.basicRequirements': '基本要求',
    'clientEdit.prefAgeRange': '年龄范围',
    'clientEdit.prefHeightRange': '身高范围',
    'clientEdit.prefWorkCity': '工作地区',
    'clientEdit.prefCityPlaceholder': '期望城市',
    'clientEdit.singleSelect': '单选条件',
    'clientEdit.prefMinEducation': '最低学历',
    'clientEdit.prefMinIncome': '最低月薪',
    'clientEdit.prefMarriageTimeline': '结婚时间',
    'clientEdit.multiSelect': '多选条件',
    'clientEdit.prefMarital': '婚况',
    'clientEdit.prefHousing': '住房',
    'clientEdit.prefCar': '车子',
    'clientEdit.prefSmoking': '吸烟',
    'clientEdit.prefChildren': '子女',
    'clientEdit.otherRequirements': '其他',
    'clientEdit.prefOther': '其他要求',
    'clientEdit.prefOtherPlaceholder': '对另一半的其他期望...',
    'clientEdit.phone': '手机',
    'clientEdit.phonePlaceholder': '输入手机号',
    'clientEdit.wechat': '微信号',
    'clientEdit.wechatPlaceholder': '输入微信号',
    'clientEdit.email': '邮箱',
    'clientEdit.marital.single': '未婚',
    'clientEdit.marital.divorcePending': '离婚（处理中）',
    'clientEdit.marital.divorced': '离婚',
    'clientEdit.marital.widowed': '丧偶',
    'clientEdit.marital.separated': '分居',
    'clientEdit.edu.juniorHigh': '初中',
    'clientEdit.edu.highSchool': '高中',
    'clientEdit.edu.university': '大学',
    'clientEdit.edu.universityDropout': '大学肄业',
    'clientEdit.edu.masters': '硕士',
    'clientEdit.edu.doctorate': '博士',
    'clientEdit.edu.preferNot': '暂不分享',
    'clientEdit.income.below5k': '少于5k起',
    'clientEdit.income.5kTo15k': '5k~15k',
    'clientEdit.income.15kTo30k': '15k~30k',
    'clientEdit.income.30kTo50k': '30k~50k',
    'clientEdit.income.above50k': '50k以上',
    'clientEdit.income.preferNot': '暂不分享',
    'clientEdit.job.fullTime': '全职',
    'clientEdit.job.partTime': '兼职',
    'clientEdit.job.freelance': '自由职业',
    'clientEdit.job.businessOwner': '企业主/老板',
    'clientEdit.job.student': '学生',
    'clientEdit.job.it': 'IT/互联网/通信',
    'clientEdit.job.finance': '金融/银行/保险',
    'clientEdit.job.realEstate': '房地产/建筑',
    'clientEdit.job.trade': '贸易/零售/批发',
    'clientEdit.job.service': '服务业',
    'clientEdit.job.media': '文化/传媒/娱乐',
    'clientEdit.job.medical': '医疗/护理/制药',
    'clientEdit.job.education': '教育/培训',
    'clientEdit.job.government': '政府/事业单位',
    'clientEdit.job.legal': '法律/咨询',
    'clientEdit.job.manufacturing': '制造业/工厂',
    'clientEdit.job.transport': '交通/运输/物流',
    'clientEdit.job.agriculture': '农业/林业/牧业',
    'clientEdit.job.military': '军人/警察',
    'clientEdit.job.retired': '退休',
    'clientEdit.job.unemployed': '待业/无业',
    'clientEdit.job.other': '其他',
    'clientEdit.housing.own': '有房',
    'clientEdit.housing.none': '没有房',
    'clientEdit.housing.overseas': '有房在国外',
    'clientEdit.housing.withParents': '与父母同住',
    'clientEdit.housing.canAfford': '有购房能力',
    'clientEdit.housing.preferNot': '暂不分享',
    'clientEdit.car.own': '有车',
    'clientEdit.car.none': '暂时没车',
    'clientEdit.car.cantDrive': '不会开车',
    'clientEdit.marriage.anytime': '随时',
    'clientEdit.marriage.halfYear': '半年',
    'clientEdit.marriage.oneYear': '一年',
    'clientEdit.marriage.twoYears': '两年',
    'clientEdit.marriage.threeYears': '三年',
    'clientEdit.children.none': '没有',
    'clientEdit.children.withSelf': '有（随自己）',
    'clientEdit.children.withOther': '有（随对方）',
    'clientEdit.children.independent': '有（已独立）',
    'clientEdit.wantKids.yes': '要生小孩',
    'clientEdit.wantKids.no': '不生小孩',
    'clientEdit.wantKids.either': '无所谓',
    'clientEdit.parents.bothAlive': '父母均健在',
    'clientEdit.parents.motherOnly': '只有母亲健在',
    'clientEdit.parents.fatherOnly': '只有父亲健在',
    'clientEdit.parents.bothPassed': '父母均已离世',
    'clientEdit.siblings.onlyChild': '独生子女',
    'clientEdit.zodiac.aries': '白羊座',
    'clientEdit.zodiac.taurus': '金牛座',
    'clientEdit.zodiac.gemini': '双子座',
    'clientEdit.zodiac.cancer': '巨蟹座',
    'clientEdit.zodiac.leo': '狮子座',
    'clientEdit.zodiac.virgo': '处女座',
    'clientEdit.zodiac.libra': '天秤座',
    'clientEdit.zodiac.scorpio': '天蝎座',
    'clientEdit.zodiac.sagittarius': '射手座',
    'clientEdit.zodiac.capricorn': '摩羯座',
    'clientEdit.zodiac.aquarius': '水瓶座',
    'clientEdit.zodiac.pisces': '双鱼座',
    'clientEdit.ethnicity.asian': '亚洲人',
    'clientEdit.ethnicity.white': '白人',
    'clientEdit.ethnicity.mexican': '墨西哥人',
    'clientEdit.ethnicity.alaskan': '阿拉斯加人',
    'clientEdit.ethnicity.black': '黑人',
    'clientEdit.ethnicity.other': '其他',
    'clientEdit.religion.christianity': '基督宗教',
    'clientEdit.religion.islam': '伊斯兰教',
    'clientEdit.religion.hinduism': '印度教',
    'clientEdit.religion.buddhism': '佛教',
    'clientEdit.religion.judaism': '犹太教',
    'clientEdit.religion.none': '无宗教信仰',
    'clientEdit.religion.catholic': '天主教',
    'clientEdit.smoking.no': '不吸',
    'clientEdit.smoking.occasionally': '偶尔吸',
    'clientEdit.smoking.packADay': '一天一包',
    'clientEdit.smoking.whenever': '有烟就吸',
    'clientEdit.smoking.quitting': '正在戒烟',
    'clientEdit.smoking.quit': '已经戒了',
    'clientEdit.drinking.never': '滴酒不沾',
    'clientEdit.drinking.sometimes': '有时喝一些',
    'clientEdit.drinking.enjoy': '喜欢来两杯',
    'clientEdit.drinking.daily': '好酒量天天喝',
    'clientEdit.drinking.quitting': '正在戒酒',
    'clientEdit.drinking.quit': '已经戒了',
    'clientEdit.sleep.regular': '早睡早起很规律',
    'clientEdit.sleep.nightOwl': '经常夜猫子',
    'clientEdit.sleep.earlyBird': '总是早起鸟',
    'clientEdit.sleep.occasional': '偶尔懒散',
    'clientEdit.sleep.irregular': '没有规律',
    'clientEdit.exercise.daily': '每天锻炼',
    'clientEdit.exercise.weekly': '每周至少一次',
    'clientEdit.exercise.monthly': '每月几次',
    'clientEdit.exercise.noTime': '没时间锻炼',
    'clientEdit.exercise.focused': '集中时间锻炼',
    'clientEdit.exercise.none': '不喜欢锻炼',
    'clientEdit.hobby.sports': '运动',
    'clientEdit.hobby.food': '美食',
    'clientEdit.hobby.music': '音乐',
    'clientEdit.hobby.movies': '电影',
    'clientEdit.hobby.entertainment': '娱乐',
    'clientEdit.hobby.shopping': '购物',
    'clientEdit.hobby.hiking': '爬山',
    'clientEdit.hobby.travel': '旅游',
    'clientEdit.hobby.fishing': '钓鱼',
    'clientEdit.hobby.golf': '高尔夫',
    'clientEdit.hobby.yoga': '瑜伽',
    'clientEdit.hobby.swimming': '游泳',
    'clientEdit.hobby.reading': '看书',
    'clientEdit.hobby.climbing': '攀岩',
    'clientEdit.hobby.diving': '潜水',
    'clientEdit.hobby.walking': '散步',
    'clientEdit.hobby.cooking': '烹饪',
    'clientEdit.hobby.dancing': '跳舞',
    'clientEdit.hobby.pets': '宠物',
    'clientEdit.hobby.camping': '露营',
    'clientEdit.hobby.outdoor': '户外运动',
    'clientEdit.hobby.offroad': '越野',
    'clientEdit.hobby.shooting': '射击',
    'clientEdit.hobby.boardGames': '棋牌',
    'clientEdit.hobby.cycling': '自行车',
    'clientEdit.hobby.painting': '绘画',
    'clientEdit.hobby.gaming': '打游戏',
    'clientEdit.hobby.meditation': '冥想',
    'clientEdit.hobby.sailing': '出海',
    'clientEdit.hobby.wine': '品酒',
    'clientEdit.hobby.museum': '博物馆',
    'clientEdit.hobby.concerts': '音乐会',
    'clientEdit.hobby.theater': '看戏剧',
    'clientEdit.hobby.beach': '海滩度假',
    'clientEdit.hobby.horseRiding': '骑马',
    'clientEdit.hobby.hunting': '狩猎',
    'clientEdit.hobby.trekking': '徒步',
    'clientEdit.hobby.skiing': '滑雪',
    'clientEdit.lang.mandarin': '国语',
    'clientEdit.lang.cantonese': '粤语',
    'clientEdit.lang.englishBasic': '英文（简单）',
    'clientEdit.lang.englishDaily': '英文（日常）',
    'clientEdit.lang.englishFluent': '英文（流利）',
    'clientEdit.relocation.accept': '接受',
    'clientEdit.relocation.notNow': '暂时不接受',
    'clientEdit.relocation.partnerMove': '可接受对方搬过来',
    'clientEdit.purpose.marriage': '结婚',
    'clientEdit.purpose.dating': '交友',
    'clientEdit.priority.familyFirst': '家庭第一',
    'clientEdit.priority.careerFirst': '事业第一',
    'clientEdit.priority.looksFirst': '外貌第一',
    'clientEdit.priority.familyCareer': '家庭与事业并重',
    'clientEdit.priority.familyLooks': '家庭与外貌并重',
    'clientEdit.priority.careerLooks': '事业与外貌并重',
    'clientEdit.priority.balanced': '三者均衡',
    'clientEdit.rangeUnit.years': '岁',
    'clientEdit.rangeUnit.cm': 'CM',

    // Finance Page
    'finance.title': '财务',
    'finance.subtitle': '管理收入、支出和财务报表',
    'finance.totalRevenue': '总收入',
    'finance.totalExpenses': '总支出',
    'finance.netProfit': '净利润',
    'finance.outstanding': '未结清',
    'finance.overview': '概览',
    'finance.income': '收入',
    'finance.expenses': '支出',
    'finance.statements': '报表',
    'finance.incomeVsExpenses': '收入与支出',
    'finance.recentTransactions': '最近交易',
    'finance.noTransactionsYet': '暂无交易记录',
    'finance.date': '日期',
    'finance.description': '描述',
    'finance.category': '类别',
    'finance.type': '类型',
    'finance.amount': '金额',
    'finance.source': '来源',
    'finance.payee': '收款人',
    'finance.paidBy': '付款人',
    'finance.affiliate': '推荐人',
    'finance.reference': '参考编号',
    'finance.actions': '操作',
    'finance.auto': '自动',
    'finance.manual': '手动',
    'finance.autoGenerated': '自动生成',
    'finance.searchIncome': '搜索收入交易...',
    'finance.searchExpense': '搜索支出交易...',
    'finance.allCategories': '所有类别',
    'finance.from': '从',
    'finance.to': '至',
    'finance.addIncome': '添加收入',
    'finance.addExpense': '添加支出',
    'finance.noIncomeFound': '未找到收入交易',
    'finance.noExpenseFound': '未找到支出交易',
    'finance.adjustFilters': '请调整筛选条件或添加新记录',
    'finance.showing': '显示',
    'finance.of': '共',
    'finance.prev': '上一页',
    'finance.next': '下一页',
    'finance.financialStatement': '财务报表',
    'finance.monthly': '月度',
    'finance.annual': '年度',
    'finance.exportCsv': '导出CSV',
    'finance.loadingStatement': '加载报表中...',
    'finance.noStatementData': '暂无报表数据',
    'finance.selectPeriod': '请选择上方的时间段查看报表',
    'finance.totalIncome': '总收入',
    'finance.transactions': '交易',
    'finance.annualTransactions': '年度交易',
    'finance.noTransactionsForPeriod': '该时间段暂无交易',
    'finance.netTotal': '净额合计：',
    'finance.totalOutstanding': '总未结清',
    'finance.overdue': '逾期',
    'finance.pending': '待处理',
    'finance.partial': '部分支付',
    'finance.awaitingApproval': '待审批',
    'finance.awaiting': '待审批',
    'finance.outstandingInvoices': '未结清发票',
    'finance.searchInvoices': '搜索发票...',
    'finance.all': '全部',
    'finance.invoiceNumber': '发票编号',
    'finance.client': '客户',
    'finance.plan': '方案',
    'finance.status': '状态',
    'finance.total': '总计',
    'finance.paid': '已付',
    'finance.due': '待付',
    'finance.dueDate': '到期日',
    'finance.created': '创建时间',
    'finance.noOutstandingInvoices': '没有未结清发票',
    'finance.allPaymentsUpToDate': '所有付款均已更新',
    'finance.daysOverdue': '天逾期',
    'finance.dayOverdue': '天逾期',
    'finance.invoices': '张发票',
    'finance.editIncome': '编辑收入',
    'finance.editExpense': '编辑支出',
    'finance.updateIncome': '更新收入',
    'finance.updateExpense': '更新支出',
    'finance.createIncome': '创建收入',
    'finance.createExpense': '创建支出',
    'finance.dateLabel': '日期 *',
    'finance.amountLabel': '金额 *',
    'finance.descriptionLabel': '描述 *',
    'finance.categoryLabel': '类别',
    'finance.referenceLabel': '参考编号',
    'finance.payeeLabel': '收款人',
    'finance.notesLabel': '备注',
    'finance.enterDescription': '请输入描述...',
    'finance.optional': '可选',
    'finance.whoWasPaid': '付款给谁？',
    'finance.additionalNotes': '补充说明...',
    'finance.statementExported': '报表已导出',
    'finance.expense': '支出',
    'finance.viewAll': '查看全部',
    'finance.matchmaker': '红娘',
    'finance.transactionDetails': '交易详情',
    'finance.invoiceDetails': '发票详情',
    'finance.paymentMethod': '支付方式',
    'leaderCoach.deliverableUpdated': '可交付物状态已更新',
    'leaderCoach.updateFailed': '更新可交付物失败',
    'leaderCoach.viewPages': '查看页面',
    'leaderCoach.approveDeliverable': '批准可交付物',
    'leaderCoach.rejectDeliverable': '拒绝可交付物',
    'leaderCoach.pageNavigation': '第 {current}/{total} 页',
    'leaderCoach.previousPage': '上一页',
    'leaderCoach.nextPage': '下一页',

    // ─────────────────────────────────────────────────────────────────────
    // Free-form English literals → Chinese.
    // These keys are the English source text itself (callers do
    // `t('Save Profile')` instead of `t('profiles.save')`). Add new entries
    // here whenever you wrap a new English literal with `t()`. Auto-translate
    // is disabled, so missing keys fall through to the English source.
    // ─────────────────────────────────────────────────────────────────────
    'Able to buy house': '有能力买房',
    'Acceptable': '可接受',
    'Access Password': '访问密码',
    'Actions': '操作',
    'Agreement revoked': '协议已撤销',
    'All': '全部',
    'Are you sure you want to revoke this agreement?': '确定要撤销该协议吗？',
    'Associate': '大专',
    'Bachelor': '本科',
    'Background': '背景',
    'Bottom Line': '绝对底线',
    'Bottom line': '底线',
    'Buddhism': '佛教',
    'Cannot delete role with assigned users': '该角色已分配用户，无法删除',
    'Catholic': '天主教',
    'Children Status': '子女情况',
    'Christianity': '基督教',
    'Click below to generate / download the matchmaking card.': '点击下方按钮生成 / 下载相亲卡片。',
    'Click below to open the profile editor.': '点击下方按钮进入档案编辑页。',
    'Client': '客户',
    'Client confirm': '客户需确认',
    'Client confirm (copy link)': '客户需确认（复制链接）',
    'Client confirm link copied': '客户确认链接已复制',
    'Confirm': '确认',
    'Copy': '复制',
    'Copy client confirm link': '复制客户确认链接',
    'Copy failed': '复制失败',
    'Copied!': '已复制！',
    'Copy Link + Password': '复制链接 + 密码',
    'Copy profile text': '复制档案文本',
    'Create Match': '创建匹配',
    'Dating Schedules': '约会安排',
    'Details': '详情',
    'Divorced': '离异',
    "Don't have house": '没房',
    'Download QR': '下载二维码',
    'Edit': '编辑',
    'Edit questionnaire': '编辑问卷',
    'Education': '学历',
    'Email': '邮箱',
    'Enter rejection reason:': '输入拒绝原因：',
    'Failed': '失败',
    'Failed to bulk delete matches': '批量删除匹配失败',
    'Failed to bulk update matches': '批量更新匹配失败',
    'Failed to create match': '创建匹配失败',
    'Failed to delete match': '删除匹配失败',
    'Failed to delete profile: ': '删除档案失败：',
    'Failed to delete some profiles: ': '部分档案删除失败：',
    'Failed to load profile': '加载档案失败',
    'Failed to revoke agreement': '撤销协议失败',
    'Failed to start conversation. Please try again.': '创建会话失败，请重试。',
    'Failed to update match status': '更新匹配状态失败',
    'Failed to update status': '更新状态失败',
    'Female': '女',
    'Filters': '筛选条件',
    'FindU Love - Matchmaking Service Agreement': 'FindU Love 婚介服务协议',
    'General': '基本信息',
    'Generate': '生成',
    'Generate & copy login': '生成并复制登录',
    'Generate Card': '生成卡片',
    'Have house': '有房',
    'Have house in another country': '在其他国家有房',
    'High School': '高中',
    'Hinduism': '印度教',
    'Ideal': '理想标准',
    'Islam': '伊斯兰教',
    'Judaism': '犹太教',
    'Lifestyle': '生活方式',
    'Link & password copied!': '链接和密码已复制！',
    'Live with parent': '与父母同住',
    'Loading agreements...': '加载协议中...',
    'Loading candidates...': '正在加载候选人 ...',
    'Loading...': '正在加载 ...',
    'Loading profiles...': '加载档案中 ...',
    'Male': '男',
    'Mapping saved successfully': '映射保存成功',
    'Married': '已婚',
    'Master': '硕士',
    'Match created': '匹配已创建',
    'Match with {name}': '与 {name} 匹配',
    'Mate Preferences': '择偶偏好',
    'Max': '最大',
    'Maximum 10 gallery photos allowed': '相册最多 10 张照片',
    'Min': '最小',
    'Monthly Income': '月收入',
    'Name': '姓名',
    'Next': '下一页',
    'No': '否',
    'No candidates': '没有候选人',
    'No cities found': '未找到城市',
    'No countries found': '未找到国家',
    'No matching profiles found': '未找到匹配的档案',
    'No partner agreements found.': '未找到合伙人协议。',
    'No Religion': '无宗教',
    'No states found': '未找到省份',
    'None': '无',
    'Not provided': '未填写',
    'Offline ID': '线下ID',
    'Open': '打开',
    'Open Editor': '打开编辑器',
    'Other': '其他',
    'Password generated': '密码已生成',
    'Per page': '每页',
    'Personal': '个人信息',
    'PhD': '博士',
    'Please add at least one entry': '请至少添加一项',
    'Please fill in field name and label': '请填写字段名和标签',
    'Please fill in required fields (First Name, Last Name, Email)': '请填写必填字段（名字、姓氏、邮箱）',
    'Please fill in rule name, ID source field, and ID local field': '请填写规则名、ID 源字段和 ID 本地字段',
    'Please provide options for this field type': '请为该字段类型提供选项',
    'Please select an image file': '请选择图片文件',
    'Please select profiles to delete': '请选择要删除的档案',
    'Please select tables and create at least one field mapping': '请选择数据表并至少创建一项字段映射',
    'Please select two profiles to match': '请选择两个档案进行匹配',
    'Prefer not to say': '不愿透露',
    'Prev': '上一页',
    'Previous': '上一页',
    'Profile approved successfully': '档案审核通过',
    'Profile created successfully': '档案创建成功',
    'Profile deleted successfully': '档案删除成功',
    'Profile flagged for review': '档案已标记复审',
    'Profile photo must be less than 5MB': '头像必须小于 5MB',
    'Profile rejected': '档案已拒绝',
    'Profile updated successfully': '档案更新成功',
    'Public Link': '公开链接',
    'Real Estate': '房产',
    'Reason for rejection (optional):': '拒绝原因（可选）：',
    'Religion': '宗教',
    'Reset': '重置',
    'Revoke': '撤销',
    'Save Profile': '保存档案',
    'Saving...': '保存中...',
    'Search': '搜索',
    'Search by name / email / city...': '搜索姓名 / 邮箱 / 城市 ...',
    'Search by name, email, city...': '搜索姓名、邮箱、城市 ...',
    'Search name / email / city / offline ID...': '搜索姓名 / 邮箱 / 城市 / 线下ID...',
    'Search offline ID': '搜索线下ID',
    'Select country first': '请先选择国家',
    'Select state first': '请先选择省份',
    'Share': '分享',
    'Showing': '显示',
    'Signature': '签名',
    'Signed At': '签署时间',
    'Single': '未婚',
    'Status': '状态',
    'Status updated successfully': '状态更新成功',
    'Status updated': '状态已更新',
    'Submitting...': '提交中...',
    'Switch view mode': '切换显示效果',
    'System roles cannot be deleted': '系统角色无法删除',
    'Type': '类型',
    'Under $3,000': '低于 $3,000',
    'View': '查看',
    'Widowed': '丧偶',
    'Yes': '是',
    'Yes (Independent)': '有（独立）',
    'Yes (Living with me)': '有（和我同住）',
    'Yes (Living with other parent)': '有（和其他父母同住）',
    '{age} years old': '{age}岁',
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Default to Chinese for everyone (primary user base is Chinese-speaking).
    // One-time migration: flip any existing 'en' session to 'zh' once, then respect user choice.
    try {
      if (!localStorage.getItem('language_default_v2')) {
        localStorage.setItem('language', 'zh');
        localStorage.setItem('language_default_v2', '1');
      }
    } catch (_) {}
    return localStorage.getItem('language') || 'zh';
  });
  
  const [translations, setTranslations] = useState(() => {
    const saved = localStorage.getItem('customTranslations');
    if (saved) {
      try {
        const custom = JSON.parse(saved);
        return {
          en: { ...defaultTranslations.en, ...custom.en },
          zh: { ...defaultTranslations.zh, ...custom.zh }
        };
      } catch {
        return defaultTranslations;
      }
    }
    return defaultTranslations;
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  // Auto-translate cache for keys that aren't in the dictionary.
  // Populated lazily when `t()` is called with a missing free-form string.
  const [autoCache, setAutoCache] = useState({});
  const autoInflight = React.useRef(new Set());

  // Third-party translation disabled (blocked / slow from mainland China).
  // Missing keys now fall through to the source text only.
  const requestAutoTranslate = () => {};

  // Supported signatures:
  //   t(key)                    → look up, else key / auto-translate free-form
  //   t(key, params)            → look up + interpolate {name} placeholders
  //   t(key, defaultText)       → look up, else show + auto-translate defaultText
  //   t(key, defaultText, params) → combo: default with interpolation
  const t = (key, arg2, arg3) => {
    const defaultText = typeof arg2 === 'string' ? arg2 : null;
    const params = defaultText ? (arg3 || null) : (typeof arg2 === 'object' ? arg2 : null);

    let text = translations[language]?.[key] || translations['en']?.[key];

    if (!text) {
      // Key not in dictionary.
      const isDottedKey = /^[a-z][a-zA-Z0-9_]*(\.[a-zA-Z0-9_]+)+$/.test(key);
      if (defaultText) {
        // Use caller-provided default text, and auto-translate it.
        const cacheKey = `${language}|${defaultText}`;
        text = autoCache[cacheKey] || defaultText;
        if (!autoCache[cacheKey]) requestAutoTranslate(defaultText, language);
      } else if (!isDottedKey && /[A-Za-z\u4e00-\u9fff]/.test(key)) {
        // Free-form string passed as the key itself (e.g. t('Hello world')).
        const cacheKey = `${language}|${key}`;
        text = autoCache[cacheKey] || key;
        if (!autoCache[cacheKey]) requestAutoTranslate(key, language);
      } else {
        text = key;
      }
    }

    if (params) {
      Object.keys(params).forEach(param => {
        text = String(text).replace(new RegExp(`\\{${param}\\}`, 'g'), params[param]);
      });
    }
    return text;
  };

  const switchLanguage = (lang) => {
    setLanguage(lang);
  };

  const updateTranslation = (lang, key, value) => {
    setTranslations(prev => {
      const updated = {
        ...prev,
        [lang]: {
          ...prev[lang],
          [key]: value
        }
      };
      // Save custom translations to localStorage
      const custom = {
        en: {},
        zh: {}
      };
      Object.keys(updated.en).forEach(k => {
        if (updated.en[k] !== defaultTranslations.en[k]) {
          custom.en[k] = updated.en[k];
        }
      });
      Object.keys(updated.zh).forEach(k => {
        if (updated.zh[k] !== defaultTranslations.zh[k]) {
          custom.zh[k] = updated.zh[k];
        }
      });
      localStorage.setItem('customTranslations', JSON.stringify(custom));
      return updated;
    });
  };

  const addTranslation = (key, enValue, zhValue) => {
    setTranslations(prev => {
      const updated = {
        en: { ...prev.en, [key]: enValue },
        zh: { ...prev.zh, [key]: zhValue }
      };
      const custom = {
        en: {},
        zh: {}
      };
      Object.keys(updated.en).forEach(k => {
        if (updated.en[k] !== defaultTranslations.en[k]) {
          custom.en[k] = updated.en[k];
        }
      });
      Object.keys(updated.zh).forEach(k => {
        if (updated.zh[k] !== defaultTranslations.zh[k]) {
          custom.zh[k] = updated.zh[k];
        }
      });
      localStorage.setItem('customTranslations', JSON.stringify(custom));
      return updated;
    });
  };

  const deleteTranslation = (key) => {
    setTranslations(prev => {
      const updated = { ...prev };
      delete updated.en[key];
      delete updated.zh[key];
      const custom = {
        en: {},
        zh: {}
      };
      Object.keys(updated.en).forEach(k => {
        if (updated.en[k] !== defaultTranslations.en[k]) {
          custom.en[k] = updated.en[k];
        }
      });
      Object.keys(updated.zh).forEach(k => {
        if (updated.zh[k] !== defaultTranslations.zh[k]) {
          custom.zh[k] = updated.zh[k];
        }
      });
      localStorage.setItem('customTranslations', JSON.stringify(custom));
      return updated;
    });
  };

  const resetToDefaults = () => {
    localStorage.removeItem('customTranslations');
    setTranslations(defaultTranslations);
  };

  const getAllKeys = () => {
    const allKeys = new Set([
      ...Object.keys(translations.en),
      ...Object.keys(translations.zh)
    ]);
    return Array.from(allKeys).sort();
  };

  return (
    <LanguageContext.Provider value={{
      language,
      translations,
      t,
      switchLanguage,
      setLanguage: switchLanguage,
      updateTranslation,
      addTranslation,
      deleteTranslation,
      resetToDefaults,
      getAllKeys,
      defaultTranslations
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
